(
    function (skyward, $)
    {
        /**
         * @typedef {import("./types/skyward/reporting").PanelData} PanelData
         * @typedef {import("./types/skyward/reporting").ReportFieldProps} ReportFieldProps
         * @typedef {import("./types/skyward/reporting").SelectFieldHandler} SelectFieldHandler
         */

        "use strict";
        skyward.reporting = skyward.reporting || {};

        var _reportDesignerObserver = skyward.mvvm.newObserver(); // This is a temporary implementation of just the observer/observable portion of the new MVVM framework

        const CalculatedFields_Model = skyward.reporting.calculatedFields;

        skyward.reporting.currentReportDefinition = {}; // before long we should make this a model

        /**
         * For requesting fields in use in the report
         */
        skyward.reporting.fields = (function ()
        {
            const exposedMethods =
            {
                buildFieldListByModuleAndObject: function (schemaObject, relationshipPath, removeCalculatedInCSharpFields, removeColumnGroups, convertImages = true)
                {
                    const sections = getSectionsByModuleAndObject(schemaObject, relationshipPath);
                    if (sections.length)
                    {
                        return getUniqueFieldsForSections(sections, removeCalculatedInCSharpFields, removeColumnGroups, convertImages);
                    }
                    return [];
                },
                buildFieldsList: function (filterFields, sectionID, removeCalculatedInCSharpFields, removeColumnGroups, convertImages = true)
                {
                    const section = getSectionElementBySectionID(sectionID);
                    if (section.length)
                    {
                        return getUniqueFields(section, filterFields, removeCalculatedInCSharpFields, removeColumnGroups, convertImages);
                    }
                    return [];
                },
                buildFieldsListOfType: function (fieldDataType, filterFields, sectionID, removeCalculatedInCSharpFields, removeColumnGroups, convertImages = true)
                {
                    const section = getSectionElementBySectionID(sectionID);
                    if (section.length)
                    {
                        let fieldsOfType = getUniqueFields(section, filterFields, removeCalculatedInCSharpFields, removeColumnGroups, convertImages);

                        fieldsOfType = fieldsOfType.filter((field) =>
                        {
                            return dataTypesAreComparable(field.fieldType, fieldDataType);
                        });

                        return fieldsOfType;
                    }
                    return [];
                },
                buildCalculatedFieldsList: function (calculatedFields)
                {
                    let convertedFields = [];

                    calculatedFields.forEach((field) =>
                    {
                        const convertedField =
                        {
                            id: field.id,
                            calculatedFieldName: field.fieldName,
                            isCalculatedField: true,
                            fieldType: field.dataType,
                            displayName: field.fieldName,
                            displayPathText: field.fieldName,
                            svgClass: skyward.reporting.getSvgNameByDataType(field.dataType)
                        };

                        convertedFields.push(convertedField);
                    });

                    return convertedFields
                }
            };

            function getFieldsInSection(section, includeFilters, convertImages)
            {
                return skyward.reporting.getFieldsUsedInSection(section, includeFilters, convertImages);
            }

            function getUniqueFieldsForSections(sections, removeCalculatedInCSharpFields, removeColumnGroups, convertImages)
            {
                let fieldsInSections = [];

                sections.forEach(function (section)
                {
                    const fieldsInSection = getFieldsBySection(section, [], removeCalculatedInCSharpFields, removeColumnGroups, convertImages)
                    fieldsInSections.push(...fieldsInSection);
                });

                return filterOutDuplicateFields(fieldsInSections);
            }

            function getUniqueFields(section, filterFields, removeCalculatedInCSharpFields, removeColumnGroups, convertImages)
            {
                const fieldsInSection = getFieldsBySection(section, filterFields, removeCalculatedInCSharpFields, removeColumnGroups, convertImages);
                return filterOutDuplicateFields(fieldsInSection);
            }

            function getFieldsBySection(section, filterFields, removeCalculatedInCSharpFields, removeColumnGroups, convertImages)
            {
                const includeFilters = filterFields.length == 0;
                const fieldsInSection = [...getFieldsInSection(section, includeFilters, convertImages), ...filterFields];
                let fieldsToReturn = [];

                for (let field of fieldsInSection)
                {
                    // Column Groups have isCalculated set to true so they will also be removed by this check
                    if (removeCalculatedInCSharpFields && field.isCalculated)
                    {
                        continue;
                    }
                    if (removeColumnGroups && field.isColumnGroup)
                    {
                        continue;
                    }
                    fieldsToReturn.push(field);
                }

                return fieldsToReturn;
            }

            function filterOutDuplicateFields(fieldsInSection)
            {
                //filtering out duplicates based on unique absoluteFieldPaths, since it's the property that can be expected to be the same no matter what type of report element the field came from
                //It is expected that column groups will have the absoluteFieldPath set to it's fieldPath before we get here, since it normally won't have an absolute field path
                const fieldKey = 'absoluteFieldPath';
                fieldsInSection = [...new Map(fieldsInSection.map(item => [item[fieldKey], item])).values()];
                return fieldsInSection;
            }

            return exposedMethods;
        })();

        /**
         * Keeps track of the columns the user has currently selected
         */
        const currentSelectedColumns =
        {
            selectedColumnMap: new Map(),
            getColumnID: function (column)
            {
                return column.data("columnId");
            },
            addSelectedClass: function (column)
            {
                column.addClass("ui-selected");
            },
            removeSelectedClass: function (column)
            {
                column.removeClass("ui-selected");
            },
            getFirstSelectedColumn: function ()
            {
                return this.selectedColumnMap.values().next().value;
            },
            getLastSelectedColumn: function ()
            {
                return Array.from(this.getCurrentSelectedColumns()).pop();
            },
            getCurrentSelectedColumns: function ()
            {
                return this.selectedColumnMap.values();
            },
            getRowByFirstSelectedColumn: function ()
            {
                if (this.areColumnsSelected())
                {
                    return this.getFirstSelectedColumn().parent();
                }

                return null;
            },
            getRowByLastSelectedColumnBySectionID: function (sectionID)
            {
                if (this.areColumnsSelected())
                {
                    for (const column of Array.from(this.getCurrentSelectedColumns()).reverse())
                    {
                        const line = column.parent();
                        const section = line.closest(".jsSection");
                        const lineSectionID = section.data("sectionId");

                        if (lineSectionID == sectionID)
                        {
                            return line;
                        }
                    }
                }

                return null;
            },
            toggleColumn: function (column)
            {
                const columnID = this.getColumnID(column);
                if (!this.isColumnSelected(column))
                {
                    this.selectedColumnMap.set(columnID, column);
                    this.addSelectedClass(column);
                }
                else if (this.selectedColumnMap.size > 1) //prevent ctrl click from unselecting when only 1 column
                {
                    this.removeSelectedClass(column);
                    this.selectedColumnMap.delete(columnID);
                    updateColumnIsHiddenConditionFormatting(column);
                }
                this.runActionOnSelected(updateColumnIsHiddenConditionFormatting);
            },
            delete: function (column)
            {
                const columnID = this.getColumnID(column);
                this.selectedColumnMap.delete(columnID);
            },
            clear: function ()
            {
                let columns = Array.from(this.selectedColumnMap.values());
                this.selectedColumnMap.clear();

                for (let column of columns)
                {
                    this.removeSelectedClass(column);
                    updateColumnIsHiddenConditionFormatting(column);
                }
            },
            isColumnSelected: function (column)
            {
                const columnID = this.getColumnID(column);
                return this.selectedColumnMap.has(columnID);
            },
            isSingleColumnSelected: function ()
            {
                return this.selectedColumnMap.size == 1;
            },
            areColumnsSelected: function ()
            {
                return this.selectedColumnMap.size > 0;
            },
            areMultipleColumnsSelected: function ()
            {
                return this.selectedColumnMap.size > 1;
            },
            areColumnsSelectedAllOnOneLine: function ()
            {
                let firstFoundLineID = null;
                for (const column of this.getCurrentSelectedColumns())
                {
                    const lineID = column.parent().data("lineId");
                    if (!firstFoundLineID)
                    {
                        firstFoundLineID = lineID
                    }
                    else if (firstFoundLineID != lineID)
                    {
                        return false;
                    }
                }

                return this.selectedColumnMap.size > 0;
            },
            runActionOnSelected: function (action)
            {
                this.selectedColumnMap.forEach((column) =>
                {
                    action(column);
                });
            }
        };

        //Keyboard controls
        const keyboardCommands = (function ()
        {
            const exposedCommands =
            {
                isMultiSelectKeyPressed: (event) => isActionModifierKeyPressed(event),
                isSmartDragKeyPressed: (event) => isActionModifierKeyPressed(event),
                isSmartResizeKeyPressed: (event) => isActionModifierKeyPressed(event),
                isMoveToLeftColumnPressed: (event) => getEventKeys(event).ARROW_LEFT,
                isMoveToUpColumnPressed: (event) => getEventKeys(event).ARROW_UP,
                isMoveToRightColumnPressed: (event) => getEventKeys(event).ARROW_RIGHT,
                isMoveToDownColumnPressed: (event) => getEventKeys(event).ARROW_DOWN,
                isDeleteColumnPressed: (event) =>
                {
                    const eventKeys = getEventKeys(event);
                    return eventKeys.DELETE || eventKeys.BACKSPACE;
                },
                isColumnKeyPressed: (event) =>
                {
                    const eventKeys = getEventKeys(event);
                    return eventKeys.ARROW_LEFT || eventKeys.ARROW_UP || eventKeys.ARROW_RIGHT || eventKeys.ARROW_DOWN || eventKeys.DELETE || eventKeys.BACKSPACE;
                },
                isDeletePromptPressed: (event) =>
                {
                    const eventKeys = getEventKeys(event);
                    return eventKeys.BACKSPACE || eventKeys.DELETE;
                },
            };

            function isActionModifierKeyPressed(event)
            {
                const eventKeys = getEventKeys(event);
                return eventKeys.CTRL || eventKeys.META;
            }

            function getEventKeys(event)
            {
                return skyward.keys.getKeyNamesFromEvent(event);
            }

            return exposedCommands;
        })();

        const FORMATTER_NAMES = {
                // this is not a complete list, only those we need to reference in javascript
                DEFAULT: "Default",
                COPY_FIELD: "CopyField"
            },
            COLUMN_TYPES = skyward.reporting.COLUMN_TYPES = {
                HEADER_COLUMN: 'Skyward.Business.Reporting.HeaderColumn, Skyward.Business',
                FOOTER_COLUMN: 'Skyward.Business.Reporting.FooterColumn, Skyward.Business',
                AGGREGATE_COLUMN: 'Skyward.Business.Reporting.ReportAggregate, Skyward.Business',
                TEXT_COLUMN: 'Skyward.Business.Reporting.TextColumn, Skyward.Business',
                FORMTEXT_COLUMN: 'Skyward.Business.Reporting.FormTextColumn, Skyward.Business',
                IMAGE_COLUMN: 'Skyward.Business.Reporting.ReportImage, Skyward.Business',
                DATA_COLUMN: 'Skyward.Business.Reporting.DataColumn, Skyward.Business',
                DYNAMIC_IMAGE_COLUMN: 'Skyward.Business.Reporting.DynamicImageColumn, Skyward.Business',
                PAGE_SECTION_COLUMN: 'Skyward.Business.Reporting.PageSectionColumn, Skyward.Business',
                CALCULATED_COLUMN: 'Skyward.Business.Reporting.CalculatedColumn, Skyward.Business',
                GROUP_COLUMN: 'Skyward.Business.Reporting.GroupColumn, Skyward.Business',
                GROUP_COLUMN_HEADER: 'Skyward.Business.Reporting.GroupColumnHeader, Skyward.Business',
                GROUP_COLUMN_AGGREGATE: 'Skyward.Business.Reporting.GroupColumnAggregate, Skyward.Business',
                VARIABLE_COLUMN: 'Skyward.Business.Reporting.VariableColumn, Skyward.Business',
                FUTURE_VALUE_COLUMN: 'Skyward.Business.Reporting.FutureValueColumn, Skyward.Business',
            },
            LINE_TYPES = {
                DATA_LINE: 'Skyward.Business.Reporting.DataLine, Skyward.Business',
                HEADER_LINE: 'Skyward.Business.Reporting.HeaderLine, Skyward.Business',
                FOOTER_LINE: 'Skyward.Business.Reporting.FooterLine, Skyward.Business',
                AGGREGATE_LINE: 'Skyward.Business.Reporting.AggregateLine, Skyward.Business',
                SORT_AGGREGATE_LINE: 'Skyward.Business.Reporting.SortAggregateLine, Skyward.Business',
                SORT_HEADER_LINE: 'Skyward.Business.Reporting.SortHeaderLine, Skyward.Business'
            },
            SECTION_TYPES = {
                MAIN_SECTION: "Skyward.Business.Reporting.ReportSection, Skyward.Business",
                DETAIL_SECTION: "Skyward.Business.Reporting.DetailSection, Skyward.Business",
            },
            ADDITIONAL_TYPES = {
                TREE_BROWSE_FILTER: "Skyward.Web.Areas.Reporting.Controllers.ReportController+ReportingTreeBrowseFilter"
            },
            RESET_ON_TYPES = {
                NEVER_RESET: "_neverReset",
                MAIN_SECTIONS: "_mainSections",
                SECTION: "Section"
            },
            DATA_TYPES = skyward.reporting.DATA_TYPES = {
                NUMBER_DECIMAL: "Number Decimal",
                NUMBER_LONG: "Number Long",
                STRING: "String",
                BOOLEAN: "Boolean",
                DATETIME: "DateTime",
                TIME: "Time",
                TIMESPAN: "TimeSpan",
                GROUP_COLUMN: "Group Column"
            },
            PAGE_SECTION_COLUMN_DISPLAY_TYPES = {
                LABEL: "Label",
                DISPLAY_VALUE: "DisplayValue",
                PROMPT_VALUE: "PromptValue"
            },
            BARCODE_FONT_NAME = "Barcode39",
            HIDDEN_CLASS = "reportDesignerHidden",
            ALWAYS_HIDDEN_CLASS = "reportDesignerAlwaysHidden",
            CONDITIONALLY_HIDDEN_CLASS = "reportDesignerConditionallyHidden",
            ALWAYS_HIDDEN_VALUE = 2,
            CONDITIONALLY_HIDDEN_VALUE = 1,
            NOT_HIDDEN_VALUE = 0,
            MAX_COLUMN_WIDTH = 10000,
            MAX_COLUMN_HEIGHT = 10000,
            DEFAULT_COLUMN_WIDTH = 150,
            DEFAULT_COLUMN_HEIGHT = 18,
            SINGLE_LINE_HEADER_INDEX_BINDING = "{Index}";

        let defaultDataFont = "",
            defaultDataFontSize = "",
            defaultDataForeColor = "#000000",
            defaultDataBackColor = "",
            defaultDataFontBold = "",
            defaultDataFontItalic = "",
            defaultDataFontUnderline = "",
            defaultColumnPadding = "",
            underlineCssClass = "",
            boldCssClass = "",
            italicCssClass = "",
            reportDesignerProperties,
            columnWidth,
            columnHeight,
            lineHeight,
            editorColumnText,
            editorVariableToSet,
            editorMediaId,
            editorUseSystemLogo,
            columnDataPath,
            seekToColumnFieldButton,
            editorAggregateType,
            editorAggregateDelimiter,
            editorAggregateDelimiterContainer,
            pageSectionColumnDisplayTypeFS,
            pageSectionColumnDisplayType,
            pageSectionColumnPromptDisplayType,
            groupColumnSortHeaderTypes,
            positionX,
            positionY,
            editorPrintAtBottomOfPage,
            editorPrintReportFooterOnNewPage,
            editorRepeatLineAfterStart,
            editorRepeatRowOnPageIfDetailsPresent,
            editorRepeatHeaderForEachDataRow,
            editorVariablesToReset,
            editorPageBreakBeforeSectionPrints,
            editorDefaultSubjects,
            isHiddenSelector,
            whenHiddenOption,
            dataFont,
            dataForeColor,
            dataBackgroundColor,
            dataFontSize,
            rotationValue,
            rotationRange,
            dataBold,
            dataItalic,
            dataUnderline,
            dataFormatPreview,
            dataTextFormat,
            formatterControlsData,
            formatterControlsDataMap,
            borderStyle,
            borderTop,
            borderBottom,
            borderLeft,
            borderRight,
            borderWidth,
            borderColor,
            editorTextAlign,
            editorVerticalAlign,
            editorPaddingBottom,
            editorPaddingTop,
            editorPaddingRight,
            editorPaddingLeft,
            editorPrintIfEmpty,
            editorDisplayOnSingleLine,
            singleLineRecordCountFieldSetTemplate,
            editorEmptyLinesToPrint,
            editorColumnFilter,
            editorColumnFilterInput,
            editorColumnFilterLaunchButton,
            editorSectionAndLineDisplayName,
            sectionAndLineFullName,
            resetSectionAndLineDisplayName,
            runningTotal,
            resetOption,
            overlapMode,
            designer,
            changeTracker,
            leftSidePanel,
            usedFieldsMenu,
            allFieldsMenu,
            calculatedFieldMenu,
            aggregateFieldsMenu,
            promptFieldsMenu,
            environmentVariableFieldsMenu,
            variablesMenu,
            specialFieldsMenu,
            xmlExportSettingsMenu,
            csvExportSettingsMenu,
            fixedWidthExportSettingsMenu,
            jsonExportSettingsMenu,
            entireCanvas,
            currentRow,
            enumMaps,
            booleanAggregateOptions = {},
            numericAggregateOptions = {},
            stringAggregateOptions = {},
            dateTimeAggregateOptions = {},
            timeSpanAggregateOptions = {},
            delimitedAggregators = [],
            exportProperties =
            {
                xml:
                {
                    wrapper: null,
                    section:
                    {
                        wrapper: null,
                        nodeName: null
                    },
                    row:
                    {
                        wrapper: null,
                        nodeName: null
                    },
                    column:
                    {
                        wrapper: null,
                        nodeName: null,
                        nodeType: null
                    }
                },
                csv:
                {
                    wrapper: null,
                    delimiter: null,
                    customDelimiter: null,
                    textQualifier: null,
                    includeTextQualifierOnlyWhenRequired: null
                },
                fixedWidth:
                {
                    wrapper: null,
                    column:
                    {
                        wrapper: null,
                        startPosition: null,
                        length: null
                    },
                },
                json:
                {
                    wrapper: null,
                    column:
                    {
                        name: null
                    },
                    section:
                    {
                        name: null
                    }
                }

            };

        skyward.reporting.getSvgNameByDataType = function (dataType)
        {
            var svgName = "CheckBox";

            if (dataType != null)
            {
                if (dataType.startsWith("Number") || dataType == "Long" || dataType == "Int" || dataType == "Short" || dataType == "Byte" || dataType == "Decimal")
                {
                    svgName = "Number";
                }
                else if (dataType.startsWith(DATA_TYPES.STRING))
                {
                    svgName = "TextCharacter";
                }
                else if (dataType == DATA_TYPES.DATETIME)
                {
                    svgName = "Date";
                }
                else if (dataType == DATA_TYPES.TIME)
                {
                    svgName = "Time";
                }
                else if (dataType == DATA_TYPES.TIMESPAN)
                {
                    svgName = "Timespan";
                }
                else if (dataType == "Image")
                {
                    svgName = "InsertImage";
                }
                else if (dataType == DATA_TYPES.GROUP_COLUMN)
                {
                    svgName = "Columns";
                }
            }

            return svgName;
        };

        var getSvgByDataType = function (dataType, svgType)
        {
            return skyward.svg.getSvg(skyward.reporting.getSvgNameByDataType(dataType), svgType || 'TreeBrowse');
        };

        var dataTypesAreComparable = function (leftDataType, rightDataType)
        {
            if (leftDataType === rightDataType)
            {
                return true;
            }
            else if (leftDataType == undefined || rightDataType == undefined)
            {
                return false;
            }
            else if (leftDataType.startsWith("Number") && rightDataType.startsWith("Number"))
            {
                return true;
            }

            return false;
        };

        var processKeyDown = function (e)
        {
            if (!keyboardCommands.isColumnKeyPressed(e))
            {
                return;
            }

            if (!$('.modalLayerContainer').length && $(document.activeElement).hasClass('reportColumn'))
            {
                e.preventDefault();

                let rowColumns;
                let selectedRow;

                if (currentSelectedColumns.areColumnsSelected())
                {
                    selectedRow = currentSelectedColumns.getRowByFirstSelectedColumn();
                    rowColumns = selectedRow.find('.reportColumn');
                }
                else
                {
                    return;
                }

                const allRows = getAllNonPreviewLines().has(".reportColumn");

                if (allRows.find(".reportColumn").length === 0)
                {
                    return;
                }

                let newColumn;
                let newRow;

                const mostRecentSelectedColumn = currentSelectedColumns.getLastSelectedColumn();
                const currentColumnIndex = rowColumns.index(mostRecentSelectedColumn);
                const selectedRowIndex = allRows.length === 0 ? -2 : allRows.index(selectedRow);

                if (keyboardCommands.isDeleteColumnPressed(e))
                {
                    removeColumns(currentSelectedColumns.getCurrentSelectedColumns(), true, true);
                }
                else if (keyboardCommands.isMoveToLeftColumnPressed(e))
                {
                    if (currentColumnIndex > 0)
                    {
                        newColumn = mostRecentSelectedColumn.prev();
                    }
                }
                else if (keyboardCommands.isMoveToRightColumnPressed(e))
                {
                    if (currentColumnIndex !== rowColumns.length - 1)
                    {
                        newColumn = mostRecentSelectedColumn.next();
                    }
                }
                else if (keyboardCommands.isMoveToUpColumnPressed(e))
                {
                    if (selectedRowIndex > 0)
                    {
                        newRow = $(allRows[selectedRowIndex - 1]);
                    }
                }
                else if (keyboardCommands.isMoveToDownColumnPressed(e))
                {
                    if (selectedRowIndex < allRows.length - 1)
                    {
                        newRow = $(allRows[selectedRowIndex + 1]);
                    }
                }

                const clearSelectedColumns = !keyboardCommands.isMultiSelectKeyPressed(e);

                if (newColumn)
                {
                    setSelectedColumn(newColumn, clearSelectedColumns);
                }

                if (newRow)
                {
                    setActiveSection(newRow.closest('.jsSection'));
                    setSelectedRowByRow(newRow);

                    let newRowColumns = newRow.find(".reportColumn");
                    if (newRowColumns.length > 0)
                    {
                        if (newRowColumns.length - 1 < currentColumnIndex)
                        {
                            setSelectedColumn($(newRowColumns[newRowColumns.length - 1]), clearSelectedColumns);
                        }
                        else
                        {
                            setSelectedColumn($(newRowColumns[currentColumnIndex]), clearSelectedColumns);
                        }
                    }
                }
            }
        };

        var labelBuilder = (function ()
        {
            var rootList;
            function initializeLabelBuilder()
            {
                rootList = designer.find(".reportLabels:visible");

                rootList.on("click", "li.dataLineLabel", labelClickToDataLine).on("click", "ul", listClickToSection);
                entireCanvas.on("resize", "div.reportLine", dataLineResizeToLabel).on("expand collapse", "div.jsSection", sectionExpandCollapseToList);
            }

            function getRelatedLabel(sectionOrRow)
            {
                if (sectionOrRow.is(".jsSection"))
                {
                    return sectionOrRow.find(".sectionToolbar").data("relatedLabel");
                }
                return sectionOrRow.data("relatedLabel");
            }

            function setLabelHeight(selectedRow, newHeight)
            {
                getRelatedLabel(selectedRow)?.css("line-height", newHeight + "px").height(newHeight);
            }

            function setRelatedLabel(selectedRow, newLabel)
            {
                getRelatedLabel(selectedRow).find(".labelText").text(newLabel);
            }

            function buildLabelColumns(recalculateAll)
            {
                if (!rootList || !rootList.is(":visible"))
                {
                    initializeLabelBuilder();
                }

                let labelListHeader = rootList.children("li").first();
                if (recalculateAll)
                {
                    designer.find(".collapseSection").addClass("doCollapseSection").removeClass("collapseSection");
                    rootList.html(labelListHeader);
                }

                let sectionsAndDataLinesToProcess = entireCanvas.find("div[data-report-label-text]" + (recalculateAll ? "" : ":not(.labelCreated)"));
                if (sectionsAndDataLinesToProcess.length > 0)
                {
                    let currentList = rootList;
                    let currentSectionContainer = null;
                    let lastLabel = $();
                    sectionsAndDataLinesToProcess.each(function ()
                    {
                        let sectionOrDataLine = $(this);

                        let labelText = sectionOrDataLine.data("reportLabelText");

                        let labelTextContainer = $("<span class='labelText labelMatchParent'></span>").text(labelText);
                        let label = $("<li></li>").append(labelTextContainer);
                        sectionOrDataLine.data("relatedLabel", label);
                        let actualSectionContainer;
                        if (sectionOrDataLine.is(".sectionToolbar"))
                        {
                            let section = sectionOrDataLine;
                            label.addClass("reportSectionLabel");

                            let parentList;
                            actualSectionContainer = section.parent();
                            updateIsHiddenConditionClass(label, getSectionOrRowHiddenConditionType(actualSectionContainer));

                            let isDetailsSection = actualSectionContainer.hasClass("detailsSection");
                            let isMainSection = actualSectionContainer.hasClass("jsMainSection");

                            if (!currentSectionContainer || actualSectionContainer.parent().is(currentSectionContainer))
                            {
                                // Child section (root falls through to here too)
                                parentList = currentList;
                            }
                            else
                            {
                                let actualParentSection = actualSectionContainer.parent().closest(".jsSection");
                                if (actualParentSection.length)
                                {
                                    // We're in an ancestor section. The correct list is stored in the header's data()
                                    parentList = actualParentSection.data("relatedList");
                                }
                                else
                                {
                                    // All details sections completed, back to root.
                                    parentList = rootList;
                                }
                            }

                            // Make a new list for this section
                            let currentListClasses = "reportLabels nested" + ((isDetailsSection || isMainSection) ? " sectionLabels" : "") + (isMainSection ? " mainSectionLabels" : "");
                            currentList = $('<ul class="' + currentListClasses + '"></ul>')
                                .data("relatedSection", section)
                                .appendTo($("<li/>").appendTo(parentList));

                            currentSectionContainer = actualSectionContainer;
                            currentSectionContainer.data("relatedList", currentList);
                        }
                        else
                        {
                            label.addClass("dataLineLabel");
                            let dataLine = sectionOrDataLine;
                            actualSectionContainer = dataLine.closest(".jsSection");
                            updateIsHiddenConditionClass(label, getSectionOrRowHiddenConditionType(dataLine));

                            // DataLines must be placed after at least one section header
                            if (!actualSectionContainer.is(currentSectionContainer))
                            {
                                // Moved out of one or more details-sections. The new parent-section is holding a reference to the related list.
                                currentList = actualSectionContainer.data("relatedList");
                                currentSectionContainer = actualSectionContainer;
                            }
                            label.data("relatedDataLine", dataLine);
                        }

                        // Set label height to match section. Line-height centers the text.
                        let sectionHeight = sectionOrDataLine.outerHeight();
                        label.appendTo(currentList).css("line-height", sectionHeight + "px").height(sectionHeight);

                        let sectionTop = sectionOrDataLine.position().top;
                        let margin = Math.round(sectionTop - label.position().top);
                        lastLabel.css("margin-bottom", margin + "px");
                        lastLabel = label;

                        label.toggleClass("row-selected", sectionOrDataLine.is(".row-selected"));
                    });
                }

                designer.find(".doCollapseSection")
                    .removeClass("doCollapseSection")
                    .addClass("collapseSection")
                    .trigger("collapse");
            }

            function listClickToSection()
            {
                var relatedSection = $(this).data("relatedSection");
                if (relatedSection)
                {
                    relatedSection.click();
                }
                return false;
            }

            function labelClickToDataLine()
            {
                var relatedDataLine = $(this).data("relatedDataLine");
                if (relatedDataLine && typeof relatedDataLine.click === "function")
                {
                    relatedDataLine.click();
                }
                return false;
            }

            function sectionExpandCollapseToList(event)
            {
                $(this).data("relatedList").toggleClass("collapsed", event.type == "collapse");
                return false;
            }

            function dataLineResizeToLabel()
            {
                var dataLine = $(this);
                var height = dataLine.outerHeight() + "px";
                var relatedLabel = dataLine.data("relatedLabel");

                if (!dataLine.is(":visible"))
                {
                    relatedLabel?.css("display", "none");
                }
                else
                {
                    relatedLabel?.css("display", "");
                }

                relatedLabel?.css({ "line-height": height, "height": height });
            }

            return {
                getRelatedLabel: getRelatedLabel,
                setLabelHeight: setLabelHeight,
                setRelatedLabel: setRelatedLabel,
                buildLabelColumns: buildLabelColumns
            };
        })();

        var initCanvas = function (selector)
        {
            $(selector).each(function ()
            {
                if (!designer)
                {
                    skyward.error.registerErrorDataGatherer("reporting", gatherDataForError);
                }
                designer = $(this);
                changeTracker = skyward.changeTracking.getChangeTracker(designer);
                _reportDesignerObserver.stopObservingEverything();
                //@TODO This will only work until the UI framework changes. It needs to be brought back into compliance when time allows.
                designer.closest('.menuContentArea')
                    .find(".tabListContainer").hide()
                    .next().css("left", 0);

                $(".contentArea, .currentRecordContent").css({ "margin-left": "0px" });

                designer.find('.reportPropertiesPanelToggle').on('click', function ()
                {
                    var icon = $(this).find("svg");
                    var newIconName = reportDesignerProperties.toggleClass("collapsed").hasClass("collapsed") ? "ChevronUp" : "ChevronDown";
                    skyward.svg.swapSvg(icon, newIconName, "BrowseLevel");
                    $(window).trigger("resize");
                });

                enumMaps = skyward.reporting.enummaps = JSON.parse(designer.find(".enumMaps").val());

                $.each(skyward.reporting.enummaps.AggregateNamesWithAggregatorAttribute, function (optionName, aggregateAttribute)
                {
                    if (aggregateAttribute.IsBooleanAggregator)
                    {
                        booleanAggregateOptions[optionName] = optionName.toDisplayName();
                    }
                    if (aggregateAttribute.IsDateTimeAggregator)
                    {
                        dateTimeAggregateOptions[optionName] = optionName.toDisplayName();
                    }
                    if (aggregateAttribute.IsNumericAggregator)
                    {
                        numericAggregateOptions[optionName] = optionName.toDisplayName();
                    }
                    if (aggregateAttribute.IsStringAggregator)
                    {
                        stringAggregateOptions[optionName] = optionName.toDisplayName();
                    }
                    if (aggregateAttribute.IsTimeSpanAggregator)
                    {
                        timeSpanAggregateOptions[optionName] = optionName.toDisplayName();
                    }
                    if (aggregateAttribute.IsDelimitedAggregator)
                    {
                        delimitedAggregators.push(optionName);
                    }
                });

                setGlobalSelectors();

                entireCanvas.disableSelection();

                entireCanvas.on("click", ".lineWrapper:not(.jsPrintOnOneLinePreviewLine)", function ()
                {
                    var target = $(this);
                    $(document.activeElement).blur();
                    setActiveSection(target.closest('.jsSection'));
                    setSelectedRowByRow(target);
                    return false;
                });

                entireCanvas.on("click", ".lineWrapper:not(.jsPrintOnOneLinePreviewLine) .jsReportAggregate, .lineWrapper:not(.jsPrintOnOneLinePreviewLine) .jsFieldColumn", function (event)
                {
                    const clearSelectedColumns = !keyboardCommands.isMultiSelectKeyPressed(event);

                    const target = $(this);
                    $(document.activeElement).blur();
                    setActiveSection(target.closest('.jsSection'), clearSelectedColumns);
                    setSelectedRowByColumn(target);
                    setSelectedColumn(target, clearSelectedColumns);
                    return false;
                });

                entireCanvas.on("dblclick", ".jsFieldColumn[data-calculation]", function ()
                {
                    var calculatedColumn = $(this);
                    var calculatedColumnData = calculatedColumn.data();

                    var data =
                    {
                        fieldName: calculatedColumnData.designerText,
                        displayCalculation: calculatedColumnData.displayCalculation,
                        calculation: calculatedColumnData.calculation,
                        calculationJson: calculatedColumnData.calculationJson,
                        columnId: calculatedColumnData.columnId
                    };
                    getCalculatedColumnBuilder(data, calculatedColumn);
                });

                seekToColumnFieldButton.on('click', seekToColumnField);

                // canvas keyboard nav
                $(document).off('keydown', processKeyDown).on('keydown', '.reportDesignerGrid', processKeyDown);

                setPropertiesEventHandlers();

                resetAllStringDataAttributes();

                initLeftSidePanel();

                // Correct differences between JSON.stringify() and the Json provided by the server
                var editorHiddenData = designer.find(".editorHiddenData");
                var originalJson = editorHiddenData.data("original-value");
                editorHiddenData.val(JSON.stringify(originalJson));

                CalculatedFields_Model.Reset(originalJson.CalculatedFields);

                skyward.reporting.searchParameters.viewModel.init(originalJson.SearchParameters);
                _reportDesignerObserver.observe(skyward.reporting.searchParameters.model, function ()
                {
                    if (!this.massUpdateInProgress)
                    {
                        updateHiddenField();
                        skyward.reporting.prompts.populateExtraColumnOptionsWithPrompts(designer.find(".reportDesignerPromptFieldsMenu"));
                    }
                });

                // Variables
                initializeVariables(originalJson.Variables, leftSidePanel.find(".variablesView"), entireCanvas);

                skyward.reporting.prompts.importPrompts(originalJson.Prompts);

                var aggregateCalculationColumns = [];
                getAllSections().each(function ()
                {
                    var section = $(this);
                    var sectionID = section.data("sectionId");

                    getSectionAllAggregateLines(section).each(function ()
                    {
                        var aggregateLine = $(this);
                        var lineID = aggregateLine.data("lineId");

                        getColumnsInLine(aggregateLine).each(function ()
                        {
                            var column = $(this);
                            var columnData = column.data();
                            if (columnData.fieldColumnType == COLUMN_TYPES.CALCULATED_COLUMN)
                            {
                                aggregateCalculationColumns.push({
                                    columnId: columnData.columnId,
                                    name: columnData.designerText,
                                    sectionID: sectionID,
                                    lineID: lineID,
                                    calculation: columnData.calculation
                                });
                            }
                        });
                    });
                });

                skyward.reporting.aggregateCalculationCollection.populate(aggregateCalculationColumns);

                var dirtyOnLoad = editorHiddenData.data("dirtyOnLoad");
                var newValue = buildHiddenFieldJson();
                var originalValue = dirtyOnLoad ? {} : newValue;
                editorHiddenData.data("original-value", originalValue);
                updateDirtyStatus(editorHiddenData, originalValue, newValue);

                //update styling of sections and rows
                for (let reportLine of designer.find(".reportLine"))
                {
                    let row = $(reportLine);
                    applyRowCss(row);

                    for (let column of row.find(".jsColumn"))
                    {
                        applyColumnCss($(column));
                    }
                }

                labelBuilder.buildLabelColumns(false);
                setInitialSelectedRow();

                designer.find('.userConditionValueSelectorButton').on('click', function ()
                {
                    // not supported when multiple columns are selected
                    let section = getCurrentSection();
                    if (section.length > 0)
                    {
                        const sectionData = section.data();
                        let sectionDisplayOnSingleLine = sectionData.displayOnSingleLine;

                        const parentDisplayOnSingleLine = isParentDisplayOnSingleLine(section);

                        let allowCurrentColumnValue = false;
                        let currentColumnFieldType = undefined;
                        if (currentSelectedColumns.areColumnsSelected())
                        {
                            const currentSelectedColumnData = currentSelectedColumns.getFirstSelectedColumn().data();
                            currentColumnFieldType = currentSelectedColumnData.fieldType;
                            allowCurrentColumnValue = currentSelectedColumnData && !currentSelectedColumnData.isFutureValue &&
                                currentSelectedColumnData.allowCurrentValueHiding === true && currentSelectedColumnData.fieldType !== null &&
                                !currentSelectedColumnData.fieldType?.startsWith("Other-") && currentSelectedColumnData.fieldType !== "List";
                        }

                        const line = currentRow || getParentLine(currentSelectedColumns.getFirstSelectedColumn());

                        let isAggregateLine = isAggregateLineType(line.data("lineType"));

                        if (sectionDisplayOnSingleLine)
                        {
                            allowCurrentColumnValue = allowCurrentColumnValue && !parentDisplayOnSingleLine && isAggregateLine;
                        }

                        let allowFieldSelection = !sectionDisplayOnSingleLine && line.data("lineType") === LINE_TYPES.DATA_LINE;

                        let columns = [];
                        if (!parentDisplayOnSingleLine && isAggregateLine)
                        {
                            columns = buildReportColumnCollection(getColumnsInLine(line));
                        }

                        const allPrompts = skyward.reporting.prompts.promptCollection.toJsonObject();

                        const allowOptions =
                        {
                            fieldSelection: allowFieldSelection,
                            currentColumnValue: allowCurrentColumnValue,
                            prompts: true,
                            alwaysHidden: true,
                            detailSectionsHaveAnyRows: false,
                        }

                        let operandButton = $(this);
                        if (operandButton.hasClass("leftOperand"))
                        {
                            if (line.hasClass("jsSortAggregateLine") && getNestedDetailSections(section).length)
                            {
                                const relatedSort = getSortDataByReportSortID(section, line.data("relatedReportSortId"));
                                const sortFieldName = relatedSort.RelativeDataPath?.toLowerCase();
                                const sectionPrimaryKeyFieldName = sectionData.primaryKeyFieldName.toLowerCase();
                                allowOptions.detailSectionsHaveAnyRows = sortFieldName === sectionPrimaryKeyFieldName;
                            }
                            isHiddenSelector.getUserConditionOperandFilterValueSelector(operandButton, allowOptions, columns, allPrompts, null, currentColumnFieldType, true, valueSelectorClickEvents, null, sectionData.sectionId);
                        }
                        else
                        {
                            let leftOperandFieldType = getEditorFieldDataType(isHiddenSelector.elements.left);
                            let allowCurrentColumnValueOnRight = allowCurrentColumnValue && dataTypesAreComparable(leftOperandFieldType, currentColumnFieldType);
                            let leftPromptUniqueID = isHiddenSelector.elements.left.data().promptUniqueId;

                            allowOptions.currentColumnValue = allowCurrentColumnValueOnRight;

                            if (leftOperandFieldType)
                            {
                                isHiddenSelector.getUserConditionOperandFilterValueSelector(operandButton, allowOptions, columns, allPrompts, leftOperandFieldType, currentColumnFieldType, false, valueSelectorClickEvents, leftPromptUniqueID);
                            }
                            else
                            {
                                isHiddenSelector.getUserConditionOperandFilterValueSelector(operandButton, allowOptions, columns, allPrompts, currentColumnFieldType, currentColumnFieldType, false, valueSelectorClickEvents, leftPromptUniqueID);
                            }
                        }
                    }
                });

                skyward.fieldset.alignTitles(leftSidePanel);

                $.each(designer.find(".jsSection:not(.jsHeaderFooterSection)"), function (index, value)
                {
                    var section = $(value);
                    var sectionData = section.data();
                    var pageWidth = skyward.reporting.currentReportDefinition.PrintablePageWidth;
                    var unusableWidth = sectionData.columnWidth === "" ? 0 : (pageWidth - sectionData.columnWidth) * 72;

                    var unusableAreaBox = skyward.reporting.multiColumnSection.generateUnusableAreaBox(unusableWidth);

                    updateUnsableAreaBox(section, unusableAreaBox, sectionData.applyColumnsToDataLinesOnly);
                });
            });
        };

        const resetAllStringDataAttributes = function()
        {
            // reset string data attributes because jquery.data() will try to convert the values into their corresponding JavaScript data types otherwise
            const stringAttributes = ['designer-text', 'display-name', 'field-name', 'display-path', 'absolute-field-path', 'report-label-text', 'default-line-label'];
            const resetStringAttributes = function (obj)
            {
                for (const attr of stringAttributes)
                {
                    let attributeValue = obj.attr(`data-${attr}`);
                    if (attributeValue !== undefined)
                    {
                        obj.data(attr, attributeValue);
                    }
                }
            };
            getAllSections().each(function ()
            {
                const section = $(this);
                resetStringAttributes(section);
                getSectionLines(section).each(function ()
                {
                    const line = $(this);
                    resetStringAttributes(line);
                    getColumnsInLine(line).each(function ()
                    {
                        const column = $(this);
                        resetStringAttributes(column);
                    });
                });
            });
        }

        var seekToColumnField = function ()
        {
            // not supported when multiple columns are selected
            const currentSelectedColumn = currentSelectedColumns.getFirstSelectedColumn();
            const columnData = currentSelectedColumn.data();
            const sectionData = currentSelectedColumn.closest('.jsSection').data();

            var treeBrowse = skyward.treebrowse.getTreeBrowseContainer("reportObjectBrowser");
            var treeBrowseData = treeBrowse.data();

            // a sourceColumnId indicates that this field was added from the aggregate field list
            if (!columnData.sourceColumnId && columnData.absoluteFieldPath)
            {
                $.ajax(
                    {
                        url: skyward.global.virtualDir + "Reporting/Report/GetSeekToFieldData",
                        type: "POST",
                        dataType: "JSON",
                        data:
                        {
                            absoluteFieldPath: columnData.absoluteFieldPath,
                            rootModuleName: sectionData.rootModule,
                            rootObjectName: sectionData.rootObject
                        }
                    }).done(function (data)
                    {
                        if (typeof treeBrowseData.replaceTreeBrowseData === 'function')
                        {
                            skyward.collapsePanel.show($('.js-reportDesignerFieldsMenu'));

                            treeBrowseData.replaceTreeBrowseData(data.moduleName, data.objectName, data.positionObjectPath, null,
                                data.objectDisplayName, data.breadCrumbData, sectionData.rootModule, sectionData.rootObject,
                                data.oneToManyPath, false, true).done(function ()
                                {
                                    treeBrowse.find('.parentSectionComboBox option[data-absolute-tree-browse-object-path="' + data.oneToManyPath + '"]').prop('selected', 'selected');
                                    treeBrowseData.selectRecord(columnData.fieldName);
                                });

                        }
                    });
            }
            else if (columnData.sourceColumnId)
            {
                openPanelAndSelectField("aggregate_" + columnData.sourceColumnId);
            }
            else if (columnData.operationId)
            {
                openPanelAndSelectField(columnData.operationId);
            }
        };

        var openPanelAndSelectField = function (fieldKey)
        {
            var treeBrowseData = skyward.treebrowse.getTreeBrowseDataByName("reportObjectBrowser");
            treeBrowseData.selectRecord(fieldKey);
            var selectedRecord = treeBrowseData.selectedRecord;
            skyward.collapsePanel.show(selectedRecord.closest(".collapsePanel"));
            selectedRecord.scrollintoview();
        };

        var updateUnsableAreaBox = function (section, unusableAreaBox, applyColumnsToDataLinesOnly)
        {
            var sectionBody = section.find(".sectionBody:first");
            var lines;
            if (applyColumnsToDataLinesOnly === true || applyColumnsToDataLinesOnly === "true")
            {
                lines = sectionBody.find(".reportLine.jsDataLine");
            }
            else
            {
                lines = sectionBody.find(".reportLine");
            }

            sectionBody.find(".unusableArea").remove();
            lines.prepend(unusableAreaBox);
        };

        var setInitialSelectedRow = function ()
        {
            var mainSection = getAllMainSections().first();

            if (mainSection.length)
            {
                setSelectedRowByRow(getSectionDataLines(mainSection).first());
            }
            else
            {
                setSelectedRowByRow(getPageHeaderLine());
            }
        };

        var setGlobalSelectors = function ()
        {
            //canvas
            entireCanvas = designer.find('.reportDesignerCanvas');

            leftSidePanel = designer.find(".reportDesignerLeftSidePanel");

            // left side panel collapsables
            usedFieldsMenu = leftSidePanel.find('.js-usedFieldsPanel .collapsePanelContents');
            allFieldsMenu = leftSidePanel.find('.js-reportDesignerFieldsMenu .collapsePanelContents');
            calculatedFieldMenu = leftSidePanel.find('.reportDesignerCalculatedFieldsMenu .collapsePanelContents');
            aggregateFieldsMenu = leftSidePanel.find('.reportDesignerAggregateFieldsMenu .collapsePanelContents');
            promptFieldsMenu = leftSidePanel.find('.reportDesignerPromptFieldsMenu .collapsePanelContents');
            environmentVariableFieldsMenu = leftSidePanel.find('.reportDesignerEnvironmentVariableFieldsMenu .collapsePanelContents');
            variablesMenu = leftSidePanel.find('.reportDesignerVariablesMenu .collapsePanelContents');
            specialFieldsMenu = leftSidePanel.find('.reportDesignerSpecialFieldsMenu .collapsePanelContents');
            xmlExportSettingsMenu = leftSidePanel.find('.jsXMLExportProperties .collapsePanelContents');
            csvExportSettingsMenu = leftSidePanel.find('.reportDesignerCSVProperties .collapsePanelContents');
            fixedWidthExportSettingsMenu = leftSidePanel.find('.jsFixedWidthExportProperties .collapsePanelContents');
            jsonExportSettingsMenu = leftSidePanel.find('.jsJSONExportProperties .collapsePanelContents');

            //field properties area
            reportDesignerProperties = designer.find(".reportProperties");

            columnWidth = reportDesignerProperties.find(".columnWidth");
            columnHeight = reportDesignerProperties.find(".columnHeight");
            lineHeight = reportDesignerProperties.find(".lineHeight");
            editorColumnText = reportDesignerProperties.find(".editorColumnText");
            editorVariableToSet = reportDesignerProperties.find(".editorVariableToSet");
            editorMediaId = reportDesignerProperties.find(".editorMediaId");
            editorUseSystemLogo = reportDesignerProperties.find(".editorUseSystemLogo");
            columnDataPath = reportDesignerProperties.find(".columnDataPath");
            seekToColumnFieldButton = reportDesignerProperties.find(".seekToColumnField");
            positionX = reportDesignerProperties.find(".positionX");
            positionY = reportDesignerProperties.find(".positionY");

            editorAggregateType = reportDesignerProperties.find(".editorAggregateTypes");
            editorAggregateDelimiter = reportDesignerProperties.find(".editorAggregateDelimiters");
            editorAggregateDelimiterContainer = editorAggregateDelimiter.parent();

            pageSectionColumnDisplayTypeFS = reportDesignerProperties.find(".pageSectionColumnDisplayTypeFS");
            pageSectionColumnDisplayType = reportDesignerProperties.find(".pageSectionColumnComboBox");
            pageSectionColumnPromptDisplayType = reportDesignerProperties.find(".pageSectionColumnPromptComboBox");

            groupColumnSortHeaderTypes = reportDesignerProperties.find(".editorGroupColumnSortHeaderTypes");

            editorPrintAtBottomOfPage = reportDesignerProperties.find(".editorPrintAtBottomOfPage");
            editorPrintReportFooterOnNewPage = reportDesignerProperties.find(".editorPrintReportFooterOnNewPage");
            editorRepeatLineAfterStart = reportDesignerProperties.find(".editorRepeatLineAfterStart");
            editorRepeatRowOnPageIfDetailsPresent = reportDesignerProperties.find(".editorRepeatRowOnPageIfDetailsPresent");
            editorRepeatHeaderForEachDataRow = reportDesignerProperties.find(".editorRepeatHeaderForEachDataRow");
            editorVariablesToReset = reportDesignerProperties.find(".editorVariablesToReset");
            editorPageBreakBeforeSectionPrints = reportDesignerProperties.find(".editorPageBreakBeforeSectionPrints");

            editorDefaultSubjects = reportDesignerProperties.find(".editorDefaultSubjects");

            //data format panel
            dataFont = reportDesignerProperties.find(".editorFont");
            dataForeColor = reportDesignerProperties.find(".editorForeColor");
            dataBackgroundColor = reportDesignerProperties.find(".editorBackgroundColor");
            dataFontSize = reportDesignerProperties.find(".editorFontSize");
            dataBold = reportDesignerProperties.find(".editorBold");
            dataItalic = reportDesignerProperties.find(".editorItalic");
            dataUnderline = reportDesignerProperties.find(".editorUnderline");
            dataFormatPreview = reportDesignerProperties.find(".reportFormatPreview");

            rotationValue = reportDesignerProperties.find(".rotationValue");
            rotationRange = reportDesignerProperties.find(".rotationRange");

            dataTextFormat = reportDesignerProperties.find(".editorTextFormat");

            formatterControlsData = [];
            formatterControlsDataMap = new Map();

            for (let formatterOption of dataTextFormat.find("option"))
            {
                formatterControlsDataMap.set(formatterOption.value, []);
            }

            reportDesignerProperties.find(".js-formatterInput").each(function ()
            {
                var element = $(this);
                var controlData = element.data();
                if (controlData.formatDataSources !== undefined)
                {
                    let formatterData = {
                        element: element,
                        dataBindings: controlData.formatDataSources || {},
                        defaults: controlData.formatDataDefaults || {}
                    };

                    formatterControlsData.push(formatterData);

                    for (let formatterName of controlData.formatters)
                    {
                        formatterControlsDataMap.get(formatterName).push(formatterData);
                    }
                }
            });

            borderStyle = reportDesignerProperties.find(".borderStyle");
            borderTop = reportDesignerProperties.find(".borderTop");
            borderBottom = reportDesignerProperties.find(".borderBottom");
            borderLeft = reportDesignerProperties.find(".borderLeft");
            borderRight = reportDesignerProperties.find(".borderRight");
            borderWidth = reportDesignerProperties.find(".borderWidth");
            borderColor = reportDesignerProperties.find(".borderColor");
            editorTextAlign = reportDesignerProperties.find(".textAlignmentRadioButtonGroup");
            editorVerticalAlign = reportDesignerProperties.find(".verticalAlignmentRadioButtonGroup");
            editorPaddingBottom = reportDesignerProperties.find(".editorPaddingBottom");
            editorPaddingTop = reportDesignerProperties.find(".editorPaddingTop");
            editorPaddingRight = reportDesignerProperties.find(".editorPaddingRight");
            editorPaddingLeft = reportDesignerProperties.find(".editorPaddingLeft");
            editorPrintIfEmpty = reportDesignerProperties.find(".editorPrintIfEmpty");
            editorDisplayOnSingleLine = reportDesignerProperties.find(".editorDisplayOnSingleLine");
            singleLineRecordCountFieldSetTemplate = reportDesignerProperties.find(".singleLineRecordCountFieldSet.jsTemplate");
            editorSectionAndLineDisplayName = reportDesignerProperties.find(".editorSectionAndLineDisplayName");
            sectionAndLineFullName = reportDesignerProperties.find(".sectionAndLineFullName");
            resetSectionAndLineDisplayName = reportDesignerProperties.find(".resetSectionAndLineDisplayName");
            editorEmptyLinesToPrint = reportDesignerProperties.find(".editorEmptyLinesToPrint");
            editorColumnFilter = reportDesignerProperties.find(".filterContainer");
            editorColumnFilterInput = editorColumnFilter.find(".filterViewValue");
            editorColumnFilterLaunchButton = editorColumnFilter.find(".filterBuilderLaunchButton");
            overlapMode = reportDesignerProperties.find(".editorOverlapMode");
            resetOption = reportDesignerProperties.find(".resetOption");
            runningTotal = reportDesignerProperties.find(".runningTotal");

            const isHiddenWrapper = reportDesignerProperties.find('.jsIsHiddenCondition');
            isHiddenSelector = skyward.reporting.userCondition.init(isHiddenWrapper);
            whenHiddenOption = reportDesignerProperties.find("select[name='whenHiddenOption']");

            exportProperties.xml.wrapper = $(".jsXMLExportProperties");
            exportProperties.xml.includeRootNode = exportProperties.xml.wrapper.find("input[name='includeRootNodeXML']");
            exportProperties.xml.nodeName = exportProperties.xml.wrapper.find("input[name='reportNameXML']");
            exportProperties.xml.section.wrapper = exportProperties.xml.wrapper.find(".jsExportSectionProperties");
            exportProperties.xml.section.nodeName = exportProperties.xml.section.wrapper.find("input[name='sectionNameXML']");
            exportProperties.xml.row.wrapper = exportProperties.xml.wrapper.find(".jsExportRowProperties");
            exportProperties.xml.row.nodeName = exportProperties.xml.row.wrapper.find("input[name='rowNameXML']");
            exportProperties.xml.column.wrapper = exportProperties.xml.wrapper.find(".jsExportColumnProperties");
            exportProperties.xml.column.nodeName = exportProperties.xml.column.wrapper.find("input[name='columnNameXML']");
            exportProperties.xml.column.nodeType = exportProperties.xml.column.wrapper.find(".radioButtonGroup[data-name='columnTypeXML']");

            exportProperties.csv.wrapper = $(".jsCSVExportProperties");
            exportProperties.csv.delimiter = exportProperties.csv.wrapper.find("select[name='delimiterCSV']");
            exportProperties.csv.customDelimiter = exportProperties.csv.wrapper.find("input[name='customDelimiterCSV']");
            exportProperties.csv.textQualifier = exportProperties.csv.wrapper.find("select[name='textQualifierCSV']");
            exportProperties.csv.includeTextQualifierOnlyWhenRequired = exportProperties.csv.wrapper.find("input[name='textQualifierOnlyWhenRequired']");

            exportProperties.fixedWidth.wrapper = $(".jsFixedWidthExportProperties");
            exportProperties.fixedWidth.column.startPosition = exportProperties.fixedWidth.wrapper.find("input[name='columnStartPosition']");
            exportProperties.fixedWidth.column.length = exportProperties.fixedWidth.wrapper.find("input[name='columnTextLength']");

            exportProperties.json.wrapper = $(".jsJSONExportProperties");
            exportProperties.json.column.name = exportProperties.json.wrapper.find("input[name='jsonExportPropertyName']");
            exportProperties.json.section.name = exportProperties.json.wrapper.find("input[name='jsonExportPropertyName']");

            let defaultFormatting = dataFormatPreview.data();

            if (defaultFormatting)
            {
                underlineCssClass = defaultFormatting.cssClassUnderline;
                boldCssClass = defaultFormatting.cssClassBold;
                italicCssClass = defaultFormatting.cssClassItalic;

                defaultDataFont = defaultFormatting.defaultDataFontFamily;
                defaultDataFontSize = defaultFormatting.defaultDataFontSize;
                defaultDataForeColor = defaultFormatting.defaultDataForeColor;
                defaultFormatting.defaultDataBackColor ? defaultDataBackColor = defaultFormatting.defaultDataBackColor : defaultDataBackColor = "";
                defaultDataFontBold = defaultFormatting.defaultDataFontBold;
                defaultDataFontItalic = defaultFormatting.defaultDataFontItalic;
                defaultDataFontUnderline = defaultFormatting.defaultDataFontUnderline;
                defaultColumnPadding = defaultFormatting.defaultColumnPadding;
            }
        };

        var setColumnHeight = function (column, newHeight)
        {
            var columnData = column.data();
            var maxHeight = columnData.maxHeight || MAX_COLUMN_HEIGHT;
            var minHeight = columnData.paddingTop + columnData.paddingBottom;
            var originalHeight = columnData.fieldHeight;

            if (newHeight < minHeight)
            {
                editorPaddingTop.stop(true, true);
                editorPaddingTop.effect("highlight", {}, 3000);
                editorPaddingBottom.stop(true, true);
                editorPaddingBottom.effect("highlight", {}, 3000);
                columnHeight.stop(true, true);
                columnHeight.effect("highlight", {}, 3000);

                newHeight = minHeight;
            }

            if (newHeight > maxHeight)
            {
                newHeight = maxHeight;
            }

            if (originalHeight != newHeight)
            {
                columnData.fieldHeight = newHeight;

                if (columnData.heightMultiplier && columnData.fieldColumnType === COLUMN_TYPES.DYNAMIC_IMAGE_COLUMN)
                {
                    setColumnWidth(column, $.safeRound(newHeight * columnData.heightMultiplier));
                }
            }
        };

        var setColumnWidth = function (column, newWidth)
        {
            var columnData = column.data();
            var maxWidth = columnData.maxWidth || MAX_COLUMN_WIDTH;
            var minWidth = columnData.paddingLeft + columnData.paddingRight;
            var originalWidth = columnData.fieldWidth;

            if (newWidth < minWidth)
            {
                editorPaddingLeft.stop(true, true);
                editorPaddingLeft.effect("highlight", {}, 3000);
                editorPaddingRight.stop(true, true);
                editorPaddingRight.effect("highlight", {}, 3000);
                columnWidth.stop(true, true);
                columnWidth.effect("highlight", {}, 3000);

                newWidth = minWidth;
            }

            if (newWidth > maxWidth)
            {
                newWidth = maxWidth;
            }

            if (originalWidth != newWidth)
            {
                columnData.fieldWidth = newWidth;

                if (columnData.widthMultiplier && columnData.fieldColumnType === COLUMN_TYPES.DYNAMIC_IMAGE_COLUMN)
                {
                    setColumnHeight(column, $.safeRound(newWidth * columnData.widthMultiplier));
                }
            }
        };

        var setRowHeight = function (selectedLine, height)
        {
            var adjustedHeight = Math.max(height, 10);
            selectedLine.data("lineHeight", adjustedHeight);
            labelBuilder.setLabelHeight(selectedLine, adjustedHeight);
        };

        var setPropertiesEventHandlers = function ()
        {
            function applyDataToAllCurrentSelectedColumns(applyData)
            {
                Promise.all(currentSelectedColumns.getCurrentSelectedColumns()
                    .map(c =>
                    {
                        const applyDataResult = applyData(c);
                        if (applyDataResult instanceof Promise)
                        {
                            return applyDataResult.then(() => applyColumnCss(c));
                        }
                        else if (applyDataResult && typeof applyDataResult.promise === 'function')
                        {
                            return applyDataResult.done(() => applyColumnCss(c)).promise();
                        }
                        else
                        {
                            applyColumnCss(c);
                            return Promise.resolve();
                        }
                    })).then(() =>
                    {
                        updateHiddenField();
                        setColumnProperties();
                        skyward.reporting.updateVariableUsageLocations();
                    }).catch(() => { /* ignore cancel click */ });;
            }

            function applyDataToSelectedRow(applyData)
            {
                applyData(currentRow);

                setRowProperties(currentRow);
                updateHiddenField();
                applyRowCss(currentRow);
                skyward.reporting.updateVariableUsageLocations();
            }

            function applyDataToSelectedSection(applyData)
            {
                let section = getCurrentSection();

                applyData(section);

                updateHiddenField();
                setSectionProperties(section);
            }

            function isRowSelected()
            {
                if (currentRow && !currentRow.hasClass("sectionToolbar"))
                {
                    return true;
                }
                return false;
            }

            function isSectionToolbarSelected()
            {
                if (currentRow && currentRow.hasClass("sectionToolbar"))
                {
                    return true;
                }
                return false;
            }

            skyward.reporting.getCopyFieldFormatValueSelector = function ()
            {
                // copy field formatter only supports 1 selected column
                internalGetCopyFieldFormatValueSelector(currentSelectedColumns.getFirstSelectedColumn());
            };

            var internalGetCopyFieldFormatValueSelector = function (currentSelectedColumn)
            {
                var formatterData = currentSelectedColumn.data("formatter");
                var previousFormatterData = formatterData;
                var currentSectionData = getSectionData(getCurrentSection());

                var rootModule;
                var rootObject;
                if ((previousFormatterData || {}).formatterName == FORMATTER_NAMES.COPY_FIELD)
                {
                    // Setup treebrowse for seek-to-field if editing a previously defined CopyField formatter
                    rootModule = previousFormatterData.fieldModule || currentSectionData.baseModule;
                    rootObject = previousFormatterData.fieldObject || currentSectionData.baseObject;
                }
                else
                {
                    rootModule = currentSectionData.baseModule;
                    rootObject = currentSectionData.baseObject;
                }

                var options = {
                    title: "Select Format to Copy",
                    selectItemDoubleClickSelector: ".treebrowserow.reportColumn",
                    treeBrowseOptions: {
                        renderOneToMany: false,
                        allowOneToManyBrowse: false,
                        renderStandardRelationships: true,
                        maxOneToManyNestedLevels: 5,
                        renderOperationFields: true,
                        renderColumnGroups: false,
                        renderProxies: true,
                        mode: "report",
                        seekField: (previousFormatterData || {}).absoluteFieldPath,
                        fieldsOfType: currentSelectedColumn.data("fieldType")
                    }
                };
                selectFieldDialog(null, rootModule, rootObject, options)
                    .done(function (selectedItemData, moduleObjectData)
                    {
                        // Merge Treebrowse-column-data with M/O/F names and formatter-specific data
                        let newFormatterData = $.extend(getFormatterDataDefaults(FORMATTER_NAMES.COPY_FIELD), selectedItemData, moduleObjectData, { fieldDisplayName: selectedItemData.displayName });
                        currentSelectedColumn.data("formatter", newFormatterData);
                        if (isBarcodeFormatter(previousFormatterData))
                        {
                            resetFontProperties(currentSelectedColumn);
                        }
                        applyColumnCss(currentSelectedColumn);
                        updateHiddenField();
                    })
                    .always(function ()
                    {
                        setColumnProperties();
                    });
            };

            resetSectionAndLineDisplayName.on("click", function ()
            {
                var defaultValue;
                if (currentRow.hasClass("sectionToolbar"))
                {
                    defaultValue = currentRow.closest(".jsSection").data().sectionObjectName;
                }
                else if (currentRow.hasClass("sortHeaderLine") || currentRow.hasClass("jsSortAggregateLine"))
                {
                    defaultValue = currentRow.data().defaultLineLabel;
                }
                editorSectionAndLineDisplayName.val(defaultValue);
                editorSectionAndLineDisplayName.change();
            });

            // newchangeevents

            function getIntegerValue(input)
            {
                let value = $.safeRound(input.val()) || 0;
                var min = parseInt(input.prop("min")) || Number.MIN_SAFE_INTEGER;
                var max = parseInt(input.prop("max")) || Number.MAX_SAFE_INTEGER;

                if (value < min)
                {
                    return min;
                }

                if (value > max)
                {
                    return max;
                }

                return value;
            }

            function getTextValue(input)
            {
                let value = input.val();
                if (value)
                {
                    return value.trim();
                }
                return null;
            }

            function getInputValue(input)
            {
                if (input.is(".radioButtonGroup"))
                {
                    return skyward.radioButtonGroup.getValue(input);
                }

                const type = (input.prop("type") || "").toLowerCase();
                switch (type)
                {
                    case "checkbox":
                        return input.prop("checked");
                    case "number":
                        return getIntegerValue(input);
                    case "text":
                        return getTextValue(input);
                }

                return input.val();
            }

            function getInputData(input)
            {
                return input.data();
            }

            function setupColorPreviewEvent(input, { prop })
            {
                input.on("move.spectrum hide.spectrum", function (events, colorParam)
                {
                    const value = colorParam?.toString();

                    if (isRowSelected())
                    {
                        currentRow.data(prop, value);
                        applyRowCss(currentRow);
                    }
                    else if (currentSelectedColumns.areColumnsSelected())
                    {
                        for (const column of currentSelectedColumns.getCurrentSelectedColumns())
                        {
                            column.data(prop, value);
                            applyColumnCss(column);
                        }
                    }
                });
            }

            function setupChangeEvent(input, { prop, getValueOverride, setDataOverride })
            {
                if ((prop && setDataOverride) || (!prop && !setDataOverride))
                {
                    console.error("either prop or setDataOverride must have a value, but not both.");
                }

                function setData(element, value)
                {
                    if (setDataOverride)
                    {
                        return setDataOverride(element, value);
                    }
                    else if (prop)
                    {
                        element.data(prop, value);
                    }
                }

                input.on("change", function ()
                {
                    const value = getValueOverride ? getValueOverride(input) : getInputValue(input);

                    if (isSectionToolbarSelected())
                    {
                        applyDataToSelectedSection(function (section)
                        {
                            setData(section, value);
                        });
                    }
                    else if (isRowSelected())
                    {
                        applyDataToSelectedRow(function (row)
                        {
                            setData(row, value);
                        });
                    }
                    else if (currentSelectedColumns.areColumnsSelected())
                    {
                        applyDataToAllCurrentSelectedColumns(function (column)
                        {
                            return setData(column, value);
                        });
                    }
                });
            }

            reportDesignerProperties.on("change", ".editorSingleLineRecordCount", function ()
            {
                let input = $(this);
                let section = getCurrentSection();

                const value = getInputValue(input);
                const sectionID = input.data("sectionID");

                getSectionElementBySectionID(sectionID).data("singleLineRecordCount", value);

                updateHiddenField();
                setSectionProperties(section);
            });

            function setColumnRotation(column, value)
            {
                column.data("rotation", value);
                initColumnDragging(column);
            }

            function getBoolPromptSetDataFunc(prop)
            {
                return function (row, inputData)
                {
                    const promptUniqueIDProp = prop + "-prompt-unique-id";
                    const promptUniqueIDAttr = "data-" + promptUniqueIDProp;

                    if (inputData.promptUniqueId)
                    {
                        row.data(prop, false);
                        row.data(promptUniqueIDProp, inputData.promptUniqueId)
                            .attr(promptUniqueIDAttr, inputData.promptUniqueId);
                    }
                    else if (typeof inputData.boolvalue == "boolean")
                    {
                        row.data(prop, inputData.boolvalue);
                        row.removeData(promptUniqueIDProp).removeAttr(promptUniqueIDAttr);
                    }
                }
            }

            // start formatter
            dataTextFormat.on("change", function (event)
            {
                let formatterName = dataTextFormat.val();

                if (formatterName === FORMATTER_NAMES.COPY_FIELD)
                {
                    internalGetCopyFieldFormatValueSelector(currentSelectedColumns.getFirstSelectedColumn());
                    event.stopPropagation(); // Wait for user to confirm a field before storing the value;
                    return false;
                }

                applyDataToAllCurrentSelectedColumns(function (column)
                {
                    let oldFormatter = column.data("formatter");
                    let newFormatter = getFormatterDataDefaults(formatterName);

                    if (!newFormatter)
                    {
                        column.removeData("formatter");
                        if (oldFormatter && isBarcodeFormatter(oldFormatter))
                        {
                            resetFontProperties(column);
                        }
                    }
                    else
                    {
                        if (!oldFormatter || oldFormatter.formatterName !== formatterName)
                        {
                            // some columns may already have the formatter, so avoid overriding all their existing settings
                            column.data("formatter", newFormatter);
                            if (isBarcodeFormatter(oldFormatter) && !isBarcodeFormatter(newFormatter))
                            {
                                resetFontProperties(column);
                            }
                        }
                    }

                });

                return true;
            });

            for (let controlData of formatterControlsData)
            {
                controlData.element.on("change", function ()
                {
                    let formatterPropertyValues = getFormatterInputPropertyValues(controlData);

                    applyDataToAllCurrentSelectedColumns(function (column)
                    {
                        let newFormatter = $.extend({}, column.data("formatter"), formatterPropertyValues);

                        column.data("formatter", newFormatter);
                    });
                });
            }

            // end formatter

            //These Export Properties are for the whole report so they don't set any data on the column, row, or section, but still need to updateHiddenField() so the dirty logic is triggered
            exportProperties.csv.delimiter.change(updateHiddenField);
            exportProperties.csv.customDelimiter.change(updateHiddenField);
            exportProperties.csv.textQualifier.change(updateHiddenField);
            exportProperties.csv.includeTextQualifierOnlyWhenRequired.change(updateHiddenField);
            exportProperties.xml.includeRootNode.change(function ()
            {
                const currentValue = skyward.controls.getValue(exportProperties.xml.includeRootNode);
                skyward.controls.toggle(exportProperties.xml.nodeName, currentValue);

                if (currentValue === false)
                {
                    //clearing this input will also trigger updateHiddenField()
                    skyward.controls.clearValue(exportProperties.xml.nodeName);
                }
                else
                {
                    updateHiddenField();
                }
            });
            exportProperties.xml.nodeName.change(updateHiddenField);

            setupChangeEvent(dataFont, { prop: "dataFontFamily" });
            setupChangeEvent(dataFontSize, { prop: "dataFontSize" });
            setupChangeEvent(editorPaddingLeft, { prop: "paddingLeft" });
            setupChangeEvent(editorPaddingRight, { prop: "paddingRight" });
            setupChangeEvent(editorPaddingTop, { prop: "paddingTop" });
            setupChangeEvent(editorPaddingBottom, { prop: "paddingBottom" });
            setupChangeEvent(runningTotal, { prop: "isRunningTotal" });
            setupChangeEvent(groupColumnSortHeaderTypes, { prop: "fieldColumnType" });
            setupChangeEvent(overlapMode, { prop: "overlapMode" });
            setupChangeEvent(dataItalic, { prop: "dataItalic" });
            setupChangeEvent(dataBold, { prop: "dataBold" });
            setupChangeEvent(dataUnderline, { prop: "dataUnderline" });
            setupChangeEvent(dataBackgroundColor, {
                setDataOverride: (element, value) =>
                {
                    element.data("backColor", value);
                    if (isLine(element))
                    {
                        for (let column of element.find(".jsColumn.reportDesignerHidden"))
                        {
                            applyColumnCss($(column));
                        }
                    }
                }
            });
            setupColorPreviewEvent(dataBackgroundColor, { prop: "backColor" });
            setupChangeEvent(dataForeColor, { prop: "dataForeColor" });
            setupColorPreviewEvent(dataForeColor, { prop: "dataForeColor" });
            setupChangeEvent(editorVerticalAlign, { prop: "verticalAlignment" });
            setupChangeEvent(editorTextAlign, { prop: "dataTextAlignment" });
            setupChangeEvent(pageSectionColumnDisplayType, { prop: "displayType" });
            setupChangeEvent(pageSectionColumnPromptDisplayType, { prop: "displayType" });
            setupChangeEvent(positionX, { prop: "positionX" });
            setupChangeEvent(positionY, { prop: "positionY" });
            setupChangeEvent(editorAggregateDelimiter, { prop: "aggregateDelimiter" });
            setupChangeEvent(exportProperties.json.column.name, { prop: "jsonPropertyName" });
            setupChangeEvent(exportProperties.xml.row.nodeName, { prop: "nodeName" });
            setupChangeEvent(exportProperties.xml.column.nodeName, { prop: "nodeName" });
            setupChangeEvent(exportProperties.xml.column.nodeType, { prop: "nodeType" });
            setupChangeEvent(exportProperties.fixedWidth.column.startPosition, { prop: "fixedWidthStartPosition" });
            setupChangeEvent(exportProperties.fixedWidth.column.length, { prop: "fixedWidthLength" });
            setupChangeEvent(borderLeft, { prop: "borderLeft" });
            setupChangeEvent(borderRight, { prop: "borderRight" });
            setupChangeEvent(borderTop, { prop: "borderTop" });
            setupChangeEvent(borderBottom, { prop: "borderBottom" });
            setupChangeEvent(borderWidth, { prop: "borderWidth" });
            setupChangeEvent(borderStyle, { prop: "borderStyle" });
            setupChangeEvent(borderColor, { prop: "borderColor" });
            setupColorPreviewEvent(borderColor, { prop: "borderColor" });
            setupChangeEvent(editorEmptyLinesToPrint, { prop: "emptyLinesToPrint" });
            setupChangeEvent(editorPageBreakBeforeSectionPrints, {
                setDataOverride: (section, value) =>
                {
                    section.data("pageBreakBeforeSection", value);
                    if (!value)
                    {
                        removeInvalidPageNumberColumns();
                    }
                }
            });
            setupChangeEvent(editorPrintIfEmpty, { prop: "printIfEmpty" });
            setupChangeEvent(exportProperties.xml.section.nodeName, { prop: "nodeName" });
            setupChangeEvent(exportProperties.json.section.name, { prop: "jsonPropertyName" });
            setupChangeEvent(editorSectionAndLineDisplayName, {
                setDataOverride: (element, value) =>
                {
                    let selectedRow = element;
                    if (isSection(element))
                    {
                        element.data("displayName", value);
                        selectedRow = getSectionToolbar(element);
                    }

                    selectedRow.data("reportLabelText", value);
                    labelBuilder.setRelatedLabel(selectedRow, value);
                }
            });
            setupChangeEvent(columnHeight, { setDataOverride: setColumnHeight })
            setupChangeEvent(columnWidth, { setDataOverride: setColumnWidth });
            setupChangeEvent(lineHeight, { setDataOverride: setRowHeight });
            setupChangeEvent(editorRepeatHeaderForEachDataRow, { prop: "repeatHeaderForEachDataRow" });
            setupChangeEvent(editorRepeatRowOnPageIfDetailsPresent, { prop: "repeatOnPageIfDetailsPresent" });
            setupChangeEvent(editorPrintAtBottomOfPage, {
                getValueOverride: getInputData,
                setDataOverride: getBoolPromptSetDataFunc("print-at-bottom-of-page")
            });
            setupChangeEvent(editorRepeatLineAfterStart, {
                getValueOverride: getInputData,
                setDataOverride: getBoolPromptSetDataFunc("repeat-line-after-start")
            });
            setupChangeEvent(editorPrintReportFooterOnNewPage, {
                getValueOverride: getInputData,
                setDataOverride: getBoolPromptSetDataFunc("print-report-footer-on-new-page")
            });

            setupChangeEvent(editorColumnText, {
                setDataOverride: (column, designerText) =>
                {
                    let columnData = column.data();
                    if (columnData.fieldColumnType === COLUMN_TYPES.FORMTEXT_COLUMN)
                    {
                        designerText = skyward.reporting.prompts.promptCollection.ConvertFriendlyNamesToPromptBindings(designerText);
                        designerText = CalculatedFields_Model.ConvertFriendlyNamesToBindings(designerText);
                    }

                    const section = column.closest(".jsSection");
                    if (isParentDisplayOnSingleLine(section) && columnData.designerText?.indexOf(SINGLE_LINE_HEADER_INDEX_BINDING) >= 0 && !designerText?.indexOf(SINGLE_LINE_HEADER_INDEX_BINDING) >= 0)
                    {
                        columnData.singleLineIndexRemoved = true;
                    }
                    else
                    {
                        columnData.singleLineIndexRemoved = false;
                    }

                    columnData.designerText = designerText;
                }
            });

            setupChangeEvent(editorDisplayOnSingleLine, {
                setDataOverride: (section, value) =>
                {
                    section.data("displayOnSingleLine", value);
                    for (let detailSection of getNestedDetailSections(section))
                    {
                        $(detailSection).data("displayOnSingleLine", value);
                    }
                }
            });

            setupChangeEvent(isHiddenSelector.elements.wrapper, {
                getValueOverride: () => isHiddenSelector.getUpdatedIsHiddenCondition(),
                setDataOverride: (element, value) =>
                {
                    element.data("isHiddenCondition", value);
                    if (isSection(element) || isLine(element))
                    {
                        applySectionOrRowHiddenCondition(element);
                    }
                }
            });

            setupChangeEvent(whenHiddenOption, { prop: "whenHiddenOption" });

            setupChangeEvent(editorVariablesToReset, {
                getValueOverride: () => skyward.reporting.getVariableIDsToReset(),
                setDataOverride: (row, value) => row.data("variableIdsToReset", value).attr("data-variable-ids-to-reset", JSON.stringify(value))
            });

            setupChangeEvent(resetOption, {
                setDataOverride: (column, value) => column.data("resetOn", value).attr("data-reset-on", value)
            }); // Attribute must also be set for css-search in deleteInvalidPageNumberColumns()

            setupChangeEvent(editorColumnFilterInput, {
                getValueOverride: (input) =>
                {
                    return {
                        filter: input.data("filterCondition"),
                        parameters: input.data("parameters"),
                        filterSchemaFields: input.data("filterSchemaFields")
                    };
                },
                setDataOverride: (column, value) =>
                {
                    column.data("columnFilterJson", value.filter).attr("data-column-filter-json", value.filter);
                    column.data("parameters", value.parameters || []);
                    column.data("filterSchemaFields", value.filterSchemaFields);
                }
            });

            setupChangeEvent(rotationValue, { setDataOverride: setColumnRotation });
            setupChangeEvent(rotationRange, { setDataOverride: setColumnRotation });

            setupChangeEvent(editorMediaId, {
                setDataOverride: (column, value) =>
                {
                    let columnData = column.data();
                    columnData.useSystemLogo = false;
                    columnData.mediaId = value;

                    let url = skyward.global.virtualDir + "SkySys/Media/DownloadReportMedia/" + value;
                    column.find("img").attr('src', url);
                }
            });

            setupChangeEvent(editorUseSystemLogo, {
                setDataOverride: (column, value) =>
                {
                    let columnData = column.data();
                    columnData.useSystemLogo = value;
                    columnData.mediaId = null;
                    let url = value ? skyward.global.virtualDir + "SkySys/Media/DownloadLogo" : null;
                    column.find("img").attr('src', url);
                }
            });

            setupChangeEvent(editorVariableToSet, {
                getValueOverride: (input) => input.data("variableIdToSet"),
                setDataOverride: (column, value) =>
                {
                    if (value != undefined && value !== "")
                    {
                        column.data("variableIdToSet", value).attr("data-variable-id-to-set", value);
                    }
                    else
                    {
                        column.removeData("variableIdToSet").removeAttr("data-variable-id-to-set");
                    }
                }
            });

            setupChangeEvent(editorAggregateType, {
                setDataOverride: (column, aggregateType) =>
                {
                    let columnData = column.data();

                    const previousAggregateType = columnData.aggregateType;
                    let isDelimitedAggregate = delimitedAggregators.indexOf(aggregateType) != -1;
                    let fieldType = skyward.reporting.getAggregateColumnFieldType(columnData.columnFieldType, aggregateType);

                    if (aggregateType != previousAggregateType)
                    {
                        return confirmUpdateAggregateTypeColumn(column)
                            .done((foundNodes) =>
                            {
                                // call back will be called when the user clicks "Yes" or if there are no usages found
                                columnData.aggregateType = aggregateType;
                                columnData.fieldType = fieldType;

                                if (!isDelimitedAggregate)
                                {
                                    column.removeData("aggregateDelimiter");
                                }
                                else if (!columnData.aggregateDelimiter)
                                {
                                    columnData.aggregateDelimiter = editorAggregateDelimiter.data("defaultValue");
                                }

                                column.attr("data-field-type", fieldType); // I'm not sure why we need to set the attr here, but it was happening before

                                for (const relatedColumn of getColumnsBySourceColumnID(columnData.columnId).map((i, e) => $(e)))
                                {
                                    const relatedColumnData = relatedColumn.data();

                                    switch (relatedColumnData.fieldColumnType)
                                    {
                                        case COLUMN_TYPES.AGGREGATE_COLUMN:
                                            relatedColumnData.columnFieldType = fieldType;
                                            break;
                                        case COLUMN_TYPES.FUTURE_VALUE_COLUMN:
                                            relatedColumnData.fieldType = fieldType;
                                            relatedColumnData.designerText = `Future ${getColumnLabelWithPreText(column)}`;
                                            applyColumnCss(relatedColumn);
                                            break;
                                    }
                                }

                                if (foundNodes)
                                {
                                    // null out any hidden conditions using this column
                                    for (const rootNode of foundNodes)
                                    {
                                        for (const node of rootNode.allNodes())
                                        {
                                            if (node instanceof usageLocations.HiddenConditionUsageNode)
                                            {
                                                let element = node.sourceDetail.element;
                                                if (element)
                                                {
                                                    removeHiddenConditionWithColumnID(element, columnData.columnId);
                                                    if (node.sourceDetail.type == usageLocations.SourceTypes.Column)
                                                    {
                                                        updateColumnIsHiddenConditionFormatting(element)
                                                    }
                                                    else if (node.sourceDetail.type == usageLocations.SourceTypes.Line || node.sourceDetail.type == usageLocations.SourceTypes.Section)
                                                    {
                                                        applySectionOrRowHiddenCondition(element);
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }).fail(() =>
                            {
                                // undo input change
                                skyward.controls.setValue(editorAggregateType, previousAggregateType, true);
                            });
                    }
                }
            });
        };

        var isBarcodeFormatter = function (formatterData)
        {
            return formatterData && formatterData.formatterName === "Barcode";
        };

        var getFormatterDataDefaults = function (formatterName)
        {
            if (!formatterName || formatterName == FORMATTER_NAMES.DEFAULT || enumMaps.FormatterTypeNames.indexOf(formatterName) === -1)
            {
                return null;
            }

            var results = {
                $type: "Skyward.Business.Reporting.TypeFormatting." + formatterName + "Formatter, Skyward.Business",
                formatterName: formatterName
            };
            $.each(formatterControlsDataMap.get(formatterName), function (i, controlData)
            {
                $.each(controlData.dataBindings, function (propertyName, dataSource)
                {
                    results[propertyName] = (controlData.defaults[formatterName] || {})[propertyName];
                });
            });
            return results;
        };

        var getFormatterInputPropertyValues = function (controlData)
        {
            let element = controlData.element;

            let results = {}; // an object of the properties to set on the formatter

            $.each(controlData.dataBindings, function (propertyName, dataSource)
            {
                switch (dataSource)
                {
                    case "Val":
                        if (element.is("input.checkbox"))
                        {
                            results[propertyName] = element.prop("checked");
                        }
                        else
                        {
                            var value = element.val();

                            if (element.attr("type") == "text")
                            {
                                results[propertyName] = element.val().toString();
                            }
                            else if (element.attr("type") == "number")
                            {
                                value = $.safeRound(value) || 0;
                                var min = parseInt(element.attr("min"));
                                var max = parseInt(element.attr("max"));

                                if (value < min)
                                {
                                    value = min;
                                }
                                else if (value > max)
                                {
                                    value = max;
                                }
                            }
                            else if (skyward.extensions.isNumeric(value))
                            {
                                value = $.safeRound(value);
                            }

                            results[propertyName] = value;
                        }
                        break;
                    case "FieldSelectorText":
                        results[propertyName] = element.find(".fieldSelectorText").text();
                        break;
                    case "Data":
                    case "FieldSelectorIcon":
                        results[propertyName] = element.data(propertyName);
                        break;
                }
            });

            return results;
        };

        //END INIT FUNCTIONS

        var columnResizableOptions =
        {
            minWidth: 5,
            handles: "e, s, se",
            containment: "parent",
            autoHide: true,
            start: function (e, ui)
            {
                const clearSelectedColumns = !keyboardCommands.isMultiSelectKeyPressed(e);
                setSelectedColumn(ui.element, clearSelectedColumns);
            },
            stop: function (e, ui)
            {
                if (ui.size.height !== ui.originalSize.height)
                {
                    setColumnHeight(ui.element, ui.size.height);

                    if (keyboardCommands.isSmartResizeKeyPressed(e))
                    {
                        cloneDataAndCssToRelatedColumns(ui.element, { 'fieldHeight': ui.size.height }, { height: ui.size.height + "px" });
                    }
                }

                if (ui.size.width !== ui.originalSize.width)
                {
                    setColumnWidth(ui.element, ui.size.width);

                    if (keyboardCommands.isSmartResizeKeyPressed(e))
                    {
                        cloneDataAndCssToRelatedColumns(ui.element, { 'fieldWidth': ui.size.width }, { width: ui.size.width + "px" });
                    }
                }

                applyColumnCss(ui.element);
                updateHiddenField();
                setColumnProperties();
            }
        };

        var getNewColumnType = function (line, item, isGroupColumn)
        {
            const lineType = line.data("lineType");
            const itemData = item.data();
            const fieldType = skyward.reporting.reportColumn.getFieldType(itemData);

            if (itemData.isFutureColumn)
            {
                return COLUMN_TYPES.FUTURE_VALUE_COLUMN;
            }

            switch (lineType)
            {
                case LINE_TYPES.DATA_LINE:
                case LINE_TYPES.SORT_HEADER_LINE:
                    if (isGroupColumn)
                    {
                        return COLUMN_TYPES.GROUP_COLUMN;
                    }
                    else if (itemData.pageSectionColumnType)
                    {
                        return COLUMN_TYPES.PAGE_SECTION_COLUMN;
                    }
                    else if (fieldType === "Image")
                    {
                        return COLUMN_TYPES.DYNAMIC_IMAGE_COLUMN;
                    }
                    else
                    {
                        return COLUMN_TYPES.DATA_COLUMN;
                    }
                case LINE_TYPES.FOOTER_LINE:
                    if (itemData.pageSectionColumnType || line.hasClass(".jsHeaderFooter"))
                    {
                        return COLUMN_TYPES.PAGE_SECTION_COLUMN;
                    }
                    else if (itemData.sourceColumnId)
                    {
                        return itemData.columnFieldType == "Image" ? COLUMN_TYPES.DYNAMIC_IMAGE_COLUMN : COLUMN_TYPES.AGGREGATE_COLUMN;
                    }
                    else
                    {
                        return COLUMN_TYPES.FOOTER_COLUMN;
                    }
                case LINE_TYPES.HEADER_LINE:
                    if (line.hasClass('jsHeaderFooter'))
                    {
                        return COLUMN_TYPES.PAGE_SECTION_COLUMN;
                    }
                    else if (isGroupColumn)
                    {
                        return COLUMN_TYPES.GROUP_COLUMN_HEADER;
                    }
                    else if (itemData.pageSectionColumnType)
                    {
                        return COLUMN_TYPES.PAGE_SECTION_COLUMN;
                    }
                    return COLUMN_TYPES.HEADER_COLUMN;
                case LINE_TYPES.AGGREGATE_LINE:
                case LINE_TYPES.SORT_AGGREGATE_LINE:
                    if (itemData.pageSectionColumnType)
                    {
                        return COLUMN_TYPES.PAGE_SECTION_COLUMN;
                    }
                    else if (isGroupColumn)
                    {
                        return COLUMN_TYPES.GROUP_COLUMN_AGGREGATE;
                    }
                    return fieldType === "Image" ? COLUMN_TYPES.DYNAMIC_IMAGE_COLUMN : COLUMN_TYPES.AGGREGATE_COLUMN;
                default:
                    return COLUMN_TYPES.DATA_COLUMN;
            }
        };

        var designerError = function (message)
        {
            skyward.modalLayer.showDialog("Error", message, [{ text: "Close", icon: "Close" }]);
            return;
        };

        /**
         * @param {Object} params
         * @param {Partial<ReportFieldProps>} params.treeBrowseData
         * @param {JQuery} params.currentLine
         * @param {JQuery} params.section
         * @param {{ top: number | null, left: number | null }} params.position
         * @param {boolean} [params.autoCreatedColumn]
         */
        const insertTreeBrowseItem = function (params)
        {
            const currentLine = params.currentLine;
            let treeBrowseItem;

            if (params.treeBrowseData)
            {
                // Ideally, in the future it will be possible to work with treeBrowseData directly,
                // rather than masquerading as a jQuery element.  .data("key") is specific to jQuery
                // and makes this more challenging.
                treeBrowseItem =
                {
                    data: function (key)
                    {
                        if (arguments.length > 0)
                        {
                            console.warn("Please don't rely on this data wrapper!");
                            const camelCaseKey = key.replace(/-[a-z]/g, function (match) { return match[1].toUpperCase(); });
                            return params.treeBrowseData[camelCaseKey];
                        }
                        return params.treeBrowseData;
                    }
                };
            }
            else
            {
                treeBrowseItem = { data: function () { return undefined; } };
            }

            const columnParams = {
                section: params.section,
                newItem: treeBrowseItem,
                reportLine: currentLine,
                position: params.position,
                autoCreatedColumn: params.autoCreatedColumn,
                isDynamicColumn: params.treeBrowseData?.isDynamicColumn || params.treeBrowseData?.isColumnGroup // isDynamicColumn is from tree browse, while isColumnGroup is from field selection panels
            };

            return insertFieldItem(currentLine, columnParams);
        };

        var insertFieldItem = function (currentLine, columnParams)
        {
            const currentSection = getParentSection(currentLine);
            if (currentSection.hasClass('collapseSection'))
            {
                return false;
            }

            const lineData = currentLine.data();
            if (lineData.lineType)
            {
                const newType = getNewColumnType(currentLine, columnParams.newItem, columnParams.isDynamicColumn);

                switch (lineData.lineType)
                {
                    case LINE_TYPES.DATA_LINE:
                        if (columnParams.newItem.data().sourceColumnId)
                        {
                            return designerError("Unable to add aggregate field to data line.");
                        }
                        insertDataColumn(columnParams);
                        break;
                    case LINE_TYPES.HEADER_LINE:
                        columnParams.newItem = createNewColumn({ type: newType, relatedItem: columnParams.newItem, usesSingleLineIndex: isParentDisplayOnSingleLine(columnParams.section) });
                        insertColumnHelperFunction(columnParams);
                        break;
                    case LINE_TYPES.FOOTER_LINE:
                        if (columnParams.isDynamicColumn)
                        {
                            return designerError("Unable to add a column group to a footer line.");
                        }
                        columnParams.newItem = createNewColumn({ type: newType, relatedItem: columnParams.newItem });
                        insertColumnHelperFunction(columnParams);
                        break;
                    case LINE_TYPES.SORT_HEADER_LINE:
                    case LINE_TYPES.AGGREGATE_LINE:
                    case LINE_TYPES.SORT_AGGREGATE_LINE:
                        columnParams.newItem = createNewColumn({ relatedItem: columnParams.newItem, type: newType });
                        if (newType === COLUMN_TYPES.FUTURE_VALUE_COLUMN)
                        {
                            insertColumnHelperFunction(columnParams);
                        }
                        else
                        {
                            insertColumnIntoAggregateLine(columnParams);
                        }
                        break;
                }
                setSelectedColumn(columnParams.newItem, true);
                columnParams.newItem.find('.visualLabel').text(getColumnLabelWithPreText(columnParams.newItem));
                initFieldsUsedInReport(getSectionData(columnParams.section));
                return true;
            }
            return false;
        };

        var insertColumnIntoAggregateLine = function (columnParams)
        {
            const sectionDataLines = getSectionDataLines(columnParams.section);
            const sectionDataLine = sectionDataLines.first();

            const newItemData = columnParams.newItem.data();
            const columnExists = newItemData.sourceColumnId !== undefined ||
                (newItemData.absoluteFieldPath ? 
                    getColumnsInLineByAbsoluteFieldPath($.merge(sectionDataLines, getSectionAllAggregateLines(columnParams.section)), newItemData.absoluteFieldPath).length > 0 :
                    getColumnsInLineByFieldName($.merge(sectionDataLines, getSectionAllAggregateLines(columnParams.section)), newItemData.fieldName).length > 0); //See @NOTE:FIELDNAME

            if (columnExists === false)
            {
                const dataColumnParams = {
                    isDynamicColumn: columnParams.isDynamicColumn,
                    section: columnParams.section,
                    newItem: columnParams.newItem,
                    reportLine: sectionDataLine,
                    position: columnParams.position,
                    autoCreatedColumn: columnParams.autoCreatedColumn
                };

                //insert the datacolumn (which also inserts the header column)
                const position = insertDataColumn(dataColumnParams);
                columnParams.position.left = columnParams.position.left || position.left;
                columnParams.position.top = columnParams.position.top || position.top;
                columnParams.autoCreatedColumn = true;
            }

            //insert the aggregate column
            insertColumnHelperFunction(columnParams);

            toggleAggregateMathButtonState(columnParams.reportLine);
        };

        var insertPromptListItem = function (params)
        {
            var currentLine = params.currentLine;
            var columnParams = {
                section: params.section,
                newItem: params.promptItem,
                reportLine: currentLine,
                position: params.position
            };

            columnParams.newItem = createNewColumn({
                type: COLUMN_TYPES.PAGE_SECTION_COLUMN,
                relatedItem: columnParams.newItem
            });

            insertFieldItem(currentLine, columnParams);
        };

        var getDefaultDraggableOptions = function ()
        {
            return {
                cursorAt: { top: 0, left: 0 },
                delay: 150,
                scope: 'designer',
                helper: function ()
                {
                    const listItem = $(this);
                    const convertedItem = $("<div/>").html(listItem.children().first().html()).data(listItem.data());
                    convertedItem.find('svg').remove();
                    convertedItem.css({ "z-index": 999, display: undefined, "background-color": "white" });
                    convertedItem.width(convertedItem.data('fieldWidth') || DEFAULT_COLUMN_WIDTH);
                    convertedItem.height(DEFAULT_COLUMN_HEIGHT);
                    return convertedItem;
                },
                appendTo: $(document.body),
                grid: [1, 1],
                opacity: 0.8
            };
        };

        //UI UPDATE FUNCTIONS
        var initDesignerUI = function ($section)
        {
            //if no section is passed in, get the currently selected one
            if (typeof $section === "undefined")
            {
                $section = designer.find('.sectionSelected');

                //none selected
                if ($section.length === 0)
                {
                    return;
                }
            }
            else if ($section.length === 0)
            {
                initializeToolbarEvents();
                return;
            }

            var sectionData = getSectionData($section);
            updateDesignerSidebarCalculatedFieldList(sectionData);

            skyward.reporting.prompts.populateExtraColumnOptionsWithPrompts(designer.find(".reportDesignerPromptFieldsMenu"));

            // disable existing ui-droppable
            designer.find('.ui-droppable').each(function ()
            {
                $(this).droppable('disable');
            });

            getAllNonPreviewLines($section).each(function ()
            {
                var currentLine = $(this);

                currentLine.find('.reportColumn').each(function ()
                {
                    initColumnDragging($(this));
                });

                if (currentLine.hasClass('ui-droppable'))
                {
                    currentLine.droppable('enable');
                }
                else
                {
                    currentLine.droppable(
                        {
                            scope: 'designer',
                            accept: function (item)
                            {
                                var section = currentLine.closest(".jsSection");

                                if (!section.hasClass('sectionSelected'))
                                {
                                    return false;
                                }

                                if (item.hasClass("calculatedField"))
                                {
                                    var calculatedField = CalculatedFields_Model.Get(item.data().fieldid);
                                    if (calculatedField == undefined)
                                    {
                                        return false;
                                    }

                                    var currentSectionData = getSectionData(section);
                                    return calculatedField.schemaObject === currentSectionData.rootModule + "." + currentSectionData.rootObject &&
                                        (calculatedField.relationshipPath ?? "") === (currentSectionData.entireRelationshipPath ?? "");
                                }
                                else
                                {
                                    return (item.is(".variable.treebrowserow")) || !item.hasClass('jsColumn');
                                }
                            },
                            drop: function (event, ui)
                            {
                                if (ui.draggable.hasClass("calculatedField"))
                                {
                                    var calculatedFieldID = ui.draggable.data().fieldid;
                                    var calculatedField = CalculatedFields_Model.Get(calculatedFieldID);
                                    skyward.reporting.addCalculatedField(calculatedField, currentLine, {
                                            top: ui.offset.top - currentLine.offset().top,
                                            left: ui.offset.left - currentLine.offset().left
                                        });
                                }
                                else if (ui.draggable.hasClass("variable"))
                                {
                                    const data = ui.draggable.data();
                                    skyward.reporting.insertVariableColumn(data.model, data.view, {
                                            top: ui.offset.top - currentLine.offset().top,
                                            left: ui.offset.left - currentLine.offset().left
                                        }, currentLine);
                                }
                                else
                                {
                                    var currentSection = currentLine.closest(".sectionSelected");
                                    var params = {
                                        currentLine: currentLine,
                                        section: currentSection,
                                        position: {
                                            top: ui.offset.top - currentLine.offset().top,
                                            left: ui.offset.left - currentLine.offset().left
                                        },
                                        autoCreatedColumn: true
                                    };

                                    if (ui.draggable.hasClass("promptItem"))
                                    {
                                        params.promptItem = ui.draggable;
                                        insertPromptListItem(params);
                                    }
                                    else if (ui.draggable.hasClass("fieldSelectionSectionRow"))
                                    {
                                        params.treeBrowseData = ui.draggable.data();
                                        insertTreeBrowseItem(params);
                                    }
                                    else
                                    {
                                        var treeBrowseData = skyward.treebrowse.getTreeBrowseDataByName("reportObjectBrowser");
                                        params.treeBrowseData = treeBrowseData.getFieldData(ui.draggable);
                                        insertTreeBrowseItem(params);
                                    }
                                }
                            },
                            tolerance: "pointer"
                        }).disableSelection();
                }
            });

            // Disable selection on relationships
            designer.find('.selectable.relationship').each(function ()
            {
                var $label = $(this);
                var $span = $label.parent();
                var $li = $span.parent();

                $label.disableSelection();
                $span.disableSelection();
                $li.disableSelection();
            });

            designer.find(".reportDesignerPromptsList").each(function (index, element)
            {
                $(element).find(".jsDraggable").draggable(getDefaultDraggableOptions()).disableSelection();
            });

            var promptList = designer.find(".reportDesignerPromptsList");

            promptList.off("dblclick.insertPromptListItem", ".promptItem").on("dblclick.insertPromptListItem", ".promptItem", function ()
            {
                const row = currentRow || currentSelectedColumns.getRowByFirstSelectedColumn();

                if (row)
                {
                    var promptItem = $(this);
                    var currentSection = row.closest(".sectionSelected");
                    var params = {
                        currentLine: row,
                        section: currentSection,
                        position: { top: null, left: null }
                    };

                    params.promptItem = promptItem;
                    insertPromptListItem(params);
                }
            });

            initLineResizing();
            initColumnWidthSlider($section);

            initializeToolbarEvents();
            initFieldsUsedInReport(sectionData);
        };

        skyward.reporting.onFilterBuilderSave = function (data)
        {
            skyward.reporting.updateHiddenField();
            if (data.conditionChanged)
            {
                skyward.reporting.prompts.populateExtraColumnOptionsWithPrompts($(".reportDesignerPromptFieldsMenu"));
            }
            initFieldsUsedInReport();
        }

        const initFieldsUsedInReport = function (sectionData)
        {
            if (!sectionData)
            {
                const section = getCurrentSection();
                if (section && section.length > 0)
                {
                    sectionData = getSectionData(section);
                }
            }

            if (sectionData)
            {
                const usedFields = skyward.reporting.fields.buildFieldsList([], sectionData.sectionID, false, false, false);

                /** @type {PanelData[]} */
                const panelData = [
                    {
                        selector: ".js-usedFieldsPanel",
                        fields: usedFields,
                        buildFieldPath: true
                    }
                ];

                skyward.fieldselectionpanels.initializePanels(leftSidePanel, panelData, insertFieldsUsedInReportItem, null);
            }

            leftSidePanel.find(".fieldSelectionSectionTable").each(function (index, element)
            {
                $(element).find(".fieldSelectionSectionRow").draggable(
                    $.extend(getDefaultDraggableOptions(),
                        {
                            helper: function ()
                            {
                                const convertedItem = $(this).clone();
                                convertedItem.find('svg,input').remove();
                                convertedItem.text(convertedItem.find('.fieldCell .fieldCellContent').text());
                                convertedItem.css({ "z-index": 999, display: undefined, "background-color": "white" });
                                convertedItem.width(convertedItem.data('fieldWidth') || DEFAULT_COLUMN_WIDTH);
                                convertedItem.height(convertedItem.data('fieldHeight') || DEFAULT_COLUMN_HEIGHT);
                                return convertedItem;
                            },
                            appendTo: ".reportDesignerGrid"
                        })
                ).disableSelection()
            });
        };

        skyward.reporting.openFieldUsageLocationsModal = function (field)
        {
            const section = getCurrentSection();
            const sectionData = getSectionData(section);
            const placesToSearch = usageLocations.fieldPlacesToSearch(field, sectionData.sectionID);

            usageLocations.openModal("field-usages-dialog", placesToSearch.traverse, field.displayPathText);
        }

        /** @type {SelectFieldHandler} */
        const insertFieldsUsedInReportItem = function (event, fieldData)
        {
            const currentSection = designer.find(".sectionSelected");
            const currentLine = getSectionSelectedRow(currentSection);

            insertTreeBrowseItem({
                    treeBrowseData: fieldData,
                    currentLine: currentLine,
                    section: currentSection,
                    position:
                    {
                        top: null,
                        left: null
                    }
                });
        };

        var initColumnDragging = function ($column)
        {
            if ($column.draggable("instance"))
            {
                $column.draggable("destroy"); //remove the draggable if it exists already
            }

            const isRotated = $column.data("rotation") != 0; //used to determine if we should do custom containment which does not work with snapping

            $column.draggable(
                {
                    cursorAt: { left: 0, top: 0 },
                    cursor: "move",
                    containment: isRotated ? "false" : "parent",
                    scroll: true,
                    helper: "original",
                    distance: 5,
                    snap: true,
                    snapMode: "inner",
                    snapTolerance: 10,
                    grid: [1, 1],
                    opacity: 0.8,
                    start: function (event, ui)
                    {
                        ui.helper.css("z-index", 9999);

                        if (isRotated)
                        {
                            let parentRect = this.parentElement.getBoundingClientRect();
                            let rect = this.getBoundingClientRect();
                            // [x,y] position of top left point
                            let xPos = $(ui.helper).data().positionX + parentRect.left;
                            let yPos = $(ui.helper).data().positionY + parentRect.top;
                            ui.helper.bounds =
                            {
                                parentRect: parentRect,
                                leftWidth: xPos - rect.left,
                                rightWidth: rect.right - xPos,
                                topHeight: yPos - rect.top,
                                bottomHeight: rect.bottom - yPos,
                                mouseXOffset: event.pageX - xPos,
                                mouseYOffset: event.pageY - yPos
                            };
                        }
                    },
                    drag: function (event, ui)
                    {
                        if (isRotated)
                        {
                            let parentRect = ui.helper.bounds.parentRect;
                            let bottomMax = parentRect.height - ui.helper.bounds.bottomHeight;
                            let rightMax = parentRect.width - ui.helper.bounds.rightWidth;
                            ui.position.top = Math.min(Math.max(ui.helper.bounds.topHeight, event.pageY - ui.helper.bounds.parentRect.top - ui.helper.bounds.mouseYOffset), bottomMax);
                            ui.position.left = Math.min(Math.max(ui.helper.bounds.leftWidth, event.pageX - ui.helper.bounds.parentRect.left - ui.helper.bounds.mouseXOffset), rightMax);
                        }
                    },
                    stop: function (event, ui)
                    {
                        const clearSelectedColumns = !keyboardCommands.isMultiSelectKeyPressed(event);

                        setSelectedColumn(ui.helper, clearSelectedColumns);
                        const roundedLeft = $.safeRound(ui.position.left);
                        const roundedTop = $.safeRound(ui.position.top);
                        positionX.val(roundedLeft);
                        positionY.val(roundedTop);
                        ui.helper.data('positionX', roundedLeft);
                        ui.helper.data('positionY', roundedTop);
                        ui.helper.css('z-index', "");

                        if (keyboardCommands.isSmartDragKeyPressed(event))
                        {
                            cloneDataAndCssToRelatedColumns(ui.helper, { 'positionX': roundedLeft, 'positionY': roundedTop }, { "top": roundedTop + "px", "left": roundedLeft + "px" });
                        }

                        updateHiddenField();
                    }
                });
        };

        var initLineResizing = function ()
        {
            var resizeLines = designer.find(".lineWrapper:not(.jsPrintOnOneLinePreviewLine)");
            resizeLines.resizable({
                minWidth: 5,
                handles: "s",
                autoHide: false,
                stop: function (e, ui)
                {
                    setRowHeight(ui.element, ui.element.outerHeight());
                    updateHiddenField();
                    if (ui.element.hasClass("row-selected"))
                    {
                        setRowProperties(ui.element);
                    }
                }
            });
        };

        var initColumnWidthSlider = function ($section)
        {
            getSectionAllColumns($section).each(function ()
            {
                var jqThis = $(this);
                jqThis.resizable(columnResizableOptions);
            });
        };

        var initLeftSidePanelResizing = function ()
        {
            leftSidePanel.resizable({
                minWidth: 230,
                handles: "e",
                autoHide: false,
                stop: function (e, ui)
                {
                    ui.element.data("widthOverride", ui.size.width);
                    updateUserSettings();
                }
            });
        };

        const initLeftSidePanelToggling = function ()
        {
            if (leftSidePanel.data('panelInitialized'))
            {
                return;
            }

            leftSidePanel.data('panelInitialized', true);

            const panelStateData = leftSidePanel.data('initPanelState');

            const panelMap = {
                usedFieldsIsOpen: leftSidePanel.find('.js-usedFieldsPanel'),
                allFieldsIsOpen: leftSidePanel.find('.js-reportDesignerFieldsMenu'),
                aggregateFieldsIsOpen: leftSidePanel.find('.reportDesignerAggregateFieldsMenu'),
                calculatedFieldsIsOpen: leftSidePanel.find('.reportDesignerCalculatedFieldsMenu'),
                promptFieldsIsOpen: leftSidePanel.find('.reportDesignerPromptFieldsMenu'),
                environmentVariableFieldsIsOpen: leftSidePanel.find('.reportDesignerEnvironmentVariableFieldsMenu'),
                variablesIsOpen: leftSidePanel.find('.reportDesignerVariablesMenu'),
                specialFieldsIsOpen: leftSidePanel.find('.reportDesignerSpecialFieldsMenu'),
                xmlExportSettingsIsOpen: leftSidePanel.find('.jsXMLExportProperties'),
                csvExportSettingsIsOpen: leftSidePanel.find('.reportDesignerCSVProperties'),
                fixedWidthExportSettingsIsOpen: leftSidePanel.find('.jsFixedWidthExportProperties'),
                jsonExportSettingsIsOpen: leftSidePanel.find('.jsJSONExportProperties')
            };

            const debouncedUpdateUserSettings = skyward.debounce(updateUserSettings, 1000, 1000);

            $.each(panelMap, function (stateKey, $panel)
            {
                const $header = $panel.find('.collapsePanelHeader');
                const $contents = $panel.find('.collapsePanelContents');
                if (panelStateData[stateKey] !== $contents.hasClass('expanded'))
                {
                    skyward.collapsePanel.toggle.apply($header);
                }

                $panel.off('collapsepanel:toggled').on('collapsepanel:toggled', function (e, data)
                {
                    panelStateData[stateKey] = data.expanded;
                    leftSidePanel.data('initPanelState', panelStateData);
                    debouncedUpdateUserSettings();
                });
            });
        };

        var cloneDataAndCssToRelatedColumns = function (sourceColumn, data, css)
        {
            var columnId = sourceColumn.data("dataColumnId");
            if (columnId)
            {
                var relatedHeaderColumn = getSectionHeaderLines(getParentSection(sourceColumn)).find("[data-data-column-id='" + columnId + "']");
                if (relatedHeaderColumn)
                {
                    relatedHeaderColumn.data(data);
                    relatedHeaderColumn.css(css);
                }
            }
        };

        var initLeftSidePanel = function ()
        {
            const $section = designer.find('.sectionSelected');
            initDesignerUI($section);
            initLeftSidePanelResizing();
            initLeftSidePanelToggling();

            const treeGridDiv = leftSidePanel.find(".treebrowse__treeGridDiv");

            if (treeGridDiv.length > 0)
            {
                treeGridDiv.off("treeBrowseLevelChanged").on("treeBrowseLevelChanged", initLeftSidePanel);
                treeGridDiv.off("treeBrowseInitialize").on("treeBrowseInitialize", function (event, treeBrowseData, ajaxSuccessData)
                {
                    hidePageNumberFields(!$section.hasClass("jsHeaderFooterSection"));
                    initDesignerUI($section);

                    treeBrowseData.onRowMounted = function (rowElement)
                    {
                        $(rowElement).draggable($.extend(
                            getDefaultDraggableOptions(),
                            {
                                helper: function ()
                                {
                                    const convertedItem = $(this).clone();
                                    convertedItem.find('svg,input').remove();
                                    convertedItem.css("z-index", 999);
                                    convertedItem.width(convertedItem.data('fieldWidth'));
                                    convertedItem.height(DEFAULT_COLUMN_HEIGHT);
                                    return convertedItem;
                                },
                                appendTo: ".reportDesignerGrid"
                            })
                        ).disableSelection();
                    };
                });
            }

            if (!leftSidePanel.hasClass("expanded"))
            {
                leftSidePanel.find(".ui-resizable-handle").hide();
            }

            leftSidePanel.off('click', '.outlineSelected').on('click', '.outlineSelected', function ()
            {
                leftSidePanel.find('.outlineSelected.selected').removeClass('selected');
                $(this).addClass('selected');
            });

            const panelParts = leftSidePanel.find(".collapsePanel");
            const fieldMenu = panelParts.filter(".js-reportDesignerFieldsMenu");
            fieldMenu.off("treeBrowseSave").on("treeBrowseSave", function (_event, _treeBrowseData, rowData)
            {
                insertSidePanelField(rowData);
            });

            $(".jsDesignerLeftSidePanel .slidingContainerButton").off("click.reportDesigner").on("click.reportDesigner", skyward.debounce(updateUserSettings, 1000, 1000));
        };

        var insertSidePanelField = function (rowData)
        {
            var currentLine = getSectionSelectedRow(designer.find('.sectionSelected'));
            var currentSection = currentLine.closest(".sectionSelected");
            if (rowData.isCalculatedField)
            {
                skyward.reporting.addCalculatedField(rowData.id);
            }
            else
            {
                insertTreeBrowseItem(
                    {
                        treeBrowseData: rowData,
                        currentLine: currentLine,
                        section: currentSection,
                        position:
                        {
                            top: null,
                            left: null
                        }
                    });
            }
        };

        var updateUserSettings = function ()
        {
            $.ajax(
                {
                    url: skyward.global.virtualDir + "Reporting/Report/UpdateUserSettings",
                    type: "POST",
                    dataType: "JSON",
                    data:
                    {
                        width: leftSidePanel.data("widthOverride"),
                        visible: leftSidePanel.hasClass("expanded"),
                        usedFieldsIsOpen: usedFieldsMenu.hasClass("expanded"),
                        allFieldsIsOpen: allFieldsMenu.hasClass("expanded"),
                        aggregateFieldsIsOpen: aggregateFieldsMenu.hasClass("expanded"),
                        calculatedFieldsIsOpen: calculatedFieldMenu.hasClass("expanded"),
                        promptFieldsIsOpen: promptFieldsMenu.hasClass("expanded"),
                        environmentVariableFieldsIsOpen: environmentVariableFieldsMenu.hasClass("expanded"),
                        variablesIsOpen: variablesMenu.hasClass("expanded"),
                        specialFieldsMenuIsOpen: specialFieldsMenu.hasClass("expanded"),
                        exportSettingsIsOpen: (xmlExportSettingsMenu.hasClass("expanded") || csvExportSettingsMenu.hasClass("expanded") || fixedWidthExportSettingsMenu.hasClass("expanded") || jsonExportSettingsMenu.hasClass("expanded"))
                    }
                });

            return true;
        };

        var hidePageNumberFields = function (hide)
        {
            //page number fields can lead to off by one errors if the section's data line is on the next page, so don't allow the user to put them in a main or detail section
            //end time is not known until the report is complete, results in errors if not used in a header/footer as well
            var pageNumberFields = designer.find(".reportDesignerSpecialFieldsMenu").find("[data-page-section-column-type='PageNumber'], " +
                "[data-page-section-column-type='PageTotal'], " +
                "[data-page-section-column-type='EndTime']");
            if (hide)
            {
                pageNumberFields.hide();
            }
            else
            {
                pageNumberFields.show();
            }
        };

        var getNewID = function ()
        {
            return Math.floor((Math.random() + 1) * 10000000);
        };

        var cloneColumnTemplateByClass = function (className)
        {
            return $(".columnTemplates").find(`.${className}`).clone().removeClass(className);
        };

        // accepts args = { type: string, text: string (optional), relatedItem: jQuery object, usesSingleLineIndex: boolean }
        var createNewColumn = function (args)
        {
            let newColumn, relatedData, newDataColumnId = getNewID();
            let fieldType;

            switch (args.type)
            {
                case COLUMN_TYPES.DATA_COLUMN:
                case COLUMN_TYPES.GROUP_COLUMN:
                    newColumn = cloneColumnTemplateByClass('jsDataColumnTemplate');
                    break;
                case COLUMN_TYPES.TEXT_COLUMN:
                    newColumn = cloneColumnTemplateByClass('jsTextColumnTemplate');
                    break;
                case COLUMN_TYPES.FORMTEXT_COLUMN:
                    newColumn = cloneColumnTemplateByClass('jsFormTextColumnTemplate');
                    break;
                case COLUMN_TYPES.DYNAMIC_IMAGE_COLUMN:
                    newColumn = cloneColumnTemplateByClass('jsDynamicImageColumnTemplate');
                    break;
                case COLUMN_TYPES.IMAGE_COLUMN:
                    newColumn = cloneColumnTemplateByClass('jsImageColumnTemplate');
                    break;
                case COLUMN_TYPES.HEADER_COLUMN:
                case COLUMN_TYPES.GROUP_COLUMN_HEADER:
                    newColumn = cloneColumnTemplateByClass('jsHeaderColumnTemplate');
                    fieldType = DATA_TYPES.STRING
                    break;
                case COLUMN_TYPES.FOOTER_COLUMN:
                    newColumn = cloneColumnTemplateByClass('jsFooterColumnTemplate');
                    break;
                case COLUMN_TYPES.PAGE_SECTION_COLUMN:
                    newColumn = cloneColumnTemplateByClass('jsPageSectionColumnTemplate');
                    break;
                case COLUMN_TYPES.AGGREGATE_COLUMN:
                case COLUMN_TYPES.GROUP_COLUMN_AGGREGATE:
                    newColumn = cloneColumnTemplateByClass('jsReportAggregateColumnTemplate');
                    break;
                case COLUMN_TYPES.CALCULATED_COLUMN:
                    newColumn = cloneColumnTemplateByClass('jsCalculatedColumnTemplate');
                    break;
                case COLUMN_TYPES.VARIABLE_COLUMN:
                    newColumn = cloneColumnTemplateByClass('jsVariableColumnTemplate');
                    newColumn.data("variableIdToGet", args.variableIdToGet).attr("data-variable-id-to-get", args.variableIdToGet);
                    newColumn.data("fieldName", args.variableBinding);
                    break;
                case COLUMN_TYPES.FUTURE_VALUE_COLUMN:
                    newColumn = cloneColumnTemplateByClass('jsFutureValueColumnTemplate');
                    break;
            }

            if (newColumn)
            {
                newColumn.removeAttr('id');
                newColumn.attr('data-field-column-type', args.type);

                if (args.relatedItem)
                {
                    relatedData = args.relatedItem.data();
                    if (!fieldType)
                    {
                        fieldType = skyward.reporting.reportColumn.getFieldType(relatedData);
                    }
                    if (args.type != COLUMN_TYPES.VARIABLE_COLUMN)
                    {
                        if (!args.text && relatedData.isTreeBrowseRow || args.relatedItem.hasClass('promptItem'))
                        {
                            // calculated fields only have displayPathText
                            relatedData.designerText = relatedData.displayName || relatedData.displayPathText || args.relatedItem.find(".visualLabel").text();
                            relatedData.dataColumnId = newDataColumnId;
                        }

                        if (args.type == COLUMN_TYPES.DYNAMIC_IMAGE_COLUMN)
                        {
                            const maxWidth = relatedData.maxWidth ? relatedData.maxWidth : relatedData.fieldWidth;
                            const maxHeight = relatedData.maxHeight ? relatedData.maxHeight : relatedData.fieldHeight;
                            newColumn.attr('data-max-width', maxWidth);
                            newColumn.attr('data-max-height', maxHeight);
                            newColumn.attr('data-height-multiplier', maxWidth / maxHeight);
                            newColumn.attr('data-width-multiplier', maxHeight / maxWidth);
                        }

                        newColumn.attr('data-data-column-id', relatedData.dataColumnId);
                        newColumn.attr('data-field-name', fieldType == DATA_TYPES.GROUP_COLUMN ? relatedData.fieldPath : relatedData.fieldName);
                        newColumn.attr('data-field-width', relatedData.fieldWidth);
                        newColumn.attr('data-field-height', relatedData.fieldHeight);
                        newColumn.attr('data-is-calculated', relatedData.isCalculated);
                        newColumn.attr('data-is-operation', relatedData.isOperation);

                        if (relatedData.pageSectionColumnType)
                        {
                            newColumn.attr('data-page-section-column-type', relatedData.pageSectionColumnType);
                        }

                        if (relatedData.sourceColumnId)
                        {
                            newColumn.attr('data-source-column-id', relatedData.sourceColumnId);
                        }

                        if (relatedData.operationId)
                        {
                            newColumn.attr('data-operation-id', relatedData.operationId);
                        }

                        newColumn.attr('data-column-field-type', fieldType);
                        newColumn.attr('data-prompt-unique-id', relatedData.promptUniqueId);
                        //not sure if we need these
                        newColumn.attr('data-field-path', relatedData.fieldPath);
                        newColumn.attr('data-display-path', relatedData.displayPathText || relatedData.displayPath);
                        newColumn.attr('data-data-object-full-name', relatedData.dataObjectFullName);
                        newColumn.attr('data-is-dynamic-column', relatedData.isDynamicColumn);

                        if (relatedData.parameters)
                        {
                            newColumn.data("parameters", relatedData.parameters);
                        }

                        if (!relatedData.isDynamicColumn)
                        {
                            //it'd be better if this was handled on the server and the client didn't need to worry about it
                            newColumn.attr('data-absolute-field-path', relatedData.absoluteFieldPath);
                        }
                    }
                    else
                    {
                        newColumn.data("fieldName", "{_variable." + args.variableIdToGet + "}");
                    }

                    const newColumnID = getNewID();
                    newColumn.attr('data-column-id', newColumnID);
                    newColumn.attr('data-field-type', args.dataTypeCategoryName || fieldType);
                    newColumn.attr('data-binding-string', "{_column." + newColumnID + "}");
                }
                else
                {
                    newColumn.attr('data-data-column-id', newDataColumnId);
                    newColumn.attr('data-column-id', newDataColumnId);
                    newColumn.attr('data-binding-string', "{_column." + newDataColumnId + "}");
                }

                if (args.type == COLUMN_TYPES.HEADER_COLUMN || args.type == COLUMN_TYPES.TEXT_COLUMN)
                {
                    if (args.usesSingleLineIndex)
                    {
                        args.text = withSingleLineIndexBinding(args.text ?? relatedData.designerText);
                    }
                }

                // override the text if it is provided
                if (args.text)
                {
                    newColumn.find('.visualLabel').text(args.text);
                    newColumn.data("designerText", args.text);  // we need to set both data and attribute
                    newColumn.attr('data-designer-text', args.text);
                }
                else if (relatedData)
                {
                    newColumn.data("designerText", relatedData.designerText);  // we need to set both data and attribute
                    newColumn.attr('data-designer-text', relatedData.designerText);
                }
            }

            return newColumn;
        };

        // sectionID is required, but the sections object is optional
        // this should eventually go on a report definition model
        var getSectionByIDFromCurrentReportDefinition = function (sectionID, sections)
        {
            sections = sections || skyward.reporting.currentReportDefinition.MainSections;
            for (var i = 0; i < sections.length; i++)
            {
                if (sections[i].SectionID == sectionID)
                {
                    return sections[i];
                }

                var nestedSection = getSectionByIDFromCurrentReportDefinition(sectionID, sections[i].DetailSections);
                if (nestedSection)
                {
                    return nestedSection;
                }
            }

            return undefined;
        };

        var getSectionElementBySectionID = function (sectionID)
        {
            return $(`.jsSection[data-section-id='${sectionID}']`);
        }

        const getSectionElementByColumn = function (column)
        {
            return column.closest(".jsSection");
        };

        // accepts a sectionDefinition from the current report definition
        var removeSection = function (sectionDefinition, removingNestedSection)
        {
            for (let i = 0; i < sectionDefinition.DetailSections.length; i++)
            {
                // remove deepest child sections first
                removeSection(sectionDefinition.DetailSections[i], true);
            }

            const section = getSectionElementBySectionID(sectionDefinition.SectionID);

            getSectionAllColumns(section).each(function (index, e)
            {
                removeRelatedColumns($(e), false);
            });

            if (!areThereMultipleSectionsOnTheSameObject(section))
            {
                getSectionAllCalculatedFieldIDs(section).forEach(function (calculatedFieldID)
                {
                    removeCalculatedFieldBySection(section, calculatedFieldID);
                });
            }

            section.remove();

            if (!removingNestedSection)
            {
                // these things only need to happen once all sections have been removed
                removeInvalidPageNumberColumns(false);
                updateHiddenField();
                labelBuilder.buildLabelColumns(true);
                setActiveSection(entireCanvas.children('.jsSection').first());  // Select report header section
            }

            var mainSections = designer.find('.jsMainSection');
            if (mainSections.length < 2)
            {
                skyward.controls.enable(exportProperties.xml.includeRootNode);
            }

            initFieldsUsedInReport();
            skyward.reporting.updateVariableUsageLocations();
        };

        var initializeToolbarEvents = function ()
        {
            entireCanvas.find(".sectionToolbarRight .button").each(function ()
            {
                var toolbarButton = $(this);
                toolbarButton.attr("title", toolbarButton.attr("alt"));
            });

            entireCanvas.find('.moveSection.down').off('click').on('click', function ()
            {
                var currentSection = $(this).closest('.jsSection');
                var nextSection = currentSection.next('.jsSection');
                if (nextSection.length > 0)
                {
                    currentSection.detach().insertAfter(nextSection);
                    updateUIAfterSectionMove(currentSection, nextSection);
                }
            });

            entireCanvas.find('.moveSection.up').off('click').on('click', function ()
            {
                var currentSection = $(this).closest('.jsSection');
                var prevSection = currentSection.prev('.jsSection');
                if (prevSection.length > 0)
                {
                    currentSection.detach().insertBefore(prevSection);
                    updateUIAfterSectionMove(currentSection, prevSection);
                }
            });

            var updateUIAfterSectionMove = function (currentSection, siblingSection)
            {
                currentSection.data("pageBreakBeforeSection", false);
                siblingSection.data("pageBreakBeforeSection", false);
                removeInvalidPageNumberColumns();
                updateHiddenField();
                labelBuilder.buildLabelColumns(true);
            };

            entireCanvas.find('.sectionToolbar').off('click').on('click', function (event)
            {
                if (event.target != this)
                {
                    //don't do anything if clicking on a button
                    return;
                }

                var sectionToolbar = $(this);
                var section = sectionToolbar.closest('.jsSection');
                setActiveSection(section);
                setSelectedRowByRow(sectionToolbar);
            });

            entireCanvas.find('.toggleButtonSpan').off('click').on('click', function ()
            {
                var span = $(this);
                var toolbar = span.closest('.sectionToolbar');
                var parentSection = toolbar.parent();
                var bothSections = parentSection.add(span);
                var icon = span.find("svg");

                if (span.hasClass('showSection'))
                {
                    skyward.svg.swapSvg(icon, "ChevronDown", "BrowseLevel");
                    bothSections.removeClass('showSection').addClass('collapseSection');
                    parentSection.trigger("collapse");
                }
                else
                {
                    skyward.svg.swapSvg(icon, "ChevronUp", "BrowseLevel");
                    bothSections.removeClass('collapseSection').addClass('showSection');
                    parentSection.trigger("expand");
                }
            });

            entireCanvas.find('.removeSectionUnbound').removeClass('removeSectionUnbound').on('click', function ()
            {
                var button = $(this);
                skyward.modalLayer.confirmDelete("Section").then(function ()
                {
                    var sectionID = button.closest('.jsSection').data('sectionId');
                    var sectionDefinition = getSectionByIDFromCurrentReportDefinition(sectionID);
                    removeSection(sectionDefinition);
                });
            });

            $('.jsPromptEditor').off('click').on('click', skyward.reporting.prompts.promptEditor.open);

            entireCanvas.find('.get_sorts_modal').off('click').on('click', function ()
            {
                const $section = $(this).closest('.jsSection');
                setActiveSection($section);
                if ($section.find(".ui-selected, .row-selected").length <= 0)
                {
                    setSelectedRowByRow(getSectionDataLines($section).first());
                }
                getSorts($section, designer.data("reportType"));
            });

            entireCanvas.find('.get_detail_modal').off('click').on('click', function ()
            {
                var detailButton = $(this);
                var section = detailButton.closest('.jsSection');
                setActiveSection(section);
                getDetail(section, detailButton);
            });

            var addTextFieldOfType = function (toolbarButton, columnType, createTextFieldAction)
            {
                if (!skyward.buttonManager.isElementDisabled(toolbarButton))
                {
                    const currentSection = toolbarButton.closest('.jsSection');
                    const reportLine = getSectionSelectedRow(currentSection);
                    if (!reportLine.hasClass("sectionToolbar"))
                    {
                        const params =
                        {
                            newItem: createNewColumn({ type: columnType }),
                            reportLine: reportLine,
                            section: currentSection,
                            position: { top: null, left: null }
                        };

                        insertColumnHelperFunction(params);
                        createTextFieldAction(reportLine);
                    }
                }
            }

            entireCanvas.find('.add_text_field').off('click').on('click', function ()
            {
                var currentSection = $(this).closest('.jsSection');
                setActiveSection(currentSection);
                addTextFieldOfType($(this), COLUMN_TYPES.TEXT_COLUMN, function (_reportLine)
                {
                    editorColumnText.focus();
                });
            });

            entireCanvas.find('.add_formtext_field').off('click').on('click', function ()
            {
                var currentSection = $(this).closest('.jsSection');
                setActiveSection(currentSection);
                addTextFieldOfType($(this), COLUMN_TYPES.FORMTEXT_COLUMN, function (reportLine)
                {
                    const currentRowData = reportLine.data();
                    const sectionData = getCurrentSection().data();
                    showFormTextModal(currentRowData, sectionData);
                });
            });

            entireCanvas.find('.add_image_field').off('click').on('click', function ()
            {
                var currentSection = $(this).closest('.jsSection');
                setActiveSection(currentSection);
                var reportLine = getSectionSelectedRow(currentSection);
                setSelectedRowByRow(reportLine);
                if (!reportLine.hasClass("sectionToolbar"))
                {
                    // create the data column
                    var params = {
                        newItem: createNewColumn({ type: COLUMN_TYPES.IMAGE_COLUMN }),
                        reportLine: reportLine,
                        section: currentSection,
                        position: { top: null, left: null }
                    };

                    insertColumnHelperFunction(params);
                }
            });

            entireCanvas.find('.add_report_line').off('click').on('click', function ()
            {
                const section = $(this).closest('.jsSection');
                setActiveSection(section);
                const sectionDisplayOnSingleLine = section.data("displayOnSingleLine");
                const parentSectionHasDisplayOnSingleLine = isParentDisplayOnSingleLine(section);
                const reportSorts = section.data("reportSorts");

                $.ajax(
                    {
                        url: skyward.global.virtualDir + "Reporting/Report/GetReportLineOptions/",
                        type: "POST",
                        dataType: "JSON",
                        data:
                        {
                            reportSortsJson: JSON.stringify(reportSorts),
                            allowSortHeadersAndSortAggregates: !parentSectionHasDisplayOnSingleLine,
                            allowFooterLines: !parentSectionHasDisplayOnSingleLine
                        },
                        success: showReportLineOptions
                    });

                async function showReportLineOptions(data)
                {
                    // Process HTML through processViewPartHtml to handle any script tags
                    var processedHtml = await skyward.global.processViewPartHtml(data.html);
                    var popupContent = $("<div class='popup addReportLinePopup'/>").append(processedHtml);
                    var okButton = { text: "Save", click: insertAndRefresh, additionalClasses: "okButton", icon: "Save" };
                    var menu = skyward.modalLayer.showDialog("Add Report Line", popupContent, [okButton, { text: "Cancel", icon: "Cancel" }], { width: 650, closeOnClick: false, automationID: "add-line-dialog" });

                    menu.content.on("dblclick", ".reportLineOptions", function ()
                    {
                        menu.footer.find(".okButton").click();
                    });
                    menu.content.on("click", '.reportLineOptions', function ()
                    {
                        $(this).addClass('line-selected')
                            .siblings().removeClass('line-selected');
                    });

                    function insertAndRefresh()
                    {
                        insertLine().then(refresh);

                        function insertLine()
                        {
                            const selectedOption = menu.content.find('.line-selected');
                            const waitForInsert = $.Deferred();

                            if (selectedOption.length != 0)
                            {
                                const selectedOptionData = selectedOption.data();
                                insertSelectedLineOption(selectedOptionData, waitForInsert);
                            }
                            menu.close();
                            return waitForInsert;
                        }

                        function insertSelectedLineOption(selectedOptionData, waitForInsert)
                        {
                            let lineIndex = 1;
                            if (selectedOptionData.lineType == 'DataLine' || selectedOptionData.lineType == 'HeaderLine')
                            {
                                lineIndex = getMaxSectionLineIndex(section) + 1;
                            }
                            else if (selectedOptionData.lineType == 'SortAggregateLine')
                            {
                                lineIndex = getNextSortAggregateLineIndex(section, selectedOptionData.relatedReportSortId) || 1;
                            }
                            else if (selectedOptionData.lineType == 'SortHeaderLine')
                            {
                                lineIndex = getNextSortHeaderLineIndex(section, selectedOptionData.relatedReportSortId) || 1;
                            }
                            else if (selectedOptionData.lineType == 'SectionAggregateLine')
                            {
                                const maxSectionAggregateLineIndex = getMaxSectionAggregateLineIndex(section) || 0;
                                lineIndex = maxSectionAggregateLineIndex + 1;
                            }

                            $.ajax(
                                {
                                    url: skyward.global.virtualDir + "Reporting/Report/GetNewReportLineHTML/",
                                    type: "POST",
                                    dataType: "JSON",
                                    data:
                                    {
                                        reportLineType: selectedOptionData.lineType,
                                        sortFieldName: selectedOptionData.fieldName,
                                        calculatedFieldID: selectedOptionData.operationId,
                                        lineIndex: lineIndex,
                                        displayOnSingleLine: sectionDisplayOnSingleLine,
                                        parentDisplayOnSingleLine: parentSectionHasDisplayOnSingleLine,
                                        sortOrder: selectedOptionData.sortOrder,
                                        relatedReportSortID: selectedOptionData.relatedReportSortId
                                    },
                                    success: function (returnData)
                                    {
                                        const newLine = $(returnData.html);
                                        insertNewLine(section, newLine);
                                        setSelectedRowByRow(newLine);
                                        setActiveSection(section);
                                        if (returnData.additionalLine)
                                        {
                                            insertNewLine(section, $(returnData.additionalLine));
                                        }

                                        waitForInsert.resolve();
                                    }
                                });
                        }

                        function refresh()
                        {
                            updateHiddenField();
                            labelBuilder.buildLabelColumns(true);

                            var sectionData = section.data();
                            var pageWidth = skyward.reporting.currentReportDefinition.PrintablePageWidth;
                            var unusableWidth = sectionData.columnWidth === "" ? 0 : (pageWidth - sectionData.columnWidth) * 72;

                            var unusableAreaBox = skyward.reporting.multiColumnSection.generateUnusableAreaBox(unusableWidth);

                            updateUnsableAreaBox(section, unusableAreaBox, sectionData.applyColumnsToDataLinesOnly);
                        }
                    }
                }
            });

            entireCanvas.find('.remove_report_line').off('click').on('click', function ()
            {
                const section = $(this).closest('.jsSection');
                setActiveSection(section);
                let rowSelected = getSectionSelectedRow(section);

                removeLine(rowSelected);
            });

            entireCanvas.find('.simple_aggregate_math').off('click').on('click', function ()
            {
                var button = $(this);
                if (skyward.buttonManager.isElementDisabled(button))
                {
                    return;
                }

                getCalculatedColumnBuilder({}, button);

            });

            entireCanvas.find('.get_multi_column_modal').off('click').on('click', function ()
            {
                var section = $(this).closest(".jsSection");
                var sectionData = section.data();
                var data =
                {
                    columnWidth: sectionData.columnWidth,
                    columnSpacing: sectionData.columnSpacing,
                    applyColumnsToDataLinesOnly: sectionData.applyColumnsToDataLinesOnly,
                    columnDirection: sectionData.columnDirection,
                    defaultWidth: skyward.reporting.currentReportDefinition.PrintablePageWidth,
                    balanceColumns: sectionData.balanceColumns,
                    currentSectionDisplayOnSingleLine: sectionData.displayOnSingleLine,
                    callBack: function (newData)
                    {
                        var unusableAreaBox = newData.unusableAreaBox;
                        delete newData.unusableAreaBox;
                        updateUnsableAreaBox(section, unusableAreaBox, newData.applyColumnsToDataLinesOnly);

                        $.extend(section.data(), newData);
                        updateHiddenField();
                    }
                };

                skyward.reporting.multiColumnSection.init(data);
            });

            $('.jsAddMainSection').off('click').on('click', function ()
            {
                getMainSection($(this).closest('.jsSection'));
                labelBuilder.buildLabelColumns(true);
            });
        };
        //END UI UPDATE FUNCTIONS

        //FORMATTING & PROPERTIES
        var applyColumnCss = function (item)
        {
            dataFont.find('option').each(function (i, fontOption)
            {
                item.removeClass(fontOption.value);
            });
            item.removeClass(BARCODE_FONT_NAME);

            var itemData = item.data();
            var isMultiline = false;
            var lineHeightByFontFamily = 1;
            var fontSizeAsNumber = parseInt(itemData.dataFontSize.replace("px", ""));

            if (isBarcodeFormatter(itemData.formatter))
            {
                lineHeightByFontFamily = enumMaps.FontFamilyLineHeights[BARCODE_FONT_NAME];
                item.addClass(BARCODE_FONT_NAME);
            }
            else if (itemData.dataFontFamily)
            {
                item.addClass(itemData.dataFontFamily);

                lineHeightByFontFamily = enumMaps.FontFamilyLineHeights[itemData.dataFontFamily];
                isMultiline = itemData.overlapMode == "Expand" || ((itemData.fieldHeight - itemData.paddingTop - itemData.paddingBottom) > fontSizeAsNumber * 2 * lineHeightByFontFamily);
                item.toggleClass("isMultiline", isMultiline);
            }

            item.toggleClass(boldCssClass, itemData.dataBold);
            item.toggleClass(underlineCssClass, itemData.dataUnderline);
            item.toggleClass(italicCssClass, itemData.dataItalic);

            item.css("background-color", itemData.backColor ? itemData.backColor : "transparent");

            let hiddenConditionType = getHiddenConditionType(itemData.isHiddenCondition);

            updateColumnIsHiddenConditionFormatting(item, hiddenConditionType);

            let backgroundColor = itemData.backColor;
            if (!backgroundColor)
            {
                backgroundColor = item.parent('.reportLine').data("backColor");
            }

            if (hiddenConditionType != NOT_HIDDEN_VALUE && backgroundColor)
            {
                setLabelTextBackground(item, backgroundColor);
            }
            else
            {
                removeLabelTextBackground(item);
            }

            item.css({
                "font-size": itemData.dataFontSize,
                "line-height": Math.round(fontSizeAsNumber * lineHeightByFontFamily) + "px",
                "color": itemData.dataForeColor,
                "text-align": (itemData.dataTextAlignment != 'None') ? itemData.dataTextAlignment : 'Left',
                "top": itemData.positionY + "px",
                "left": itemData.positionX + "px",
                "height": itemData.fieldHeight,
                "width": itemData.fieldWidth,
                "padding-left": itemData.paddingLeft + "px",
                "padding-right": itemData.paddingRight + "px",
                "padding-top": itemData.paddingTop + "px",
                "padding-bottom": itemData.paddingBottom + "px",
                "vertical-align": itemData.verticalAlignment != 'None' ? itemData.verticalAlignment : 'Top',
                "transform-origin": "Top Left",
                "transform": "rotate(" + itemData.rotation + "deg)"
            });

            applyBorderCss(item, itemData);

            var itemLabel = item.find('.column_content');
            itemLabel.text(getColumnLabelWithPreText(item));

            if (!isMultiline)
            {
                $.each(itemLabel.text().split("\n"), function (index, value)
                {
                    itemLabel.text(value);
                    return false;
                });
            }
        };

        var updateColumnIsHiddenConditionFormatting = function (column, hiddenConditionType)
        {
            if (hiddenConditionType === undefined)
            {
                hiddenConditionType = getHiddenConditionType(column.data().isHiddenCondition);
            }

            if (currentSelectedColumns.isColumnSelected(column) && currentSelectedColumns.isSingleColumnSelected())
            {
                clearIsHiddenConditionFormatting(column);
            }
            else
            {
                updateIsHiddenConditionClass(column, hiddenConditionType);
            }
        };

        function applyBorderCss(item, itemData)
        {
            if (itemData.borderLeft === true && itemData.borderStyle !== "None")
            {
                item.css("border-left", itemData.borderStyle + " " + itemData.borderWidth + "px " + itemData.borderColor);
            }
            else
            {
                item.css("border-left", "");
            }

            if (itemData.borderRight === true && itemData.borderStyle !== "None")
            {
                item.css("border-right", itemData.borderStyle + " " + itemData.borderWidth + "px " + itemData.borderColor);
            }
            else
            {
                item.css("border-right", "");
            }

            if (itemData.borderTop === true && itemData.borderStyle !== "None")
            {
                item.css("border-top", itemData.borderStyle + " " + itemData.borderWidth + "px " + itemData.borderColor);
            }
            else
            {
                item.css("border-top", "");
            }

            if (itemData.borderBottom === true && itemData.borderStyle !== "None")
            {
                item.css("border-bottom", itemData.borderStyle + " " + itemData.borderWidth + "px " + itemData.borderColor);
            }
            else
            {
                item.css("border-bottom", "");
            }
        }

        var applyRowCss = function (row)
        {
            var rowData = row.data();

            row.css("height", rowData.lineHeight + "px");

            row.css("background-color", rowData.backColor ? rowData.backColor : "transparent");

            applyBorderCss(row, rowData);
        };

        var getSectionOrRowHiddenConditionType = function (sectionOrRow, parentSectionIsHiddenConditionType)
        {
            let conditionType = getHiddenConditionType(sectionOrRow.data().isHiddenCondition);

            if (conditionType === ALWAYS_HIDDEN_VALUE)
            {
                return ALWAYS_HIDDEN_VALUE;
            }

            if (parentSectionIsHiddenConditionType === undefined)
            {
                let parentSection = getParentSection(sectionOrRow);

                parentSectionIsHiddenConditionType = parentSection.length === 0 ? NOT_HIDDEN_VALUE : getSectionOrRowHiddenConditionType(parentSection);
            }

            return Math.max(conditionType, parentSectionIsHiddenConditionType);
        };

        var getParentSectionHiddenConditionType = function (sectionOrRow)
        {
            let parentSection = getParentSection(sectionOrRow);
            if (parentSection.length === 0)
            {
                return NOT_HIDDEN_VALUE;
            }

            let conditionType = getHiddenConditionType(parentSection.data().isHiddenCondition);

            if (conditionType === ALWAYS_HIDDEN_VALUE)
            {
                return ALWAYS_HIDDEN_VALUE;
            }

            return Math.max(conditionType, getParentSectionHiddenConditionType(parentSection));
        };

        var applySectionOrRowHiddenCondition = function (sectionOrRow, parentSectionIsHiddenConditionType)
        {
            let currentSectionIsHiddenConditionType = getSectionOrRowHiddenConditionType(sectionOrRow, parentSectionIsHiddenConditionType);

            updateIsHiddenConditionClass(labelBuilder.getRelatedLabel(sectionOrRow), currentSectionIsHiddenConditionType);

            if (isSection(sectionOrRow))
            {
                for (let row of sectionOrRow.children('.sectionBody').find(".reportLine:not(.jsPrintOnOneLinePreviewLine)"))
                {
                    applySectionOrRowHiddenCondition($(row), currentSectionIsHiddenConditionType);
                }

                for (let detailSection of getDetailSections(sectionOrRow))
                {
                    applySectionOrRowHiddenCondition($(detailSection), currentSectionIsHiddenConditionType);
                }
            }
        };

        var getHiddenConditionType = function (hiddenCondition)
        {
            if (isHiddenSelector.isConditionAlwaysDisplayed(hiddenCondition))
            {
                return NOT_HIDDEN_VALUE;
            }

            if (isHiddenSelector.isConditionAlwaysHidden(hiddenCondition))
            {
                return ALWAYS_HIDDEN_VALUE;
            }

            return CONDITIONALLY_HIDDEN_VALUE;
        };

        var updateIsHiddenConditionClass = function (item, isHiddenConditionType)
        {
            if (!item)
            {
                // the section and row labels can be undefined on initial load
                return;
            }

            clearIsHiddenConditionFormatting(item);

            if (isHiddenConditionType === NOT_HIDDEN_VALUE)
            {
                return;
            }
            item.addClass(HIDDEN_CLASS);
            if (isHiddenConditionType === CONDITIONALLY_HIDDEN_VALUE)
            {
                item.addClass(CONDITIONALLY_HIDDEN_CLASS);
            }
            else if (isHiddenConditionType === ALWAYS_HIDDEN_VALUE)
            {
                item.addClass(ALWAYS_HIDDEN_CLASS);
            }
        };

        var clearIsHiddenConditionFormatting = function (item)
        {
            if (item)
            {
                item.removeClass(ALWAYS_HIDDEN_CLASS);
                item.removeClass(CONDITIONALLY_HIDDEN_CLASS);
                item.removeClass(HIDDEN_CLASS);
            }
        };

        var setDefaultItemFormatting = function (item)
        {
            var parentLine = item.closest('.reportLine');
            if (parentLine.length > 0)
            {
                var cellFormat = JSON.parse(decodeURIComponent(parentLine.data("defaultCellFormatting")));
                applyCellFormatToItem(item, cellFormat);

                var textFormat = JSON.parse(decodeURIComponent(parentLine.data("defaultTextFormatting")));
                applyTextFormatToItem(item, textFormat);

                var borderFormat = JSON.parse(decodeURIComponent(parentLine.data("defaultBorderFormatting")));
                applyBorderFormatToItem(item, borderFormat);
            }
            return item;
        };

        function hideAllProperties()
        {
            reportDesignerProperties.find(".cellProperties, .rowProperties, .sectionProperties, .sortRowProperties, .textProperties, .singleLineHeaderProperties").hide();
        }

        function setSectionProperties(section)
        {
            hideAllProperties();

            var isHeaderFooterSection = section.hasClass("jsHeaderFooterSection");

            if (!isHeaderFooterSection)
            {
                var hasPreviousSibling = section.prev(".jsSection").not(".jsHeaderFooterSection").children(".sectionToolbar").length > 0;

                reportDesignerProperties.find(".sectionProperties").show();
                reportDesignerProperties.find(".sectionWithSiblingProperties").toggle(hasPreviousSibling);
                setSectionPropertiesValues(section);
            }
        }

        var setRowProperties = function (row)
        {
            if (row)
            {
                if (row.hasClass("sectionToolbar"))
                {
                    setSectionProperties(row.parent(".jsSection"));
                }
                else
                {
                    hideAllProperties();

                    var canRepeatAfterStart = false;
                    var isSortHeaderLine = row.hasClass("sortHeaderLine");
                    if (isSortHeaderLine || row.hasClass("jsSortAggregateLine"))
                    {
                        if (isSortHeaderLine)
                        {
                            reportDesignerProperties.find(".sortHeaderRowProperties").show();
                            canRepeatAfterStart = true;
                        }
                        reportDesignerProperties.find(".sortRowProperties").show();
                    }

                    if (row.hasClass("jsDataLine"))
                    {
                        reportDesignerProperties.find(".dataRowProperties").show();
                    }

                    if (row.hasClass("jsHeaderLine"))
                    {
                        reportDesignerProperties.find(".headerRowProperties").show();
                        canRepeatAfterStart = true;
                    }

                    if (row.hasClass("jsFooterLine") || row.hasClass("jsSectionAggregateLine") || row.hasClass("jsSortAggregateLine"))
                    {
                        reportDesignerProperties.find(".endingRowProperties").show();
                    }

                    if (row.hasClass('jsReportFooterLine'))
                    {
                        editorPrintReportFooterOnNewPage.add(editorPrintReportFooterOnNewPage.parent()).show();
                    }

                    reportDesignerProperties.find(".allRowProperties").show();

                    setRowPropertiesValues(row.data(), canRepeatAfterStart);
                }
            }
        };

        //FORMATTING & PROPERTIES
        var setColumnProperties = function ()
        {
            hideAllProperties();

            const identicalColumnData = {};
            const allColumnData = [];
            let selectorToShow = {};
            let anyFutureValue = false;
            for (const column of currentSelectedColumns.getCurrentSelectedColumns())
            {
                const columnData = column.data();
                columnData.lineType = column.parent().data("line-type");

                allColumnData.push(columnData);

                if (columnData.isFutureValue)
                {
                    anyFutureValue = true;
                }

                if (columnData.fieldColumnType === COLUMN_TYPES.AGGREGATE_COLUMN || columnData.fieldColumnType === COLUMN_TYPES.GROUP_COLUMN_AGGREGATE)
                {
                    selectorToShow[".aggregateProperties"] = true;
                }
                else if (columnData.fieldColumnType === COLUMN_TYPES.DYNAMIC_IMAGE_COLUMN)
                {
                    selectorToShow[".dynamicImageProperties"] = true;
                }
                else if (columnData.fieldColumnType === COLUMN_TYPES.IMAGE_COLUMN)
                {
                    selectorToShow[".imageProperties"] = true;
                }
                else if (columnData.fieldColumnType === COLUMN_TYPES.DATA_COLUMN && !columnData.calculation && !columnData.operationId)
                {
                    selectorToShow[".dataProperties"] = true;
                }
                else if (columnData.fieldColumnType === COLUMN_TYPES.VARIABLE_COLUMN)
                {
                    selectorToShow[".variableProperties"] = true;
                }
                else if (columnData.fieldColumnType === COLUMN_TYPES.PAGE_SECTION_COLUMN)
                {
                    selectorToShow[".pageSectionColumnProperties"] = true;
                }
                else if (columnData.fieldColumnType === COLUMN_TYPES.GROUP_COLUMN)
                {
                    if (columnData.lineType === LINE_TYPES.DATA_LINE)
                    {
                        columnData.isFilterableGroupColumn = true;
                        selectorToShow[".groupColumnProperties"] = true;
                    }
                    else if (columnData.lineType === LINE_TYPES.SORT_HEADER_LINE)
                    {
                        selectorToShow[".dataProperties"] = true;
                    }
                }
                else if (columnData.fieldColumnType === COLUMN_TYPES.FUTURE_VALUE_COLUMN)
                {
                    selectorToShow[".futureValueProperties"] = true;
                }
                else if ((columnData.fieldColumnType === COLUMN_TYPES.HEADER_COLUMN || columnData.fieldColumnType === COLUMN_TYPES.TEXT_COLUMN)
                    && isParentDisplayOnSingleLine(column.closest('.jsSection'))
                    && columnData.lineType === LINE_TYPES.HEADER_LINE)
                {
                    selectorToShow[".singleLineHeaderProperties"] = true;
                }
                else
                {
                    selectorToShow[".textProperties"] = true;
                }

                for (let property in columnData)
                {
                    if (identicalColumnData.hasOwnProperty(property))
                    {
                        if (identicalColumnData[property] !== columnData[property])
                        {
                            identicalColumnData[property] = null;
                        }
                    }
                    else if (allColumnData.length === 1)
                    {
                        identicalColumnData[property] = columnData[property];
                    }
                }
            }

            if (allColumnData.length > 1)
            {
                selectorToShow[".multiColumnProperties"] = true;
            }

            reportDesignerProperties.find(Object.keys(selectorToShow).join("")).show();

            if (anyFutureValue)
            {
                editorVariableToSet.closest(".fieldWrapper").hide();
            }

            let hasPromptColumns;
            let allSameColumnType = allColumnData.every(c =>
            {
                let thisIsPromptColumn = isPromptColumn(c);
                if (hasPromptColumns === undefined)
                {
                    hasPromptColumns = thisIsPromptColumn;
                }
                return thisIsPromptColumn == hasPromptColumns;
            });
            if (!allSameColumnType)
            {
                skyward.controls.toggleVisible(pageSectionColumnDisplayTypeFS, false, false);
            }
            else
            {
                skyward.controls.toggleVisible(pageSectionColumnDisplayType, !hasPromptColumns, false);
                skyward.controls.toggleVisible(pageSectionColumnPromptDisplayType, hasPromptColumns, false);
            }

            setColumnExportPropertiesValues(identicalColumnData);
            setAllColumnPropertiesValues(identicalColumnData, allColumnData);
            setFormattingPropertiesValues(identicalColumnData, allColumnData);
        };

        function setColumnExportPropertiesValues(columnData)
        {
            var designerText = columnData.designerText;

            if (columnData.nodeName?.trim().length > 0)
            {
                exportProperties.xml.column.nodeName.val(columnData.nodeName);
            }
            else if (exportProperties.xml.column.nodeName.length > 0)
            {
                columnData.nodeName = designerText;
                exportProperties.xml.column.nodeName.val(designerText);
            }

            if (exportProperties.xml.column.nodeType.length > 0)
            {
                setRadioButtonGroupValue(exportProperties.xml.column.nodeType, columnData.nodeType);
            }

            exportProperties.fixedWidth.column.startPosition.val(columnData.fixedWidthStartPosition ? columnData.fixedWidthStartPosition : null);
            exportProperties.fixedWidth.column.length.val(columnData.fixedWidthLength ? columnData.fixedWidthLength : null);

            if (exportProperties.json.column.name.length > 0)
            {
                if (columnData.jsonPropertyName?.trim().length > 0)
                {
                    exportProperties.json.column.name.val(columnData.jsonPropertyName);
                }
                else
                {
                    columnData.jsonPropertyName = designerText;
                    exportProperties.json.column.name.val(designerText);
                }
            }

        }

        function setAllColumnPropertiesValues(identicalColumnData, allColumnData)
        {
            if (identicalColumnData.fieldColumnType == COLUMN_TYPES.AGGREGATE_COLUMN || identicalColumnData.fieldColumnType == COLUMN_TYPES.GROUP_COLUMN_AGGREGATE)
            {
                setAggregatePropertiesValues(identicalColumnData);
            }
            else
            {
                setColumnPropertiesValues(identicalColumnData, allColumnData);
            }
            positionX.val(identicalColumnData.positionX);
            positionY.val(identicalColumnData.positionY);

            if (identicalColumnData.sourceColumnId)
            {
                const sourceColumn = getColumnByID(identicalColumnData.sourceColumnId);

                if (sourceColumn.length === 1)
                {
                    const sourceLine = sourceColumn.parent();
                    const sourceSection = getSectionByLine(sourceLine);

                    columnDataPath.val(`${sourceSection.data('displayName')} > ${sourceLine.data('reportLabelText')} > ${getColumnLabelWithPreText(sourceColumn)}`);
                }
            }
            else
            {
                columnDataPath.val(identicalColumnData.absoluteFieldPath || identicalColumnData.fieldPath);
            }

            if (allColumnData.length === 1)
            {
                updateGroupColumnFilterLaunchButton(identicalColumnData);

                isHiddenSelector.setIsHiddenCondition(identicalColumnData.isHiddenCondition, "Column", getSvgByDataType, identicalColumnData.columnId, getColumnLabelWithPreTextByColumnID);
                whenHiddenOption.val(identicalColumnData.whenHiddenOption);
                skyward.controls.toggleVisible(whenHiddenOption.parent(), getHiddenConditionType(identicalColumnData.isHiddenCondition) !== NOT_HIDDEN_VALUE, false);

                setColumnVariablePropertiesValues(identicalColumnData.variableIdToSet);
            }
        }

        function setColumnVariablePropertiesValues(idOrModel)
        {
            var variableToSet = (typeof idOrModel === 'string' ? skyward.reporting.variables.collectionModel.getById(idOrModel) : idOrModel) || {};
            editorVariableToSet.text(variableToSet.uniqueId ? variableToSet.name : "[None]");
            editorVariableToSet.data("variableIdToSet", variableToSet.uniqueId || "");
            editorVariableToSet.find(".js-icon").html(skyward.reporting.variables.viewModel.getIcon(variableToSet));
        }

        function setAggregatePropertiesValues(identicalColumnData)
        {
            const columnType = identicalColumnData.columnFieldType;
            editorAggregateType.attr('disabled', !columnType);

            if (!columnType)
            {
                setAggregateTypeOptions({ "": "" });
                editorAggregateType.val("");
                editorAggregateDelimiterContainer.hide();
                return;
            }

            if (columnType.toUpperCase().substring(0, 6) == "NUMBER")
            {
                setAggregateTypeOptions(numericAggregateOptions);
            }
            else if (columnType == DATA_TYPES.DATETIME)
            {
                setAggregateTypeOptions(dateTimeAggregateOptions);
            }
            else if (columnType == DATA_TYPES.BOOLEAN)
            {
                setAggregateTypeOptions(booleanAggregateOptions);
            }
            else if (columnType == DATA_TYPES.TIMESPAN)
            {
                setAggregateTypeOptions(timeSpanAggregateOptions);
            }
            else
            {
                setAggregateTypeOptions(stringAggregateOptions);
            }

            editorAggregateType.val(identicalColumnData.aggregateType || "");

            if (delimitedAggregators.indexOf(identicalColumnData.aggregateType) != -1)
            {
                editorAggregateDelimiter.val(identicalColumnData.aggregateDelimiter || "");
                editorAggregateDelimiterContainer.show();
            }
            else
            {
                editorAggregateDelimiterContainer.hide();
            }

            function setAggregateTypeOptions(optionsData)
            {
                editorAggregateType.empty();
                $.each(optionsData, function (value, label)
                {
                    editorAggregateType.append($("<option></option").val(value).text(label));
                });

                // It is possible that propertyData.aggregate type is invalid (not in optionsData)
                if (identicalColumnData.aggregateType && !optionsData.hasOwnProperty(identicalColumnData.aggregateType))
                {
                    identicalColumnData.aggregateType = Object.keys(optionsData)[0];
                }
            }
        }

        function setColumnPropertiesValues(columnData, allColumnData)
        {
            editorColumnText.off('click'); // remove the onclick for calculated fields

            var isNumericDataField = allColumnData.every(function (data)
            {
                return (data.lineType !== LINE_TYPES.SORT_HEADER_LINE &&
                    data.fieldColumnType === COLUMN_TYPES.DATA_COLUMN &&
                    skyward.reporting.reportColumn.getFieldType(data).toUpperCase().substring(0, 6) == "NUMBER")
            });

            if (isNumericDataField)
            {
                runningTotal.prop('checked', columnData.isRunningTotal);
                runningTotal.parent().show();
            }

            var currentSection = getCurrentSection();
            var sectionData = currentSection.data();
            if ((isNumericDataField && columnData.isRunningTotal) || isPageNumberOrPageTotalColumn(columnData))
            {
                buildResetOnOptions(currentSection, true, columnData);
            }

            // display custom text box for header and custom text fields
            if (columnData.fieldColumnType === COLUMN_TYPES.TEXT_COLUMN ||
                columnData.fieldColumnType === COLUMN_TYPES.HEADER_COLUMN ||
                columnData.fieldColumnType === COLUMN_TYPES.FOOTER_COLUMN)
            {
                editorColumnText.val(columnData.designerText);
            }
            else if (columnData.fieldColumnType === COLUMN_TYPES.FORMTEXT_COLUMN)
            {
                var friendlyText = skyward.reporting.prompts.promptCollection.ConvertPromptBindingsToFriendlyNames(columnData.designerText);
                friendlyText = skyward.reporting.calculatedFields.ConvertBindingsToFriendlyNames(friendlyText);
                editorColumnText.val(friendlyText);

                editorColumnText.on('click', function ()
                {
                    showFormTextModal(columnData.lineType, sectionData);
                });
            }
            else if (columnData.operationId)
            {
                var calculatedField = CalculatedFields_Model.Get(columnData.operationId);
                editorColumnText.val(skyward.reporting.prompts.promptCollection.ConvertPromptBindingsToFriendlyNames(calculatedField.expression));
                editorColumnText.on('click', function ()
                {
                    skyward.reporting.updateCalculatedField(columnData.operationId);
                });
            }
            else if (columnData.calculation)
            {
                editorColumnText.val(columnData.displayCalculation);
                editorColumnText.on('click', function ()
                {
                    var clickedElement = $(this);
                    let newData = getColumnByID(columnData.columnId).data();
                    var data =
                    {
                        fieldName: newData.designerText,
                        displayCalculation: newData.displayCalculation,
                        calculation: newData.calculation,
                        columnId: newData.columnId
                    };
                    getCalculatedColumnBuilder(data, clickedElement);
                });
            }

            editorMediaId.val(columnData.mediaId).parent().toggle(!columnData.useSystemLogo);
            editorUseSystemLogo.prop("checked", columnData.useSystemLogo);

            if (columnData.isDynamicColumn && columnData.lineType == LINE_TYPES.SORT_HEADER_LINE)
            {
                groupColumnSortHeaderTypes.val(columnData.fieldColumnType);
                reportDesignerProperties.find(".sortHeaderGroupColumnCellProperties").show();
            }

            let hasPromptColumns;
            let allSameColumnType = allColumnData.every(c =>
            {
                let thisIsPromptColumn = isPromptColumn(c);
                if (hasPromptColumns === undefined)
                {
                    hasPromptColumns = thisIsPromptColumn;
                }
                return thisIsPromptColumn == hasPromptColumns;
            });
            let relatedDisplayTypeComboBox = allSameColumnType ? (hasPromptColumns ? pageSectionColumnPromptDisplayType : pageSectionColumnDisplayType) : null;
            if (relatedDisplayTypeComboBox)
            {
                if (!columnData.displayType)
                {
                    relatedDisplayTypeComboBox.val(PAGE_SECTION_COLUMN_DISPLAY_TYPES.DISPLAY_VALUE);
                }
                else
                {
                    relatedDisplayTypeComboBox.val(columnData.displayType);
                }
            }

            setPropertiesOnColumnForPrintOnOneLine();
        }

        function showFormTextModal(lineType, sectionData)
        {
            let options =
            {
                prompts: skyward.reporting.prompts.promptCollection,
                calculatedFields: skyward.reporting.calculatedFields,
                aggregateFields: (lineType !== LINE_TYPES.DATA_LINE && lineType !== LINE_TYPES.SORT_HEADER_LINE) ? getCurrentAndDetailSectionsAggregateColumns() : undefined,
                additionalFilterTypeName: ADDITIONAL_TYPES.TREE_BROWSE_FILTER
            };
            let awaitFormTextEditor = skyward.formTextEditor.launch(
                editorColumnText.val(), sectionData.rootModule, sectionData.rootObject, sectionData.entireRelationshipPath || "", options);
            awaitFormTextEditor.done(function (data)
            {
                editorColumnText.val(data);
                editorColumnText.trigger('change');
            });
        }

        function updateGroupColumnFilterLaunchButton(columnData)
        {
            var sectionFilterData = columnData.isFilterableGroupColumn ?
                getCurrentSectionFilterLaunchButton().data() :
                { rootModule: null, rootObject: null, module: null, object: null, relationshipPath: null };

            // if propertyData.columnFilterJson is undefined we need set it to null to clear out any previous value
            var filterCondition = columnData.columnFilterJson ? columnData.columnFilterJson : null;

            editorColumnFilterLaunchButton.data(
                {
                    rootModule: sectionFilterData.rootModule,
                    rootObject: sectionFilterData.rootObject,
                    module: sectionFilterData.module,
                    object: sectionFilterData.object,
                    relationshipPath: sectionFilterData.relationshipPath,
                });
            editorColumnFilterInput.data({ filterCondition: filterCondition });

            if (columnData.isFilterableGroupColumn)
            {
                editorColumnFilterInput.trigger("updateFilterViewerDisplay");
            }
        }

        function setFormattingPropertiesValues(columnData, allColumnData)
        {
            setTextFormattingPropertiesControls(columnData, allColumnData);
            setCellFormattingPropertiesControls(columnData);
            setBorderFormattingPropertiesControls(columnData);
        }

        function columnSupportsFormatter(columnData)
        {
            const fieldColumnType = columnData.fieldColumnType;
            if (fieldColumnType === COLUMN_TYPES.DATA_COLUMN || fieldColumnType === COLUMN_TYPES.AGGREGATE_COLUMN ||
                fieldColumnType === COLUMN_TYPES.VARIABLE_COLUMN || fieldColumnType == COLUMN_TYPES.CALCULATED_COLUMN ||
                fieldColumnType === COLUMN_TYPES.FUTURE_VALUE_COLUMN)
            {
                return true;
            }
            else if (fieldColumnType == COLUMN_TYPES.PAGE_SECTION_COLUMN)
            {
                const prompt = columnData.promptUniqueId ? skyward.reporting.prompts.promptCollection.Get(columnData.promptUniqueId) : undefined;
                if (!prompt || (prompt.InputType !== DATA_TYPES.BOOLEAN && (prompt.Type == enumMaps.PromptTypes["Value"] || prompt.Type == enumMaps.PromptTypes["SingleSelect"])))
                {
                    return true;
                }
            }
            return false;
        }

        function setTextFormattingPropertiesControls(columnData, allColumnData)
        {
            setFontPropertiesValues(columnData, allColumnData);
            columnData.backColor ? dataBackgroundColor.spectrum("set", columnData.backColor) : dataBackgroundColor.spectrum("set", defaultDataBackColor);
            setTextAlignmentPropertiesValues(columnData);
            setVerticalAlignmentPropertiesValues(columnData);

            if (allColumnData.every(columnSupportsFormatter))
            {
                setFormatterControls(allColumnData);
            }

            overlapMode.val(columnData.overlapMode);
        }

        function setFontPropertiesValues(identicalColumnData, allColumnData)
        {
            let anyColumnIsBarcode = allColumnData.some(function (data) { return isBarcodeFormatter(data.formatter) });
            dataFont.attr('disabled', anyColumnIsBarcode);

            let fontFamily = identicalColumnData.dataFontFamily;
            let fontSize = identicalColumnData.dataFontSize;
            let foreColor = allColumnData[0].dataForeColor;
            // the foreground color picker will always have a color selected
            if (!foreColor)
            {
                foreColor = defaultDataForeColor;
            }

            if (allColumnData.length == 1)
            {
                if (!fontFamily)
                {
                    fontFamily = defaultDataFont;
                }

                if (!fontSize)
                {
                    fontSize = defaultDataFontSize;
                }
            }

            dataFont.val(anyColumnIsBarcode ? "" : fontFamily);
            dataFontSize.val(fontSize);
            dataForeColor.data("color", foreColor ?? defaultDataForeColor); // set this on the element so when colorPicker initializes it starts with the correct color.
            dataForeColor.spectrum("set", foreColor);

            dataBold.prop('checked', identicalColumnData.dataBold);
            dataItalic.prop('checked', identicalColumnData.dataItalic);
            dataUnderline.prop('checked', identicalColumnData.dataUnderline);
        }

        function resetFontProperties(column)
        {
            column.data("dataFontFamily", defaultDataFont);
            column.data("dataFontSize", defaultDataFontSize);
            column.data("dataForeColor", defaultDataForeColor);
            column.data("dataBold", defaultDataFontBold);
            column.data("dataItalic", defaultDataFontItalic);
            column.data("dataUnderline", defaultDataFontUnderline);
        }

        function setRadioButtonGroupValue(groupElement, value)
        {
            // we aren't using skyward.radioButtonGroup.setValue because it does not support clearing a radio button group
            var group = skyward.radioButtonGroup.getRadioButtonGroup(groupElement);
            group.valueElement.val(value);

            for (let element of group.radioInputs)
            {
                var radioButton = $(element);
                const currentValue = radioButton.prop("checked");
                const newValue = radioButton.val() == value;
                if (currentValue != newValue)
                {
                    radioButton.prop("checked", newValue);
                }
            }
        }

        function setTextAlignmentPropertiesValues(columnData)
        {
            setRadioButtonGroupValue(editorTextAlign, columnData.dataTextAlignment);
        }

        function setVerticalAlignmentPropertiesValues(columnData)
        {
            setRadioButtonGroupValue(editorVerticalAlign, columnData.verticalAlignment);
        }

        function getFormatterOrNull(formatterData)
        {
            if (!formatterData || enumMaps.FormatterTypeNames.indexOf(formatterData.formatterName || "") == -1)
            {
                return null;
            }

            return formatterData;
        }

        function isParentDisplayOnSingleLine(section) {
            const parentSection = getParentSection(section);
            return parentSection.length ? parentSection.data("displayOnSingleLine") : false;
        }

        function setFormatterControls(allColumnData)
        {
            reportDesignerProperties.find(".formatterFieldSet").hide();
            dataTextFormat.show().parent().show();

            const allColumnDataTypeClasses = new Set(allColumnData.map(function (data)
            {
                if (data.fieldType?.startsWith("String Enum-"))
                {
                    return ".textFormat-StringEnum-";
                }
                else
                {
                    return ".textFormat-" + data.fieldType?.replace(/\s+/g, '') ?? ""; //Remove blank spaces
                }
            }));

            dataTextFormat.find("option").hide();
            const typeClasses = [...allColumnDataTypeClasses].join("");
            dataTextFormat.find("option" + typeClasses).show();

            const allFormatterData = allColumnData.map(function (data) { return getFormatterOrNull(data.formatter) });
            if (allFormatterData.length > 1)
            {
                dataTextFormat.find("option[value='CopyField']").hide();
            }

            var identicalFormatterData = {};

            var first = true;
            for (let formatterData of allFormatterData)
            {
                if (formatterData === null)
                {
                    formatterData = { formatterName: FORMATTER_NAMES.DEFAULT };
                }

                for (let property in formatterData)
                {
                    if (identicalFormatterData.hasOwnProperty(property))
                    {
                        if (identicalFormatterData[property] !== formatterData[property])
                        {
                            identicalFormatterData[property] = null;
                        }
                    }
                    else if (first)
                    {
                        identicalFormatterData[property] = formatterData[property];
                    }
                }
                first = false;
            }

            if (!identicalFormatterData.formatterName)
            {
                dataTextFormat.val("");
                return;
            }

            dataTextFormat.val(identicalFormatterData.formatterName);

            if (identicalFormatterData.formatterName)
            {
                if (identicalFormatterData.formatterName === FORMATTER_NAMES.COPY_FIELD && allFormatterData.length > 1)
                {
                    return;
                }

                for (let controlData of formatterControlsDataMap.get(identicalFormatterData.formatterName))
                {
                    var element = controlData.element;

                    $.each(controlData.dataBindings, function (propertyName, sourceType)
                    {
                        var value = identicalFormatterData[propertyName];
                        if (value === undefined)
                        {
                            value = "";
                        }

                        switch (sourceType)
                        {
                            case "Val":
                                if (element.is("input.checkbox"))
                                {
                                    element.prop("checked", value && value !== "False" ? true : false);
                                }
                                else
                                {
                                    if (element.attr("type") == "text")
                                    {
                                        element.val(value === undefined ? "" : value);
                                    }
                                    else if (skyward.extensions.isNumeric(value))
                                    {
                                        element.val(parseInt(value, 10));
                                    }
                                    else
                                    {
                                        element.val(value === undefined ? "" : value);
                                    }
                                }
                                break;
                            case "FieldSelectorText":
                                element.find(".fieldSelectorText").text(value);
                                break;
                            case "FieldSelectorIcon":
                                var svgHolder = element.find(".svgHolder");
                                $.when(getSvgByDataType(value.split(" ")[0], "TreeBrowse")).done(function (svg)
                                {
                                    svgHolder.html(svg);
                                });
                            // don't break - Field Selector SVG icon is displayed, but the type-string needs to be saved as "Data" too.
                            case "Data":
                                if (value === undefined)
                                {
                                    element.removeData(propertyName);
                                }
                                else
                                {
                                    element.data(propertyName, value);
                                }
                                break;
                        }
                    });
                }
            }

            reportDesignerProperties.find(".fieldSet" + identicalFormatterData.formatterName + "Formatter").show();

        }

        function setCellFormattingPropertiesControls(columnData)
        {
            rotationValue.val(columnData.rotation);
            rotationRange.val(columnData.rotation);
            columnWidth.val(columnData.fieldWidth);
            columnHeight.val(columnData.fieldHeight);
            columnData.paddingTop >= 0 ? editorPaddingTop.val(columnData.paddingTop) : editorPaddingTop.val(defaultColumnPadding);
            columnData.paddingBottom >= 0 ? editorPaddingBottom.val(columnData.paddingBottom) : editorPaddingBottom.val(defaultColumnPadding);
            columnData.paddingLeft >= 0 ? editorPaddingLeft.val(columnData.paddingLeft) : editorPaddingLeft.val(defaultColumnPadding);
            columnData.paddingRight >= 0 ? editorPaddingRight.val(columnData.paddingRight) : editorPaddingRight.val(defaultColumnPadding);
        }

        function setBorderFormattingPropertiesControls(values)
        {
            values.borderStyle ? borderStyle.val(values.borderStyle) : borderStyle.val();
            borderTop.prop('checked', values.borderTop);
            borderBottom.prop('checked', values.borderBottom);
            borderLeft.prop('checked', values.borderLeft);
            borderRight.prop('checked', values.borderRight);
            borderWidth.val(values.borderWidth);

            borderColor.data("color", values.borderColor ?? defaultDataForeColor); // set this on the element so when colorPicker initializes it starts with the correct color.
            values.borderColor ? borderColor.spectrum("set", values.borderColor) : borderColor.spectrum("set", defaultDataForeColor);
        }

        function setRowExportPropertiesValues(rowData)
        {
            if (rowData.nodeName?.trim().length > 0)
            {
                exportProperties.xml.row.nodeName.val(rowData.nodeName);
            }
            else if (exportProperties.xml.row.nodeName.length > 0)
            {
                const rowName = getRowFieldName(rowData) || rowData.defaultLineLabel;
                rowData.nodeName = rowName;
                exportProperties.xml.row.nodeName.val(rowName);
            }
        }

        function setRowPropertiesValues(rowData, canRepeatAfterStart)
        {
            setPrintAtBottomOfPageValue(rowData);

            if (rowData.reportFooterLine)
            {
                setPrintReportFooterOnNewPageValue(rowData);
            }

            if (canRepeatAfterStart)
            {
                setRepeatLineAfterStartValue(rowData);
            }

            isHiddenSelector.setIsHiddenCondition(rowData.isHiddenCondition, "Row", getSvgByDataType, null, getColumnLabelWithPreTextByColumnID);

            lineHeight.val(rowData.lineHeight);

            dataBackgroundColor.data("color", rowData.backColor ?? defaultDataBackColor); // set this on the element so when colorPicker initializes it starts with the correct color.
            rowData.backColor ? dataBackgroundColor.spectrum("set", rowData.backColor) : dataBackgroundColor.spectrum("set", defaultDataBackColor);
            editorRepeatRowOnPageIfDetailsPresent.prop('checked', rowData.repeatOnPageIfDetailsPresent);
            editorRepeatHeaderForEachDataRow.prop('checked', rowData.repeatHeaderForEachDataRow);

            skyward.reporting.selectVariablesToReset(rowData.variableIdsToReset);

            editorSectionAndLineDisplayName.val(rowData.reportLabelText);
            sectionAndLineFullName.siblings(".propertiesLabel").text("Sort Field Path");
            sectionAndLineFullName.val(getRowFieldName(rowData));

            setRowExportPropertiesValues(rowData);

            setBorderFormattingPropertiesControls(rowData);

            setPropertiesOnRowForPrintOnOneLine();
        }

        function setPrintAtBottomOfPageValue(rowData)
        {
            if (rowData.printAtBottomOfPagePromptUniqueId)
            {
                var printAtBottomPrompt = skyward.reporting.prompts.promptCollection.Get(rowData.printAtBottomOfPagePromptUniqueId);
                skyward.filterbuilder.setConditionSpanValue(editorPrintAtBottomOfPage.parent(), printAtBottomPrompt.Label, { promptUniqueId: printAtBottomPrompt.PromptUniqueId }, "DialogPrompt", false);
            }
            else if (rowData.printAtBottomOfPage != undefined)
            {
                skyward.filterbuilder.setConditionSpanValue(editorPrintAtBottomOfPage.parent(),
                    rowData.printAtBottomOfPage.toDisplayString(),
                    { boolvalue: rowData.printAtBottomOfPage },
                    null,
                    false);
            }
        }

        function setPrintReportFooterOnNewPageValue(rowData)
        {
            if (rowData.printReportFooterOnNewPagePromptUniqueId)
            {
                var prompt = skyward.reporting.prompts.promptCollection.Get(rowData.printReportFooterOnNewPagePromptUniqueId);
                skyward.filterbuilder.setConditionSpanValue(editorPrintReportFooterOnNewPage.parent(), prompt.Label, { promptUniqueId: prompt.PromptUniqueId }, "DialogPrompt", false);
            }
            else
            {
                if (rowData.printReportFooterOnNewPage === undefined)
                {
                    rowData.printReportFooterOnNewPage = false;
                }

                skyward.filterbuilder.setConditionSpanValue(editorPrintReportFooterOnNewPage.parent(),
                    rowData.printReportFooterOnNewPage.toDisplayString(),
                    { boolvalue: rowData.printReportFooterOnNewPage },
                    null,
                    false);
            }
        }

        function setRepeatLineAfterStartValue(rowData)
        {
            if (rowData.repeatLineAfterStartPromptUniqueId)
            {
                var repeatLineAfterStartPrompt = skyward.reporting.prompts.promptCollection.Get(rowData.repeatLineAfterStartPromptUniqueId);
                skyward.filterbuilder.setConditionSpanValue(editorRepeatLineAfterStart.parent(), repeatLineAfterStartPrompt.Label, { promptUniqueId: repeatLineAfterStartPrompt.PromptUniqueId }, "DialogPrompt", false);
            }
            else
            {
                skyward.filterbuilder.setConditionSpanValue(editorRepeatLineAfterStart.parent(),
                    rowData.repeatLineAfterStart.toDisplayString(),
                    { boolvalue: rowData.repeatLineAfterStart },
                    null,
                    false);
            }
        }

        function setSectionExportPropertiesValues(sectionData)
        {
            if (sectionData.nodeName?.trim().length > 0)
            {
                exportProperties.xml.section.nodeName.val(sectionData.nodeName);
            }
            else if (exportProperties.xml.section.nodeName.length > 0)
            {
                currentRow.data("nodeName", sectionData.displayName);
                exportProperties.xml.section.nodeName.val(sectionData.displayName);
            }

            if (sectionData.jsonPropertyName?.trim().length > 0)
            {
                exportProperties.json.section.name.val(sectionData.jsonPropertyName);
            }
            else if (exportProperties.json.section.name.length > 0)
            {
                currentRow.data("jsonPropertyName", sectionData.displayName);
                exportProperties.json.section.name.val(sectionData.displayName);
            }
        }

        function setSectionPropertiesValues(section)
        {
            const sectionData = section.data();
            const isDetailSection = section.hasClass("detailsSection");
            editorPageBreakBeforeSectionPrints.prop('checked', sectionData.pageBreakBeforeSection);
            editorPrintIfEmpty.prop('checked', sectionData.printIfEmpty);
            editorSectionAndLineDisplayName.val(sectionData.displayName);
            sectionAndLineFullName.siblings(".propertiesLabel").text(isDetailSection ? "Relationship Path" : "Section Object");
            let sectionAndLineFullNameValue;

            if (isDetailSection)
            {
                sectionAndLineFullNameValue = sectionData.relationshipPath.substring(sectionData.relationshipPath.indexOf(".") + 1);
            }
            else
            {
                sectionAndLineFullNameValue = sectionData.sectionObjectName;
            }

            sectionAndLineFullName.val(sectionAndLineFullNameValue);
            editorEmptyLinesToPrint.val(sectionData.emptyLinesToPrint);

            setPropertiesOnSectionForDefaultSubjects(section);
            setPropertiesOnSectionForPrintOnOneLine(section);
            setSectionExportPropertiesValues(sectionData);

            isHiddenSelector.setIsHiddenCondition(sectionData.isHiddenCondition, "Section", getSvgByDataType, null, null);
        }

        function setPropertiesOnSectionForPrintOnOneLine(section)
        {
            const sectionData = section.data();
            const isDetailSection = section.hasClass("detailsSection");
            skyward.controls.setValue(editorDisplayOnSingleLine, sectionData.displayOnSingleLine, true);

            const parentDisplayOnSingleLine = isParentDisplayOnSingleLine(section);
            const hasDetailSections = section.find(".detailsSection").length > 0;

            skyward.controls.toggle(editorPrintIfEmpty, !(isDetailSection && parentDisplayOnSingleLine));
            skyward.controls.toggle(editorPageBreakBeforeSectionPrints, !(isDetailSection && parentDisplayOnSingleLine));
            skyward.controls.toggle(editorDisplayOnSingleLine, hasDetailSections && !(isDetailSection && parentDisplayOnSingleLine));

            skyward.controls.hide(singleLineRecordCountFieldSetTemplate, false);
            removeSingleLineRecordCountInputs();
            if (sectionData.displayOnSingleLine && !parentDisplayOnSingleLine)
            {
                addSingleLineRecordCountInputs(section);
            }

            reportDesignerProperties.find(".emptyLinesToPrintFieldset").toggle(sectionData.printIfEmpty);
        }

        function setPropertiesOnSectionForDefaultSubjects(section)
        {
            const sectionData = section.data();
            editorDefaultSubjects.find(".js-processing-list-subject").each(function ()
            {
                const subjectElement = $(this);
                const subjectData = subjectElement.data();
                const subjectSkywardID = subjectData.subjectSkywardId;

                let displaySubject = false;
                if (sectionData.defaultProcessingListSubjectSkywardIds)
                {
                    displaySubject = sectionData.defaultProcessingListSubjectSkywardIds.some(skywardID => skywardID.toLowerCase() === subjectSkywardID.toLowerCase());
                }

                skyward.controls.toggleVisible(subjectElement, displaySubject, false);
            });
        }

        function addSingleLineRecordCountInputs(section)
        {
            var detailSections = section.find(".jsSection");
            $.each(detailSections, function (index, detailSection)
            {
                detailSection = $(detailSection);
                var sectionID = detailSection.data("sectionId");
                var sectionName = detailSection.data("displayName");

                var newFieldSet = singleLineRecordCountFieldSetTemplate.clone(true, true);
                newFieldSet.removeClass("jsTemplate");
                newFieldSet.data("sectionID", sectionID);

                var labelElement = newFieldSet.find(".propertiesLabel");
                labelElement.text(labelElement.text().replace("[Object]", sectionName));

                var singleLineRecordCount = detailSection.data("singleLineRecordCount");
                var singleLineRecordCountInput = newFieldSet.find(".editorSingleLineRecordCount");
                singleLineRecordCountInput.val(singleLineRecordCount);
                singleLineRecordCountInput.data("sectionID", sectionID);

                singleLineRecordCountFieldSetTemplate.before(newFieldSet);
                skyward.controls.show(newFieldSet, false);
            });
        }

        function removeSingleLineRecordCountInputs()
        {
            reportDesignerProperties.find(".singleLineRecordCountFieldSet:not(.jsTemplate)").remove();
        }

        function setPropertiesOnRowForPrintOnOneLine()
        {
            let section = getCurrentSection();
            if (section.length > 0)
            {
                let sectionData = section.data();

                const parentDisplayOnSingleLine = isParentDisplayOnSingleLine(section)

                skyward.controls.toggle(editorRepeatRowOnPageIfDetailsPresent, !sectionData.displayOnSingleLine);
                skyward.controls.toggle(editorRepeatHeaderForEachDataRow, !sectionData.displayOnSingleLine);
                editorPrintAtBottomOfPage.closest(".conditionValuesContainer").toggleClass("ui-disabled", parentDisplayOnSingleLine);
                editorRepeatLineAfterStart.closest(".conditionValuesContainer").toggleClass("ui-disabled", parentDisplayOnSingleLine);
            }
        }

        function setPropertiesOnColumnForPrintOnOneLine()
        {
            let section = getCurrentSection();
            if (section.length > 0)
            {
                let displayOnSingleLine = section.data("displayOnSingleLine");

                skyward.controls.toggle(runningTotal, !displayOnSingleLine);
            }
        }

        var buildResetOnOptions = function (section, toggleControlVisibility, columnData)
        {
            const options = {};
            let valueToSelect;
            const isHeaderFooterSection = section.is(".jsHeaderFooterSection");

            resetOption.val("").find('option').remove();
            if (isHeaderFooterSection)
            {
                valueToSelect = columnData ? columnData.resetOn : RESET_ON_TYPES.NEVER_RESET;
                options[RESET_ON_TYPES.NEVER_RESET] = "Never Reset";

                let anyMainSectionHasPageBreak = false;

                designer.find(".jsSection").each(function ()
                {
                    const section = $(this);
                    // page break aggregates
                    const sectionData = getSectionData(section);
                    if (section.hasClass("jsMainSection") && sectionData.pageBreakBeforeSection)
                    {
                        anyMainSectionHasPageBreak = true;
                    }

                    const sorts = getSectionSorts(section);
                    if (sorts)
                    {
                        sorts.forEach(function (sortData)
                        {
                            if (sortData.PageBreak === skyward.reportingSorts.pageBreakOptions.True ||
                                sortData.PageBreak === skyward.reportingSorts.pageBreakOptions.Burst)
                            {
                                options[sortData.FieldName] = "Sort: " + (sortData.RelativeDataPath ?? sortData.FieldName);
                            }
                        });
                    }
                });

                if (anyMainSectionHasPageBreak)
                {
                    options[RESET_ON_TYPES.MAIN_SECTIONS] = "Main Sections with Page Breaks";
                }
            }
            else
            {
                const isResettablePageColumn = isPageNumberOrPageTotalColumn(columnData);
                valueToSelect = (columnData && columnData.resetOn) ? columnData.resetOn : (isResettablePageColumn ? RESET_ON_TYPES.NEVER_RESET : RESET_ON_TYPES.SECTION);

                if (isResettablePageColumn)
                {
                    options[RESET_ON_TYPES.NEVER_RESET] = "Never Reset";
                }
                else
                {
                    options[RESET_ON_TYPES.SECTION] = "Section";
                }

                getSectionSorts(section).forEach(function (sortData)
                {
                    if (columnData.isRunningTotal ||
                        sortData.PageBreak === skyward.reportingSorts.pageBreakOptions.True ||
                        sortData.PageBreak === skyward.reportingSorts.pageBreakOptions.Burst)
                    {
                        const fieldName = sortData.FieldName;
                        const sortDisplayName = sortData.RelativeDataPath ?? sortData.FieldName;
                        options[fieldName] = sortDisplayName;
                    }
                });
            }


            $.each(options, function (value, displayName)
            {
                resetOption.append('<option value="' + value + '">' + displayName + '</option>');
                if (value == valueToSelect)
                {
                    resetOption.val(valueToSelect);
                }
            });

            if (resetOption.val() == "")
            {
                for (let firstOption in options)
                {
                    resetOption.val(firstOption);
                    break;
                }
            }

            if (toggleControlVisibility)
            {
                if (resetOption.children().length === 1 && resetOption.val() === RESET_ON_TYPES.NEVER_RESET)
                {
                    reportDesignerProperties.find(".resetOptionsFieldset").hide();
                }
                else
                {
                    reportDesignerProperties.find(".resetOptionsFieldset").show();
                }
            }
        };

        let isPageNumberOrPageTotalColumn = function (columnData)
        {
            return columnData.fieldColumnType == COLUMN_TYPES.PAGE_SECTION_COLUMN && (columnData.pageSectionColumnType == "PageTotal" || columnData.pageSectionColumnType == "PageNumber");
        }

        let isPromptColumn = function (columnData)
        {
            return columnData.fieldColumnType == COLUMN_TYPES.PAGE_SECTION_COLUMN && columnData.pageSectionColumnType == "PromptValue";
        }

        var isCalculatedFieldUsedInAnyExistFilters = function (calculatedField)
        {
            var sections = getAllSections();

            for (const section of sections)
            {
                var currentFilterData = getSectionFilterObjectAndParameters($(section));
                var filters = currentFilterData ? currentFilterData.filterObject : null;
                if (filters != null)
                {
                    if (filters["@Type"] === skyward.searchCondition.Types.SearchConditionGroup && isCalculatedFieldUsedInExistFilters(calculatedField.id, filters.SearchCondition, false))
                    {
                        return true;
                    }
                    else if (filters["@Type"] === skyward.searchCondition.Types.ExistsSearchCondition && isCalculatedFieldUsedInExistFilters(calculatedField.id, filters.SearchCondition, true))
                    {
                        return true;
                    }
                }
            }

            return false;
        };

        var isCalculatedFieldUsedInExistFilters = function (calculatedFieldName, searchCondition, inExists)
        {
            if (!searchCondition)
            {
                return false;
            }

            var filters = Array.isArray(searchCondition) ? searchCondition : [searchCondition];
            for (const filter of filters)
            {
                if (filter["@Type"] === skyward.searchCondition.Types.SearchConditionGroup && isCalculatedFieldUsedInExistFilters(calculatedFieldName, filter.SearchCondition, inExists))
                {
                    return true;
                }
                else if (filter["@Type"] === skyward.searchCondition.Types.ExistsSearchCondition && isCalculatedFieldUsedInExistFilters(calculatedFieldName, filter.SearchCondition, true))
                {
                    return true;
                }
                else if (filter["@CalculatedFieldName"] === calculatedFieldName && inExists)
                {
                    return true;
                }
            }

            return false;
        };

        var areThereMultipleSectionsOnTheSameObject = function (currentSection)
        {
            let sectionCount = 0;
            const currentSectionData = getSectionData(currentSection);
            const currentModule = currentSectionData.rootModule;
            const currentObject = currentSectionData.rootObject;
            const currentRelationshipPath = currentSectionData.entireRelationshipPath;

            getAllSections().each(function ()
            {
                const section = $(this);
                const sectionData = getSectionData(section);
                if (sectionData.rootModule == currentModule && sectionData.rootObject == currentObject && sectionData.entireRelationshipPath == currentRelationshipPath)
                {
                    sectionCount++;
                }
            });

            return sectionCount > 1;
        };

        skyward.reporting.updateCalculatedField = function (calculatedFieldID)
        {
            var calculatedField = CalculatedFields_Model.Get(calculatedFieldID);
            skyward.calculatedFieldBuilder.launch({
                fieldName: calculatedField.fieldName,
                expression: calculatedField.expression,
                schemaObject: calculatedField.schemaObject,
                id: calculatedField.id,
                relationshipPath: calculatedField.relationshipPath,
                prompts: skyward.reporting.prompts.promptCollection,
                usedInExistsFilter: isCalculatedFieldUsedInAnyExistFilters(calculatedField),
                callback: function (result)
                {
                    CalculatedFields_Model.Set(result);
                    editorColumnText.val(skyward.reporting.prompts.promptCollection.ConvertPromptBindingsToFriendlyNames(result.expression));
                    columnDataPath.val(result.fieldName);
                    //update designer text and related data for json serialization
                    entireCanvas.find('[data-operation-id=' + result.id + ']').each(function ()
                    {
                        const reportElement = $(this);

                        reportElement.data("field-name", result.fieldName);
                        reportElement.attr("data-field-name", result.fieldName); //Update the attribute because automated tests look at this

                        if (reportElement.hasClass("reportColumn"))
                        {
                            reportElement.data("designer-text", result.fieldName); // we need to set both data and attribute
                            reportElement.attr("data-designer-text", result.fieldName); //Update the attribute because automated tests look at this

                            //we don't care about field type or column field type on header column containers
                            if (reportElement.data("field-column-type") !== COLUMN_TYPES.HEADER_COLUMN)
                            {
                                reportElement.data("field-type", result.dataType);

                                if (reportElement.data("column-field-type"))
                                {
                                    reportElement.data("column-field-type", result.dataType);
                                }
                            }

                            const columnLabel = getColumnLabelWithPreText(reportElement);

                            const affectedColumn = reportElement.children(".column_content");
                            if (affectedColumn.length > 0)
                            {
                                affectedColumn.text(columnLabel);
                            }
                        }
                    });

                    designer.find(".jsSection").each(function ()
                    {
                        const section = $(this);

                        const sortIDsUpdated = [];

                        const sectionSorts = getSectionSorts(section);
                        if (sectionSorts)
                        {
                            for (let sort of sectionSorts)
                            {
                                if (sort.CalculatedField && sort.CalculatedField.id == result.id)
                                {
                                    sortIDsUpdated.push(sort.ReportSortID);
                                    sort.FieldName = result.fieldName;
                                    sort.CalculatedField.fieldName = result.fieldName;
                                }
                            }
                            updateSectionSortData(section, sectionSorts);
                        }

                        const sortHeaderLines = getSectionSortHeaderLines(section);
                        for (let sortHeaderLine of sortHeaderLines)
                        {
                            const line = $(sortHeaderLine);
                            const lineData = line.data();

                            if (sortIDsUpdated.includes(lineData.relatedReportSortId))
                            {
                                const lineIndex = lineData.lineIndex;
                                const oldDefaultLabel = lineData.defaultLineLabel;
                                const newDefaultLabel = result.fieldName + " " + lineIndex;

                                line.data("default-line-label", newDefaultLabel);
                                line.attr("data-default-line-label", newDefaultLabel); //Update the attribute because automated tests look at this

                                if (lineData.reportLabelText === oldDefaultLabel)
                                {
                                    line.data("report-label-text", newDefaultLabel);
                                }
                            }
                        }

                        const sortAggregateLines = getSectionSortAggregateLines(section);
                        for (let sortAggregateLine of sortAggregateLines)
                        {
                            const line = $(sortAggregateLine);
                            const lineData = line.data();

                            if (sortIDsUpdated.includes(lineData.relatedReportSortId))
                            {
                                const lineIndex = lineData.lineIndex;
                                const oldDefaultLabel = lineData.defaultLineLabel;
                                const newDefaultLabel = result.fieldName + " " + lineIndex;

                                if (oldDefaultLabel)
                                {
                                    line.data("default-line-label", newDefaultLabel);
                                    line.attr("data-default-line-label", newDefaultLabel); //Update the attribute because automated tests look at this
                                }
                                if (lineData.reportLabelText === oldDefaultLabel)
                                {
                                    line.data("report-label-text", newDefaultLabel);
                                }
                            }
                        }
                    });

                    updateDesignerSidebarCalculatedFieldList(getCurrentSection().data());
                    designer.find(".calculatedField[data-fieldid='" + result.id + "'] .column_content").html(result.fieldName);
                    updateHiddenField();
                    labelBuilder.buildLabelColumns(true);
                }
            });
        };

        //GENERAL HELPERS
        const getSectionData = function ($section)
        {
            const sectionData = $section.data();
            const filterData = getSectionFilterObjectAndParameters($section);
            let section =
            {
                relationshipPath: sectionData.relationshipPath,
                entireRelationshipPath: sectionData.entireRelationshipPath,
                baseObject: sectionData.baseObject,
                baseModule: sectionData.baseModule,
                rootObject: sectionData.rootObject,
                rootModule: sectionData.rootModule,
                sectionObjectName: sectionData.sectionObjectName,
                displayName: sectionData.displayName,
                Filters: filterData && filterData.filterObject ? filterData.filterObject : null,
                FiltersParameters: filterData && filterData.parameters ? filterData.parameters : null,
                sectionID: sectionData.sectionId,
                pageBreakBeforeSection: sectionData.pageBreakBeforeSection,
                parameters: sectionData.parameters,
                promptForSortOrder: sectionData.promptForSortOrder,
                sortPromptLabel: sectionData.sortPromptLabel,
                sortPromptGuid: sectionData.sortPromptGuid,
                printIfEmpty: sectionData.printIfEmpty,
                displayOnSingleLine: sectionData.displayOnSingleLine,
                singleLineRecordCount: $.safeRound(sectionData.singleLineRecordCount),
                emptyLinesToPrint: $.safeRound(sectionData.emptyLinesToPrint),
                nodeName: sectionData.nodeName,
                jsonPropertyName: sectionData.jsonPropertyName,
                isHiddenCondition: sectionData.isHiddenCondition,
                columnWidth: sectionData.columnWidth,
                columnSpacing: sectionData.columnSpacing,
                applyColumnsToDataLinesOnly: sectionData.applyColumnsToDataLinesOnly,
                balanceColumns: sectionData.balanceColumns,
                columnDirection: sectionData.columnDirection,
                reportSorts: sectionData.reportSorts,
                defaultProcessingListSubjectSkywardIDs: sectionData.defaultProcessingListSubjectSkywardIds
            };

            return section;
        };

        const isAggregateLineType = (lineType) => lineType === LINE_TYPES.SORT_AGGREGATE_LINE || lineType === LINE_TYPES.AGGREGATE_LINE;

        const getParentSection = function (sectionOrRow)
        {
            return sectionOrRow.parent().closest(".jsSection");
        };

        const getAllParentSections = (section) => section.parents(".jsSection");

        const getSectionFilterObjectAndParameters = function ($section)
        {
            const filterData = $section.find(".filterViewValue").data();
            let filterObj = filterData ? filterData.filterCondition : {};
            if (typeof (filterObj) === "string")
            {
                if (filterObj === "" || filterObj == "{}")
                {
                    return null;
                }
                filterObj = JSON.parse(filterObj);
            }

            if ($.isEmptyObject(filterObj))
            {
                return null;
            }

            let filterSchemaFields = filterData ? filterData.filterSchemaFields : {};
            if (typeof (filterSchemaFields) === "string")
            {
                filterSchemaFields = JSON.parse(filterSchemaFields);
            }

            return { "filterObject": filterObj, "parameters": filterData.parameters, "filterSchemaFields": filterSchemaFields };
        };

        const getSectionAllAggregateLines = function (section)
        {
            return $.merge(getSectionSortAggregateLines(section), getSectionAggregateLines(section));
        };

        const getSectionSingleLinePreview = function (section)
        {
            return section.find(">.sectionBody>.printOnOneLinePreview");
        };

        const getSectionLines = function (section)
        {
            return section.find('>.sectionBody>.reportLine');
        };

        const getCorrespondingHeaderLine = function (section, dataLine)
        {
            var dataLineIndex = dataLine.data("lineIndex");
            return section.find('>.sectionBody>.jsHeaderLine[data-line-index="' + dataLineIndex + '"]');
        };

        const getSectionHeaderLines = function (section)
        {
            return section.find('>.sectionBody>.jsHeaderLine');
        };

        const getSectionFooterLines = function (section)
        {
            return section.find('>.sectionBody>.jsFooterLine');
        };

        const getSectionDataLines = function (section)
        {
            return section.find('>.sectionBody>.jsDataLine');
        };

        const getMaxSectionLineIndex = function (section)
        {
            return Math.max(getSectionDataLines(section).last().data("lineIndex"), getSectionHeaderLines(section).last().data("lineIndex"));
        };

        const getMaxSectionAggregateLineIndex = function (section)
        {
            return getSectionAggregateLines(section).last().data("lineIndex");
        };

        const getNextSortAggregateLineIndex = function (section, reportSortId)
        {
            const lastSortAggregateLine = getSectionSortAggregateLinesByReportSortID(section, reportSortId).pop();
            if (lastSortAggregateLine)
            {
                const lineIndex = $(lastSortAggregateLine).data("lineIndex");
                return lineIndex + 1;
            }
            return 0;
        };

        const getNextSortHeaderLineIndex = function (section, reportSortId)
        {
            const lastSortHeaderLine = getSectionSortHeaderLinesByReportSortID(section, reportSortId).pop();
            if (lastSortHeaderLine)
            {
                const lineIndex = $(lastSortHeaderLine).data("lineIndex");
                return lineIndex + 1;
            }
            return 0;
        };

        const getSectionSortAggregateLinesByReportSortID = function (section, reportSortId)
        {
            const sortAggregateLines = getSectionSortAggregateLines(section);
            const filteredSortAggregateLines = [];
            if (sortAggregateLines.length > 0)
            {
                for (let sortAggregateLine of sortAggregateLines)
                {
                    const line = $(sortAggregateLine);
                    if (line.data("relatedReportSortId") === reportSortId)
                    {
                        filteredSortAggregateLines.push(sortAggregateLine);
                    }
                }
            }

            return filteredSortAggregateLines;
        }

        const getSectionSortHeaderLinesByReportSortID = function (section, reportSortId)
        {
            const sortHeaderLines = getSectionSortHeaderLines(section);
            const filteredSortHeaderLines = [];
            if (sortHeaderLines.length > 0)
            {
                for (let sortHeaderLine of sortHeaderLines)
                {
                    const line = $(sortHeaderLine);
                    if (line.data("relatedReportSortId") === reportSortId)
                    {
                        filteredSortHeaderLines.push(sortHeaderLine);
                    }
                }
            }

            return filteredSortHeaderLines;
        }

        const getSectionSortAggregateLines = function (section)
        {
            return section.find(">.sectionBody>.jsSortAggregateLine");
        };

        const getSectionSortHeaderLines = function (section)
        {
            return section.find(">.sectionBody>.sortHeaderLine");
        };

        const getSectionAggregateLines = function (section)
        {
            return section.find('>.sectionBody>.jsSectionAggregateLine');
        };

        const getSectionSorts = function (section)
        {
            return section.data("reportSorts");
        };

        var getSectionSortOrderByReportSortID = function (section, reportSortID)
        {
            const allSectionSorts = getSectionSorts(section);
            const sortData = allSectionSorts.find(sort => { return sort.ReportSortID === reportSortID });
            if (sortData)
            {
                return sortData.SortOrder;
            }

            return 0;
        };

        var getSortDataByReportSortID = function (section, reportSortID)
        {
            const allSectionSorts = getSectionSorts(section);
            const sortData = allSectionSorts.find(sort => { return sort.ReportSortID === reportSortID });
            return sortData;
        };

        const getRowFieldName = function (rowData)
        {
            if (rowData.relatedReportSortId)
            {
                const section = getSectionByLine(getLineByID(rowData.lineId));
                const relatedSort = getSortDataByReportSortID(section, rowData.relatedReportSortId);
                return relatedSort.FieldName;
            }

            return rowData.fieldName;
        };

        const getColumnsInLine = function (lines)
        {
            return lines.find('.jsColumn');
        };

        const getColumnsInLineByFieldName = function (lines, fieldName)
        {
            return lines.find('[data-field-name="' + $.escapeSelector(fieldName) + '"]');
        };

        const getColumnsInLineByAbsoluteFieldPath = function (lines, absoluteFieldPath)
        {
            return lines.find('[data-absolute-field-path="' + $.escapeSelector(absoluteFieldPath) + '"]');
        };

        const getSectionAllColumns = function (section)
        {
            return section.find('>.sectionBody>.reportLine:not(.jsPrintOnOneLinePreviewLine)>.jsColumn');
        };

        const getSectionAllCalculatedFieldIDs = function (section)
        {
            const sectionData = section.data();
            const calculatedFieldsForSection = CalculatedFields_Model.AllFieldsOfModuleAndObject(sectionData.rootModule, sectionData.rootObject, sectionData.entireRelationshipPath);

            const calculatedFieldIDsForSection = [];
            $.each(calculatedFieldsForSection, function (key)
            {
                calculatedFieldIDsForSection.push(key);
            });

            return calculatedFieldIDsForSection;
        };

        const getColumnsBySourceColumnID = (columnID) => entireCanvas.find(`[data-source-column-id='${columnID}']`);

        const getColumnByID = (columnID) => entireCanvas.find(`[data-column-id='${columnID}']`);

        const getReportHeaderLine = function ()
        {
            return designer.find('.jsReportHeaderLine');
        };

        const getReportFooterLine = function ()
        {
            return designer.find('.jsReportFooterLine');
        };

        const getPageHeaderLine = function ()
        {
            return designer.find('.jsPageHeaderLine');
        };

        const getPageFooterLine = function ()
        {
            return designer.find('.jsPageFooterLine');
        };

        const getAllHeaderFooterSections = function ()
        {
            return designer.find('.jsHeaderFooterSection');
        };

        const getAllSorts = function ()
        {
            const reportSorts = [];
            designer.find(".jsSection").each(function ()
            {
                const section = $(this);
                const sectionSorts = getSectionSorts(section);
                if (sectionSorts)
                {
                    sectionSorts.forEach(function (sort)
                    {
                        reportSorts.push(sort);
                    });
                }
            });
            return reportSorts;
        };

        const getAllMainSections = function ()
        {
            return designer.find('.jsMainSection');
        };

        const getAllSections = function ()
        {
            return designer.find(".jsSection");
        }

        const getCurrentSection = function ()
        {
            return entireCanvas.find(".sectionSelected");
        };

        const getAllNonPreviewLines = function (section)
        {
            return (section ?? entireCanvas).find(".reportLine:not(.jsPrintOnOneLinePreviewLine)");
        };

        const getSectionNonPreviewLines = function (section)
        {
            return section.children('.sectionBody').find(".reportLine:not(.jsPrintOnOneLinePreviewLine)");
        };

        const getCurrentAndDetailSections = function (section)
        {
            return (section ?? getCurrentSection()).find(".jsSection").addBack();
        };

        const getDetailSections = function (section)
        {
            return section.children(".detailSectionContainer").children(".jsSection");
        };

        const getNestedDetailSections = function (section)
        {
            return section.find(".jsSection");
        };

        const getParentLine = function (column)
        {
            return column.closest(".reportLine");
        };

        const isSection = (element) => element.is(".jsSection");

        const isLine = (element) => element.is(".reportLine");

        const getLineByID = (id) => entireCanvas.find(".reportLine[data-line-id='" + id + "']");

        const getSectionByLine = (line) => line.closest(".jsSection");

        const getSectionToolbar = (section) => section.find(".sectionToolbar").first();

        const getCurrentSectionToolbar = () => getSectionToolbar(getCurrentSection());

        const getCurrentSectionFilterLaunchButton = function ()
        {
            return getCurrentSectionToolbar().find(".filterBuilderLaunchButton");
        };

        const getSectionsByModuleAndObject = function (schemaObject, relationshipPath)
        {
            let matchingSections = [];

            getAllMainSections().each(function (index, sectionElement)
            {
                const section = $(sectionElement);

                if (sectionMatchesSchemaAndPath(section, schemaObject, relationshipPath))
                {
                    matchingSections.push(section);
                }

                const detailSections = getNestedDetailSections(section);

                detailSections.each(function (index, detailSectionElement)
                {
                    const detailSection = $(detailSectionElement);

                    if (sectionMatchesSchemaAndPath(detailSection, schemaObject, relationshipPath))
                    {
                        matchingSections.push(detailSection);
                    }
                })
            });

            return matchingSections;
        };

        function sectionMatchesSchemaAndPath(section, schemaObject, relationshipPath)
        {
            const sectionData = section.data();
            const sectionSchemaObject = `${sectionData.rootModule}.${sectionData.rootObject}`;

            return sectionSchemaObject === schemaObject && (sectionData.entireRelationshipPath ?? "") === (relationshipPath ?? "");
        }

        const insertNewLine = function ($section, html)
        {
            if (html.hasClass('sortHeaderLine'))
            {
                insertSortHeaderLine($section, html);
            }
            else if (html.hasClass('jsHeaderLine'))
            {
                insertHeaderLine($section, html);
            }
            else if (html.hasClass('jsDataLine'))
            {
                insertDataLine($section, html);
            }
            else if (html.hasClass('jsFooterLine'))
            {
                insertFooterLine($section, html);
            }
            else if (html.hasClass('jsSectionAggregateLine'))
            {
                insertSectionAggregateLine($section, html);
            }
            else if (html.hasClass('jsSortAggregateLine'))
            {
                insertSortAggregateLine($section, html);
                setSelectedRow(html);
            }

            initDesignerUI($section);

            updateHiddenField();
        };

        const insertHeaderLine = function ($section, html)
        {
            html.insertAfter(getSectionHeaderLines($section).last());
        };

        const insertDataLine = function ($section, html)
        {
            html.insertAfter(getSectionDataLines($section).last());
        };

        const reInsertSingleLinePreview = function ($section)
        {
            const preview = getSectionSingleLinePreview($section);
            if (preview.length > 0)
            {
                preview.remove();
                preview.insertAfter(getSectionDataLines($section).last());
            }
        };

        const insertSortAggregateLine = function ($section, html)
        {
            const sortAggregateLines = getSectionSortAggregateLines($section);
            const data = html.data();
            const existingSameSortLine = getSortLineForExistingSameSort(sortAggregateLines, data.relatedReportSortId);

            if (existingSameSortLine)
            {
                html.insertAfter(existingSameSortLine);
            }
            else
            {
                const lineSortOrder = getSectionSortOrderByReportSortID($section, data.relatedReportSortId);
                const beforeSortAggregateLine = getSortLineForSortAggregateLine($section, sortAggregateLines, lineSortOrder);
                if (beforeSortAggregateLine)
                {
                    html.insertBefore(beforeSortAggregateLine);
                }
                else
                {
                    const sectionLines = getSectionLines($section);
                    for (let i = sectionLines.length - 1; i >= 0; i--)
                    {
                        const line = $(sectionLines[i]);
                        if (!(line.hasClass("jsFooterLine") || line.hasClass("jsSectionAggregateLine")))
                        {
                            html.insertAfter(line);
                            break;
                        }
                    }
                }
            }

            reInsertSingleLinePreview($section);
        };

        const getSortLineForSortAggregateLine = function (section, sortLines, sortOrder)
        {
            let sortAggregateLine;
            let foundSortOrder = -1;

            for (const line of sortLines)
            {
                const lineData = $(line).data();
                const lineSortOrder = getSectionSortOrderByReportSortID(section, lineData.relatedReportSortId);

                if (sortOrder > lineSortOrder && lineSortOrder > foundSortOrder)
                {
                    foundSortOrder = lineSortOrder;
                    sortAggregateLine = line;
                }
            }

            return sortAggregateLine;
        };

        const getSortLineForExistingSameSort = function (sortLines, relatedReportSortId)
        {
            let existingSameSortLine;
            for (const line of sortLines)
            {
                const lineData = $(line).data();
                if (lineData.relatedReportSortId == relatedReportSortId)
                {
                    existingSameSortLine = line;
                }
            }

            return existingSameSortLine;
        };

        const insertSortHeaderLine = function ($section, html)
        {
            const sortHeaderLines = getSectionSortHeaderLines($section);
            const data = html.data();
            const existingSameSortLine = getSortLineForExistingSameSort(sortHeaderLines, data.relatedReportSortId);

            if (existingSameSortLine)
            {
                html.insertAfter(existingSameSortLine);
            }
            else
            {
                const lineSortOrder = getSectionSortOrderByReportSortID($section, data.relatedReportSortId);
                const beforeSortHeaderLine = getSortLineForSortHeaderLine($section, sortHeaderLines, lineSortOrder);
                if (beforeSortHeaderLine)
                {
                    html.insertBefore(beforeSortHeaderLine);
                }
                else
                {
                    const beforeSortLine = getSectionDataLines($section).first();
                    html.insertBefore(beforeSortLine);
                }
            }
        };

        const getSortLineForSortHeaderLine = function (section, sortLines, sortOrder)
        {
            let sortHeaderLine;
            let foundSortOrder = undefined;

            for (const line of sortLines)
            {
                const lineData = $(line).data();
                const lineSortOrder = getSectionSortOrderByReportSortID(section, lineData.relatedReportSortId);

                if (sortOrder < lineSortOrder && (!foundSortOrder || lineSortOrder < foundSortOrder))
                {
                    foundSortOrder = lineSortOrder;
                    sortHeaderLine = line;
                }
            }

            return sortHeaderLine;
        };

        const updateSortDataSortOrder = function (sortData)
        {
            $.each(sortData, function (i, sort)
            {
                sort.SortOrder = i;
            });

            return sortData;
        };

        const updateSectionSortData = function (section, sortData)
        {
            section.data("reportSorts", sortData);
        };

        const insertSectionAggregateLine = function ($section, html)
        {
            var lastSectionLine = getSectionLines($section).last();
            if (lastSectionLine.hasClass("jsFooterLine"))
            {
                var firstSectionFooter = getSectionFooterLines($section).first();
                html.insertBefore(firstSectionFooter);
            }
            else
            {
                html.insertAfter(lastSectionLine);
            }

            reInsertSingleLinePreview($section);
        };

        const insertFooterLine = function ($section, html)
        {
            html.insertAfter(getSectionLines($section).last());

            reInsertSingleLinePreview($section);
        };

        const getSectionSelectedRow = function ($section)
        {
            let selectedLine = $section.find('.row-selected').not("li");

            if (selectedLine.length == 0)
            {
                // get the parent of the selected column
                const sectionID = $section.data("sectionId");
                selectedLine = currentSelectedColumns.getRowByLastSelectedColumnBySectionID(sectionID);
                if (!selectedLine || selectedLine.length == 0) //get the first data line
                {
                    selectedLine = getSectionDataLines($section).first();
                    if (selectedLine.length == 0)//just get the first reportLine in the section
                    {
                        selectedLine = $section.find(".reportLine").first();
                    }
                }
                selectedLine.add(selectedLine.data("relatedLabel")).addClass('row-selected');
            }
            return selectedLine;
        };

        const positionNewColumn = function (newColumn, reportLine, top, left, autoCreatedColumn)
        {
            var reportLinePosition = reportLine.offset();
            var calculated = { top: 0, left: 0 };
            var newColumnPosition = newColumn.position();
            var COLUMN_PLACEMENT_SPACING = 5;
            var widthNeeded = newColumn.data("fieldWidth") + COLUMN_PLACEMENT_SPACING;
            var newColumnHeight = newColumn.data("fieldHeight");

            if (!newColumnHeight || newColumnHeight <= 0)
            {
                newColumnHeight = DEFAULT_COLUMN_HEIGHT;
            }

            if (top != null && left != null)
            {
                //normal dropped field
                //this method is used when passing in the left from a previous drop, top will be NaN
                if (isNaN(top)) { top = 0; }

                if (!autoCreatedColumn)
                {
                    calculated.top = top - reportLinePosition.top;
                    calculated.left = left - reportLinePosition.left;
                }
                else
                {
                    calculated.top = top;
                    calculated.left = left;
                }
            }
            else if (top == null && left != null)
            {
                //auto added fields - i.e. header, set the top to 0
                calculated.top = newColumnPosition.top;
                calculated.left = left;
            }
            else
            {
                //all others

                var lineWidth = reportLine.width();
                var allColumns = reportLine.find('.jsFieldColumn');

                var findNextOpenPosition = function ()
                {
                    var bottomRightmost = { x: 0, y: 0, columnElement: null };
                    // find bottom rightmost column's corner
                    allColumns.each(function ()
                    {
                        var jqThis = $(this);
                        var thisPosition = jqThis.position();
                        var thisBottom = thisPosition.top + jqThis.outerHeight();
                        var thisRight = thisPosition.left + jqThis.outerWidth();
                        if (thisBottom < bottomRightmost.y)
                        {
                            return;
                        }
                        else
                        {
                            if ((thisBottom == bottomRightmost.y && thisRight > bottomRightmost.x) || thisBottom > bottomRightmost.y)
                            {
                                bottomRightmost.x = thisRight;
                                bottomRightmost.y = thisBottom;
                                bottomRightmost.columnElement = jqThis;
                            }
                            return;
                        }
                    });

                    // if that corner is off the right edge, move down.
                    if (bottomRightmost.x + widthNeeded > lineWidth)
                    {
                        return {
                            top: bottomRightmost.y + COLUMN_PLACEMENT_SPACING,
                            left: 1
                        };
                    }
                    else
                    {
                        return {
                            top: bottomRightmost.y - newColumnHeight,
                            left: bottomRightmost.x > 1 ? bottomRightmost.x + COLUMN_PLACEMENT_SPACING : bottomRightmost.x
                        };
                    }
                };
                calculated = findNextOpenPosition();
            }

            //validate
            if (calculated.top < 0)
            {
                calculated.top = 0;
            }

            if (calculated.left < 0)
            {
                calculated.left = 0;
            }

            calculated.left = $.safeRound(calculated.left);
            calculated.top = $.safeRound(calculated.top);

            //end validate
            newColumn.data('positionY', calculated.top);
            newColumn.data('positionX', calculated.left);

            newColumn.css('left', calculated.left + 'px');
            newColumn.css('top', calculated.top + 'px');
            newColumn.css('position', 'absolute');
            newColumn.css("z-index", "");
            return calculated;
        };

        //accepts params = { newItem: newItem, reportLine: reportLine, section: section, position: { top: top, left: left } }
        const insertColumnHelperFunction = function (params)
        {
            if (params.section?.hasClass('collapseSection'))
            {
                return;
            }
            const newPosition = positionNewColumn(params.newItem, params.reportLine, params.position.top, params.position.left, params.autoCreatedColumn);
            setSelectedRowByRow(params.reportLine);
            (params.newItem).insertBefore(params.reportLine.children('.ui-resizable-handle'));
            setDefaultItemFormatting(params.newItem);
            applyColumnCss(params.newItem);
            initColumnDragging(params.newItem);
            params.newItem.resizable(columnResizableOptions);
            const newItemBottomPosition = params.newItem.position().top + params.newItem.outerHeight();
            if (newItemBottomPosition > currentRow.data("lineHeight"))
            {
                currentRow.data("lineHeight", newItemBottomPosition)
                    ?.css("height", newItemBottomPosition + "px")
                    .trigger('resize');
            }
            updateHiddenField();
            setSelectedColumn(params.newItem, true);
            skyward.reporting.updateVariableUsageLocations();
            return newPosition;
        };

        // accepts params = { newItem: jQuery object, reportLine: jQuery object, isDynamicColumn: bool }
        const insertDataColumn = function (params)
        {
            params.newItem = createNewColumn({
                type: getNewColumnType(params.reportLine, params.newItem, params.isDynamicColumn),
                relatedItem: params.newItem
            });

            // inserts the data column and returns the left position of the new element
            const dataColumnPosition = insertColumnHelperFunction(params);

            const headerLine = getCorrespondingHeaderLine(params.section, params.reportLine);

            if (headerLine.length > 0)
            {
                // make a copy of the params
                const headerParams = {
                    section: params.section,
                    reportLine: headerLine,
                    newItem: createNewColumn({
                        type: getNewColumnType(headerLine, params.newItem, params.isDynamicColumn),
                        relatedItem: params.newItem,
                        usesSingleLineIndex: isParentDisplayOnSingleLine(params.section)
                    }),
                    position: {
                        left: dataColumnPosition.left,
                        top: dataColumnPosition.top
                    },
                    autoCreatedColumn: true
                };

                // inserts the new header column
                insertColumnHelperFunction(headerParams);
            }

            return dataColumnPosition;
        };


        //END SELECTOR HELPERS

        var showXMLColumnProperties = function ()
        {
            exportProperties.xml.wrapper.show();
            exportProperties.xml.section.wrapper.hide();
            exportProperties.xml.row.wrapper.hide();
            exportProperties.xml.column.wrapper.show();
        };

        var showXMLRowProperties = function ()
        {
            exportProperties.xml.wrapper.show();
            exportProperties.xml.section.wrapper.hide();
            exportProperties.xml.row.wrapper.show();
            exportProperties.xml.column.wrapper.hide();
        };

        var showXMLSectionProperties = function ()
        {
            exportProperties.xml.wrapper.show();
            exportProperties.xml.section.wrapper.show();
            exportProperties.xml.row.wrapper.hide();
            exportProperties.xml.column.wrapper.hide();
        };

        skyward.reporting.getFieldsUsedInSection = function (section, includeFilters, convertImages)
        {
            const sectionData = section.data();

            let fieldsInUse = []

            const getFieldsFromHiddenCondition = function (isHiddenCondition)
            {
                const fields = [];
                const leftOperand = isHiddenCondition.LeftOperand;
                const rightOperand = isHiddenCondition.RightOperand;

                if (leftOperand?.FieldPath)
                {
                    const fieldPath = `${sectionData.baseObject}.${leftOperand.FieldPath}`;
                    const absoluteFieldPath = sectionData.entireRelationshipPath ? `${sectionData.entireRelationshipPath}.${leftOperand.FieldPath}` : leftOperand.FieldPath;
                    const field = skyward.reporting.buildUsedInReportField(fieldPath, absoluteFieldPath, leftOperand.DisplayName, leftOperand.FieldDisplayText, leftOperand.DataType, leftOperand.Parameters, leftOperand.FieldPathIsCalculated, leftOperand.FieldPathIsOperation, false, convertImages);
                    fields.push(field);
                }

                if (rightOperand?.FieldPath)
                {
                    const fieldPath = `${sectionData.baseObject}.${rightOperand.FieldPath}`;
                    const absoluteFieldPath = sectionData.entireRelationshipPath ? `${sectionData.entireRelationshipPath}.${rightOperand.FieldPath}` : rightOperand.FieldPath;
                    const field = skyward.reporting.buildUsedInReportField(fieldPath, absoluteFieldPath, rightOperand.DisplayName, rightOperand.FieldDisplayText, rightOperand.DataType, rightOperand.Parameters, rightOperand.FieldPathIsCalculated, rightOperand.FieldPathIsOperation, false, convertImages);
                    fields.push(field);
                }

                return fields;
            }

            const getFieldsFromFilter = function (filterSchemaFields, parameters)
            {
                const fields = [];

                if (filterSchemaFields)
                {
                    filterSchemaFields.forEach(function (schemaField)
                    {
                        const field = skyward.reporting.buildUsedInReportField(schemaField.name, schemaField.fieldPath, schemaField.displayName, schemaField.displayPath, schemaField.type, parameters, false, schemaField.isOperation, false, convertImages);
                        fields.push(field);
                    });
                }

                return fields;
            };

            const columns = getColumnsForLinesInSection(section);
            columns.forEach(function (column)
            {
                const isCalculatedField = column.operationId ?? false;
                const isHeader = column.fieldColumnType === COLUMN_TYPES.HEADER_COLUMN || column.fieldColumnType === COLUMN_TYPES.GROUP_COLUMN_HEADER;
                const isPageSection = column.fieldColumnType === COLUMN_TYPES.PAGE_SECTION_COLUMN;
                const isVariable = column.fieldColumnType === COLUMN_TYPES.VARIABLE_COLUMN;
                const isAggregate = column.fieldColumnType === COLUMN_TYPES.AGGREGATE_COLUMN;

                if (column.fieldName && !isCalculatedField && !isPageSection && !isVariable && !isHeader && !isAggregate)
                {
                    const isColumnGroup = column.fieldColumnType === (COLUMN_TYPES.GROUP_COLUMN || COLUMN_TYPES.GROUP_COLUMN_AGGREGATE);
                    const fieldPath = column.fieldPath ?? column.fieldName;
                    const absoluteFieldPath = column.absoluteFieldPath;
                    const fieldWidth = column.fieldWidth;
                    const fieldHeight = column.fieldHeight;
                    const field = skyward.reporting.buildUsedInReportField(fieldPath, absoluteFieldPath, column.designerText, column.displayPath, column.fieldType, column.parameters, column.isCalculated, column.isOperation, isColumnGroup, convertImages, fieldWidth, fieldHeight);
                    fieldsInUse.push(field);
                }

                if (column.isHiddenCondition)
                {
                    fieldsInUse.push(...getFieldsFromHiddenCondition(column.isHiddenCondition));
                }
            });

            const sectionLines = getSectionLines(section).toArray();
            sectionLines.forEach(function (line)
            {
                const lineData = $(line).data();
                if (lineData.isHiddenCondition)
                {
                    fieldsInUse.push(...getFieldsFromHiddenCondition(lineData.isHiddenCondition));
                }
            });

            const sorts = getSectionSorts(section);
            sorts.forEach(function (sort)
            {
                if (!sort.CalculatedField && sort.FieldName)
                {
                    const absoluteFieldPath = sectionData.entireRelationshipPath ? `${sectionData.entireRelationshipPath}.${sort.RelativeDataPath}` : sort.RelativeDataPath;
                    const field = skyward.reporting.buildUsedInReportField(sort.FieldName, absoluteFieldPath, sort.DisplayName, sort.DisplayPath, sort.DataType, sort.Parameters, false, sort.IsOperation, false, convertImages);
                    fieldsInUse.push(field);
                }
            });

            if (includeFilters)
            {
                const filterData = getSectionFilterObjectAndParameters(section);
                if (filterData && filterData.filterObject)
                {
                    fieldsInUse.push(...getFieldsFromFilter(filterData.filterSchemaFields, filterData.parameters ?? []));
                }
            }

            return fieldsInUse;
        };

        /**
         * Builds an object to represent a field used in the report
         * @param {string} fieldPath - The path to the field starting on the section object it is in
         * @param {string} absoluteFieldPath - The path to the field starting on the root object it is in
         * @param {string} displayName - The display name of the field
         * @param {string} displayPath - The display name of the field
         * @param {string} dataType - The type of the field
         * @param {Array} parameters - Any parameters the field has
         * @param {boolean} isCalculated - If the field is calculated in C#
         * @param {boolean} isOperation - If the field is an operation
         * @param {boolean} isColumnGroup - If the field is a column group
         * @param {boolean} convertImages - If image fields should be treated as numbers
         * @param {number} fieldWidth - The width of the field's column
         * @param {number} fieldHeight - The height of the field's column
         * @returns {ReportFieldProps} Returns field object with data related to it
         */
        skyward.reporting.buildUsedInReportField = function (fieldPath, absoluteFieldPath, displayName, displayPath, dataType, parameters, isCalculated, isOperation, isColumnGroup, convertImages, fieldWidth, fieldHeight)
        {
            // Convert Dynamic Image Columns to be of type long.
            // Do this right away so we get the correct svg class.
            if (convertImages && dataType.toLowerCase() === "image")
            {
                dataType = DATA_TYPES.NUMBER_LONG;
            }

            if (isColumnGroup)
            {
                dataType = DATA_TYPES.GROUP_COLUMN;
                absoluteFieldPath = fieldPath; //columns groups have a undefined absolute field path
            }

            const svgClass = skyward.reporting.getSvgNameByDataType(dataType);
            const displayPathText = displayPath ?? fieldPath.replaceAll(".", " > ");
            const firstDotIndex = fieldPath.indexOf(".");
            const lastDotIndex = fieldPath.lastIndexOf(".");
            const hasDot = firstDotIndex >= 0;
            const fieldName = hasDot ? fieldPath.substring(lastDotIndex + 1) : fieldPath;

            const absoluteFieldPathLastDotIndex = absoluteFieldPath ? absoluteFieldPath.lastIndexOf(".") : -1;
            const realtionshipPath = absoluteFieldPathLastDotIndex > 0 ? absoluteFieldPath.substring(0, absoluteFieldPathLastDotIndex) : "";

            const field =
            {
                absoluteFieldPath: absoluteFieldPath,
                relationshipPath: realtionshipPath,
                displayName: displayName,
                displayPathText: displayPathText,
                fieldName: fieldName,
                fieldPath: fieldPath,
                fieldType: dataType,
                isCalculated: isCalculated,
                isColumnGroup: isColumnGroup,
                isOperation: isOperation,
                isTreeBrowseRow: true,
                parameters: parameters ?? [],
                svgClass: svgClass,
                fieldWidth: fieldWidth,
                fieldHeight: fieldHeight
            };

            return field;
        };

        //SET CURRENTLY SELECTED
        var setSelectedColumn = function (column, clearSelectedColumns)
        {
            exportProperties.xml.wrapper.hide();

            if (column)
            {
                column.focus(); //lets the keydown event work correctly

                if (clearSelectedColumns)
                {
                    currentSelectedColumns.clear();
                }

                currentSelectedColumns.toggleColumn(column);

                $(document).find(".row-selected").removeClass("row-selected");
                currentRow = null;

                setColumnProperties();
            }

            if (!currentSelectedColumns.areMultipleColumnsSelected())
            {
                showXMLColumnProperties();
                exportProperties.fixedWidth.wrapper.show();
                exportProperties.json.wrapper.show();
            }
        };

        const buildAggregateFieldTreeBrowseSection = function (key, title, ul, columns)
        {
            const treeBrowseSection =
            {
                key: key,
                title: title,
                component: "aggregateField",
                container: "#" + ul.addIds().attr("id"),
                rows: columns ?? []
            };

            return treeBrowseSection;
        };

        const getColumnsForDataLinesInSection = function (section)
        {
            const foundColumns = [];
            const fieldIdentifiers = [];

            getSectionDataLines(section).find('.jsColumn').each(function (columnIndex, column)
            {
                const $column = $(column);
                const columnData = $column.data();
                const isDataColumn = columnData.fieldColumnType === COLUMN_TYPES.DATA_COLUMN;
                const isDynamicImageColumn = columnData.fieldColumnType == COLUMN_TYPES.DYNAMIC_IMAGE_COLUMN;

                const fieldIdentifier = columnData.operationId || columnData.absoluteFieldPath;
                if ((isDataColumn || isDynamicImageColumn) && fieldIdentifiers.indexOf(fieldIdentifier) < 0)
                {
                    fieldIdentifiers.push(fieldIdentifier);
                    foundColumns.push(columnData);
                }
            });

            return foundColumns;
        };

        const getColumnsForLinesInSection = function (section)
        {
            const foundColumns = [];

            getSectionLines(section).find('.jsColumn').each(function (columnIndex, column)
            {
                const $column = $(column);
                const columnData = $column.data();

                foundColumns.push(columnData);
            });

            return foundColumns;
        };

        const getColumnsForAggregateLine = function (aggregateLine)
        {
            const foundColumns = [];
            const supportedColumnTypes = [COLUMN_TYPES.AGGREGATE_COLUMN, COLUMN_TYPES.CALCULATED_COLUMN, COLUMN_TYPES.DYNAMIC_IMAGE_COLUMN]

            for (let column of getColumnsInLine(aggregateLine).map((i, c) => $(c)))
            {
                const columnData = column.data();

                if (!columnData.isFutureValue && supportedColumnTypes.includes(columnData.fieldColumnType))
                {
                    foundColumns.push(columnData);
                }
            }

            return foundColumns;
        };

        const getFutureValueSourceColumnInfo = function (aggregateLine)
        {
            const foundColumns = [];
            const supportedColumnTypes = [COLUMN_TYPES.AGGREGATE_COLUMN, COLUMN_TYPES.CALCULATED_COLUMN]

            for (let column of getColumnsInLine(aggregateLine).map((i, c) => $(c)))
            {
                const columnData = column.data();

                if (!columnData.isFutureValue && supportedColumnTypes.includes(columnData.fieldColumnType))
                {
                    // the fieldName or calculated field don't mean anything to us here, so let's not confuse things by having them set
                    const futureColumnData =
                    {
                        columnId: columnData.columnId,
                        designerText: getColumnLabelWithPreText(column),
                        fieldType: columnData.fieldType,
                        fieldWidth: columnData.fieldWidth,
                        fieldHeight: columnData.fieldHeight,
                        isFutureColumn: true
                    };
                    foundColumns.push(futureColumnData);
                }
            }

            return foundColumns;
        };

        const getPossibleSourceAggregateLinesForSection = function (selectedLine, selectedSection, workingSection)
        {
            const selectedLineData = selectedLine.data();

            if (selectedSection.is(workingSection))
            {
                if (selectedLineData.lineType == LINE_TYPES.SORT_AGGREGATE_LINE)
                {
                    const selectedLineSortOrder = getSortDataByReportSortID(selectedSection, selectedLineData.relatedReportSortId).SortOrder;

                    return getSectionSortAggregateLines(workingSection).filter((index, line) =>
                    {
                        const aggregateLineSortOrder = getSortDataByReportSortID(workingSection, $(line).data("relatedReportSortId")).SortOrder;
                        return aggregateLineSortOrder > selectedLineSortOrder;
                    });
                }
                else
                {
                    return getSectionSortAggregateLines(workingSection);
                }
            }
            else
            {
                return getSectionAllAggregateLines(workingSection);
            }
        };

        const replaceAggregateFieldsTreeBrowseSection = function (allColumnInfo)
        {
            const ul = designer.find(".reportDesignerAggregateFieldList");
            const treeBrowseData = skyward.treebrowse.getTreeBrowseDataByName("reportObjectBrowser");
            treeBrowseData.additionalSections = treeBrowseData.additionalSections.filter(function (section)
            {
                return !section.key?.startsWith("aggregate_");
            });

            let treeBrowseSectionIndex = 0;
            for (let sectionInfo of allColumnInfo)
            {
                for (let lineInfo of sectionInfo.lines)
                {
                    let treeBrowseSectionName = sectionInfo.data.displayName;

                    if (lineInfo.label)
                    {
                        treeBrowseSectionName = `${treeBrowseSectionName} (${lineInfo.label})`;
                    }

                    if (lineInfo.columns.length > 0)
                    {
                        treeBrowseSectionIndex++;
                        const treeBrowseSection = buildAggregateFieldTreeBrowseSection(`aggregate_${treeBrowseSectionIndex}`, treeBrowseSectionName, ul, lineInfo.columns);
                        treeBrowseData.additionalSections.push(treeBrowseSection);
                    }
                }
            }
        }

        const getPossibleFutureValueSourceLinesForSection = function (selectedLineData, selectedSection)
        {
            const selectedLineSortOrder = getSortDataByReportSortID(selectedSection, selectedLineData.relatedReportSortId).SortOrder;

            return getSectionAllAggregateLines(selectedSection).filter((index, line) =>
            {
                let lineData = $(line).data();

                if (lineData.lineType === LINE_TYPES.AGGREGATE_LINE)
                {
                    return selectedLineData.lineType === LINE_TYPES.SORT_AGGREGATE_LINE;
                }

                const aggregateLineSortOrder = getSortDataByReportSortID(selectedSection, lineData.relatedReportSortId).SortOrder;

                if (selectedLineData.lineType === LINE_TYPES.SORT_HEADER_LINE)
                {
                    return aggregateLineSortOrder === selectedLineSortOrder;
                }

                return aggregateLineSortOrder < selectedLineSortOrder;
            });
        };

        const getFutureValueColumnDataForSortHeader = function (selectedLine)
        {
            const selectedLineData = selectedLine.data();
            const currentSection = getSectionByLine(selectedLine);
            const sectionData = currentSection.data();

            let sectionInfo = { data: sectionData, lines: [] };
            let allColumnInfo = [sectionInfo];

            for (let aggregateLine of getPossibleFutureValueSourceLinesForSection(selectedLineData, currentSection).map((i, l) => $(l)))
            {
                const aggregateLineData = aggregateLine.data();
                sectionInfo.lines.push({ label: `Future: ${aggregateLineData.reportLabelText}`, columns: getFutureValueSourceColumnInfo(aggregateLine) });
            }

            return allColumnInfo;
        };

        const getAggregateAndFutureColumnDataForAggregateLine = function (selectedLine)
        {
            const selectedLineData = selectedLine.data();
            const currentSection = getSectionByLine(selectedLine);
            let allColumnInfo = [];

            const sections = currentSection.hasClass('jsHeaderFooterSection')
                ? entireCanvas.find(".jsSection:not(.jsHeaderFooterSection)")
                : getCurrentAndDetailSections(currentSection)

            for (let section of sections.map((i, s) => $(s)))
            {
                const sectionData = section.data();

                let sectionInfo = { data: sectionData, lines: [] };
                sectionInfo.lines.push({ columns: getColumnsForDataLinesInSection(section) });

                for (let aggregateLine of getPossibleSourceAggregateLinesForSection(selectedLine, currentSection, section).map((i, l) => $(l)))
                {
                    const aggregateLineData = aggregateLine.data();
                    sectionInfo.lines.push({ label: aggregateLineData.reportLabelText, columns: getColumnsForAggregateLine(aggregateLine) });
                }

                if (selectedLineData.lineType === LINE_TYPES.SORT_AGGREGATE_LINE && section.is(currentSection))
                {
                    for (let aggregateLine of getPossibleFutureValueSourceLinesForSection(selectedLineData, currentSection).map((i, l) => $(l)))
                    {
                        const aggregateLineData = aggregateLine.data();
                        sectionInfo.lines.push({ label: `Future: ${aggregateLineData.reportLabelText}`, columns: getFutureValueSourceColumnInfo(aggregateLine) });
                    }
                }

                allColumnInfo.push(sectionInfo);
            }

            return allColumnInfo;
        };

        var getCurrentAndDetailSectionsAggregateColumns = function ()
        {
            const aggregateColumns = [];

            getSectionSelectedRow(getCurrentSection()).find(".jsReportAggregate").each(function ()
            {
                const aggregateColumnData = $(this).data();
                aggregateColumns.push(
                    {
                        designerText: aggregateColumnData.aggregateType + ": " + aggregateColumnData.designerText,
                        fieldName: aggregateColumnData.bindingString,
                        fieldType: aggregateColumnData.columnFieldType
                    });
            });

            return aggregateColumns;
        };

        var setSelectedRowByRow = function (row, clearSelectedColumns = true)
        {
            setSelectedRow(row);
            if (clearSelectedColumns)
            {
                currentSelectedColumns.clear();
            }
            setRowProperties(row);
        };

        var setSelectedRowByColumn = function (column)
        {
            const row = column.parent();
            setSelectedRow(row);
        };

        var setSelectedRow = function (row)
        {
            if (!row.hasClass("sectionToolbar"))
            {
                showXMLRowProperties();
                exportProperties.fixedWidth.wrapper.hide();
                exportProperties.json.wrapper.hide();
            }

            if (row.is('.sortHeaderLine'))
            {
                designer.find(".reportDesignerAggregateFieldsMenu").show();
                replaceAggregateFieldsTreeBrowseSection(getFutureValueColumnDataForSortHeader(row));
            }
            else if (row.is('.jsSortAggregateLine, .jsSectionAggregateLine, .jsPageFooterLine, .jsReportFooterLine'))
            {
                designer.find(".reportDesignerAggregateFieldsMenu").show();
                replaceAggregateFieldsTreeBrowseSection(getAggregateAndFutureColumnDataForAggregateLine(row));
            }
            else
            {
                designer.find(".reportDesignerAggregateFieldsMenu").hide();
            }

            entireCanvas.find('.row-selected').not("li").each(function ()
            {
                const oldRow = $(this);
                oldRow.add(oldRow.data("relatedLabel")).removeClass('row-selected');
            });
            const section = row.closest(".jsSection");

            const showRemoveReportLineButton = row.hasClass('sectionToolbar') ||
                (row.hasClass('jsDataLine') && getSectionDataLines(section).length < 2) ||
                (row.hasClass('jsHeaderLine') && getSectionHeaderLines(section).length < 2);
            section.find(".remove_report_line").toggleClass("ui-disabled", showRemoveReportLineButton);

            toggleAggregateMathButtonState(row);
            toggleFormTextColumnButtonState(row);

            row.add(row.data("relatedLabel")).addClass('row-selected').focus();
            currentRow = row;
        };

        var setLabelTextBackground = function (element, color)
        {
            let labels = element.find('.labelMatchParent');
            if (labels && labels.length > 0)
            {
                labels.each(function ()
                {
                    $(this).css('background', `linear-gradient(transparent 10%, ${color} 15%, ${color} 85%, transparent 90%)`);
                });
            }
        };

        var removeLabelTextBackground = function (element)
        {
            let labels = element.find('.labelMatchParent');
            if (labels && labels.length > 0)
            {
                labels.each(function ()
                {
                    $(this)[0].style.removeProperty('background');
                });
            }
        };

        var setActiveSection = function ($section, clearSelectedColumns = true)
        {
            if ($section.length == 0 || $section.hasClass('sectionSelected'))
            {
                return;
            }

            // Reselect active section
            designer.find('.sectionSelected').removeClass('sectionSelected');
            $section.addClass('sectionSelected');

            // Reselect active row
            designer.find('.row-selected').removeClass('row-selected');
            Array.from($section.children()).forEach(child =>
            {
                if (child.classList.contains('sectionToolbar'))
                {
                    setSelectedRowByRow($(child), clearSelectedColumns)
                }
            })

            if ($section.hasClass('jsHeaderFooterSection'))
            {
                exportProperties.xml.wrapper.hide();
                exportProperties.json.wrapper.hide();
            }
            else
            {
                showXMLSectionProperties();
                exportProperties.json.wrapper.show();
            }
            exportProperties.fixedWidth.wrapper.hide();

            designer.find('.parentSectionComboBox').remove();

            const parentDisplayOnSingleLine = isParentDisplayOnSingleLine($section);

            const control = "reportObjectBrowser";
            const treeBrowseContainer = skyward.treebrowse.getTreeBrowseContainer(control);
            const treeBrowseData = skyward.treebrowse.getTreeBrowseData(treeBrowseContainer);
            treeBrowseData.renderColumnGroups = !parentDisplayOnSingleLine;
            treeBrowseData.renderRowForImageReferences = !parentDisplayOnSingleLine;

            if ($section.hasClass('jsHeaderFooterSection'))
            {
                designer.find(".hideForHeaderFooterSections").hide();
                const treeBrowseDataPromise = skyward.treebrowse.setBrowseTarget('', '', '', control, "Reporting/Report/ExpandTreeBrowseDesignerSidebar", "Header/Footer", true);

                treeBrowseDataPromise.then(function (data)
                {
                    hidePageNumberFields(false);
                    initDesignerUI($section);
                });
            }
            else
            {
                designer.find(".hideForHeaderFooterSections").show();
                const sectionData = getSectionData($section);
                const absoluteTreeBrowseObjectPath = sectionData.entireRelationshipPath ? sectionData.entireRelationshipPath : "";

                const treeBrowseDataPromise = skyward.treebrowse.setBrowseTarget(sectionData.baseModule, sectionData.baseObject,
                    sectionData.baseObject, control, "Reporting/Report/ExpandTreeBrowseDesignerSidebar", sectionData.sectionObjectName, null,
                    sectionData.rootModule, sectionData.rootObject, absoluteTreeBrowseObjectPath, true);

                treeBrowseDataPromise.then(function (data)
                {
                    hidePageNumberFields(true);
                    initDesignerUI($section);
                });
            }
        };

        var toggleFormTextColumnButtonState = function (line)
        {
            var lineType = line.data("lineType");
            var disabled = lineType === LINE_TYPES.HEADER_LINE || lineType === LINE_TYPES.FOOTER_LINE;

            getCurrentSectionToolbar().find(".add_formtext_field").toggleClass("ui-disabled", disabled);
        };

        var toggleAggregateMathButtonState = function (row)
        {
            var isAggregateLine = (row.hasClass("jsSortAggregateLine") || row.hasClass("jsSectionAggregateLine"));
            var showAggregateSimpleMathButton = isAggregateLine && getAvailableColumnInfoForAggregateCalculations(row).length > 0;

            var aggregateMathButton = getCurrentSectionToolbar().find('.simple_aggregate_math');
            entireCanvas.find('.simple_aggregate_math').toggleClass("ui-disabled", true);
            aggregateMathButton.toggleClass("ui-disabled", !showAggregateSimpleMathButton);
        };
        //END SET CURRENTLY SELECTED

        //BEGIN FILTERS

        function scrapeValueBoxForPromptData(valueBox)
        {
            let result = {};

            if (!valueBox || !valueBox.length)
            {
                return null;
            }

            // FieldPath, InputType, IsValue, Label
            const fieldSelector = valueBox.parentsUntil("div.condition", ".fieldWrapper").children(".filterBuilderFieldSelect");
            if (fieldSelector && fieldSelector.length > 0)
            {
                const fieldSelectorData = fieldSelector.data();

                if (fieldSelectorData.calculatedFieldName)
                {
                    const calculatedField = skyward.reporting.calculatedFields.Get(fieldSelectorData.calculatedFieldName);
                    if (calculatedField)
                    {
                        result.Label = calculatedField.fieldName;
                        result.InputType = calculatedField.dataType;
                    }
                }
                else if (fieldSelectorData.field)
                {
                    // existing field
                    result.FieldPath = fieldSelectorData.rootModule + "." + fieldSelectorData.rootObject + "." + fieldSelectorData.absoluteFieldPath;

                    const conditionTypeSelector = valueBox.parentsUntil("div.condition", ".fieldWrapper").children(".conditionTypesComboBox");
                    if (conditionTypeSelector.val() === "List" || conditionTypeSelector.val() === "NotList")
                    {
                        result.Type = enumMaps.PromptTypes["MultipleSelect"];
                        result.IsValue = false;
                    }
                }
                else // New standard filter
                {
                    return $.ajax({
                        url: skyward.global.virtualDir + "Reporting/Report/GetNewStandardFilterPrompt",
                        type: "POST",
                        dataType: "JSON",
                        data: {
                            standardFilterId: fieldSelectorData.standardFilterId
                        }
                    });
                }
            }
            else if (valueBox.parent().hasClass("rowProperties"))
            {
                const sectionName = currentRow.closest(".jsSection").find(".sectionToolbar").data("reportLabelText");
                if (valueBox.hasClass("editorPrintAtBottomOfPage"))
                {
                    result.Label = "Print " + sectionName + " " + currentRow.data("reportLabelText") + " at Bottom of Page";
                }
                else if (valueBox.hasClass("editorRepeatLineAfterStart"))
                {
                    result.Label = "Repeat " + sectionName + " " + currentRow.data("reportLabelText") + " after start";
                }
                else if (valueBox.hasClass("editorPrintReportFooterOnNewPage"))
                {
                    result.Label = "Print Report Footer on New Page";
                }
                else
                {
                    result.Label = "Hide " + sectionName + " " + currentRow.data("reportLabelText");
                }

                result.InputType = DATA_TYPES.BOOLEAN;
                result.PresenceType = "Required";
            }
            else if (valueBox.hasClass("pageBreak"))
            {
                let fullSortName = valueBox.closest(".sortItem").find(".fieldNameColumn .friendlyName:first").text();
                let separatorIndex = fullSortName.lastIndexOf(">");
                let sortFieldName = separatorIndex > -1 ? fullSortName.substr(separatorIndex + 2) : fullSortName;
                result.Label = "Page Break on " + sortFieldName + " Change";
                result.InputType = DATA_TYPES.BOOLEAN;
                result.PresenceType = "Required";
            }
            else if (valueBox.hasClass("userConditionValueSelectorDisplay"))
            {
                // not supported by multiple columns
                const promptContainer = valueBox.closest(".promptDetail");
                if (promptContainer && promptContainer.length)
                {
                    result = isHiddenSelector.getPromptDataFromPrompt(valueBox, promptContainer);
                }
                else
                {
                    result = isHiddenSelector.getPromptData(valueBox, currentSelectedColumns.getFirstSelectedColumn(), currentRow, getCurrentSection());
                }
            }

            return result;
        }

        function setNewPromptAsValue(setFilterValue, data, valueBox)
        {
            if (!data || !data.promptModel || !data.promptModel.toJsonObject)
            {
                return;
            }

            const promptData = data.promptModel.toJsonObject();
            const filterValueData = {};
            for (const key in promptData)
            {
                filterValueData[(key.substring(0, 1).toLowerCase() + key.substring(1))] = promptData[key];
            }

            filterValueData.svgClass = promptData.InputType === "standardFilter" ? "DialogPrompt" : skyward.reporting.getSvgNameByDataType(promptData.InputType);
            const valueBoxData = valueBox.data();
            if (valueBoxData.enumType)
            {
                filterValueData.enumType = valueBoxData.enumType;
            }

            setFilterValue(filterValueData);
        }

        function getEditorFieldDataType(input)
        {
            var data = input.data();
            return data.fieldType || data.inputType;
        }

        var valueSelectorClickEvents =
        {
            ".add_report_prompt": function (setFilterValue, valueBox, treatPromptAsInUse)
            {
                var treatAsInUseRequired = valueBox.hasClass("pageBreak") || valueBox.parent().hasClass("rowProperties");
                $.when(scrapeValueBoxForPromptData(valueBox)).then(function (newPrompt)
                {
                    return skyward.reporting.prompts.getPromptDetailsView(newPrompt, { treatPromptAsInUse: treatPromptAsInUse, treatPromptAsInUseRequired: treatAsInUseRequired });
                })
                    .then(function (data) { setNewPromptAsValue(setFilterValue, data, valueBox); });
            },
            ".selectFieldButton": function (setFilterValue, valueBox)
            {
                if (valueBox.hasClass("userConditionValueSelectorDisplay"))
                {
                    var currentSectionData = getSectionData(getCurrentSection());
                    var fieldDataType = !valueBox.hasClass("leftOperand") ? getEditorFieldDataType(isHiddenSelector.elements.left) : undefined;

                    var data =
                    {
                        rootModule: currentSectionData.baseModule,
                        rootObject: currentSectionData.baseObject,
                        seekField: valueBox.data("fieldName"),
                        relationshipPath: "",
                        fieldDataType: fieldDataType,
                        renderOperationFields: true,
                        includeCalculatedFields: true,
                        renderColumnGroups: false,
                        renderProxies: true,
                        renderOneToMany: false,
                        allowOneToManyBrowse: false,
                    };

                    const calculatedFieldData =
                    {
                        calculatedFieldRootModule: currentSectionData.rootModule,
                        calculatedFieldRootObject: currentSectionData.rootObject,
                        calculatedFieldRelationshipPath: currentSectionData.entireRelationshipPath,
                    };

                    skyward.window.closePopup();
                    skyward.reporting.treebrowse.getTreeBrowse(data, calculatedFieldData, valueBox, "Reporting/Report/ReportDesignerBuildTreeBrowse");
                }
                else
                {
                    skyward.filterbuilder.getTreeBrowseForFieldToField(valueBox);
                }
            },
            '.currentColumnValueButton': function (setFilterValue)
            {
                // not supported by multiple selected columns
                setFilterValue(currentSelectedColumns.getFirstSelectedColumn().data());
            }
        };

        skyward.reporting.getStandardFilterValueSelector = function (button, standardFilterId)
        {
            var requestingButton = $(button);
            var valueBox = requestingButton.closest(".conditionValuesContainer").find(".js-conditionValue--active");

            $.ajax({
                url: skyward.global.virtualDir + "Reporting/Report/GetStandardFilterValueSelector",
                type: "POST",
                dataType: "JSON",
                data: {
                    standardFilterId: standardFilterId,
                    currentPrompts: skyward.reporting.prompts.promptCollection.toJsonString()
                },
                success: function (data)
                {
                    skyward.filterbuilder.openValueSelector(data.html, button, valueBox, false, true, valueSelectorClickEvents);
                }
            });
        };

        skyward.reporting.getBooleanFilterValueSelector = function (button, allowFieldSelection, allowCurrentColumnValue)
        {
            var requestingButton = $(button);
            var container = requestingButton.closest(".conditionValuesContainer");
            if (!skyward.buttonManager.isElementDisabled(container))
            {
                var valueBox = requestingButton.closest(".conditionValuesContainer").find(".conditionValue:visible");

                $.ajax({
                    url: skyward.global.virtualDir + "Reporting/Report/GetBooleanValueSelector",
                    type: "POST",
                    dataType: "JSON",
                    data: {
                        currentPromptsJson: skyward.reporting.prompts.promptCollection.toJsonString(),
                        allowFieldSelection: allowFieldSelection,
                        allowCurrentColumnValue: allowCurrentColumnValue
                    },
                    success: function (data)
                    {
                        skyward.filterbuilder.openValueSelector(data.html, button, valueBox, false, true, valueSelectorClickEvents);
                    }
                });
            }
        };

        skyward.reporting.getSortBreakValueSelector = function (button)
        {
            var requestingButton = $(button);
            var container = requestingButton.closest(".conditionValuesContainer");
            if (!skyward.buttonManager.isElementDisabled(container))
            {
                var valueBox = requestingButton.closest(".conditionValuesContainer").find(".conditionValue:visible");

                $.ajax({
                    url: skyward.global.virtualDir + "Reporting/Report/GetSortBreakValueSelector",
                    type: "POST",
                    dataType: "JSON",
                    data:
                    {
                        currentPromptsJson: skyward.reporting.prompts.promptCollection.toJsonString(),
                        allowBurst: requestingButton.parents(".sortItem").first().find(".fieldNameColumn").data("allowBurst"),
                    },
                    success: function (data)
                    {
                        skyward.filterbuilder.openValueSelector(data.html, button, valueBox, false, true, valueSelectorClickEvents);
                    }
                });
            }
        };

        skyward.reporting.getFilterValueSelector = function (button, searchField, conditionType, dataModule, dataObject)
        {
            const filterValueSelectorData = skyward.filterbuilder.getFilterValueSelectorData(button, searchField, conditionType, dataModule, dataObject);

            let postData =
            {
                calculatedFieldJson: JSON.stringify(CalculatedFields_Model.Get(searchField)),
                currentPromptsJson: skyward.reporting.prompts.promptCollection.toJsonString()
            };

            $.extend(postData, filterValueSelectorData.postData);

            $.ajax({
                url: skyward.global.virtualDir + "Reporting/Report/GetValueSelector",
                type: "POST",
                dataType: "JSON",
                data: postData,
                success: function (data)
                {
                    skyward.filterbuilder.openValueSelector(data.html, button, filterValueSelectorData.valueBox, data.isListOfEnums, true, valueSelectorClickEvents);
                }
            }); // AJAX call
        };

        function disable(jQElements, disabled)
        {
            skyward.controls.toggle(jQElements, !disabled);
        }

        //END FILTERS

        skyward.reporting.saveAndRunReport = function (useLastValues)
        {
            var editReportDefinitionDesigner = $("#editReportDefinitionDesigner");
            editReportDefinitionDesigner.on("DOVSaveComplete.SaveAndRun DOVSaveValidationError.SaveAndRun DOVSaveCanceled.SaveAndRun", function (event)
            {
                if (event.type == "DOVSaveComplete")
                {
                    if (useLastValues)
                    {
                        $('.moreMenuArea .jsRunReportWithLastValues').click();
                    }
                    else
                    {
                        $('.moreMenuArea .jsRunReport').click();
                    }
                }
                editReportDefinitionDesigner.off("DOVSaveComplete.SaveAndRun DOVSaveValidationError.SaveAndRun DOVSaveCanceled.SaveAndRun");
            });

            $("#editReportDefinitionDesigner_save").click();
        };

        const removeLine = function (lineToRemove, updateUI = true)
        {
            if (lineToRemove && !lineToRemove.data("isRemoved"))
            {
                const section = lineToRemove.closest('.jsSection');
                const sectionData = getSectionData(section);
                const numSectionHeaderLines = getSectionHeaderLines(section).length;

                if (lineToRemove.hasClass('sectionToolbar') ||
                    (lineToRemove.hasClass('jsHeaderLine') && numSectionHeaderLines == 1) ||
                    (lineToRemove.hasClass('jsDataLine') && getSectionDataLines(section).length == 1))
                {
                    return;
                }

                for (const column of lineToRemove.find(".reportColumn"))
                {
                    removeColumn($(column), false);
                }

                // isRemoved is for tracking cascading deletes so we do not try to delete something twice
                // The attribute value needs to be set because .remove() deletes any data that jQuery is only holding in memory
                lineToRemove.data("isRemoved", true);
                lineToRemove.attr("data-is-removed", true);
                lineToRemove.remove();

                if (lineToRemove.data("lineType") == LINE_TYPES.DATA_LINE && numSectionHeaderLines > 1)
                {
                    const relatedHeaderLine = getCorrespondingHeaderLine(section, lineToRemove);
                    if (relatedHeaderLine.length > 0)
                    {
                        removeLine(relatedHeaderLine, false);
                    }
                }

                setSelectedRowByRow(getSectionDataLines(section).first());
                lineToRemove.removeData("printAtBottomOfPagePromptUniqueId");
                lineToRemove.removeData("isHiddenPromptUniqueId");

                updateHiddenField();
                labelBuilder.buildLabelColumns(true);

                if (updateUI)
                {
                    initFieldsUsedInReport(sectionData);
                    skyward.reporting.updateVariableUsageLocations();
                }
            }
        }

        const removeColumnByColumnID = function (columnID)
        {
            const column = getColumnByID(columnID);
            removeColumn(column);
        };

        const withSingleLineIndexBinding = function (text)
        {
            return `${text} ${SINGLE_LINE_HEADER_INDEX_BINDING}`;
        }

        skyward.reporting.propertiesInsertHeaderIndex = function ()
        {
            skyward.global.text.insertTextToken(editorColumnText, SINGLE_LINE_HEADER_INDEX_BINDING, false);
        };

        skyward.reporting.propertiesResetHeaderIndex = function ()
        {
            skyward.controls.setValue(editorColumnText, `${skyward.controls.getValue(editorColumnText).replace(/ \{Index\} /g, " ").replace(/\{Index\}/g, "").trim()} ${SINGLE_LINE_HEADER_INDEX_BINDING}`);
        };

        skyward.reporting.propertiesRemoveColumn = function ()
        {
            removeColumns(currentSelectedColumns.getCurrentSelectedColumns(), true, true);
        };

        const removeColumns = function (columns, updateUI = true, requireConfirmDelete = false)
        {
            const columnsToRemove = [...columns];
            for (const column of columnsToRemove)
            {
                removeColumn($(column), false, requireConfirmDelete);
            }
            if (updateUI)
            {
                initFieldsUsedInReport();
                skyward.reporting.updateVariableUsageLocations();
            }
        };

        const removeColumn = function (columnToRemove, updateUI = true, requireConfirmDelete = false)
        {
            if (columnToRemove && !columnToRemove.data("isRemoved"))
            {
                const columnData = columnToRemove.data();

                if (requireConfirmDelete && columnData.fieldColumnType === COLUMN_TYPES.AGGREGATE_COLUMN)
                {
                    confirmRemoveColumn(columnToRemove)
                        .done(() =>
                        {
                            // call back will be called when the user clicks "Yes" or if there are no usages found
                            doRemoveColumnAndRelatedColumns(columnToRemove, updateUI);
                        })
                        .fail(() => { /* ignore cancel click */ });
                }
                else
                {
                    doRemoveColumnAndRelatedColumns(columnToRemove, updateUI);
                }
            }
        };

        const confirmRemoveColumn = function (columnToRemove)
        {
            const placesToSearch = usageLocations.columnPlacesToSearch(columnToRemove.data());
            const message = "This field is currently used as a condition for, or within, other field(s) on the report. Deleting it will break the relationship with the field(s) listed below, which could affect how your report behaves or displays results. Please review the impacted field(s) before proceeding.";
            const confirmButton =
            {
                text: "Yes, Delete"
            };
            const cancelButton =
            {
                text: "No, Cancel"
            };

            return confirmDependentColumn(columnToRemove, placesToSearch, message, confirmButton, cancelButton, true,
                (messageElement, buttons, referenceItems) =>
                {
                    const messageHtml = `${skyward.modalLayer.getReferenceAreaHTML(referenceItems)}` +
                        `<span class="modalContent__deleteHeading">Are you sure you want to delete this column?</span>` +
                        `<div class="modalContent__deleteAdditionalText">${messageElement}</div>`;

                    return skyward.modalLayer.showDialog("CONFIRM DELETE", messageHtml, buttons, { closeOnClick: false, closeOnEscape: false, additionalClasses: "critical", role: "alertdialog" });
                });
        };

        const confirmUpdateAggregateTypeColumn = function (columnToUpdate)
        {
            const placesToSearch = usageLocations.simpleAggregateColumnPlacesToSearch(columnToUpdate);
            const message = "This field is currently used as a condition for, or within, other field(s) on the report. Updating it will break the relationship with the field(s) listed below, which could affect how your report behaves or displays results. Please review the impacted field(s) before proceeding.";
            const confirmButton =
            {
                text: "Yes, Update"
            };
            const cancelButton =
            {
                text: "No, Cancel"
            };

            return confirmDependentColumn(columnToUpdate, placesToSearch, message, confirmButton, cancelButton, false,
                (messageElement, buttons, referenceItems) =>
                {
                    const messageHtml = `${skyward.modalLayer.getReferenceAreaHTML(referenceItems)}` +
                        `<span class="modalContent__deleteHeading">Are you sure you want to update this aggregate type?</span>` +
                        `<div class="modalContent__deleteAdditionalText">${messageElement}</div>`;
                    return skyward.modalLayer.showDialog("Warning", messageHtml, buttons, { closeOnClick: false, closeOnEscape: false, additionalClasses: "warning", role: "alertdialog" });
                });
        };

        const confirmDependentColumn = function (dependentColumn, placesToSearch, message, confirmButtonArg, cancelButtonArg, ignoreIfUsageIsOnlySelf, getModal)
        {
            const waitForConfirmation = $.Deferred();
            const columnData = dependentColumn.data();
            const foundNodes = placesToSearch.traverse();
            if (foundNodes.some(node => node.inUse())
                && (!ignoreIfUsageIsOnlySelf || !usageLocations.usageLocationOnlyIncludesSelf(foundNodes, usageLocations.SourceTypes.Column, columnData.columnId)))
            {
                const usageLocationsElement = usageLocations.buildUsageLocationsElement(foundNodes);

                const messageElement = $("<div/>")
                    .append(`<p>${message}</p>`)
                    .append(usageLocationsElement)
                    .html();

                const line = dependentColumn.closest(".reportLine");
                const lineData = line.data();

                const section = line.closest(".jsSection");
                const sectionData = section.data();

                const confirmButton = $.extend({
                    icon: "CheckmarkConfirm",
                    click: () => waitForConfirmation.resolve(foundNodes),
                    additionalClasses: "confirmDelete_doDelete"
                }, confirmButtonArg);

                const cancelButton = $.extend({
                    icon: "Cancel",
                    click: waitForConfirmation.reject,
                    additionalClasses: "confirmDelete_cancel"
                }, cancelButtonArg);

                const referenceItems = [
                    { label: "Section", data: sectionData.displayName },
                    { label: "Report Line", data: lineData.reportLabelText },
                    { label: "Column", data: columnData.designerText }
                ];

                const dialog = getModal(messageElement, [confirmButton, cancelButton], referenceItems, foundNodes);
                waitForConfirmation.always(dialog.close);
            }
            else
            {
                waitForConfirmation.resolve();
            }
            return waitForConfirmation.promise();
        };

        const doRemoveColumnAndRelatedColumns = function (columnToRemove, updateUI)
        {
            if (currentSelectedColumns.isColumnSelected(columnToRemove))
            {
                const canSelectNewColumn = currentSelectedColumns.isSingleColumnSelected();
                currentSelectedColumns.delete(columnToRemove);

                let newSelectedItem = columnToRemove.next(".selectable");
                if (!newSelectedItem.length)
                {
                    newSelectedItem = columnToRemove.prev(".selectable");
                }

                if (!newSelectedItem.length || !canSelectNewColumn)
                {
                    setSelectedRowByRow(columnToRemove.parent());
                }
                else
                {
                    setSelectedColumn(newSelectedItem, true);
                }
            }

            removeRelatedColumns(columnToRemove, false);

            const columnToRemoveData = columnToRemove.data();
            if (columnToRemoveData.fieldColumnType === COLUMN_TYPES.CALCULATED_COLUMN)
            {
                skyward.reporting.aggregateCalculationCollection.remove(columnToRemoveData.columnId);
            }

            // isRemoved is for tracking cascading deletes so we do not try to delete something twice
            // The attribute value needs to be set because .remove() deletes any data that jQuery is only holding in memory
            columnToRemove.data("isRemoved", true);
            columnToRemove.attr("data-is-removed", true);
            columnToRemove.remove();

            if (currentRow)
            {
                // reselect current row to update any ui after removing related columns/data
                setSelectedRowByRow(currentRow);
            }

            removeInvalidPageNumberColumns(false);

            entireCanvas.focus();
            updateHiddenField();
            if (updateUI)
            {
                const sectionData = getSectionData(currentRow.closest(".jsSection"));
                initFieldsUsedInReport(sectionData);
                skyward.reporting.updateVariableUsageLocations();
            }
        };

        const removeCalculatedFieldBySection = function (section, fieldId)
        {
            const columnsToRemove = $(".jsColumn[data-operation-id='" + fieldId + "']");
            for (const column of columnsToRemove.map((i, c) => $(c)))
            {
                removeColumn(column, false);
            }

            CalculatedFields_Model.Delete(fieldId);
            removeCalculatedFieldLines(fieldId);
            removeIsHiddenConditionsThatUseCalculatedField(fieldId);
            removeCalculatedFieldFilter(fieldId);
            removeCalculatedFieldSorts(fieldId);
            updateHiddenField();
            updateDesignerSidebarCalculatedFieldList(section.data());
        };

        skyward.reporting.removeCalculatedField = function (fieldId)
        {
            removeCalculatedFieldBySection(getCurrentSection(), fieldId);
        };

        const removeCalculatedFieldLines = function (calculatedFieldID)
        {
            for (const section of getAllSections().map((i, s) => $(s)))
            {
                const sectionSorts = getSectionSorts(section);
                if (sectionSorts)
                {
                    const sort = sectionSorts.find(function (sort) { return sort.CalculatedField && sort.CalculatedField.id === calculatedFieldID; });

                    if (sort)
                    {
                        const sortLines = [...getSectionSortHeaderLinesByReportSortID(section, sort.ReportSortID), ...getSectionSortAggregateLinesByReportSortID(section, sort.ReportSortID)];

                        for (const line of sortLines.map((l) => $(l)))
                        {
                            removeLine(line);
                        }
                    }
                }
            }
        }

        var removeIsHiddenConditionsThatUseCalculatedField = function (calculatedFieldID)
        {
            var allColumns = getSectionAllColumns(getCurrentSection());

            for (var i = 0; i < allColumns.length; i++)
            {
                var columnData = $(allColumns[i]).data();
                if (columnData.isHiddenCondition && columnData.isHiddenCondition.LeftOperand.CalculatedFieldGuid === calculatedFieldID)
                {
                    columnData.isHiddenCondition = null;
                }
            }
        };

        // it'd be great to implement GetFromJSON on the SearchCondition model and do something much better here
        const removeCalculatedFieldFilter = function (fieldId)
        {
            const sections = designer.find('.jsSection');
            sections.each(function (index, element)
            {
                const section = $(element);
                if (!section.hasClass('jsHeaderFooterSection'))
                {
                    let filterChanged = false;
                    let filterModel = null;

                    const filterData = getSectionFilterObjectAndParameters(section);
                    const filters = filterData ? filterData.filterObject : null;
                    if (filters != null)
                    {
                        filterModel = skyward.searchCondition.getFromJson(filters);
                        filterModel = filterModel.removeWhen(function (condition)
                        {
                            const conditionValues = condition.get();
                            const foundReference = conditionValues["@CalculatedFieldName"] == fieldId || conditionValues["@RightCalculatedFieldName"] == fieldId;
                            filterChanged = filterChanged || foundReference;
                            return foundReference;
                        });
                    }

                    if (filterChanged)
                    {
                        const popupButton = section.find('.filterBuilderLaunchButton').first();
                        skyward.filterbuilder.updateFilter(popupButton, filterModel);
                    }
                }
            });
        };

        const removeCalculatedFieldSorts = function (fieldId)
        {
            designer.find(".jsSection").each(function ()
            {
                const section = $(this);
                let sectionSorts = getSectionSorts(section);
                if (sectionSorts)
                {
                    let sortRemoved = false;
                    sectionSorts = sectionSorts.filter(function (sort)
                    {
                        if (sort.CalculatedField && sort.CalculatedField.id === fieldId)
                        {
                            sortRemoved = true;
                            return false;
                        }

                        return true;
                    });

                    if (sortRemoved)
                    {
                        sectionSorts = updateSortDataSortOrder(sectionSorts);
                        updateSectionSortData(section, sectionSorts);
                    }
                }
            });
        };

        const removeRelatedColumns = function (columnBeingRemoved, updateUI = true)
        {
            if (columnBeingRemoved && !columnBeingRemoved.data("isRemoved"))
            {
                const columnBeingRemovedSection = columnBeingRemoved.closest(".jsSection");

                const columnBeingRemovedLine = columnBeingRemoved.closest(".reportLine");
                const columnBeingRemovedLineData = columnBeingRemovedLine.data();

                const columnBeingRemovedData = columnBeingRemoved.data();

                const columnsInHeaderFooterSections = getAllHeaderFooterSections().find(`.reportColumn[data-source-column-id='${columnBeingRemovedData.columnId}']`);
                removeColumns(columnsInHeaderFooterSections, false);

                const allSectionsToCheck = [columnBeingRemovedSection, ...getAllParentSections(columnBeingRemovedSection)];

                for (const currentSection of allSectionsToCheck.map((s) => $(s)))
                {
                    const currentSectionIsColumnBeingRemovedSection = currentSection.is(columnBeingRemovedSection);
                    const allLinesToCheck = [...getSectionAllAggregateLines(currentSection), ...getSectionSortHeaderLines(currentSection)];

                    for (const currentLine of allLinesToCheck.map((l) => $(l)))
                    {
                        const allColumnsToCheck = currentLine.find(`.reportColumn[data-source-column-id='${columnBeingRemovedData.columnId}']`);

                        for (const currentColumn of allColumnsToCheck.map((i, c) => $(c)))
                        {
                            const currentColumnData = currentColumn.data();

                            // we don't need to delete columns from the same section if we can generate them in the AggregateColumnMap at runtime
                            if (currentSectionIsColumnBeingRemovedSection &&
                                columnBeingRemovedLineData.lineType == LINE_TYPES.DATA_LINE &&
                                (currentColumnData.absoluteFieldPath || currentColumnData.operationId))
                            {
                                currentColumn.removeData("source-column-id");
                            }
                            else
                            {
                                removeColumn(currentColumn, false);
                            }
                        }

                        if (columnBeingRemovedData.fieldColumnType === COLUMN_TYPES.AGGREGATE_COLUMN)
                        {
                            removeHiddenConditionWithColumnID(currentLine, columnBeingRemovedData.columnId);
                            applySectionOrRowHiddenCondition(currentLine);

                            const allColumns = currentLine.find(".reportColumn").map((_, c) => $(c));
                            for (const currentColumn of allColumns)
                            {
                                removeHiddenConditionWithColumnID(currentColumn, columnBeingRemovedData.columnId);
                                updateColumnIsHiddenConditionFormatting(currentColumn)
                            }
                        }
                    }
                }

                skyward.reporting.aggregateCalculationCollection.deleteColumnsUsingGivenColumnID(columnBeingRemovedData.columnId, removeColumnByColumnID);

                //remove header
                if (columnBeingRemovedLineData.lineType == LINE_TYPES.DATA_LINE)
                {
                    const headerLine = getCorrespondingHeaderLine(columnBeingRemovedSection, columnBeingRemovedLine);
                    const headerColumn = headerLine.find('[data-data-column-id="' + columnBeingRemovedData.dataColumnId + '"]');
                    if (headerColumn.length > 0)
                    {
                        if (currentSelectedColumns.isColumnSelected(headerColumn))
                        {
                            currentSelectedColumns.delete(headerColumn);
                        }
                        removeColumn(headerColumn, false);
                    }
                }

                if (updateUI)
                {
                    initFieldsUsedInReport();
                    skyward.reporting.updateVariableUsageLocations();
                }
            }
        };

        const removeHiddenConditionWithColumnID = function (element, columnId)
        {
            const currentLineHiddenCondition = element.data("is-hidden-condition");

            if (currentLineHiddenCondition && (
                currentLineHiddenCondition.LeftOperand?.ColumnID === columnId ||
                currentLineHiddenCondition.RightOperand?.ColumnID === columnId))
            {
                element.data("is-hidden-condition", null);
            }
        }

        var allowKeepTogetherForSection = function (section)
        {
            let parentSection = getParentSection(section);

            while (parentSection.length == 1)
            {
                const allSectionSorts = getSectionSorts(parentSection);

                if (allSectionSorts.some(s => s.KeepLinesTogether))
                {
                    return false;
                }

                parentSection = getParentSection(parentSection);
            }

            return true;
        };

        // display Sorts popup
        var getSorts = function (section, reportType)
        {
            const dataLines = buildLineCollection(getSectionDataLines(section));
            const sectionData = section.data();
            const allowKeepTogether = allowKeepTogetherForSection(section);

            skyward.reportingSorts.editSorts(dataLines, sectionData, reportType, allowKeepTogether, CalculatedFields_Model.AllFieldsOfModuleAndObject(sectionData.rootModule, sectionData.rootObject, sectionData.entireRelationshipPath))
                .then(insertAddedSorts)
                .then(deleteRemovedSorts)
                .then(reorderSorts)
                .then(updateSectionSorts)
                .always(function ()
                {
                    removeInvalidPageNumberColumns();
                    updateHiddenField();
                    initDesignerUI(section);
                    labelBuilder.buildLabelColumns(true);
                });

            function insertAddedSorts(sortData)
            {
                $.each(sortData.selectedSorts, function (i, sort)
                {
                    if (!sort.existingSort)
                    {
                        if (sort.operationId)
                        {
                            sort.calculatedField = CalculatedFields_Model.Get(sort.operationId);
                        }
                    }
                });

                var sortPrompt = skyward.reporting.prompts.promptCollection.Get(sortData.sortPrompt.promptGuid);

                if (sortPrompt == null)
                {
                    if (sortData.sortPrompt.promptForSortOrder)
                    {
                        sortPrompt = {
                            InUse: true,
                            Label: sortData.sortPrompt.label,
                            PromptUniqueId: sortData.sortPrompt.promptGuid,
                            PromptGuid: sortData.sortPrompt.promptGuid,
                            Type: enumMaps.PromptTypes.ReportSort,
                            InputType: DATA_TYPES.STRING
                        };
                        skyward.reporting.prompts.promptCollection.Set(sortPrompt);
                    }
                }
                else
                {
                    sortPrompt.Label = sortData.sortPrompt.label;
                    skyward.reporting.prompts.promptCollection.Set(sortPrompt);
                }

                var sortPromptedSection = entireCanvas.find("[data-sort-prompt-guid='" + sortData.sortPrompt.promptGuid + "']");
                sortPromptedSection.data("sortPromptLabel", sortData.sortPrompt.label);
                sortPromptedSection.data("promptForSortOrder", sortData.sortPrompt.promptForSortOrder);
                updateHiddenField();

                return $.Deferred().resolve(sortData);
            }

            function deleteRemovedSorts(sortData)
            {
                $.each(sortData.removedSorts, function (i, sort)
                {
                    for (const line of [...getSectionSortHeaderLinesByReportSortID(section, sort.reportSortId), ...getSectionSortAggregateLinesByReportSortID(section, sort.reportSortId)])
                    {
                        removeLine($(line));
                    }
                });
                return $.Deferred().resolve(sortData);
            }

            function reorderSorts(sortData)
            {
                function sortAction(elementA, elementB, desc)
                {
                    const elementAData = $(elementA).data();
                    const elementBData = $(elementB).data();
                    const relatedReportSortIDA = elementAData.relatedReportSortId;
                    const relatedReportSortIDB = elementBData.relatedReportSortId;

                    if (relatedReportSortIDA === relatedReportSortIDB)
                    {
                        const lineIndexA = elementAData.lineIndex;
                        const lineIndexB = elementBData.lineIndex;
                        return -1 * (lineIndexA - lineIndexB);
                    }
                    const sortDataA = sortData.selectedSorts.find(sort => { return sort.reportSortId === relatedReportSortIDA });
                    const sortDataB = sortData.selectedSorts.find(sort => { return sort.reportSortId === relatedReportSortIDB });

                    return (desc ? 1 : -1) * sortDataA.sortOrder - sortDataB.sortOrder;
                };

                const aggregateOrder = $("<div class='temporary floating div never inserted into DOM'/>"); // Great band
                const headerOrder = $("<div class='temporary floating div never inserted into DOM'/>");

                const sorts = sortData.selectedSorts;
                const sectionSortAggregateLinesCollection = buildLineCollection(getSectionSortAggregateLines(section));
                const processedReportSortIDs = new Set();

                sorts.forEach(function (sort, i)
                {
                    const sectionSortAggregateLinesByReportSortID = getSectionSortAggregateLinesByReportSortID(section, sort.reportSortId);

                    if (sort.sortOrder != i && sectionSortAggregateLinesByReportSortID.length > 0)
                    {
                        const relatedSortAggregateLines = buildLineCollection($(sectionSortAggregateLinesByReportSortID));
                        relatedSortAggregateLines.forEach((sortAggregateLine) =>
                        {
                            sortAggregateLine.columns.forEach((column) =>
                            {
                                if (column.isFutureValue === true)
                                {
                                    const sourceColumnId = column.sourceColumnId;
                                    if (!sourceColumnId)
                                    {
                                        return;
                                    }

                                    const sourceLine = sectionSortAggregateLinesCollection.find(line => line.columns.some(column => column.columnId === sourceColumnId));
                                    if (!sourceLine)
                                    {
                                        return;
                                    }

                                    const relatedReportSortId = sourceLine.relatedReportSortId;
                                    if (!relatedReportSortId)
                                    {
                                        return;
                                    }

                                    if (!processedReportSortIDs.has(relatedReportSortId))
                                    {
                                        removeColumnByColumnID(column.columnId);
                                    }
                                }
                            })
                        })
                    }

                    sort.sortOrder = i;

                    if (sectionSortAggregateLinesByReportSortID.length)
                    {
                        $(sectionSortAggregateLinesByReportSortID).appendTo(aggregateOrder);
                    }

                    const sortHeaderLinesForReportSortID = getSectionSortHeaderLinesByReportSortID(section, sort.reportSortId);
                    if (sortHeaderLinesForReportSortID.length)
                    {
                        $(sortHeaderLinesForReportSortID).appendTo(headerOrder);
                    }

                    processedReportSortIDs.add(sort.reportSortId);
                })

                if (aggregateOrder.children().length > 0)
                {
                    const sortAggregateLinesInNewOrder = aggregateOrder.sortChildrenByAction(sortAction, true).children().detach();
                    insertSortAggregateLine(section, sortAggregateLinesInNewOrder);
                }

                if (headerOrder.children().length > 0)
                {
                    const sortHeaderLineinNewOrder = headerOrder.children().detach();
                    sortHeaderLineinNewOrder.insertAfter(getSectionHeaderLines(section).last());
                }

                return $.Deferred().resolve(sortData);
            }

            function updateSectionSorts(sortData)
            {
                const selectedSortsData = [];
                for (const data of sortData.selectedSorts)
                {
                    const calculatedField = CalculatedFields_Model.Get(data.operationId);
                    const sort =
                    {
                        ReportSortID: data.reportSortId,
                        AbsoluteFieldPath: data.absoluteFieldPath,
                        FieldName: data.operationId ? calculatedField.fieldName : data.fieldName,
                        DisplayName: data.displayName,
                        DisplayPath: data.displayPath,
                        Descending: (data.descending === true || data.descending == "True") ? "True" : "False",
                        PageBreak: data.pageBreak,
                        PageBreakPromptUniqueId: data.pageBreakPromptUniqueId,
                        SortOrder: data.sortOrder,
                        FriendlyName: data.friendlyName,
                        RelativeDataPath: data.relativeDataPath,
                        CalculatedField: data.operationId ? calculatedField : null,
                        KeepLinesTogether: data.keepLinesTogether,
                        CreateBookmark: data.createBookmark,
                        DataType: data.dataType,
                        IsOperation: data.isOperation,
                        Parameters: data.Parameters
                    };
                    selectedSortsData.push(sort);
                }
                updateSectionSortData(section, selectedSortsData);
            }
        };
        //END SORTS

        var removeInvalidPageNumberColumns = function (updateUI = true)
        {
            // now that we have currentReportDefinition as an object, we could look at that instead of the dom
            const allSorts = getAllSorts();
            let anyMainSectionHasPageBreak = false;
            getAllMainSections().each(function (i, section)
            {
                if ($(section).data('pageBreakBeforeSection'))
                {
                    anyMainSectionHasPageBreak = true;
                }
            });
            getAllSections().each(function (i, section)
            {
                const $section = $(section);
                const isHeaderFooterSection = $section.is(".jsHeaderFooterSection");
                const sectionSortsMap = {};
                const sectionSorts = getSectionSorts($section);
                if (sectionSorts)
                {
                    sectionSorts.forEach(function (sort)
                    {
                        sectionSortsMap[sort.FieldName] = sort;
                    });
                }
                getSectionAllColumns($section).each(function (j, column)
                {
                    const $column = $(column);
                    const columnData = $column.data();
                    const isResettablePageColumn = isPageNumberOrPageTotalColumn(columnData);
                    if (isResettablePageColumn && columnData.resetOn && columnData.resetOn !== RESET_ON_TYPES.NEVER_RESET)
                    {
                        if (isHeaderFooterSection)
                        {
                            if (columnData.resetOn === RESET_ON_TYPES.MAIN_SECTIONS && !anyMainSectionHasPageBreak)
                            {
                                removeColumn($column, false);
                            }
                            else
                            {
                                let validSortExist = false;
                                allSorts.forEach(function (sort)
                                {
                                    if (sort.FieldName === columnData.resetOn && sort.PageBreak !== skyward.reportingSorts.pageBreakOptions.False)
                                    {
                                        validSortExist = true;
                                        return;
                                    }
                                });
                                if (!validSortExist)
                                {
                                    removeColumn($column, false);
                                }
                            }
                        }
                        else
                        {
                            const existingSort = sectionSortsMap[columnData.resetOn];
                            if (!existingSort || existingSort.PageBreak === skyward.reportingSorts.pageBreakOptions.False)
                            {
                                removeColumn($column, false);
                            }
                        }
                    }
                });
            });

            if (updateUI)
            {
                initFieldsUsedInReport();
            }
        };

        //DETAILS SECTIONS
        var getDetail = function ($section, launchDetailButton)
        {
            const sectionData = getSectionData($section);
            const fullUrl = skyward.global.virtualDir + "Reporting/Report/GetDetail/";
            const rootModule = launchDetailButton.data().rootModule;
            const rootObject = launchDetailButton.data().rootObject;

            $.ajax(fullUrl,
                {
                    type: "POST",
                    dataType: "JSON",
                    data:
                    {
                        module: sectionData.baseModule,
                        obj: sectionData.baseObject,
                        expandUrl: "Reporting/Report/ExpandTreeBrowse",
                    }
                }).done(async function (data)
                {
                    const container = $("<div style='overflow-x: auto; height: 600px; min-width: 400px;'>");
                    const popupContent = $("<div class='popup detailSectionPopUp'/>");
                    container.append(popupContent);

                    // Process HTML through processViewPartHtml to handle any script tags
                    const processedHtml = await skyward.global.processViewPartHtml(data.html);
                    popupContent.append(processedHtml);
                    const treeBrowse = popupContent.find("#detailModalTreeBrowse_main"); // popupContent isn't yet in the DOM, so using skyward.treebrowse.getTreeBrowseContainer won't work
                    treeBrowse.addClass("treeBrowse--flush");

                    const createDetailSection = function ($el, object, module, displayName, fieldPath, entireRelationshipPath, parentDisplayOnSingleLine)
                    {
                        const fullUrl = skyward.global.virtualDir + "Reporting/Report/GetNewDetailsSection";
                        const deferred = $.Deferred();

                        $.ajax(fullUrl,
                            {
                                type: "POST",
                                dataType: "JSON",
                                data:
                                {
                                    baseObject: object,
                                    baseModule: module,
                                    rootModule: rootModule,
                                    rootObject: rootObject,
                                    displayName: displayName,
                                    fieldPath: fieldPath,
                                    entireFieldPath: entireRelationshipPath,
                                    parentDisplayOnSingleLine: parentDisplayOnSingleLine
                                },
                                success: async function (data)
                                {
                                    entireCanvas.find('.sectionSelected').removeClass('sectionSelected');
                                    entireCanvas.find('.row-selected').removeClass('row-selected');

                                    // Process HTML through processViewPartHtml to handle any script tags
                                    const processedHtml = await skyward.global.processViewPartHtml(data.html);
                                    $el.children(".detailSectionContainer").append(processedHtml);
                                    const newSection = designer.find('.sectionSelected');
                                    newSection.data("displayOnSingleLine", sectionData.displayOnSingleLine);

                                    setActiveSection(newSection);
                                    setSelectedRowByRow(newSection.find('div.jsDataLine'));

                                    updateHiddenField();
                                    const newSectionData = getSectionData(newSection);

                                    updateDesignerSidebarCalculatedFieldList(newSectionData);

                                    initializeToolbarEvents();
                                    labelBuilder.buildLabelColumns(true);
                                    skyward.window.notifyDOMLoad(".sectionSelected");
                                    deferred.resolve();
                                }
                            });
                        return deferred;
                    };

                    const getSelectedSectionData = function ()
                    {
                        let data;
                        let displayName;
                        const selectedNode = menu.content.find(".selected");

                        if (selectedNode.hasClass("breadcrumbspan"))
                        {
                            const selected1M = selectedNode.parent();
                            data = selected1M.data("relationshipData");
                            displayName = selected1M.text();
                        }
                        else
                        {
                            data = skyward.treebrowse.getSelectedFieldData(treeBrowse);
                            displayName = data ? data.relationshipName : null;
                        }

                        let selectedSectionData;
                        if (data)
                        {
                            const fieldPath = data.fieldName;
                            const parentSection = designer.find('.sectionSelected');
                            const parentSectionData = getSectionData(parentSection);
                            const dotIndex = data.fieldName.indexOf('.');
                            const secondPartAbsolutePath = dotIndex !== -1 ? fieldPath.substring(dotIndex + 1) : "";
                            const absoluteTreeBrowseObjectPath = !parentSectionData.entireRelationshipPath
                                ? secondPartAbsolutePath
                                : parentSectionData.entireRelationshipPath + '.' + secondPartAbsolutePath;

                            selectedSectionData =
                            {
                                baseModule: data.fieldModule,
                                baseObject: data.fieldObject,
                                fieldPath: data.fieldName,
                                displayName: displayName,
                                absoluteTreeBrowseObjectPath: absoluteTreeBrowseObjectPath,
                                entireRelationshipPath: parentSectionData.entireRelationshipPath,
                                displayOnSingleLine: parentSectionData.displayOnSingleLine,
                                parentSection: parentSection,
                                parentSectionID: parentSectionData.sectionID
                            };
                        }

                        return selectedSectionData;
                    };

                    const insertDetailsSection = function ()
                    {
                        const selectedSectionData = getSelectedSectionData();
                        if (selectedSectionData)
                        {
                            const parentSection = selectedSectionData.parentSection;
                            const object = selectedSectionData.baseObject;
                            const module = selectedSectionData.baseModule;
                            const fieldPath = selectedSectionData.fieldPath;
                            const displayName = selectedSectionData.displayName;
                            const entireRelationshipPath = selectedSectionData.entireRelationshipPath;
                            const displayOnSingleLine = selectedSectionData.displayOnSingleLine;
                            const absoluteTreeBrowseObjectPath = selectedSectionData.absoluteTreeBrowseObjectPath;

                            createDetailSection(parentSection, object, module, displayName, fieldPath, entireRelationshipPath, displayOnSingleLine).then(function ()
                            {
                                const control = "reportObjectBrowser";
                                const treeBrowseContainer = skyward.treebrowse.getTreeBrowseContainer(control);
                                const treeBrowseData = skyward.treebrowse.getTreeBrowseData(treeBrowseContainer);
                                treeBrowseData.renderColumnGroups = !displayOnSingleLine;
                                treeBrowseData.renderRowForImageReferences = !displayOnSingleLine;

                                const treeBrowseDataPromise = skyward.treebrowse.setBrowseTarget(module, object, object, control,
                                    "Reporting/Report/ExpandTreeBrowseDesignerSidebar", displayName, null, rootModule, rootObject,
                                    absoluteTreeBrowseObjectPath, true);
                                treeBrowseDataPromise.then(function (data)
                                {
                                    designer.find(".hideForHeaderFooterSections").show();
                                    hidePageNumberFields(true);
                                    initDesignerUI(designer.find(".sectionSelected"));
                                });
                                menu.close();
                            });
                        }
                        else
                        {
                            menu.close();
                        }
                    };

                    const importDetailsSection = function ()
                    {
                        const selectedSectionData = getSelectedSectionData();

                        if (selectedSectionData)
                        {
                            const reportData = getReportData();
                            const data =
                            {
                                hash: reportData.reportIDHash,
                                reportID: reportData.reportID,
                                reportDefinitionJSON: JSON.stringify(buildHiddenFieldJson()),
                                sectionID: selectedSectionData.parentSectionID,
                                baseModuleName: selectedSectionData.baseModule,
                                baseObjectName: selectedSectionData.baseObject,
                                relationshipPath: selectedSectionData.fieldPath,
                                hasUnsavedChanges: isReportDirty(),
                            };

                            skyward.changeTracking.overrideGlobalDirtyState(false);

                            // If the workflow is canceled clear the dirty status override
                            $(document).one("WorkflowCancelled.Reporting.Report.ImportReportAsNewSection", () =>
                            {
                                skyward.changeTracking.overrideGlobalDirtyState(null);
                            });


                            $.ajax({
                                url: skyward.global.virtualDir + "Reporting/Report/ImportDetailsSection",
                                type: "POST",
                                dataType: "JSON",
                                data: data,
                                success: function (data)
                                {
                                    const workflowData =
                                    {
                                        "Workflow.ReportID": data["ReportID"],
                                        "Workflow.ReportID_SecurityHash": data["ReportIDSecurityHash"],
                                        "Workflow.HasUnsavedChanges": data["HasUnsavedChanges"],
                                        "Workflow.HasUnsavedChanges_SecurityHash": data["HasUnsavedChangesSecurityHash"],
                                        "Workflow.ExistingReportDefinitionJSON": data["ReportDefinitionJSON"],
                                        "Workflow.ExistingReportDefinitionJSON_SecurityHash": data["ReportDefinitionJSONHash"],
                                        "Workflow.ParentSectionID": data["ParentSectionID"],
                                        "Workflow.ParentSectionID_SecurityHash": data["ParentSectionIDSecurityHash"],
                                        "Workflow.BaseModuleName": data["BaseModuleName"],
                                        "Workflow.BaseModuleName_SecurityHash": data["BaseModuleNameSecurityHash"],
                                        "Workflow.BaseObjectName": data["BaseObjectName"],
                                        "Workflow.BaseObjectName_SecurityHash": data["BaseObjectNameSecurityHash"],
                                        "Workflow.SelectedRelationshipPath": data["SelectedRelationshipPath"],
                                        "Workflow.SelectedRelationshipPath_SecurityHash": data["SelectedRelationshipPathSecurityHash"]
                                    };
                                    skyward.window.loadContent("popup", "Workflow", "WorkflowInstance", "Create/Reporting/Report/ImportReportAsNewSection", null, workflowData);
                                }
                            });
                        }
                    };

                    const treeBrowseRowChanged = function (event, data, selectedRowData)
                    {
                        const saveButton = menu.footer.find(".addDetailSaveButton");
                        const importButton = menu.footer.find(".addDetailImportButton");
                        if (selectedRowData)    // Its possible there are no relationships for this data object
                        {
                            const selectedFieldType = selectedRowData.fieldType;

                            if (selectedFieldType == "OneToMany" || data.selectedRecord.hasClass("breadcrumbspan"))
                            {
                                saveButton.show();
                                importButton.show();
                            }
                            else
                            {
                                saveButton.hide();
                                importButton.hide();
                            }
                        }
                    };

                    const options =
                    {
                        automationID: "insert-details-dialog",
                        closeOnClick: false,
                        resizable: false
                    };
                    const okButton =
                    {
                        additionalClasses: "addDetailSaveButton",
                        click: insertDetailsSection,
                        icon: "Save",
                        text: "Save"
                    };
                    const importButton =
                    {
                        additionalClasses: "addDetailImportButton",
                        click: importDetailsSection,
                        icon: "Import",
                        text: "Import",
                    };
                    const cancelButton =
                    {
                        icon: "Cancel",
                        text: "Cancel"
                    };

                    const menu = skyward.modalLayer.showDialog("Insert Details Section", container, [importButton, okButton, cancelButton], options);
                    menu.content.on("treeBrowseRowChange", treeBrowseRowChanged)
                        .on("treeBrowseSave", insertDetailsSection);

                    skyward.window.notifyDOMLoad("#" + popupContent.find(".treeBrowse").attr("id"));
                });
        };
        //END DETAILS SECTIONS

        //MAIN SECTIONS
        let getMainSection = function ()
        {
            let menu = {};

            let insertMainSection = function (html)
            {
                const $newSection = $(html);
                const mainSections = designer.find('.jsMainSection');
                const headerSections = designer.find('.jsSection');
                if (mainSections.length > 0)
                {
                    $newSection.insertAfter(mainSections.last());
                    skyward.controls.setValue(exportProperties.xml.includeRootNode, true);
                    skyward.controls.disable(exportProperties.xml.includeRootNode);
                }
                else if (headerSections.length > 1)
                {
                    // Place under report and page headers
                    $newSection.insertAfter(headerSections[1]);
                }
                else
                {
                    // Exportable reports do not have headers
                    designer.find('.reportDesignerCanvas').append($newSection);
                }

                setActiveSection($newSection);
                setSelectedRowByRow(getSectionDataLines($newSection).first());
                initDesignerUI($newSection);
                initializeToolbarEvents();
                updateHiddenField();
                labelBuilder.buildLabelColumns(true);
                skyward.window.notifyDOMLoad(".jsMainSection.sectionSelected");
            };

            var fullUrl = skyward.global.virtualDir + "Reporting/Report/GetObjectSelector/";

            const importMainSection = function ()
            {
                const reportData = getReportData();

                const data =
                {
                    hash: reportData.reportIDHash,
                    reportID: reportData.reportID,
                    reportDefinitionJSON: JSON.stringify(buildHiddenFieldJson()),
                    hasUnsavedChanges: isReportDirty()
                };

                skyward.changeTracking.overrideGlobalDirtyState(false);

                // If the workflow is canceled clear the dirty status override
                $(document).one("WorkflowCancelled.Reporting.Report.ImportReportAsNewSection", () =>
                {
                    skyward.changeTracking.overrideGlobalDirtyState(null);
                });

                $.ajax({
                    url: skyward.global.virtualDir + "Reporting/Report/ImportMainSection",
                    type: "POST",
                    dataType: "JSON",
                    data: data,
                    success: function (data)
                    {
                        const workflowData =
                        {
                            "Workflow.ReportID": data["ReportID"],
                            "Workflow.ReportID_SecurityHash": data["ReportIDSecurityHash"],
                            "Workflow.HasUnsavedChanges": data["HasUnsavedChanges"],
                            "Workflow.HasUnsavedChanges_SecurityHash": data["HasUnsavedChangesSecurityHash"],
                            "Workflow.ExistingReportDefinitionJSON": data["ReportDefinitionJSON"],
                            "Workflow.ExistingReportDefinitionJSON_SecurityHash": data["ReportDefinitionJSONHash"],
                        };

                        skyward.window.loadContent("popup", "Workflow", "WorkflowInstance", "Create/Reporting/Report/ImportReportAsNewSection", null, workflowData);
                    }
                });
            }

            $.ajax(fullUrl,
                {
                    type: "POST",
                    dataType: "JSON",
                    data:
                    {

                    }
                }).done(function (data)
                {
                    var getSection = function ()
                    {
                        return $.ajax({
                            url: skyward.global.virtualDir + "Reporting/Report/GetNewMainSection/",
                            type: "POST",
                            dataType: "JSON",
                            data:
                            {
                                moduleId: skyward.controls.getValue(menu.content.find('#ModuleID')),
                                objectId: skyward.controls.getValue(menu.content.find('#ObjectID'))
                            },
                            success: function (data)
                            {

                                if (!data.success)
                                {
                                    var container = $('.messageBox.content');
                                    skyward.validation.displayValidationMessages(data, container, null);
                                }
                                else
                                {
                                    insertMainSection(data.html);
                                    menu.close();
                                }
                            }
                        });
                    };

                    var okButton = { text: "Save", "click": getSection, "icon": "Save" };

                    const importButton =
                    {
                        additionalClasses: "addMainImportButton",
                        click: importMainSection,
                        icon: "Import",
                        text: "Import",
                    };

                    menu = skyward.modalLayer.showDialog("Add Main Section", data.html, [importButton, okButton, { text: "Cancel", "icon": "Cancel" }], { closeOnClick: false });
                });
        };
        //END MAIN SECTION

        //REPORT SERIALIZATION
        var updateHiddenField = skyward.reporting.updateHiddenField = function ()
        {
            const jsonObj = buildHiddenFieldJson();
            const editorHiddenData = designer.find(".editorHiddenData");
            const oldData = editorHiddenData.data("original-value");

            updateDirtyStatus(editorHiddenData, oldData, jsonObj);
        };

        var updateDirtyStatus = function (editorHiddenData, oldData, newData)
        {
            const reportDesignDirty = !skyward.global.deepEquals(oldData, newData);

            const moreMenu = $(".moreMenuArea");
            const moreMenuButton = moreMenu.find(".moreButton");
            const runReport = moreMenu.find(".jsRunReport");
            const runReportWithLastValues = moreMenu.find(".jsRunReportWithLastValues");
            const saveAndRunReport = moreMenu.find(".jsSaveAndRunReport")
            const saveAndRunReportWithLastValues = moreMenu.find(".jsSaveAndRunReportWithLastValues");
            const publishReport = moreMenu.find(".jsPublishReport");
            const unpublishReport = moreMenu.find(".jsUnpublishReport");
            const previewReport = moreMenu.find(".jsPreview");
            const saveStyle = moreMenu.find(".jsSaveStyle");
            const applyStyle = moreMenu.find(".jsApplyStyle");
            const setDefaultSubjects = moreMenu.find(".jsDefaultSubjects");

            let buttonsToHide = [];
            let buttonsToShow = [];

            if (reportDesignDirty || CalculatedFields_Model.IsDirty())
            {
                if (reportDesignDirty)
                {
                    editorHiddenData.val(JSON.stringify(newData));
                }

                editorHiddenData.change();

                if (designer.find(".jsMainSection").length == 0)
                {
                    buttonsToHide.push(runReport, saveAndRunReport, runReportWithLastValues, saveAndRunReportWithLastValues, publishReport, unpublishReport, setDefaultSubjects, previewReport);
                }
                else
                {
                    // Report has unsaved data
                    skyward.dataobjectviewer.getFieldValues.enableButtons("editReportDefinitionDesigner");
                    changeTracker.overrideDirtyState(true);

                    buttonsToHide.push(applyStyle, saveStyle, publishReport, unpublishReport, runReport, runReportWithLastValues, setDefaultSubjects, previewReport);
                    buttonsToShow.push(saveAndRunReport, saveAndRunReportWithLastValues);
                }
            }
            else
            {
                // Report is unaltered
                skyward.dataobjectviewer.getFieldValues.disableButtons("editReportDefinitionDesigner");
                changeTracker.overrideDirtyState(false);
                changeTracker.clearDirtyList();

                buttonsToHide.push(saveAndRunReport, saveAndRunReportWithLastValues);
                buttonsToShow.push(runReport, runReportWithLastValues, applyStyle, saveStyle, publishReport, unpublishReport, setDefaultSubjects, previewReport);
            }

            for (const button of buttonsToHide)
            {
                button.addClass("ui-hidden").hide();
            }

            for (const button of buttonsToShow)
            {
                button.removeClass("ui-hidden");

                if (!button.hasClass("moreMenuAlwaysHide"))
                {
                    button.css("display", "inline-flex");
                }
            }

            if (moreMenuButton.siblings(".moreMenuHide, .moreMenuAlwaysHide").not(".ui-hidden").length == 0)
            {
                moreMenuButton.addClass("ui-disabled");
            }
            else
            {
                moreMenuButton.removeClass("ui-disabled");
            }
        };

        var buildHiddenFieldJson = skyward.reporting.getDesignerJson = function ()
        {
            const editorHiddenData = designer.find(".editorHiddenData");
            const $mainSection = entireCanvas.find(".jsMainSection");
            let mainSections = [];
            let parameters = {};
            let defaultProcessingListSubjectSkywardIDs = new Set();

            $mainSection.each(function ()
            {
                mainSections.push(buildSectionObject($(this), false, parameters, defaultProcessingListSubjectSkywardIDs));
            });

            let jsonObj = JSON.parse(editorHiddenData.val());

            jsonObj.ReportHeader = buildLineObject(getReportHeaderLine(), { headerType: enumMaps.HeaderTypes.ReportHeader }, parameters);
            jsonObj.PageHeader = buildLineObject(getPageHeaderLine(), { headerType: enumMaps.HeaderTypes.PageHeader }, parameters);
            jsonObj.MainSections = mainSections;
            jsonObj.PageFooter = buildLineObject(getPageFooterLine(), { footerType: enumMaps.FooterTypes.PageFooter }, parameters);
            jsonObj.ReportFooter = buildLineObject(getReportFooterLine(), { footerType: enumMaps.FooterTypes.ReportFooter }, parameters);
            jsonObj.CalculatedFields = CalculatedFields_Model.AllFields();
            jsonObj.Prompts = skyward.reporting.prompts.promptCollection.toJsonObject();
            jsonObj.Variables = skyward.reporting.variables.collectionModel.toJsonObject();

            CalculatedFields_Model.Parameters().forEach(function (parameter)
            {
                skyward.reporting.searchParameters.viewModel.mergeAdd(parameters, parameter);
            });

            jsonObj.SearchParameters = skyward.reporting.searchParameters.model.inUseToJson(parameters);

            const exportFormatting = buildExportFormatting();
            if (!$.isEmptyObject(exportFormatting))
            {
                jsonObj.ExportFormatting = exportFormatting;
            }

            jsonObj.AvailableParameters = parameters;
            jsonObj.DefaultProcessingListSubjectSkywardIDs = Array.from(defaultProcessingListSubjectSkywardIDs);

            // this should be the only place we set currentReportDefinition
            skyward.reporting.currentReportDefinition = jsonObj;
            skyward.reporting.searchParameters.viewModel.toggleButton();

            return jsonObj;
        };

        var buildExportFormatting = function ()
        {
            var exportFormatting = {};
            if (exportProperties.csv.delimiter.length > 0 && exportProperties.csv.textQualifier.length > 0)
            {
                exportFormatting["DelimitedExportFormatting"] =
                {
                    Delimiter: skyward.controls.getValue(exportProperties.csv.delimiter),
                    TextQualifier: skyward.controls.getValue(exportProperties.csv.textQualifier),
                    IncludeTextQualifierOnlyWhenRequired: skyward.controls.getValue(exportProperties.csv.includeTextQualifierOnlyWhenRequired)
                };

                if (exportProperties.csv.customDelimiter.length > 0)
                {
                    exportFormatting["DelimitedExportFormatting"]["CustomDelimiter"] = skyward.controls.getValue(exportProperties.csv.customDelimiter);
                }
            }

            if (exportProperties.xml.includeRootNode.length > 0 || exportProperties.xml.nodeName.length > 0)
            {
                exportFormatting["XMLExportFormatting"] =
                {
                    nodeName: skyward.controls.getValue(exportProperties.xml.nodeName),
                    includeRootNode: skyward.controls.getValue(exportProperties.xml.includeRootNode),
                };
            }

            return exportFormatting;
        };

        var sortRelatedLineDataGatherer = function (line)
        {
            const lineData = line.data();
            const returnData =
            {
                relatedReportSortId: lineData.relatedReportSortId
            };
            return returnData;
        };

        var buildSectionExportPropertiesJSON = function (data)
        {
            var exportFormatting = {};

            if (data.nodeName)
            {
                exportFormatting.xmlExportFormatting = { nodeName: data.nodeName };
            }

            if (data.jsonPropertyName)
            {
                exportFormatting.jsonExportFormatting = { name: data.jsonPropertyName };
            }

            return exportFormatting;
        };

        var buildSectionObject = function ($section, isDetailSection, parameters, defaultProcessingListSubjectSkywardIDs)
        {
            let section = {};

            const sectionData = getSectionData($section);

            if (isDetailSection)
            {
                section.$type = SECTION_TYPES.DETAIL_SECTION;
                const addDetailSectionButtonData = $section.find(".get_detail_modal").data();
                section.RelationshipPath = sectionData.relationshipPath;
                section.EntireRelationshipPath = sectionData.entireRelationshipPath;
                section.RootModule = addDetailSectionButtonData.rootModule;
                section.RootObject = addDetailSectionButtonData.rootObject;
                sectionData.parameters.forEach(function (parameter)
                {
                    skyward.reporting.searchParameters.viewModel.mergeAdd(parameters, parameter);
                });

            }
            else
            {
                section.$type = SECTION_TYPES.MAIN_SECTION;
            }

            section.DetailSections = [];

            $section.children(".detailSectionContainer").children(".jsSection").each(function ()
            {
                section.DetailSections.push(buildSectionObject($(this), true, parameters, defaultProcessingListSubjectSkywardIDs));
            });

            section.SectionID = sectionData.sectionID;
            section.DisplayName = sectionData.displayName;
            section.BaseObject = sectionData.baseObject;
            section.BaseModule = sectionData.baseModule;
            section.SortHeaderLines = buildLineCollection(getSectionSortHeaderLines($section), sortRelatedLineDataGatherer, parameters);
            section.DataLines = buildLineCollection(getSectionDataLines($section), undefined, parameters);
            section.HeaderLines = buildLineCollection(getSectionHeaderLines($section), undefined, parameters);
            section.FooterLines = buildLineCollection(getSectionFooterLines($section), undefined, parameters);
            section.SortAggregateLines = buildLineCollection(getSectionSortAggregateLines($section), sortRelatedLineDataGatherer, parameters);
            section.SectionAggregateLines = buildLineCollection(getSectionAggregateLines($section), undefined, parameters);
            section.PageBreakBeforeSection = sectionData.pageBreakBeforeSection;
            section.ApplyDistrictEntityFilter = $section.find(".filterBuilderLaunchButton").data('applyDistrictEntityFilter');
            section.PromptForSortOrder = sectionData.promptForSortOrder;
            section.SortPromptGuid = sectionData.sortPromptGuid;
            section.SortPromptLabel = sectionData.sortPromptLabel;
            section.PrintIfEmpty = sectionData.printIfEmpty;
            section.displayOnSingleLine = sectionData.displayOnSingleLine;
            section.singleLineRecordCount = sectionData.singleLineRecordCount;
            section.EmptyLinesToPrint = sectionData.emptyLinesToPrint;
            section.IsHiddenCondition = sectionData.isHiddenCondition;
            section.ColumnWidth = sectionData.columnWidth;
            section.ColumnSpacing = sectionData.columnSpacing;
            section.ApplyColumnsToDataLinesOnly = sectionData.applyColumnsToDataLinesOnly;
            section.BalanceColumns = sectionData.balanceColumns;
            section.ColumnDirection = sectionData.columnDirection;
            section.Sorts = sectionData.reportSorts;
            section.DefaultProcessingListSubjectSkywardIDs = sectionData.defaultProcessingListSubjectSkywardIDs;

            const exportFormatting = buildSectionExportPropertiesJSON(sectionData);
            if (!$.isEmptyObject(exportFormatting))
            {
                section.exportFormatting = exportFormatting;
            }

            if (sectionData.Filters != null)
            {
                section.Filters = sectionData.Filters;
            }

            if (sectionData.FiltersParameters)
            {
                sectionData.FiltersParameters.forEach(function (parameter)
                {
                    skyward.reporting.searchParameters.viewModel.mergeAdd(parameters, parameter);
                });
            }

            if (sectionData.defaultProcessingListSubjectSkywardIDs)
            {
                sectionData.defaultProcessingListSubjectSkywardIDs.forEach(skywardID => defaultProcessingListSubjectSkywardIDs.add(skywardID));
            }

            return section;
        };

        var buildLineCollection = function ($reportLines, additionalDataGatherer, parameters)
        {
            /// <summary>Convert an entire section to post-data</summary>
            /// <param name="$reportLines" type="JQuery">The section to convert</param>
            /// <param name="additionalDataGatherer" type="Function">function(line) delegate to add data to individual lines. If passed, this function will be called once per line</param>
            /// <returns type="Object">json object representing a set of report lines</returns>

            additionalDataGatherer = additionalDataGatherer || function () { return {}; };
            var reportLines = [];

            $reportLines.each(function ()
            {
                var jqThis = $(this);
                reportLines.push(buildLineObject(jqThis, additionalDataGatherer(jqThis), parameters));
            });

            return reportLines;
        };

        var buildLineExportPropertiesJSON = function (data)
        {
            var exportFormatting = {};

            if (data.nodeName)
            {
                exportFormatting["xmlExportFormatting"] = { nodeName: data.nodeName };
            }

            return exportFormatting;
        };

        var buildLineObject = function ($reportLine, additionalData, parameters)
        {
            var reportLine = {};
            var canvasColumns = $reportLine.find(".jsColumn");
            var data = $reportLine.data();
            if (!data)
            {
                return;
            }

            reportLine.$type = data.lineType; //$type NEEDS to be the first item in
            reportLine.cellFormat = { height: $.safeRound(data.lineHeight) };
            reportLine.textFormat = { backColor: data.backColor };
            reportLine.columns = buildReportColumnCollection(canvasColumns, parameters);
            reportLine.isHiddenCondition = data.isHiddenCondition;
            reportLine.printAtBottomOfPage = data.printAtBottomOfPage;
            reportLine.printAtBottomOfPagePromptUniqueId = data.printAtBottomOfPagePromptUniqueId;

            if (data.reportFooterLine)
            {
                reportLine.printReportFooterOnNewPagePromptUniqueId = data.printReportFooterOnNewPagePromptUniqueId;
                reportLine.printReportFooterOnNewPage = data.printReportFooterOnNewPage;
            }

            reportLine.repeatOnPageIfDetailsPresent = data.repeatOnPageIfDetailsPresent;
            reportLine.repeatHeaderForEachDataRow = data.repeatHeaderForEachDataRow;
            reportLine.repeatLineAfterStart = data.repeatLineAfterStart;
            reportLine.repeatLineAfterStartPromptUniqueId = data.repeatLineAfterStartPromptUniqueId;
            reportLine.variableIDsToReset = data.variableIdsToReset;
            reportLine.lineID = data.lineId;
            reportLine.lineLabel = data.reportLabelText;
            reportLine.borderFormatting = selectBorderFormattingData(data);
            reportLine.backgroundColorBinding = data.backgroundColorBinding;
            reportLine.borderColorBinding = data.borderColorBinding;

            var exportFormatting = buildLineExportPropertiesJSON(data);
            if (!$.isEmptyObject(exportFormatting))
            {
                reportLine.exportFormatting = exportFormatting;
            }

            if (data.lineIndex)
            {
                reportLine.lineIndex = data.lineIndex;
            }

            if (data.relatedReportSortId)
            {
                reportLine.relatedReportSortId = data.relatedReportSortId;
            }

            if (data.relatedReportSortID)
            {
                reportLine.relatedReportSortID = data.relatedReportSortID;
            }

            $.extend(reportLine, additionalData);

            return reportLine;
        };

        var buildColumnExportPropertiesJSON = function (data)
        {
            var exportFormatting = {};

            if (data.nodeName)
            {
                exportFormatting.xmlExportFormatting = {};
                exportFormatting.xmlExportFormatting.nodeName = data.nodeName;
            }

            if (data.nodeType)
            {
                exportFormatting.xmlExportFormatting = exportFormatting.xmlExportFormatting || {};
                exportFormatting.xmlExportFormatting.nodeType = data.nodeType;
            }

            if (data.fixedWidthStartPosition)
            {
                exportFormatting.fixedWidthExportFormatting = {};
                exportFormatting.fixedWidthExportFormatting.startPosition = data.fixedWidthStartPosition;
            }

            if (data.fixedWidthLength)
            {
                exportFormatting.fixedWidthExportFormatting = exportFormatting.fixedWidthExportFormatting || {};
                exportFormatting.fixedWidthExportFormatting.length = data.fixedWidthLength;
            }

            if (data.jsonPropertyName)
            {
                exportFormatting.jsonExportFormatting = { name: data.jsonPropertyName };
            }

            return exportFormatting;
        };

        var buildReportColumnCollection = function ($reportColumns, parameters)
        {
            const reportColumns = [];
            parameters = parameters || {};

            $reportColumns.each(function ()
            {
                const self = $(this);
                const data = self.data();
                if (!data)
                {
                    return;
                }

                self.attr('tabindex', self.index());

                const reportColumn = {};
                reportColumn.$type = data.fieldColumnType;
                reportColumn.fieldName = data.fieldName;
                reportColumn.isFutureValue = data.isFutureValue;

                if (data.fieldHeaderText != undefined)
                {
                    reportColumn.headerText = data.fieldHeaderText;
                }

                reportColumn.fieldType = skyward.reporting.reportColumn.getFieldType(data);
                if (data.columnFieldType != undefined)
                {
                    reportColumn.columnFieldType = data.columnFieldType;
                }
                if (data.columnFilterJson)
                {
                    reportColumn.columnFilterJson = typeof (data.columnFilterJson) === "object" ? JSON.stringify(data.columnFilterJson) : data.columnFilterJson;
                }
                reportColumn.isCalculated = data.isCalculated;
                reportColumn.isOperation = data.isOperation;
                reportColumn.isHiddenCondition = data.isHiddenCondition;
                if (data.isHiddenPromptUniqueId != undefined)
                {
                    reportColumn.isHiddenPromptUniqueId = data.isHiddenPromptUniqueId;
                }
                reportColumn.whenHiddenOption = data.whenHiddenOption;
                if (data.pageSectionColumnType != null)
                {
                    reportColumn.pageSectionColumnType = data.pageSectionColumnType;
                    reportColumn.promptUniqueId = data.promptUniqueId;
                    reportColumn.displayType = data.displayType;
                }

                const dataFormatting = serializeFormattingAttributes(data);
                reportColumn.textFormat = dataFormatting.textFormat;

                if (enumMaps.FormatterTypeNames.indexOf((data.formatter || {}).formatterName || "") == -1)
                {
                    reportColumn.formatter = getFormatterDataDefaults();
                }
                else
                {
                    reportColumn.formatter = data.formatter;
                }

                reportColumn.borderFormat = dataFormatting.borderFormat;
                reportColumn.cellFormat = dataFormatting.cellFormat;
                reportColumn.designerText = data.designerText;
                if (data.mediaId)
                {
                    reportColumn.mediaId = parseInt(data.mediaId);
                }
                if (data.useSystemLogo != undefined)
                {
                    reportColumn.useSystemLogo = data.useSystemLogo;
                }
                if (data.aggregateType != undefined)
                {
                    reportColumn.aggregateType = data.aggregateType;
                    reportColumn.aggregateDelimiter = data.aggregateDelimiter;
                    reportColumn.aggregateIndex = data.aggregateIndex;
                }

                reportColumn.dataColumnId = data.dataColumnId;
                reportColumn.columnId = data.columnId;
                reportColumn.positionX = $.safeRound(data.positionX);
                reportColumn.positionY = $.safeRound(data.positionY);
                reportColumn.designerText = data.designerText;
                reportColumn.singleLineIndexRemoved = data.singleLineIndexRemoved;
                reportColumn.displayPath = data.displayPath;
                reportColumn.isRunningTotal = data.isRunningTotal;
                reportColumn.resetOn = data.resetOn;
                reportColumn.absoluteFieldPath = data.absoluteFieldPath;
                reportColumn.styleName = data.styleName;
                reportColumn.backgroundColorBinding = data.backgroundColorBinding;
                reportColumn.foregroundColorBinding = data.foregroundColorBinding;
                reportColumn.borderColorBinding = data.borderColorBinding;

                if (data.variableIdToSet != undefined)
                {
                    reportColumn.variableIdToSet = data.variableIdToSet;
                }
                if (data.variableIdToGet != undefined)
                {
                    reportColumn.variableIdToGet = data.variableIdToGet;
                }
                if (data.sourceColumnId != undefined)
                {
                    reportColumn.sourceColumnId = data.sourceColumnId;
                }

                const exportFormatting = buildColumnExportPropertiesJSON(data);
                if (!$.isEmptyObject(exportFormatting))
                {
                    reportColumn.exportFormatting = exportFormatting;
                }

                if (data.calculationJson)
                {
                    reportColumn.calculation = data.calculationJson;
                }

                if (data.operationId)
                {
                    reportColumn.calculatedField = CalculatedFields_Model.Get(data.operationId);
                }

                if (data.fieldColumnType != COLUMN_TYPES.HEADER_COLUMN)
                {
                    data.parameters.forEach(function (parameter)
                    {
                        skyward.reporting.searchParameters.viewModel.mergeAdd(parameters, parameter);
                    });
                }

                reportColumns.push(reportColumn);
            });

            return reportColumns;
        };

        function serializeFormattingAttributes(data)
        {
            return {
                borderFormat: selectBorderFormattingData(data),
                cellFormat: {
                    rotation: $.safeRound(data.rotation),
                    width: $.safeRound(data.fieldWidth),
                    height: $.safeRound(data.fieldHeight),
                    paddingLeft: $.safeRound(data.paddingLeft),
                    paddingRight: $.safeRound(data.paddingRight),
                    paddingTop: $.safeRound(data.paddingTop),
                    paddingBottom: $.safeRound(data.paddingBottom)
                },
                textFormat: {
                    backColor: data.backColor,
                    textAlignment: data.dataTextAlignment,
                    overlapMode: enumMaps.OverlapModes[data.overlapMode],
                    verticalAlignment: data.verticalAlignment,
                    font: {
                        foreColor: data.dataForeColor,
                        bold: data.dataBold,
                        fontFamily: isBarcodeFormatter(data.formatter) ? BARCODE_FONT_NAME : data.dataFontFamily,
                        italic: data.dataItalic,
                        size: data.dataFontSize,
                        underline: data.dataUnderline,
                    }
                }
            };
        }

        var selectBorderFormattingData = function (data)
        {
            var borderFormattingData = {
                borderStyle: data.borderStyle,
                borderWidth: $.safeRound(data.borderWidth),
                borderRight: data.borderRight == true,
                borderLeft: data.borderLeft == true,
                borderTop: data.borderTop == true,
                borderBottom: data.borderBottom == true,
                borderColor: data.borderColor
            };
            return borderFormattingData;
        };
        //END REPORT SERIALIZATION

        var isReportDirty = function ()
        {
            return changeTracker.isDirty(designer);
        }

        var getReportData = function ()
        {
            const data = designer.data();
            const reportData =
            {
                reportID: data.primaryid,
                reportIDHash: data.primaryidhash
            };
            return reportData;
        }

        //accepts: newfield (either the newly created calculated field object, or an existing calculated field id),
        //         targetLine (the reportLine to insert the column into),
        //         position: (the position object, of which the left is used to place the new columns)
        skyward.reporting.addCalculatedField = function (newField, targetLine, position)
        {
            if (newField.id)
            {
                CalculatedFields_Model.Set(newField);
            }
            else
            {
                newField = CalculatedFields_Model.Get(newField);
            }

            const currentSection = entireCanvas.find('.sectionSelected');

            var sectionDataLines = getSectionDataLines(currentSection);

            targetLine = targetLine || currentRow || sectionDataLines.first();

            var dataLine = targetLine.hasClass('jsDataLine') ? targetLine : sectionDataLines.first();

            var dataColumn;

            var addHeaderColumn = false;
            var addDataColumn = false;
            var addAggregateColumn = false;
            var addSortHeaderColumn = false;
            var addFooterColumn = false;
            var currentRowLineType;

            currentRowLineType = targetLine.data().lineType;
            if (currentRowLineType == LINE_TYPES.FOOTER_LINE)
            {
                addFooterColumn = true;
            }
            if (currentRowLineType == LINE_TYPES.HEADER_LINE)
            {
                addHeaderColumn = true;
            }
            else if (currentRowLineType == LINE_TYPES.DATA_LINE)
            {
                addHeaderColumn = true;
                addDataColumn = true;
            }
            else if (currentRowLineType == LINE_TYPES.SORT_HEADER_LINE)
            {
                addSortHeaderColumn = true;
                if (sectionDataLines.find("[data-operation-id='" + newField.id + "']").length <= 0)
                {
                    addHeaderColumn = true;
                    addDataColumn = true;
                }
            }
            else if (isAggregateLineType(currentRowLineType))
            {
                addAggregateColumn = true;
                if (sectionDataLines.find("[data-operation-id='" + newField.id + "']").length <= 0)
                {
                    addHeaderColumn = true;
                    addDataColumn = true;
                }
            }

            var columnData = { 'operation-id': newField.id, 'field-name': newField.fieldName, 'designer-text': newField.fieldName, 'column-field-type': newField.dataType, 'field-type': newField.dataType };
            var columnAttr = { 'data-operation-id': newField.id, 'data-field-name': newField.fieldName, 'data-designer-text': newField.fieldName, 'data-column-field-type': newField.dataType, 'data-field-type': newField.dataType };

            position = position || { top: null, left: null };
            if (addDataColumn)
            {
                dataColumn = createNewColumn({ type: COLUMN_TYPES.DATA_COLUMN, text: newField.fieldName }).data(columnData).attr(columnAttr);

                // create the data column
                var params = {
                    newItem: dataColumn,
                    reportLine: dataLine,
                    section: currentSection,
                    position: position,
                    autoCreatedColumn: true
                };
                position = insertColumnHelperFunction(params);
            }

            if (addHeaderColumn)
            {
                var targetHeaderLine = !addDataColumn ? targetLine : getCorrespondingHeaderLine(currentSection, dataLine);
                if (targetHeaderLine.length > 0)
                {
                    let headerColumn = createNewColumn(
                        {
                            type: COLUMN_TYPES.HEADER_COLUMN,
                            text: newField.fieldName,
                            relatedItem: dataColumn || null,
                            usesSingleLineIndex: isParentDisplayOnSingleLine(currentSection)
                        });
                    position = insertColumnHelperFunction({
                        newItem: headerColumn,
                        reportLine: targetHeaderLine,
                        section: currentSection,
                        position: position,
                        autoCreatedColumn: true
                    });
                }
            }

            if (addFooterColumn)
            {
                var footerColumn = createNewColumn({ type: COLUMN_TYPES.FOOTER_COLUMN, text: newField.fieldName }).data(columnData).attr(columnAttr);
                position = insertColumnHelperFunction({
                    newItem: footerColumn,
                    reportLine: targetLine,
                    section: currentSection,
                    position: position,
                    autoCreatedColumn: true
                });
            }

            if (addAggregateColumn)
            {
                var aggregateColumn = createNewColumn({ type: COLUMN_TYPES.AGGREGATE_COLUMN, text: newField.fieldName, relatedItem: dataColumn || null }).data(columnData).attr(columnAttr);

                insertColumnHelperFunction({
                    newItem: aggregateColumn,
                    reportLine: targetLine,
                    section: currentSection,
                    position: position,
                    autoCreatedColumn: true
                });
            }

            if (addSortHeaderColumn)
            {
                var sortHeaderColumn = createNewColumn({ type: COLUMN_TYPES.DATA_COLUMN, text: newField.fieldName, relatedItem: dataColumn || null }).data(columnData).attr(columnAttr);

                var params = {
                    newItem: sortHeaderColumn,
                    reportLine: targetLine,
                    section: currentSection,
                    position: position,
                    autoCreatedColumn: true
                };
                position = insertColumnHelperFunction(params);
            }

            updateDesignerSidebarCalculatedFieldList(currentSection.data());
            initDesignerUI(currentSection);
        };

        var getAvailableColumnInfoForAggregateCalculations = function (selectedRow, existingAggregateCalculationColumnID)
        {
            var processedSectionNames = {};
            var processedColumnNames = {};

            function getNameIncrement(collection, name)
            {
                let increment = 0;
                if (collection.hasOwnProperty(name))
                {
                    increment = collection[name] + 1;
                }
                collection[name] = increment;
                return increment;
            }

            function getColumnNameIncrement(name)
            {
                return getNameIncrement(processedColumnNames, name);
            }

            function cleanColumnName(name)
            {
                if (name != null)
                {
                    return name.toString().replace(/\.+$/, '');
                }
                return "";
            }

            const selectedSection = getCurrentSection();

            const allSectionInfo = [];

            //Get the columns from the data lines for all sections
            getCurrentAndDetailSections(selectedSection).each(function ()
            {
                const workingSection = $(this);
                const workingSectionData = workingSection.data();

                const workingSectionInfo = getSectionInfo(workingSectionData, getNameIncrement(processedSectionNames, workingSectionData.displayName));

                getSectionDataLines(workingSection).each(function ()
                {
                    processLine(workingSectionInfo.lines, $(this), workingSectionData);
                });

                getPossibleSourceAggregateLinesForSection(selectedRow, selectedSection, workingSection).each(function ()
                {
                    processLine(workingSectionInfo.lines, $(this), workingSectionData);
                });

                if (workingSection.is(selectedSection))
                {
                    processLine(workingSectionInfo.lines, selectedRow, workingSectionData, true);
                }

                if (workingSectionInfo.lines.length > 0)
                {
                    allSectionInfo.push(workingSectionInfo);
                }
            });

            function getSectionInfo(sectionData, nameIncrement)
            {
                const sectionID = sectionData.sectionId;
                let sectionName = sectionData.displayName;

                if (nameIncrement)
                {
                    sectionName += " (" + nameIncrement + ")"
                }

                const sectionInfo =
                {
                    sectionID: sectionID,
                    name: sectionName,
                    lines: []
                };

                return sectionInfo;
            }

            function processLine(lineCollection, line, workingSectionData, isCurrentLine)
            {
                const lineData = line.data();
                const lineInfo =
                {
                    name: lineData.reportLabelText,
                    columns: []
                };

                getColumnsInLine(line).each(function ()
                {
                    processColumn(lineInfo.columns, workingSectionData, lineData, $(this), isCurrentLine ?? false);
                });

                if (lineInfo.columns.length > 0)
                {
                    lineCollection.push(lineInfo);
                }

                return lineInfo;
            }

            function processColumn(columnCollection, sectionData, lineData, column, isOnCurrentLine)
            {
                let columnData = column.data();

                let supportedAggregateColumnTypes = [COLUMN_TYPES.AGGREGATE_COLUMN, COLUMN_TYPES.CALCULATED_COLUMN, COLUMN_TYPES.DATA_COLUMN];
                let supportedCurrentLineColumnTypes = [COLUMN_TYPES.AGGREGATE_COLUMN, COLUMN_TYPES.CALCULATED_COLUMN, COLUMN_TYPES.FUTURE_VALUE_COLUMN, COLUMN_TYPES.VARIABLE_COLUMN, COLUMN_TYPES.TEXT_COLUMN];

                if (!existingAggregateCalculationColumnID || columnData.columnId !== existingAggregateCalculationColumnID)
                {
                    let isValidForAggregateMath = false;

                    let isTextColumn = columnData.fieldColumnType === COLUMN_TYPES.TEXT_COLUMN;
                    let columnDataType = isTextColumn && skyward.extensions.isNumeric(columnData.designerText) ? DATA_TYPES.NUMBER_DECIMAL : skyward.reporting.reportColumn.getFieldType(columnData);

                    if (isOnCurrentLine)
                    {
                        if (supportedCurrentLineColumnTypes.includes(columnData.fieldColumnType))
                        {
                            let columnIsNumeric = columnDataType && columnDataType.startsWith("Number");
                            let columnIsString = columnDataType && columnDataType.startsWith(DATA_TYPES.STRING);

                            isValidForAggregateMath = isTextColumn ? columnIsNumeric : (columnIsNumeric || columnIsString);
                        }
                    }
                    else if (supportedAggregateColumnTypes.includes(columnData.fieldColumnType))
                    {
                        isValidForAggregateMath = !columnData.isFutureValue;
                    }

                    if (isValidForAggregateMath)
                    {
                        let columnName = cleanColumnName(getColumnLabelWithPreText(column));

                        let nameIncrement = getColumnNameIncrement(`${columnName}_${lineData.lineId}_${sectionData.sectionId}`);

                        if (nameIncrement)
                        {
                            columnName += " (" + nameIncrement + ")"
                        }

                        if (!columnDataType)
                        {
                            const exception = new window.SkywardException("Column has undefined data type.", `Column: '${columnName}', Section: '${sectionData.displayName}'`);
                            skyward.error.reportException(exception);
                            return;
                        }

                        let columnInfo =
                        {
                            isOnCurrentLine: isOnCurrentLine,
                            columnID: columnData.columnId,
                            binding: columnData.bindingString,
                            name: columnName,
                            dataType: columnDataType,
                            isFutureValue: columnData.isFutureValue
                        };
                        columnCollection.push(columnInfo);
                    }
                }
            }

            return allSectionInfo;
        };

        var getCalculatedColumnBuilder = function (data, clickedElement)
        {
            if (!clickedElement.hasClass("editorColumnText"))
            {
                const closestSection = clickedElement.closest('.jsSection');
                setActiveSection(closestSection);
            }

            const selectedSection = getCurrentSection();
            const selectedRow = getSectionSelectedRow(selectedSection);
            let existingAggregateCalculationColumnID = data.columnId;
            const allSectionInfo = getAvailableColumnInfoForAggregateCalculations(selectedRow, existingAggregateCalculationColumnID);

            var postedData =
            {
                name: skyward.extensions.encodeHtml(data.fieldName),
                expression: data.calculation,
                allSectionInfo: skyward.extensions.encodeHtml(JSON.stringify(allSectionInfo)),
            };

            var popup;
            var url = skyward.global.virtualDir + "Reporting/Report/GetAggregateCalculationBuilder";
            $.ajax({
                type: "POST",
                dataType: "JSON",
                url: url,
                data: postedData,
                success: function (popupData)
                {
                    var okButton = { text: "Save", click: saveCalculatedColumn, additionalClasses: "okButton", icon: "Save" };
                    var cancelButton = { text: "Cancel", icon: "Cancel" };
                    popup = skyward.modalLayer.showDialog("Aggregate Calculations", popupData.html, [okButton, cancelButton], {
                        closeOnClick: false,
                        automationID: "aggregate-calculation-builder-dialog"
                    });
                    popup.bodyPromise.done(function ()
                    {
                        if (popupData.errors && !jQuery.isEmptyObject(popupData.errors))
                        {
                            skyward.validation.displayValidationMessages({ errors: popupData.errors }, popup.container, $());
                        }
                    });
                }
            });

            var saveCalculatedColumn = function ()
            {
                var currentSection = entireCanvas.find(".sectionSelected");
                var sectionID = currentSection.data("sectionId");

                var aggregateLine = currentRow || getSectionSelectedRow(currentSection);
                var lineID = aggregateLine.data("lineId");

                skyward.reporting.aggregateCalculationBuilder.validateAndGetValues(popup.container, existingAggregateCalculationColumnID, sectionID, lineID, allSectionInfo).then(function (valuesFromPopup)
                {
                    if (valuesFromPopup.isValid)
                    {
                        const name = valuesFromPopup.name;
                        const expression = valuesFromPopup.expression;
                        const displayExpression = valuesFromPopup.displayExpression;
                        const calculationJson = valuesFromPopup.calculationJson;
                        const isFutureValue = valuesFromPopup.isFutureValue;
                        const fieldType = valuesFromPopup.fieldType;

                        var columnData = {
                            displayCalculation: displayExpression,
                            calculation: expression,
                            calculationJson: calculationJson,
                            designerText: name,
                            isFutureValue: isFutureValue,
                            fieldType: fieldType
                        };
                        var columnAttr = {
                            "data-display-calculation": displayExpression,
                            "data-calculation": expression,
                            "data-designer-text": name,
                            "is-future-value": isFutureValue
                        };
                        var calculatedColumn;
                        if (existingAggregateCalculationColumnID)
                        {
                            calculatedColumn = entireCanvas.find("[data-column-id='" + existingAggregateCalculationColumnID + "']");
                            calculatedColumn.find(".column_content").text(name);
                            calculatedColumn.data(columnData).attr(columnAttr);
                        }
                        else
                        {
                            calculatedColumn = createNewColumn({ type: COLUMN_TYPES.CALCULATED_COLUMN, text: name }).data(columnData).attr(columnAttr);
                            existingAggregateCalculationColumnID = calculatedColumn.data().columnId;

                            var params = {
                                newItem: calculatedColumn,
                                reportLine: aggregateLine,
                                section: currentSection,
                                position: { top: null, left: null },
                                autoCreatedColumn: true
                            };
                            insertColumnHelperFunction(params);
                        }

                        skyward.reporting.aggregateCalculationCollection.add(existingAggregateCalculationColumnID, expression, name, sectionID, lineID);
                        updateHiddenField();
                        editorColumnText.val(displayExpression);
                        skyward.reporting.updateVariableUsageLocations();
                        popup.close();
                    }
                });
            };
        };

        var applyCellFormatToItem = function (item, cellFormat)
        {
            var itemData = item.data();
            if (!itemData.fieldWidth)
            {
                item.data('fieldWidth', cellFormat.Width);
            }

            if (!itemData.fieldHeight || itemData.fieldColumnType === COLUMN_TYPES.HEADER_COLUMN)
            {
                item.data('fieldHeight', cellFormat.Height);
            }

            // image and dynamic image columns should not have any padding
            if (itemData.fieldColumnType !== COLUMN_TYPES.DYNAMIC_IMAGE_COLUMN && itemData.fieldColumnType !== COLUMN_TYPES.IMAGE_COLUMN)
            {
                item.data('paddingLeft', cellFormat.PaddingLeft);
                item.data('paddingRight', cellFormat.PaddingRight);
                item.data('paddingTop', cellFormat.PaddingTop);
                item.data('paddingBottom', cellFormat.PaddingBottom);
            }
        };

        var applyBorderFormatToItem = function (item, borderFormat)
        {
            item.data('borderStyle', borderFormat.BorderStyle);
            item.data('borderWidth', borderFormat.BorderWidth);
            item.data('borderRight', borderFormat.BorderRight);
            item.data('borderLeft', borderFormat.BorderLeft);
            item.data('borderTop', borderFormat.BorderTop);
            item.data('borderBottom', borderFormat.BorderBottom);
            item.data('borderColor', borderFormat.BorderColor);
        };

        var applyTextFormatToItem = function (item, textFormat)
        {
            item.data('dataFontFamily', textFormat.Font.FontFamily);
            item.data('dataBold', textFormat.Font.Bold);
            item.data('dataItalic', textFormat.Font.Italic);
            item.data('dataForeColor', textFormat.Font.ForeColor);
            item.data('dataTextFormat', textFormat.TextFormat);
            item.data('dataFontSize', textFormat.Font.Size);
            item.data('dataUnderline', textFormat.Font.Underline);
            item.data('backColor', textFormat.BackColor);
            item.data('verticalAlignment', textFormat.VerticalAlignment);
        };

        var getColumnLabelWithPreTextByColumnID = function (columnID)
        {
            var column = getSectionAllColumns(getCurrentSection()).filter('[data-column-id="' + columnID + '"]');
            return column.length ? getColumnLabelWithPreText(column) : "Column Not Found";
        };

        var getColumnLabelWithPreText = function (column)
        {
            const columnData = column.data();
            if (columnData.fieldColumnType === COLUMN_TYPES.TEXT_COLUMN ||
                columnData.fieldColumnType === COLUMN_TYPES.IMAGE_COLUMN ||
                columnData.fieldColumnType === COLUMN_TYPES.CALCULATED_COLUMN)
            {
                return columnData.designerText;
            }
            else if (columnData.fieldColumnType === COLUMN_TYPES.FORMTEXT_COLUMN)
            {
                let friendlyText = skyward.reporting.prompts.promptCollection.ConvertPromptBindingsToFriendlyNames(columnData.designerText);
                friendlyText = skyward.reporting.calculatedFields.ConvertBindingsToFriendlyNames(friendlyText);
                return friendlyText;
            }

            if (columnData.fieldColumnType == COLUMN_TYPES.VARIABLE_COLUMN)
            {
                const variableToGet = skyward.reporting.variables.collectionModel.getById(columnData.variableIdToGet) || {};
                return "Variable: " + variableToGet.name;
            }

            if (columnData.fieldColumnType == COLUMN_TYPES.FUTURE_VALUE_COLUMN)
            {
                return `Future ${getColumnLabelWithPreText(getColumnByID(columnData.sourceColumnId))}`;
            }

            const line = column.parent();
            const reportRowType = line.data('lineType');
            let preText;
            const isPageSectionColumnLabel = columnData.fieldColumnType == COLUMN_TYPES.PAGE_SECTION_COLUMN && columnData.displayType === PAGE_SECTION_COLUMN_DISPLAY_TYPES.LABEL;

            if (!isPageSectionColumnLabel && !line.hasClass("jsPrintOnOneLinePreviewLine"))
            {
                if (reportRowType === LINE_TYPES.SORT_HEADER_LINE)
                {
                    if (columnData.fieldColumnType == COLUMN_TYPES.GROUP_COLUMN_HEADER)
                    {
                        preText = "Header As Label";
                    }
                    else
                    {
                        preText = "Next Record Value";
                    }
                }
                else if (isAggregateLineType(reportRowType) ||
                    ((columnData.fieldColumnType == COLUMN_TYPES.AGGREGATE_COLUMN || columnData.fieldColumnType == COLUMN_TYPES.DYNAMIC_IMAGE_COLUMN) &&
                        reportRowType === LINE_TYPES.FOOTER_LINE))
                {
                    if (columnData.aggregateType)
                    {
                        preText = columnData.aggregateType.toDisplayName();
                    }
                    else
                    {
                        preText = "Last Record Value";
                    }
                }
                else if (columnData.fieldColumnType == COLUMN_TYPES.PAGE_SECTION_COLUMN)
                {
                    if (!isPromptColumn(columnData))
                    {
                        preText = "Value";
                    }
                    else
                    {
                        preText = columnData.displayType == PAGE_SECTION_COLUMN_DISPLAY_TYPES.PROMPT_VALUE ? "Value" : "Display Value";
                    }
                }
            }

            if (preText)
            {
                return preText + ': ' + columnData.designerText;
            }

            return columnData.designerText;
        };

        function selectFieldDialog(button, moduleName, objectName, options)
        {
            button = $(button);
            if (skyward.buttonManager.isElementDisabled(button))
            {
                return;
            }

            var defaultOptions =
            {
                title: "Field Select",
                isValidSelection: function (selection) { return selection && selection.relationshipType === undefined; },
                treeBrowseOptions: {}
            };
            options = $.extend({}, defaultOptions, options || {});

            var treeBrowseDiv = $();
            var resultsDeferred = $.Deferred();
            $.ajax(skyward.global.virtualDir + "Reporting/Report/GetSelectFieldDialog",
                {
                    type: "POST",
                    dataType: "JSON",
                    data: { moduleName: moduleName, objectName: objectName },
                    success: function (data)
                    {
                        var layerClosed = function (resultData)
                        {
                            // ModalLayer sets resultData differently depending on how it was closed: resultData is false if glass was clicked.
                            // Otherwise, resultData.reasonForClose is an event object when cancel was clicked, or treebrowse data (always includes dataObjectFullName) when Ok was clicked.
                            if (!resultData || (resultData.reasonForClose && !resultData.reasonForClose.dataObjectFullName))
                            {
                                resultsDeferred.reject();
                            }

                            // afterClose was replaced with layerClosed, call it if it was defined.
                            if (options.modalLayerOptions && typeof options.modalLayerOptions.afterClose === "function")
                            {
                                options.modalLayerOptions.afterClose(resultData);
                            }
                        };

                        var saveField = function ()
                        {
                            const itemData = skyward.treebrowse.getSelectedFieldData(treeBrowseDiv);
                            const treeBrowseData = skyward.treebrowse.getTreeBrowseData(treeBrowseDiv);

                            if (!options.isValidSelection(itemData))
                            {
                                dialog.close(false);
                            }

                            dialog.close(itemData);

                            const moduleObjectData =
                            {
                                dataObjectFullName: treeBrowseData.dataObjectFullName,
                                fieldModule: treeBrowseData.rootModule,
                                fieldObject: treeBrowseData.rootObject
                            };
                            resultsDeferred.resolve(itemData, moduleObjectData);
                        };

                        // launch the field select pop-up
                        var modalLayerOptions = $.extend({ anchorTo: button }, options.modalLayerOptions, { afterClose: layerClosed, closeOnClick: false, automationID: "field-select" }); // Always use locally defined afterClose
                        var buttons = [{ text: "Select", icon: "Select", click: saveField }, { text: "Cancel", icon: "Cancel" }];
                        var dialog = skyward.modalLayer.showDialog(options.title, data.html, buttons, modalLayerOptions);

                        dialog.bodyPromise.done(function ()
                        {
                            var cancelButton = dialog.footer.find("button[title='Cancel']");
                            var selectButton = dialog.footer.find("button[title='Select']");
                            selectButton.addClass("ui-disabled");

                            var moduleObjectSelector = dialog.content.find('.moduleObjectSelector');
                            moduleObjectSelector.on('treeBrowseSave', saveField);

                            var moduleCodeEEL = moduleObjectSelector.find('.moduleEEL');
                            moduleCodeEEL.on('change', clearTreeBrowse);

                            var objectCodeEEL = moduleObjectSelector.find('.objectEEL');
                            objectCodeEEL.on('change', function () { clearTreeBrowse(); populateTreeBrowse(); });

                            selectRow(null);
                            if (objectCodeEEL.val())
                            {
                                populateTreeBrowse();
                            }

                            function populateTreeBrowse()
                            {
                                var objValue = objectCodeEEL.val();
                                if (objValue)
                                {
                                    $.ajax(skyward.global.virtualDir + "Reporting/Report/GetSelectFieldList",
                                        {
                                            type: "POST",
                                            dataType: "JSON",
                                            data: $.extend({}, options.treeBrowseOptions,
                                                {
                                                    lookupObject: objValue,
                                                    expandUrl: "Reporting/Report/ExpandTreeBrowse"
                                                }),
                                        })
                                        .then(function (treeBrowseData)
                                        {
                                            skyward.global.processViewPartHtml(treeBrowseData.html)
                                                .then(function (html)
                                                {
                                                    moduleObjectSelector.append(html);
                                                    skyward.window.notifyDOMLoad("#" + dialog.container.attr('id'));
                                                    treeBrowseDiv = moduleObjectSelector.find(".treeBrowse");

                                                    dialog.content.on("treeBrowseRowChange", function (event, treeBrowseData)
                                                    {
                                                        selectRow(treeBrowseData.getSelectedFieldData());
                                                    });
                                                });
                                        });
                                }
                            }

                            function clearTreeBrowse()
                            {
                                // Delete parameters for seek-to-field, which is only valid when popup is first opened.
                                delete options.treeBrowseOptions.fieldModule;
                                delete options.treeBrowseOptions.fieldObject;
                                delete options.treeBrowseOptions.positionObjectPath;
                                delete options.treeBrowseOptions.lookupModule;
                                delete options.treeBrowseOptions.lookupObject;
                                delete options.treeBrowseOptions.seekField;

                                treeBrowseDiv.remove();
                                treeBrowseDiv = $();
                                selectRow(null);
                            }

                            function selectRow(rowData)
                            {
                                if (options.isValidSelection(rowData))
                                {
                                    selectButton.removeClass("ui-disabled");
                                }
                                else
                                {
                                    selectButton.addClass("ui-disabled");
                                    if (selectButton.is(":focus"))
                                    {
                                        cancelButton.focus();
                                    }
                                }
                            }
                        });
                    }
                });

            return resultsDeferred.promise();
        }

        skyward.reporting.prompts = (function ()
        {
            var promptCollection = skyward.mvvm.newModelCollection([], {}, {}, function ()
            {
                var self = this;

                var snapShot = [];

                var pendingPromptUsages = [];
                var pendingRequiredPromptUsages = [];

                function findByUniqueId(prompt, uniqueId) { return prompt.PromptUniqueId == uniqueId; }

                // exposed methods
                var exposedMethods =
                {
                    Get: function (uniqueId)
                    {
                        return self.find(findByUniqueId, uniqueId);
                    },
                    Set: function (promptData)
                    {
                        // This always creates a NEW model - If you pass an existing model into Set(), previously active observers
                        //  will NOT be observing the model that was created in the collection! (ask Matt about being here at 9PM on a Thursday)
                        var promptModel = self.find(findByUniqueId, promptData.PromptUniqueId);

                        if (!promptModel)
                        {
                            promptModel = buildPrompt_Model(promptData);
                            self.add(promptModel);
                        }
                        else
                        {
                            for (var field in promptData)
                            {
                                promptModel[field] = promptData[field];
                            }
                        }
                        self.announce("add", promptModel);
                        return promptModel;
                    },
                    Delete: function (uniqueId)
                    {
                        var deletedPrompt = self.remove(self.Get(uniqueId));
                        if (deletedPrompt)
                        {
                            self.forEach(function (promptModel)
                            {
                                if (deletedPrompt.usesPrompt(promptModel))
                                {
                                    promptModel.refreshInUse();
                                }
                            });

                            self.announce("delete", deletedPrompt);
                        }
                    },
                    toJsonObject: function ()
                    {
                        var allPrompts = [];
                        self.forEach(function (prompt)
                        {
                            allPrompts.push(prompt.toJsonObject());
                        });
                        return allPrompts;
                    },
                    toJsonString: function ()
                    {
                        return skyward.extensions.encodeHtml(JSON.stringify(skyward.reporting.prompts.promptCollection.toJsonObject()));
                    },
                    TakeSnapshot: function ()
                    {
                        snapShot = [];
                        self.toJsonObject().forEach(function (prompt)
                        {
                            snapShot.push($.extend({}, prompt));
                        });
                    },
                    Revert: function ()
                    {
                        snapShot.forEach(function (prompt)
                        {
                            self.Set(prompt);
                        });
                        self.forEach(function (prompt)
                        {
                            if (!snapShot.some(function (snapShotPrompt) { return snapShotPrompt.PromptUniqueId === prompt.PromptUniqueId; }))
                            {
                                self.Delete(prompt.PromptUniqueId);
                            }
                        });
                        snapShot = [];
                    },
                    GetInUsePromptUniqueIds: function ()
                    {
                        var inUsePromptUniqueIds = [].concat(pendingPromptUsages).concat(pendingRequiredPromptUsages);

                        self.forEach(function (prompt)
                        {
                            prompt.refreshInUse();
                            if (prompt.InUse)
                            {
                                inUsePromptUniqueIds.push(prompt.PromptUniqueId);
                            }
                        });

                        return inUsePromptUniqueIds;
                    },
                    RefreshAllPromptsInUse: function ()
                    {
                        self.forEach(function (prompt)
                        {
                            prompt.refreshInUse(self);
                        });

                        self.forEach(function (prompt)
                        {
                            if (prompt.LinkedPromptUniqueId && promptCollection.Get(prompt.LinkedPromptUniqueId).InUse === true)
                            {
                                prompt.InUse = true;
                            }
                        });
                    },
                    AddTemporaryInUseFlag: function (promptModel)
                    {
                        if (pendingPromptUsages.indexOf(promptModel.PromptUniqueId) == -1)
                        {
                            pendingPromptUsages.push(promptModel.PromptUniqueId);
                        }
                        self.RefreshAllPromptsInUse();
                    },
                    AddTemporaryInUseRequiredFlag: function (promptModel)
                    {
                        if (pendingRequiredPromptUsages.indexOf(promptModel.PromptUniqueId) == -1)
                        {
                            pendingRequiredPromptUsages.push(promptModel.PromptUniqueId);
                        }
                        self.RefreshAllPromptsInUse();
                    },
                    RemoveAllTemporaryInUseFlags: function (promptModel)
                    {
                        var index = pendingPromptUsages.indexOf(promptModel.PromptUniqueId);
                        if (index != -1)
                        {
                            pendingPromptUsages.splice(index, 1);
                        }
                        var requiredIndex = pendingRequiredPromptUsages.indexOf(promptModel.PromptUniqueId);
                        if (requiredIndex != -1)
                        {
                            pendingRequiredPromptUsages.splice(requiredIndex, 1);
                        }
                        self.RefreshAllPromptsInUse();
                    },
                    IsFlaggedForUse: function (promptModel)
                    {
                        return pendingPromptUsages.indexOf(promptModel.PromptUniqueId) >= 0;
                    },
                    IsFlaggedForRequiredUse: function (promptModel)
                    {
                        return pendingRequiredPromptUsages.indexOf(promptModel.PromptUniqueId) >= 0;
                    },
                    PromptsIsDirty: function ()
                    {
                        if (snapShot.length === self.toJsonObject().length)
                        {
                            return self.isDirty();
                        }
                        return true;
                    },
                    GetLinkablePrompts: function (promptModel)
                    {
                        var linkablePrompts = [];
                        if (promptModel.InputType !== DATA_TYPES.BOOLEAN && promptModel.Type !== enumMaps.PromptTypes["ReportSort"]
                            && (promptModel.Type === enumMaps.PromptTypes["Value"] || promptModel.Type === enumMaps.PromptTypes["SingleSelect"]))
                        {
                            self.forEach(function (prompt)
                            {
                                if (prompt.InputType === promptModel.InputType
                                    && (prompt.Type === enumMaps.PromptTypes["Value"] || prompt.Type === enumMaps.PromptTypes["SingleSelect"])
                                    && !prompt.LinkedPromptUniqueId
                                    && prompt.PromptUniqueId !== promptModel.PromptUniqueId
                                    && (prompt.IsHiddenCondition == null || prompt.IsHiddenCondition.LeftOperand.PromptUniqueID !== promptModel.PromptUniqueId))
                                {
                                    linkablePrompts.push(prompt);
                                }
                            });
                        }

                        return linkablePrompts;
                    },
                    ///<summary>
                    /// Converts a string containing references to prompt bindings (i.e. {_prompt.GuidGuidGuid})
                    /// into a string containing "friendly prompt-bindings" (i.e. {P:Label}).
                    ///</summary>
                    ///<parameter name="data">the string to convert</parameter>
                    ///<returns>If passed-in data is invalid, an empty string is returned</returns>
                    ConvertPromptBindingsToFriendlyNames: function (data)
                    {
                        if (typeof data !== "string")
                        {
                            return "";
                        }

                        var result = data;
                        skyward.reporting.prompts.promptCollection.forEach(function (prompt)
                        {
                            if (prompt.hasUniqueLabel())
                            {
                                result = result.split(prompt.bindingString()).join("{P:" + prompt.Label + "}");
                            }
                        });
                        return result;
                    },
                    ConvertFriendlyNamesToPromptBindings: function (data)
                    {
                        if (typeof data !== "string")
                        {
                            return "";
                        }

                        var result = data;
                        skyward.reporting.prompts.promptCollection.forEach(function (prompt)
                        {
                            if (prompt.hasUniqueLabel())
                            {
                                result = result.split("{P:" + prompt.Label + "}").join(prompt.bindingString());
                            }
                        });
                        return result;
                    }
                };

                var _observer = skyward.mvvm.newObserver();
                _observer.observe(self, "model:FiltersJson", function (model)
                {
                    self.forEach(function (promptModel)
                    {
                        if (promptModel.PromptUniqueId != model.PromptUniqueId)
                        {
                            promptModel.refreshInUse(self);
                        }
                    });
                });
                _observer.observe(skyward.reporting.searchParameters.model, "model:value", exposedMethods.RefreshAllPromptsInUse);

                return exposedMethods;
            });

            var importPrompts = function (promptsJson)
            {
                $.each(promptsJson, function ()
                {
                    promptCollection.Set(this);
                });

                promptCollection.RefreshAllPromptsInUse();
            };

            var buildPrompt_Model = function (promptData)
            {
                // blank fields allow mvvm to make get/sets for everything, regardless of what is populated in a particular instance.
                var defaultValues = {
                    PromptUniqueId: undefined,
                    PromptGuid: undefined,
                    Label: undefined,
                    Type: enumMaps.PromptTypes.Value,
                    Value: undefined,
                    PrefillPrompt: true,
                    FieldPath: undefined,
                    FiltersJson: undefined,
                    Columns: undefined,
                    Sorts: undefined,
                    PresenceType: "Required",
                    InUse: undefined,
                    Order: (promptCollection ? promptCollection.length : 0),
                    InputType: DATA_TYPES.STRING,
                    DisplayValueOverrides: {},
                    LinkedPromptUniqueId: undefined,
                    LinkedPromptGuid: undefined,
                    Module: undefined,
                    Object: undefined,
                    RelativeFieldPath: undefined,
                    StandardFilterId: undefined,
                    StandardFilterName: undefined,
                    HiddenCondition: undefined,
                    SelectionOptions: buildInitialSelectionOptions()
                };

                // Legacy code contained a misspelling: "DistinctMultiSelect" in some places, "DistinctMultiselect" in others.
                // This keeps the problem from breaking legacy reports after a change to a DMSE:
                if (promptData.DistinctMultiselectEelViews)
                {
                    promptData.Columns = promptData.DistinctMultiselectEelViews;
                    delete promptData.DistinctMultiselectEelViews;
                }
                if (promptData.DistinctMultiselectEelSorts)
                {
                    promptData.Sorts = promptData.DistinctMultiselectEelSorts;
                    delete promptData.DistinctMultiselectEelSorts;
                }


                promptData = $.extend(defaultValues, promptData);

                return skyward.mvvm.newModel(promptData, undefined, function ()
                {
                    var self = this;

                    const placesToSearch = usageLocations.promptPlacesToSearch(this);

                    var snapshot = {};

                    var exposedMethods = {
                        toJsonObject: function ()
                        {
                            var result = {};
                            for (var key in defaultValues)
                            {
                                result[key] = self[key];
                            }
                            return result;
                        },
                        toJsonString: function ()
                        {
                            return skyward.extensions.encodeHtml(JSON.stringify(exposedMethods.toJsonObject()));
                        },
                        takeSnapshot: function ()
                        {
                            snapshot = exposedMethods.toJsonObject();
                        },
                        revert: function ()
                        {
                            for (var prop in snapshot)
                            {
                                self[prop] = snapshot[prop];
                            }
                            snapshot = {};
                        },
                        usesPrompt: function (otherPrompt)
                        {
                            const filterContainsPrompt = (self.FiltersJson || "").indexOf(otherPrompt.PromptUniqueId) >= 0;
                            const hiddenConditionContainsPrompt = self.HiddenCondition != null && self.HiddenCondition.LeftOperand.PromptUniqueID == otherPrompt.PromptUniqueId;
                            return filterContainsPrompt || hiddenConditionContainsPrompt;
                        },
                        isRequired: function (promptsModel)
                        {
                            return this.getUsages(promptsModel, ((node) => node.isRequired())).length > 0 || promptCollection.IsFlaggedForRequiredUse(self);
                        },
                        refreshInUse: function (promptsModel)
                        {
                            self.InUse = this.getUsages(promptsModel).length > 0 || promptCollection.IsFlaggedForUse(self) || promptCollection.IsFlaggedForRequiredUse(self);
                        },
                        getUsages: function (promptsModel, predicate)
                        {
                            placesToSearch.setModel(self);
                            return placesToSearch.traverse(promptsModel || promptCollection, predicate);
                        },
                        bindingString: function ()
                        {
                            return "{_prompt." + self.PromptUniqueId + "}";
                        },
                        getIconType: function ()
                        {
                            return skyward.reporting.getSvgNameByDataType(self.InputType);
                        },
                        hasUniqueLabel: function ()
                        {
                            return !promptCollection.some(function (p)
                            {
                                return p.PromptUniqueId != self.PromptUniqueId && p.Label.trim().toLowerCase() == self.Label.trim().toLowerCase();
                            });
                        },
                        getSelectionOptionDisplayValueByKey: function (key)
                        {
                            const keyComparisonValue = key.toString();
                            for (const option of self.SelectionOptions)
                            {
                                if (option[0] === keyComparisonValue)
                                {
                                    return option[1];
                                }
                            }

                            return undefined;
                        },
                        addTemporaryParameter: function (parameter)
                        {
                            self.tempRelatedSearchParameter = parameter;
                        },
                        removeTemporaryParameter: function ()
                        {
                            self.tempRelatedSearchParameter = undefined;
                        }
                    };

                    return exposedMethods;
                });
            };

            var buildInitialSelectionOptions = function ()
            {
                return [];
            };

            var addPromptEvents = skyward.mvvm.newObservable();
            var getPromptDetailsView = function (promptData, options, existingPromptUniqueId)
            {
                var defaultOptions = {
                    treatPromptAsInUse: false,
                    treatPromptAsInUseRequired: false,
                    tempRelatedSearchParameter: undefined,
                    preserveFocus: true
                };
                options = $.extend(defaultOptions, options);

                var promptModel;
                if (existingPromptUniqueId && existingPromptUniqueId.length)
                {
                    promptModel = promptCollection.Get(existingPromptUniqueId);
                }
                else
                {
                    promptModel = buildPrompt_Model(promptData || {});
                }

                var waitForSave_Deferred = $.Deferred();

                $.ajax(skyward.global.virtualDir + "Reporting/Report/GetPromptDetails",
                    {
                        type: "POST",
                        dataType: "JSON",
                        data: { promptJson: promptModel.toJsonString() },
                        success: function (ajaxResult)
                        {
                            var promptView;

                            for (var field in ajaxResult.model)
                            {
                                promptModel[field] = ajaxResult.model[field];
                            }

                            if (options.treatPromptAsInUseRequired)
                            {
                                promptCollection.AddTemporaryInUseRequiredFlag(promptModel);
                                promptModel.refreshInUse();
                                waitForSave_Deferred.always(function () { promptCollection.RemoveAllTemporaryInUseFlags(promptModel); });
                            }
                            else if (options.treatPromptAsInUse)
                            {
                                promptCollection.AddTemporaryInUseFlag(promptModel);
                                promptModel.refreshInUse();
                                waitForSave_Deferred.always(function () { promptCollection.RemoveAllTemporaryInUseFlags(promptModel); });
                            }

                            if (options.tempRelatedSearchParameter)
                            {
                                promptModel.addTemporaryParameter(options.tempRelatedSearchParameter);
                                waitForSave_Deferred.always(function () { promptModel.removeTemporaryParameter(); });
                            }

                            var promptHtml = $(ajaxResult.html);
                            var deferredBeforeShowContent = $.Deferred();
                            var addPromptDialog = skyward.modalLayer.showDialog("Add Prompt", $("<ul>").append(promptHtml),
                                [
                                    { text: "Save", click: saveClick, icon: "Save" },
                                    { text: "Cancel", icon: "Cancel", click: cancelAndClose }
                                ],
                                {
                                    closeOnClick: false,
                                    afterClose: function ()
                                    {
                                        promptView.dispose();
                                    },
                                    draggable: true,
                                    beforeShowContentPromise: deferredBeforeShowContent.promise(),
                                    automationID: "add-prompt-dialog",
                                    preserveFocus: true
                                });

                            addPromptDialog.bodyPromise.done(function ()
                            {
                                promptModel.takeSnapshot();
                                promptView = initPromptViewModel(addPromptDialog, promptModel);
                                deferredBeforeShowContent.resolve();
                            });

                            var validate = function (errors)
                            {
                                const isEnumPrompt = promptModel.InputType?.startsWith("String Enum") ?? false;
                                const isCustomSingleValuePrompt = promptModel.Type === enumMaps.PromptTypes.CustomSingleSelect;
                                const isSingleSelectPrompt = promptModel.Type === enumMaps.PromptTypes.SingleSelect;
                                const isMultipleSelectPrompt = promptModel.Type === enumMaps.PromptTypes.MultipleSelect

                                if (!promptModel.Label || !promptModel.Label.trim())
                                {
                                    errors["promptLabel"] = ["Label is a required field."];
                                }
                                else if (!promptModel.hasUniqueLabel())
                                {
                                    errors["promptLabel"] = ["Label must be unique."];
                                }

                                if (!isEnumPrompt && !promptModel.FieldPath && (isSingleSelectPrompt || isMultipleSelectPrompt))
                                {
                                    errors["promptField"] = ["Field is a required field for single and multiple select prompts."];
                                }

                                if (promptModel.Type === enumMaps.PromptTypes.UserInstructions && promptModel.Value.trim() === "")
                                {
                                    errors["userInstructions"] = ["Text to Display is a required field for user instructions."];
                                }

                                if (!isEnumPrompt && (isSingleSelectPrompt || isMultipleSelectPrompt) && (!Boolean(promptModel.Columns)))
                                {
                                    errors["Columns"] = ["Fields to display is a required field for single and multiple select prompts."];
                                }

                                if (!isEnumPrompt && isCustomSingleValuePrompt && promptModel.SelectionOptions.length === 0)
                                {
                                    errors["SelectionOptions"] = ["Selection Options is a required field."];
                                }

                                if (!$.isEmptyObject(errors))
                                {
                                    skyward.validation.displayValidationMessages({ errors: errors }, addPromptDialog.content, $());
                                    return false;
                                }
                                return true;
                            };

                            var saveAndClose = function ()
                            {
                                if (promptCollection.Get(promptModel.PromptUniqueId))
                                {
                                    promptCollection.Set(promptModel);
                                }
                                else
                                {
                                    waitForSave_Deferred.resolve({ promptHtml: promptHtml, promptModel: promptModel });
                                    addPromptEvents.announce("newPrompt", { promptHtml: promptHtml, promptModel: promptModel });
                                }
                                addPromptDialog.close();
                                if (options.dialog)
                                {
                                    options.dialog.animateModalContents();
                                }
                            };

                            function saveClick()
                            {
                                $.ajax(skyward.global.virtualDir + "Reporting/Report/ValidatePrompt",
                                    {
                                        type: "POST",
                                        dataType: "JSON",
                                        data:
                                        {
                                            promptJson: promptModel.toJsonString(),
                                            promptCollectionJson: promptCollection.toJsonString()
                                        },
                                        success: function (results)
                                        {
                                            if (validate(results))
                                            {
                                                saveAndClose();
                                            }
                                        }
                                    });
                            }

                            function cancelAndClose()
                            {
                                promptModel.revert();
                                waitForSave_Deferred.reject();
                                addPromptDialog.close();
                            }
                        },
                        error: waitForSave_Deferred.reject
                    });

                waitForSave_Deferred.done(function (data)
                {
                    promptCollection.add(data.promptModel);
                });

                return waitForSave_Deferred;
            };

            var getPromptLinkView = skyward.reporting.linkPrompt = function (selectedPromptUniqueId, promptManager)
            {
                var promptModel = promptCollection.Get(selectedPromptUniqueId);

                $.ajax(skyward.global.virtualDir + "Reporting/Report/GetPromptsLinkView",
                    {
                        type: "POST",
                        dataType: "JSON",
                        data:
                        {
                            promptJson: promptModel.toJsonString(),
                            linkablePromptsJson: skyward.extensions.encodeHtml(JSON.stringify(promptCollection.GetLinkablePrompts(promptModel)))
                        },
                        success: function (ajaxResult)
                        {
                            var promptsLinkView;

                            var promptHtml = $(ajaxResult.html);
                            var linkPromptsDialog = skyward.modalLayer.showDialog("Link Prompts", promptHtml,
                                [
                                    { text: "Save", "click": saveAndClose, "icon": "Check" },
                                    { text: "Cancel", "icon": "Cancel", "click": cancelAndClose }
                                ],
                                {
                                    preserveFocus: true,
                                    closeOnClick: false,
                                    afterClose: function ()
                                    {
                                        promptsLinkView.dispose();
                                    },
                                    draggable: true
                                });

                            linkPromptsDialog.bodyPromise.done(function ()
                            {
                                //this should probably be a different view, but it works for now
                                promptsLinkView = initPromptViewModel(linkPromptsDialog, promptModel);
                            });


                            function saveAndClose()
                            {
                                var highPromptSelect = promptHtml.find('.highPromptValue ');
                                if (highPromptSelect.children().length)
                                {
                                    var linkedPromptUniqueId = highPromptSelect.val();
                                    promptModel.LinkedPromptUniqueId = linkedPromptUniqueId;
                                    promptModel.LinkedPromptGuid = linkedPromptUniqueId;
                                    var linkedPrompt = promptCollection.Get(linkedPromptUniqueId);
                                    linkedPrompt.LinkedPromptUniqueId = promptModel.PromptUniqueId;
                                    linkedPrompt.LinkedPromptGuid = promptModel.PromptUniqueId;
                                    linkedPrompt.HiddenCondition = null;

                                    putPromptsSideBySide(promptModel.PromptUniqueId, linkedPromptUniqueId);
                                    recalculateSortOrder();
                                }

                                linkPromptsDialog.close();

                                const linkButton = promptManager.find(".link_report_prompt");
                                const unlinkButton = promptManager.find(".unlink_report_prompt");

                                linkButton.hide();
                                unlinkButton.show();
                                unlinkButton.focus();
                            }

                            function cancelAndClose()
                            {
                                linkPromptsDialog.close();
                            }

                            function putPromptsSideBySide(lowPromptUniqueId, highPromptUniqueId)
                            {
                                var lowPromptInput = promptManager.find("[data-prompt-unique-id='" + lowPromptUniqueId + "']");
                                var highPromptInput = promptManager.find("[data-prompt-unique-id='" + highPromptUniqueId + "']");

                                highPromptInput.find("svg").remove();
                                highPromptInput.prepend('<span class="decorativeLabel visualLabel linkedPromptSeparator"> - </span>');
                                lowPromptInput.parent().append(highPromptInput);
                            }

                            function recalculateSortOrder()
                            {
                                promptManager.find("li").not(".hidden").find(".promptInput").each(function (index) { promptCollection.Get($(this).data("promptUniqueId")).Order = index; });
                            }
                        }
                    });
            };

            // launches a multi-select enum dialog
            function openMultiSelectEnumDialog(button, promptModel)
            {
                var deferred = $.Deferred();
                var menu;

                // This method is still scraping the DOM. Change it to receive a promptModel instead.
                var okButton = { text: "OK", "icon": "Check", "click": scrapeValuesAndClose };
                var cancelButton = { text: "Cancel", "icon": "Cancel", "click": cancel };
                var buttons = [okButton, cancelButton];
                var options = { closeOnClick: false, anchorTo: button };

                $.ajax(skyward.global.virtualDir + "Reporting/Report/GetMultiSelectEnumPrompt",
                    {
                        type: "POST",
                        data: { promptJson: promptModel.toJsonString() },
                        dataType: "JSON",
                        success: function (data)
                        {
                            menu = skyward.modalLayer.showDialog("Multiple Selection Builder", data.html, buttons, options);

                            $.when(menu).done(function ()
                            {
                                skyward.window.notifyDOMLoad(".enumMultiSelectOptions");
                            });
                        }
                    });

                return deferred;


                function scrapeValuesAndClose()
                {
                    const enumValue = [];

                    menu.content.find(".checkbox:checked").each(function ()
                    {
                        const value = $(this).val();
                        enumValue.push([value, value]);
                    });

                    menu.close();
                    deferred.resolve(enumValue);
                }

                function cancel()
                {
                    deferred.reject();
                    menu.close();
                }
            }

            // launches a custom prompt value dialog
            function openCustomPromptValueDialog(button, promptModel)
            {
                return skyward.customprompts.editCustomPromptValues(button, promptModel);
            }

            var selectPrompt = function (prompt)
            {
                const promptList = prompt.closest(".currentPrompts");
                const selected = promptList.find(".selected");
                selected.removeClass("selected");

                prompt.addClass("selected");

                const promptManager = promptList.closest(".PromptManager");
                const deleteButton = promptManager.find(".delete_report_prompt");
                const usagesButton = promptManager.find(".usage_locations");
                const editButton = promptManager.find(".edit_report_prompt");
                const linkButton = promptManager.find(".link_report_prompt");
                const unlinkButton = promptManager.find(".unlink_report_prompt");
                disable(editButton, false);
                const promptModel = promptCollection.Get(prompt.data().promptUniqueId);
                if (promptModel.InUse || (promptModel.LinkedPromptUniqueId && promptCollection.Get(promptModel.LinkedPromptUniqueId).InUse))
                {
                    disable(deleteButton, true);
                    disable(usagesButton, false);
                }
                else
                {
                    disable(deleteButton, false);
                    disable(usagesButton, true);
                }
                if (promptCollection.GetLinkablePrompts(promptModel).length > 0)
                {
                    if (!promptModel.LinkedPromptUniqueId)
                    {
                        linkButton.show();
                        unlinkButton.hide();
                    }
                    else
                    {
                        linkButton.hide();
                        unlinkButton.show();
                    }
                }
                else
                {
                    if (promptModel.LinkedPromptUniqueId)
                    {
                        linkButton.hide();
                        unlinkButton.show();
                        unlinkButton.focus();
                    }
                    else
                    {
                        linkButton.hide();
                        unlinkButton.hide();
                        editButton.focus();
                    }
                }
            };

            // this method belongs in a view and should notify the view model the prompt has been deleted
            function deletePrompt(deleteButton)
            {
                if (!skyward.buttonManager.isElementDisabled(deleteButton))
                {
                    var promptManager = deleteButton.closest(".PromptManager");
                    var selectedPrompt = promptManager.find(".selected");
                    if (selectedPrompt.length)
                    {

                        var promptUniqueId = selectedPrompt.data().promptUniqueId;
                        if (promptUniqueId.length)
                        {
                            var editButton = promptManager.find(".edit_report_prompt");
                            var linkedPrompt = promptCollection.Get(promptCollection.Get(promptUniqueId).LinkedPromptUniqueId);
                            if (linkedPrompt !== null && linkedPrompt !== undefined)
                            {
                                promptCollection.Delete(linkedPrompt.PromptUniqueId);
                            }
                            promptCollection.Delete(promptUniqueId);
                            selectedPrompt.parent().remove();
                            disable(deleteButton, true);
                            disable(editButton, true);
                        }
                    }
                }
            }

            function deletePromptKey(event, promptEditorDialog)
            {
                const selected = promptEditorDialog.container.find(".selected");
                if (selected.length && keyboardCommands.isDeletePromptPressed(event))
                {
                    const promptManager = selected.closest(".PromptManager");
                    const deleteButton = promptManager.find(".delete_report_prompt");
                    deletePrompt(deleteButton);
                }
            }

            function openUsageLocations(button)
            {
                if (!skyward.buttonManager.isElementDisabled(button))
                {
                    const selectedPrompt = button.parents(".PromptManager").find(".selected");
                    let promptModel;
                    if (selectedPrompt.length)
                    {
                        const promptUniqueId = selectedPrompt.data().promptUniqueId;
                        if (promptUniqueId.length)
                        {
                            promptModel = promptCollection.Get(promptUniqueId);
                        }
                    }

                    if (promptModel)
                    {
                        usageLocations.openModal("prompt-usages-dialog", promptModel.getUsages);
                    }
                }
            }

            function editPrompt(editButton)
            {
                if (!skyward.buttonManager.isElementDisabled(editButton))
                {
                    var selectedPrompt = editButton.parents('.PromptManager').find(".selected");
                    if (selectedPrompt.length)
                    {
                        var promptUniqueId = selectedPrompt.data().promptUniqueId;
                        if (promptUniqueId.length)
                        {
                            getPromptDetailsView(null, null, promptUniqueId);
                        }
                    }
                }
            }

            function linkPrompt(linkButton)
            {
                var promptManager = linkButton.parents('.PromptManager');
                var selectedPrompt = promptManager.find(".selected");
                if (selectedPrompt.length)
                {
                    var promptUniqueId = selectedPrompt.data().promptUniqueId;
                    if (promptUniqueId.length)
                    {
                        getPromptLinkView(promptUniqueId, promptManager);
                    }
                }
            }

            function unlinkPrompt(unlinkButton)
            {
                var promptManager = unlinkButton.parents('.PromptManager');
                var selectedPrompt = promptManager.find(".selected");
                if (selectedPrompt.length)
                {
                    var linkedPrompts = selectedPrompt.parent().find(".promptInput");
                    var lowPromptInput = $(linkedPrompts[0]);
                    var highPromptInput = $(linkedPrompts[1]);

                    highPromptInput.append(lowPromptInput.find("svg").clone());
                    highPromptInput.find(".linkedPromptSeparator").remove();

                    var highPrompListItem = $("<li/>");
                    highPrompListItem.addClass("ui-state-default");
                    highPrompListItem.append(highPromptInput);

                    lowPromptInput.parent().after(highPrompListItem);

                    var lowPrompt = promptCollection.Get(lowPromptInput.data().promptUniqueId);
                    var highPrompt = promptCollection.Get(highPromptInput.data().promptUniqueId);
                    highPrompt.LinkedPromptUniqueId = null;
                    highPrompt.LinkedPromptGuid = null;
                    lowPrompt.LinkedPromptUniqueId = null;
                    lowPrompt.LinkedPromptGuid = null;

                    highPrompt.refreshInUse();
                    lowPrompt.refreshInUse();

                    var linkButton = promptManager.find(".link_report_prompt");
                    linkButton.show();
                    unlinkButton.hide();
                    linkButton.focus();
                }
            }

            var promptEditor = skyward.mvvm.newViewModel(function ()
            {
                var self = this;
                return {
                    open: function ()
                    {
                        var deferred = $.Deferred();
                        var promptEditorDialog;
                        var promptsEditorHtml;
                        var promptsListHtml;
                        $.ajax(skyward.global.virtualDir + "Reporting/Report/GetPrompts",
                            {
                                type: "POST",
                                dataType: "JSON",
                                data: { promptJson: promptCollection.toJsonString() }
                            }).then(runPromptEditor);

                        return deferred;


                        function runPromptEditor(ajaxResult)
                        {
                            promptCollection.RefreshAllPromptsInUse();
                            promptsEditorHtml = $(ajaxResult.html);
                            promptsListHtml = promptsEditorHtml.find("ul").first();
                            var promptManager_View = skyward.mvvm.newView();

                            var buttons = [
                                { text: "Save", click: saveAndClose, icon: "Save" },
                                { text: "Cancel", icon: "Cancel", click: cancelAndClose }
                            ];
                            var options =
                            {
                                closeOnClick: false,
                                draggable: true,
                                afterClose: function ()
                                {
                                    self.stopObservingEverything();
                                    promptManager_View.announce("close");
                                    deferred.resolve();
                                },
                                automationID: "prompts-dialog"
                            };
                            promptEditorDialog = skyward.modalLayer.showDialog("Prompts", promptsEditorHtml, buttons, options);

                            promptEditorDialog.bodyPromise.done(function ()
                            {
                                skyward.window.notifyDOMLoad("#" + promptEditorDialog.container[0].id);

                                promptCollection.TakeSnapshot();

                                self.observe(promptCollection, "model:Label", function (model)
                                {
                                    promptsEditorHtml.find(".promptInput[data-prompt-unique-id='" + model.PromptUniqueId + "']").find(".promptLabelText").text(model.Label);
                                });

                                var setIcon = function (model)
                                {
                                    model.getIconType();
                                    promptsEditorHtml.find(".promptInput[data-prompt-unique-id='" + model.PromptUniqueId + "']")
                                        .find('svg').replaceWith(skyward.svg.getSvg(model.getIconType(), 'TreeBrowse'));
                                };
                                self.observe(promptCollection, "model:Type", setIcon);
                                self.observe(promptCollection, "model:InputType", setIcon);

                                promptsEditorHtml.find(".sortable").sortable({ stop: recalculateSortOrder });
                                promptEditorDialog.container.find(".promptInput").on("click", function () { selectPrompt($(this)); }).on("dblclick", function () { editPrompt($(this)); });
                                promptEditorDialog.container.find(".add_report_prompt").on("click", function () { getPromptDetailsView(null, { dialog: promptEditorDialog }); });
                                promptEditorDialog.container.find(".delete_report_prompt").on("click", function () { deletePrompt($(this)); });
                                promptEditorDialog.container.find(".usage_locations").on("click", function () { openUsageLocations($(this)); });
                                promptEditorDialog.container.find(".edit_report_prompt").on("click", function () { editPrompt($(this)); });
                                promptEditorDialog.container.find(".link_report_prompt").on("click", function () { linkPrompt($(this)); });
                                promptEditorDialog.container.find(".unlink_report_prompt").on("click", function () { unlinkPrompt($(this)); });
                                promptEditorDialog.container.on("keydown", function (e) { deletePromptKey(e, promptEditorDialog) });

                                self.observe(addPromptEvents, "newPrompt", function (data)
                                {
                                    var newPromptListItem = createNewPromptListItem(data.promptModel, promptsListHtml);
                                    promptsListHtml.append(newPromptListItem);
                                    promptCollection.RefreshAllPromptsInUse();
                                });
                            });

                            function createNewPromptListItem(model, promptsList)
                            {
                                var newItem = $(promptsList.find(".hidden").clone()).removeClass('hidden');
                                newItem.find('.extraOptionLabel').text(model.Label);
                                newItem.find('svg').replaceWith(skyward.svg.getSvg(model.getIconType(), 'TreeBrowse'));

                                var promptInput = newItem.find(".promptInput");
                                promptInput.data().promptUniqueId = model.PromptUniqueId;
                                promptInput.attr("data-prompt-unique-id", model.PromptUniqueId);
                                promptInput.on("click", function () { selectPrompt($(this)); });

                                return newItem;
                            }

                            function cancelAndClose()
                            {
                                self.stopObservingEverything(); // Prevent unnecessary callbacks to hide/show delete icon (we're about to close the view)
                                promptCollection.Revert();
                                promptEditorDialog.close();
                            }

                            function saveAndClose()
                            {
                                self.stopObservingEverything();
                                promptManager_View.announce("save");
                                if (promptCollection.PromptsIsDirty())
                                {
                                    updatePromptUsages(designer.find(".reportDesignerPromptFieldsMenu"));
                                    updateHiddenField();
                                    initDesignerUI(entireCanvas.find('.sectionSelected'));
                                }
                                promptEditorDialog.close();
                            }

                            function recalculateSortOrder()
                            {
                                promptsEditorHtml.find("li").not(".hidden").find(".promptInput").each(function (index) { promptCollection.Get($(this).data("promptUniqueId")).Order = index; });
                            }
                        }
                    }
                };
            });

            var updatePromptUsages = function (extraColumnOptions)
            {
                populateExtraColumnOptionsWithPrompts(extraColumnOptions);

                promptCollection.forEach(function (prompt)
                {
                    if (prompt.InUse)
                    {
                        designer.find(".formTextColumn").each(function ()
                        {
                            var column = $(this);
                            column.find(".column_content").text(getColumnLabelWithPreText(column));
                        });

                        designer.find(".jsColumn[data-prompt-unique-id='" + prompt.PromptUniqueId + "']").each(function ()
                        {
                            var column = $(this);
                            column.data("designerText", prompt.Label).attr("data-designer-text", prompt.Label);
                            var displayText = getColumnLabelWithPreText(column);
                            column.find(".column_content").text(displayText);
                        });

                        if (isHiddenSelector.elements.left.is(":visible") && isHiddenSelector.elements.left.data("promptUniqueId") === prompt.PromptUniqueId)
                        {
                            isHiddenSelector.setIsHiddenOperandPrompt(prompt, isHiddenSelector.elements.left);
                        }

                        if (isHiddenSelector.elements.right.is(":visible") && isHiddenSelector.elements.right.data("promptUniqueId") === prompt.PromptUniqueId)
                        {
                            isHiddenSelector.setIsHiddenOperandPrompt(prompt, isHiddenSelector.elements.right);
                        }

                        if (editorPrintAtBottomOfPage.is(":visible") && editorPrintAtBottomOfPage.data().promptUniqueId === prompt.PromptUniqueId)
                        {
                            editorPrintAtBottomOfPage.find("span:last").text(prompt.Label);
                        }

                        if (editorPrintReportFooterOnNewPage.is(":visible") && editorPrintReportFooterOnNewPage.data().promptUniqueId == prompt.PromptUniqueId)
                        {
                            editorPrintReportFooterOnNewPage.find("span:last").text(prompt.Label);
                        }

                        if (editorRepeatLineAfterStart.is("visible") && editorRepeatLineAfterStart.data().promptUniqueId == prompt.PromptUniqueId)
                        {
                            editorRepeatLineAfterStart.find("span:last").text(prompt.Label);
                        }
                    }
                });
            };

            var populateExtraColumnOptionsWithPrompts = function (extraColumnOptions)
            {
                var ul = extraColumnOptions.find(".reportDesignerPromptsList").empty();

                promptCollection.filter(function (prompt) { return (prompt.Type != enumMaps.PromptTypes.UserInstructions && prompt.Type != enumMaps.PromptTypes.SectionHeader); }).forEach(function (prompt)
                {
                    var li = $("<li/>").addClass("promptItem").addClass("jsDraggable").addClass("ui-draggable").attr({
                        "data-prompt-unique-id": prompt.PromptUniqueId,
                        "data-page-section-column-type": "PromptValue",
                        "data-field-name": "{_prompt." + prompt.PromptUniqueId + "}",
                        "data-designer-text": prompt.Label,
                        "data-field-type": prompt.InputType
                    });
                    var span = $("<span/>").addClass("extraOptionSpan").addClass('outlineSelected');
                    var label = $("<span/>").addClass("decorativeLabel visualLabel extraOptionLabel").text(prompt.Label);

                    span.append(label).appendTo(li.appendTo(ul));
                    ul.append(li);
                });
            };

            var getPromptTypeByEnumValue = function (enumValuePosition)
            {
                var keys = Object.keys(enumMaps.PromptTypes);
                for (var i = 0; i < keys.length; i++)
                {
                    if (enumMaps.PromptTypes[keys[i]] === enumValuePosition)
                    {
                        return keys[i];
                    }
                }

                return null;
            };

            function getModuleAndObjectFromFieldPath(fieldPath)
            {
                if (fieldPath)
                {
                    var parts = fieldPath.split('.');

                    if (parts.length > 1)
                    {
                        return {
                            dataModule: parts[0],
                            dataObject: parts[1],
                        };
                    }
                }

                return {};
            }

            function initPromptViewModel(dialog, promptModel)
            {
                var promptHtml = dialog.content;

                promptHtml.data("promptUniqueId", promptModel.PromptUniqueId);

                // where is the view model?
                // See note by "NewViewModel" in skyward.base.mvvm.js - The observer inside this call to newView effectively *makes* this a viewmodel.
                return skyward.mvvm.newView(function ()
                {
                    var _observer = skyward.mvvm.newObserver();
                    var loadingPromptValues = false;

                    // launches the multi-select field picker
                    var getPromptField = function ()
                    {
                        var options = { treeBrowseOptions: { seekField: (promptModel.Columns || "").split(' ')[0], fieldsOfType: promptModel.InUse ? promptModel.InputType : "" } };
                        selectFieldDialog(fieldSelector, promptModel.Module, promptModel.Object, options).done(function (listData, moduleObjectData)
                        {
                            loadingPromptValues = true;
                            const fieldPath = moduleObjectData.fieldModule + '.' + moduleObjectData.fieldObject + '.' + listData.absoluteFieldPath;

                            promptModel.InputType = skyward.reporting.reportColumn.getFieldType(listData);
                            if (promptModel.Type == enumMaps.PromptTypes.SingleSelect || promptModel.Type == enumMaps.PromptTypes.MultipleSelect) // if Prompt Type didn't get forcefully set to something else
                            {
                                promptModel.FieldPath = fieldPath;
                                promptModel.Module = moduleObjectData.fieldModule;
                                promptModel.Object = moduleObjectData.fieldObject;
                                promptModel.RelativeFieldPath = listData.fieldName;
                                promptModel.FiltersJson = "";
                                promptModel.Sorts = "";
                                promptModel.Columns = listData.absoluteFieldPath;
                                promptModel.Value = "";
                                promptModel.SelectionOptions = buildInitialSelectionOptions();
                            }
                            replaceDistinctMultiSelect(fieldPath, null, null, null);
                            loadingPromptValues = false;
                        });
                    };

                    function replaceDistinctMultiSelect(fieldPath, columns, sorts, selectedValues)
                    {
                        if (fieldPath)
                        {
                            const selectedValuesJson = skyward.extensions.encodeHtml(JSON.stringify(selectedValues ?? []));

                            skyward.validation.clearValidationMessages(promptHtml, $());
                            $.ajax(skyward.global.virtualDir + "Reporting/Report/GetNewDistinctMultiSelectFromFieldPath",
                                {
                                    type: "POST",
                                    dataType: "JSON",
                                    data:
                                    {
                                        fieldPath: fieldPath,
                                        columns: columns,
                                        sorts: sorts,
                                        selectedValues: selectedValuesJson
                                    }
                                }).done(function (data)
                                {
                                    if (data.valid)
                                    {
                                        selectionOptionsInput.replaceWith(data.html);

                                        selectionOptionsInput = selectionOptionsFieldSet.find(".selectionDMSE");
                                        skyward.distinctMultiSelectEEL.registerDistinctMultiSelectEEL(selectionOptionsInput);

                                        selectionOptionsBackingData = selectionOptionsInput.find(".dmsBackingData");
                                        selectionOptionsBackingData.on('change', setValueFromSelectionOptionsBackingData);
                                    }
                                    else
                                    {
                                        skyward.validation.displayValidationMessages({ errors: data.errors }, promptHtml, $());
                                    }
                                });
                        }
                    }

                    function joinSelectionOptionsForDisplay(selectionOptions)
                    {
                        let displayValues = [];
                        for (const option of selectionOptions)
                        {
                            displayValues.push(option[1]);
                        }

                        return displayValues.join(";");
                    }

                    var labelInput = promptHtml.find("[data-prompt-unique-id='" + promptModel.PromptUniqueId + "']").addBack("[data-prompt-unique-id='" + promptModel.PromptUniqueId + "']").find('.promptLabelText');
                    const promptTypeInput = promptHtml.find(".promptTypes");
                    const promptTypeComboBoxOptions = promptTypeInput.find(".option");
                    var dataTypeInput = promptHtml.find(".inputTypes");
                    var presenceTypeCombo = promptHtml.find(".presenceTypes");
                    var defaultValueFieldSet = promptHtml.find('.DefaultValueFieldSet');
                    var userInstructionsTextFieldSet = promptHtml.find(".userInstructionsTextFieldSet");

                    var dataTypeFieldSet = dataTypeInput.parent();
                    var presenceTypeFieldSet = presenceTypeCombo.parent();

                    var selectionOptionsFieldSet = promptHtml.find(".fieldSelectionOptions");
                    var selectionOptionsInput = selectionOptionsFieldSet.find(".selectionDMSE");
                    var selectionOptionsBackingData = selectionOptionsInput.find(".dmsBackingData");

                    var enumSelectionOptionsFieldSet = promptHtml.find(".enumSelectionOptions");
                    var enumMultipleSelector = enumSelectionOptionsFieldSet.find(".enumMultipleSelector");
                    var enumSelectionOptionInput = enumSelectionOptionsFieldSet.find(".enumSelectionInput");

                    var customPromptValueSelectionOptionsFieldSet = promptHtml.find(".customPromptValueSelectionOptions");
                    var customPromptValueMultipleSelector = customPromptValueSelectionOptionsFieldSet.find(".customPromptValueMultipleSelector");
                    var customPromptValueSelectionOptionInput = customPromptValueSelectionOptionsFieldSet.find(".customPromptValueSelectionInput");

                    var defaultValueInput = defaultValueFieldSet.find('.defaultValue');

                    const prefillPromptFieldSet = promptHtml.find(".prefillPromptFieldSet");
                    const prefillPromptInput = prefillPromptFieldSet.find(".prefillPrompt");

                    var userInstructionsTextArea = userInstructionsTextFieldSet.find(".userInstructions");

                    var booleanDisplayOverrideValues = promptHtml.find('.BooleanDisplayValues');
                    var booleanDisplayOverrideTrueValue = booleanDisplayOverrideValues.find(".TrueValue");
                    var booleanDisplayOverrideFalseValue = booleanDisplayOverrideValues.find(".FalseValue");
                    var removeEnvironmentVariableButton = promptHtml.find(".removeEnvironmentVariableButton");

                    var fieldSelector = promptHtml.find('.promptBuilderFieldSelect');
                    var fieldPathInput = promptHtml.find(".multiSelectField");
                    var sortDefinitionFieldSet = promptHtml.find(".SortDefinition");
                    var columnDefinitionFieldSet = promptHtml.find(".ColumnDefinition");
                    var editFieldsToDisplay = promptHtml.find(".editFieldsToDisplay");
                    var promptFieldSelectorFieldSet = promptHtml.find(".promptFieldSelector");
                    var selectionFilterFieldSet = promptHtml.find(".selectionFilter");
                    var filterBuilderLaunchButton = promptHtml.find(".filterBuilderLaunchButton");
                    var filterViewValue = promptHtml.find(".filterViewValue");
                    var multiSelectSortsInput = promptHtml.find(".multiSelectSorts");
                    var multiSelectViewsInput = promptHtml.find(".multiSelectViews");

                    const promptIsHiddenSelector = skyward.reporting.userCondition.init(promptHtml.find(".jsIsHiddenCondition"))
                    const hiddenSelectorWarning = promptHtml.find(".jsHiddenSelectorWarning");
                    toggleHiddenSelectorWarning();

                    let isHighLinkedPrompt = false;

                    if (promptModel.LinkedPromptUniqueId)
                    {
                        var linkedPrompt = promptCollection.Get(promptModel.LinkedPromptUniqueId);
                        if (promptModel.Order > linkedPrompt.Order)
                        {
                            isHighLinkedPrompt = true;
                        }
                    }

                    if (isHighLinkedPrompt)
                    {
                        promptIsHiddenSelector.elements.wrapper.hide();
                    }
                    else
                    {
                        promptIsHiddenSelector.elements.wrapper.show();

                        promptIsHiddenSelector.setIsHiddenCondition(promptModel.HiddenCondition, "Prompt", getSvgByDataType, null, null);
                        promptIsHiddenSelector.elements.wrapper.find('.userConditionValueSelectorButton').on('click', function ()
                        {
                            const allPrompts = skyward.reporting.prompts.promptCollection.toJsonObject();
                            const allPromptsExceptCurrent = allPrompts.filter(function (prompt)
                            {
                                return prompt.PromptUniqueId != promptModel.PromptUniqueId;
                            });

                            let operandButton = $(this);
                            if (operandButton.hasClass("leftOperand"))
                            {
                                const allowOptions =
                                {
                                    fieldSelection: false,
                                    currentColumnValue: false,
                                    prompts: true,
                                    alwaysHidden: false,
                                }
                                promptIsHiddenSelector.getUserConditionOperandFilterValueSelector(operandButton, allowOptions, null, allPromptsExceptCurrent, null, null, true, valueSelectorClickEvents);
                            }
                            else
                            {
                                let leftOperandFieldType = getEditorFieldDataType(promptIsHiddenSelector.elements.left);
                                let leftPromptUniqueID = promptIsHiddenSelector.elements.left.data().promptUniqueId;
                                const allowOptions =
                                {
                                    fieldSelection: false,
                                    currentColumnValue: false,
                                    prompts: false,
                                    alwaysHidden: true,
                                }
                                promptIsHiddenSelector.getUserConditionOperandFilterValueSelector(operandButton, allowOptions, null, allPromptsExceptCurrent, leftOperandFieldType, null, false, valueSelectorClickEvents, leftPromptUniqueID);
                            }
                        });

                        promptIsHiddenSelector.elements.wrapper.on("change", setIsHidden);
                        promptIsHiddenSelector.elements.wrapper.on("blur", "input", setIsHidden);

                        function setIsHidden()
                        {
                            promptModel.HiddenCondition = promptIsHiddenSelector.getUpdatedIsHiddenCondition();
                            promptIsHiddenSelector.setIsHiddenCondition(promptModel.HiddenCondition, "Prompt", getSvgByDataType, null, null);
                            toggleHiddenSelectorWarning();
                        }
                    }

                    function toggleHiddenSelectorWarning()
                    {
                        hiddenSelectorWarning.hide();
                        if (promptModel.HiddenCondition != null && !isHiddenSelector.isConditionAlwaysHidden(promptModel.HiddenCondition))
                        {
                            if (promptModel.tempRelatedSearchParameter && promptModel.tempRelatedSearchParameter.required)
                            {
                                hiddenSelectorWarning.show();
                            }
                            else
                            {
                                skyward.reporting.searchParameters.model.forEach(function (searchParameter)
                                {
                                    if (searchParameter.required && searchParameter.value === `{_prompt.${promptModel.PromptGuid}}`)
                                    {
                                        hiddenSelectorWarning.show();
                                    }
                                });
                            }
                        }
                    }

                    function showAllPromptTypeComboBoxOptions()
                    {
                        promptTypeComboBoxOptions.show();
                    }

                    function togglePromptTypeComboBoxOption(promptTypes, toggle)
                    {
                        promptTypeComboBoxOptions.each(function (i)
                        {
                            const option = $(this);
                            if (promptTypes.includes(option.val()))
                            {
                                option.toggle(toggle);
                                return true;
                            }
                        });
                    }

                    fieldSelector.on("click", getPromptField);
                    fieldPathInput.on("click", getPromptField);

                    labelInput.on("change", function ()
                    {
                        promptModel.Label = labelInput.val();
                    });
                    promptTypeInput.on("change", function ()
                    {
                        promptModel.Type = enumMaps.PromptTypes[promptTypeInput.val()];
                    });
                    dataTypeInput.on("change", function ()
                    {
                        promptModel.SelectionOptions = buildInitialSelectionOptions();
                        promptModel.InputType = dataTypeInput.val();
                    });
                    presenceTypeCombo.on("change", function ()
                    {
                        promptModel.PresenceType = presenceTypeCombo.val();
                    });
                    defaultValueInput.add(userInstructionsTextArea).on('change', function ()
                    {
                        promptModel.Value = $(this).val();
                    });
                    prefillPromptInput.on("change", function ()
                    {
                        promptModel.PrefillPrompt = prefillPromptInput.is(":checked");
                    });

                    // To get height, put the text into an offscreen textarea with identical styling (except height: height must be <= 1 x line-height,
                    // because deleting text does not alter scrollHeight) then measure the textarea's scrollHeight.
                    // Until this becomes a framework feature, creating, styling and positioning a resizeHelper is the responsibility of the developer.
                    var calculateTextAreaHeight = function (text, jqResizeHelper)
                    {
                        jqResizeHelper.text(text).show();
                        var height = Math.ceil(jqResizeHelper[0].scrollHeight);
                        jqResizeHelper.hide();
                        return height;
                    };

                    var setValueFromSelectionOptionsBackingData = function ()
                    {
                        const selectedOptionsObject = JSON.parse(selectionOptionsBackingData.val());

                        const selectionOptions = buildInitialSelectionOptions();

                        for (const key of Object.keys(selectedOptionsObject))
                        {
                            selectionOptions.push([key, key]);
                        }

                        promptModel.SelectionOptions = selectionOptions;
                    };

                    selectionOptionsBackingData.on('change', setValueFromSelectionOptionsBackingData);
                    booleanDisplayOverrideTrueValue.on("change", function ()
                    {
                        promptModel.DisplayValueOverrides.true = booleanDisplayOverrideTrueValue.val();
                    });
                    booleanDisplayOverrideFalseValue.on("change", function ()
                    {
                        promptModel.DisplayValueOverrides.false = booleanDisplayOverrideFalseValue.val();
                    });

                    removeEnvironmentVariableButton.on('click', function ()
                    {
                        promptModel.Value = "";
                    });
                    multiSelectViewsInput.on('change', function ()
                    {
                        promptModel.Columns = multiSelectViewsInput.val();
                    });
                    multiSelectSortsInput.on('change', function ()
                    {
                        promptModel.Sorts = multiSelectSortsInput.val();
                    });

                    enumMultipleSelector.on("click", function ()
                    {
                        openMultiSelectEnumDialog(this, promptModel).then(function (value)
                        {
                            promptModel.SelectionOptions = value;
                        });
                    });
                    enumSelectionOptionInput.on("click", function ()
                    {
                        openMultiSelectEnumDialog(enumMultipleSelector, promptModel).then(function (value)
                        {
                            promptModel.SelectionOptions = value;
                        });
                    });

                    customPromptValueMultipleSelector.on("click", function ()
                    {
                        openCustomPromptValueDialog(this, promptModel).then(function (value)
                        {
                            promptModel.SelectionOptions = value;
                        });
                    });
                    customPromptValueSelectionOptionInput.on("click", function ()
                    {
                        openCustomPromptValueDialog(customPromptValueMultipleSelector, promptModel).then(function (value)
                        {
                            promptModel.SelectionOptions = value;
                        });
                    });

                    filterViewValue.on("change", function ()
                    {
                        var filterViewData = filterViewValue.data();
                        if (filterViewData.filterCondition)
                        {
                            promptModel.FiltersJson = JSON.stringify(filterViewData.filterCondition);
                        }
                        else
                        {
                            promptModel.FiltersJson = null;
                        }
                    });
                    editFieldsToDisplay.on("click", function ()
                    {
                        var editFieldsToDisplayData = editFieldsToDisplay.data();
                        var args = {
                            fieldsToDisplayInfo: promptModel.Columns,
                            fieldPath: fieldPathInput.val(),
                            module: editFieldsToDisplayData.module,
                            object: editFieldsToDisplayData.object,
                            callback: function (fieldsToDisplayString) { promptModel.Columns = fieldsToDisplayString; }
                        };
                        skyward.multiselectcolumnbuilder.launch(args);
                    });

                    var anyPropertyChanged = function ()
                    {
                        const isDMSEType = (promptModel.Type == enumMaps.PromptTypes.SingleSelect || promptModel.Type == enumMaps.PromptTypes.MultipleSelect);
                        const hasUniqueId = promptModel.Value && promptModel.Value?.startsWith("{") && promptModel.Value?.endsWith("}");
                        const isEnum = promptModel.InputType?.startsWith("String Enum") ?? false;
                        const isBoolean = promptModel.InputType === DATA_TYPES.BOOLEAN;
                        const isReportSort = promptModel.Type === enumMaps.PromptTypes.ReportSort;
                        const isFreeformText = promptModel.Type == enumMaps.PromptTypes.FreeformText;
                        const isUserInstructions = promptModel.Type == enumMaps.PromptTypes.UserInstructions;
                        const isSectionHeader = promptModel.Type == enumMaps.PromptTypes.SectionHeader;
                        const isStandardFilterPrompt = promptModel.Type == enumMaps.PromptTypes.StandardFilter;
                        const hasFieldPath = Boolean(promptModel.FieldPath);
                        const isCustomPrompt = promptModel.Type == enumMaps.PromptTypes.CustomSingleSelect || promptModel.Type == enumMaps.PromptTypes.CustomMultipleSelect;

                        if (isDMSEType && hasUniqueId)
                        {
                            promptModel.Value = "";
                        }

                        //hide everything
                        enumSelectionOptionsFieldSet.hide();
                        selectionFilterFieldSet.hide();
                        sortDefinitionFieldSet.hide();
                        columnDefinitionFieldSet.hide();
                        defaultValueFieldSet.hide();
                        selectionOptionsFieldSet.hide();
                        promptFieldSelectorFieldSet.hide();
                        booleanDisplayOverrideValues.hide();
                        removeEnvironmentVariableButton.hide();
                        defaultValueInput.hide();
                        userInstructionsTextFieldSet.hide();
                        presenceTypeFieldSet.hide();
                        dataTypeFieldSet.hide();
                        customPromptValueSelectionOptionsFieldSet.hide();
                        prefillPromptFieldSet.hide();

                        if (isEnum)
                        {
                            dataTypeFieldSet.show();
                            enumSelectionOptionsFieldSet.show();
                            prefillPromptFieldSet.show();

                            if (hasFieldPath)
                            {
                                promptFieldSelectorFieldSet.show();
                            }
                        }
                        else if (isDMSEType)
                        {
                            dataTypeFieldSet.show();
                            promptFieldSelectorFieldSet.show();
                            prefillPromptFieldSet.show();

                            if (hasFieldPath)
                            {
                                selectionOptionsFieldSet.show();
                                sortDefinitionFieldSet.show();
                                columnDefinitionFieldSet.show();
                                selectionFilterFieldSet.show();
                            }

                            fieldSelector.add(fieldPathInput);
                        }
                        else if (isUserInstructions)
                        {
                            userInstructionsTextArea.val(promptModel.Value);
                            userInstructionsTextFieldSet.show();
                        }
                        else if (isCustomPrompt)
                        {
                            dataTypeFieldSet.show();
                            customPromptValueSelectionOptionsFieldSet.show();
                            prefillPromptFieldSet.show();
                        }
                        else if (!isStandardFilterPrompt && !isSectionHeader && !isReportSort)
                        {
                            dataTypeFieldSet.show();
                            prefillPromptFieldSet.show();

                            if (isBoolean)
                            {
                                booleanDisplayOverrideValues.show();
                            }

                            if (hasUniqueId)
                            {
                                removeEnvironmentVariableButton.show();
                            }
                            else
                            {
                                defaultValueInput.show();
                            }

                            if (!isFreeformText && !isBoolean && !isReportSort)
                            {
                                defaultValueFieldSet.show();
                            }
                        }

                        disable(dataTypeInput, isStandardFilterPrompt || hasFieldPath || isFreeformText || isEnum || promptModel.InUse || promptModel.LinkedPromptUniqueId || isReportSort);

                        showAllPromptTypeComboBoxOptions();
                        togglePromptTypeComboBoxOption([enumMaps.PromptTypes.StandardFilter, enumMaps.PromptTypes.ReportSort], false);

                        if (isUserInstructions || isSectionHeader)
                        {
                            disable(promptTypeInput, false);
                        }
                        else if (isStandardFilterPrompt || isReportSort || isBoolean || (promptModel.InUse && isFreeformText))
                        {
                            disable(promptTypeInput, true);
                            togglePromptTypeComboBoxOption([enumMaps.PromptTypes.StandardFilter], true);
                        }
                        else if (promptModel.InUse && (promptModel.Type == enumMaps.PromptTypes.MultipleSelect || promptModel.Type == enumMaps.PromptTypes.CustomMultipleSelect))
                        {
                            togglePromptTypeComboBoxOption(Object.values(enumMaps.PromptTypes), false);

                            const promptTypesToShow = isEnum ? [enumMaps.PromptTypes.MultipleSelect] : [enumMaps.PromptTypes.MultipleSelect, enumMaps.PromptTypes.CustomMultipleSelect];

                            togglePromptTypeComboBoxOption(promptTypesToShow, true);
                        }
                        else if (promptModel.InUse)
                        {
                            disable(promptTypeInput, false);
                            const isNotSingleValuePrompt = !(promptModel.Type === enumMaps.PromptTypes.Value || promptModel.Type === enumMaps.PromptTypes.SingleSelect || promptModel.Type === enumMaps.PromptTypes.CustomSingleSelect);
                            togglePromptTypeComboBoxOption([enumMaps.PromptTypes.MultipleSelect, enumMaps.PromptTypes.CustomMultipleSelect, enumMaps.PromptTypes.FreeformText, enumMaps.PromptTypes.UserInstructions, enumMaps.PromptTypes.SectionHeader], isNotSingleValuePrompt);
                        }
                        else
                        {
                            disable(promptTypeInput, false);
                        }

                        if (promptModel.SpecificTypeRequired)
                        {
                            disable(promptTypeInput, true);
                            disable(dataTypeInput, true);
                        }

                        if (!isFreeformText && !isStandardFilterPrompt && !isUserInstructions && !isSectionHeader && !isReportSort)
                        {
                            presenceTypeFieldSet.show();
                        }

                        if (promptModel.isRequired())
                        {
                            disable(presenceTypeCombo, true);

                            if (promptModel.PresenceType !== "Required") // TODO This should not be here, but where does it go? Somewhere in the model? In C#?
                            {
                                promptModel.PresenceType = "Required";
                            }
                        }

                        dialog.animateModalContents();
                    };

                    var propertyChangedEvents = {
                        InputType: function (newval, oldval) // DataType
                        {

                            dataTypeInput.val(newval);

                            if (promptModel.Value !== "" && oldval !== undefined && oldval != newval)
                            {
                                promptModel.Value = "";
                                promptModel.SelectionOptions = buildInitialSelectionOptions();
                            }

                            if (newval && newval.startsWith("String Enum"))
                            {
                                dataTypeInput.val(DATA_TYPES.STRING);

                                if (promptModel.Type === enumMaps.PromptTypes.Value)
                                {
                                    promptModel.Type = enumMaps.PromptTypes.SingleSelect;
                                }
                            }
                            else if (newval === DATA_TYPES.BOOLEAN)
                            {
                                if (promptModel.DisplayValueOverrides === undefined)
                                {
                                    promptModel.DisplayValueOverrides = { "true": "Yes", "false": "No" };
                                }
                            }
                            else if (newval !== undefined)
                            {
                                promptModel.DisplayValueOverrides = {};
                            }

                            if (newval == DATA_TYPES.BOOLEAN)
                            {
                                promptModel.Type = enumMaps.PromptTypes.Value;
                            }
                        },
                        FieldPath: function (newval, oldval)
                        {
                            fieldPathInput.val(newval);

                            if (newval !== oldval)
                            {
                                var oldData = getModuleAndObjectFromFieldPath(oldval);
                                var newData = getModuleAndObjectFromFieldPath(newval);

                                filterBuilderLaunchButton.data('module', newData.dataModule);
                                filterBuilderLaunchButton.data('root-module', newData.dataModule);
                                filterBuilderLaunchButton.data('object', newData.dataObject);
                                filterBuilderLaunchButton.data('root-object', newData.dataObject);

                                editFieldsToDisplay.data('module', newData.dataModule);
                                editFieldsToDisplay.data('object', newData.dataObject);

                                if (!loadingPromptValues)
                                {
                                    if (newData.dataModule !== oldData.dataModule || newData.dataObject !== oldData.dataObject)
                                    {
                                        promptModel.Columns = null;
                                        promptModel.Sorts = null;
                                        promptModel.FiltersJson = "";
                                    }

                                    replaceDistinctMultiSelect(newval, null, null, null);
                                }
                            }
                        },
                        Value: function (newval, oldval)
                        {
                            if (oldval != newval)
                            {
                                const hasUniqueId = newval && newval.startsWith("{") && newval.endsWith("}");

                                selectionOptionsInput.val("");

                                if (!hasUniqueId)
                                {
                                    defaultValueInput.val(newval);
                                }
                            }
                        },
                        PrefillPrompt: function (newval)
                        {
                            prefillPromptInput.prop("checked", newval);
                        },
                        SelectionOptions: function (newval, oldval)
                        {
                            if (oldval != newval)
                            {
                                if (newval.length === 0)
                                {
                                    skyward.controls.setValue(customPromptValueSelectionOptionInput, "", true);
                                    skyward.controls.setValue(enumSelectionOptionInput, "", true);
                                }
                                else if (promptModel.InputType?.startsWith("String Enum"))
                                {
                                    const enumValuesForDisplay = joinSelectionOptionsForDisplay(newval);
                                    skyward.controls.setValue(enumSelectionOptionInput, enumValuesForDisplay, true);
                                }
                                else if (promptModel.Type == enumMaps.PromptTypes.CustomSingleSelect || promptModel.Type == enumMaps.PromptTypes.CustomMultipleSelect)
                                {
                                    const customValuesForDisplay = joinSelectionOptionsForDisplay(newval);
                                    skyward.controls.setValue(customPromptValueSelectionOptionInput, customValuesForDisplay, true);
                                }
                            }
                        },
                        FiltersJson: function (newval)
                        {
                            filterViewValue.data("filterCondition", newval);
                            filterViewValue.trigger("updateFilterViewerDisplay");
                            promptCollection.RefreshAllPromptsInUse();
                        },
                        Label: function (newval)
                        {
                            labelInput.val(newval);
                            var promptedSection = entireCanvas.find("[data-sort-prompt-guid='" + promptModel.PromptGuid + "']");
                            promptedSection.data("sortPromptLabel", promptModel.Label); // This is not mvvm, so when possible it should be made the responsibility of a listener/viewModel for the value selector that needs this data
                        },
                        PresenceType: function (newval)
                        {
                            presenceTypeCombo.val(newval);
                        },
                        Type: function (newval, oldval)
                        {
                            var typeString = getPromptTypeByEnumValue(newval);

                            if (typeString)
                            {
                                promptTypeInput.val(typeString);

                                if (newval == enumMaps.PromptTypes.FreeformText || newval == enumMaps.PromptTypes.UserInstructions || newval == enumMaps.PromptTypes.SectionHeader)
                                {
                                    promptModel.FieldPath = "";
                                    promptModel.InputType = DATA_TYPES.STRING;
                                    promptModel.PresenceType = "OptionalAllValues";
                                }
                                else if ((oldval === enumMaps.PromptTypes.SingleSelect || oldval === enumMaps.PromptTypes.MultipleSelect) &&
                                    (newval !== enumMaps.PromptTypes.SingleSelect && newval !== enumMaps.PromptTypes.MultipleSelect))
                                {
                                    promptModel.Value = "";
                                    promptModel.SelectionOptions = buildInitialSelectionOptions();
                                }
                                else if ((oldval === enumMaps.PromptTypes.CustomSingleSelect || oldval === enumMaps.PromptTypes.CustomMultipleSelect) && newval != oldval)
                                {
                                    promptModel.SelectionOptions = buildInitialSelectionOptions();
                                }
                            }
                        },
                        DisplayValueOverrides: function (newval, oldval)
                        {
                            if (oldval !== newval && !$.isEmptyObject(newval))
                            {
                                booleanDisplayOverrideTrueValue.val(newval.true);
                                booleanDisplayOverrideFalseValue.val(newval.false);
                            }
                        },
                        Columns: function (newval)
                        {
                            multiSelectViewsInput.val(newval);
                            if (!loadingPromptValues && promptModel.FieldPath)
                            {
                                replaceDistinctMultiSelect(promptModel.FieldPath, newval, promptModel.Sorts, promptModel.SelectionOptions);
                            }
                        },
                        Sorts: function (newval)
                        {
                            multiSelectSortsInput.val(newval);
                            if (!loadingPromptValues && promptModel.FieldPath)
                            {
                                replaceDistinctMultiSelect(promptModel.FieldPath, promptModel.Columns, newval, promptModel.SelectionOptions);
                            }
                        }
                    };

                    loadingPromptValues = true;
                    for (var event in propertyChangedEvents) // Trigger each event once to set initial states
                    {
                        propertyChangedEvents[event](promptModel[event]);
                    }
                    loadingPromptValues = false;

                    _observer.observe(promptModel, propertyChangedEvents);
                    _observer.observe(promptModel, skyward.debounce(anyPropertyChanged, 100));

                    anyPropertyChanged();

                    return {
                        dispose: function ()
                        {
                            _observer.stopObservingEverything();
                        }
                    };

                });
            }

            function promptSelectorDialog(launchButton)
            {
                var dialog, display, list, selectPromptButton, addPromptButton, promptType;

                function highlightPrompt()
                {
                    list.find('.selected').removeClass('selected');
                    $(this).addClass('selected');
                }

                function selectPrompt()
                {
                    var selectedPrompt = promptCollection.Get(list.find('.selected').data('promptUniqueId'));

                    if (selectedPrompt)
                    {
                        setSelectedPrompt(selectedPrompt);
                    }

                    close();

                }

                function setSelectedPrompt(selectedPrompt)
                {
                    var svg = skyward.svg.getSvg(selectedPrompt.getIconType(), 'TreeBrowse');
                    display.html(svg).append(document.createTextNode(selectedPrompt.Label));
                    display.data('valueBinding', selectedPrompt.bindingString());
                }

                function buildPromptSelector()
                {
                    var deferred = $.Deferred();

                    list = $('<ul/>').on('dblclick', selectPrompt);
                    var all = [];

                    var first = true;

                    promptCollection.forEach(function (prompt)
                    {
                        if (!promptType || prompt.InputType == promptType)
                        {
                            var promise = skyward.svg.getSvg(prompt.getIconType(), 'TreeBrowse');

                            $.when(promise).done(function (svg)
                            {
                                var span = $('<span/>').data('prompt-unique-id', prompt.PromptUniqueId).addClass('promptInput');
                                span.append(svg).append($('<span/>').text(prompt.Label).addClass('extraOptionLabel'));

                                if (first)
                                {
                                    span.addClass('selected');
                                    first = false;
                                }

                                list.append($('<li/>').append(span.on('click', highlightPrompt)));
                            });

                            all.push(promise);
                        }
                    });

                    $.when.apply($, all).done(function ()
                    {
                        deferred.resolve(list);
                    });

                    return deferred.promise();
                }

                function selectNewPrompt(data)
                {
                    if (data && data.promptModel)
                    {
                        setSelectedPrompt(data.promptModel);
                    }

                    close();
                }

                function launchAddPromptDialog()
                {
                    $.when(skyward.reporting.prompts.getPromptDetailsView({ InputType: promptType }, { treatPromptAsInUse: true })).then(selectNewPrompt);
                }

                function launch(type)
                {
                    promptType = type;
                    display = launchButton.siblings('.userConditionValueSelectorDisplay');

                    selectPromptButton = {
                        text: "Select Prompt",
                        icon: "Check",
                        click: selectPrompt
                    };

                    addPromptButton = {
                        text: "Add Prompt",
                        icon: "AddPlus",
                        click: launchAddPromptDialog
                    };

                    dialog = skyward.modalLayer.showDialog("Select A Prompt", buildPromptSelector, [selectPromptButton, addPromptButton, { text: "Cancel", icon: "Close" }], { closeOnClick: false });
                }

                function close()
                {
                    display.trigger('change');
                    display = null;
                    list = null;
                    selectPromptButton = null;
                    addPromptButton = null;
                    dialog.close();
                    dialog = null;

                }

                return {
                    launch: launch
                };

            }

            return {
                promptCollection: promptCollection,
                importPrompts: importPrompts,
                promptEditor: promptEditor,
                initPromptViewModel: initPromptViewModel,
                getPromptDetailsView: getPromptDetailsView,
                populateExtraColumnOptionsWithPrompts: populateExtraColumnOptionsWithPrompts,
                promptSelectorDialog: promptSelectorDialog
            };
        })();

        const usageLocations = (function ()
        {
            /**
             * @param {string} automationId
             * @param {(model: Object) => UsageNode[]} getUsages A function that is used to iterate over the report, e.g. `PlacesToSearch.traverse`
             * @param {string?} dataPath
             */
            const openModal = function (automationId, getUsages, dataPath = null)
            {
                let usageLocationDialog;

                const buttons = [
                    { text: "Close", icon: "Cancel", click: () => usageLocationDialog.close() }
                ];

                const options =
                {
                    closeOnClick: true,
                    draggable: true,
                    automationID: automationId,
                    preserveFocus: true
                };

                const container = buildUsageLocationsElement(getUsages);

                if (dataPath)
                {
                    const fieldSet = $("<fieldset/>");
                    const label = $("<label/>").text("Data Path ").addClass("title");
                    const textBox = $("<textbox/>", { readonly: "true" }).text(dataPath).addClass("sortInfoPopupLabel ui-disabled");
                    fieldSet.append(label).append(textBox);
                    container.append(fieldSet);
                }

                usageLocationDialog = skyward.modalLayer.showDialog("Usage Locations", container, buttons, options);
                usageLocationDialog.bodyPromise.done(() => skyward.window.notifyDOMLoad(`#${usageLocationDialog.container[0].id}`));
            };

            /**
             * @param {(model: Object) => UsageNode[] | Array} getUsages A function that is used to iterate over the report, e.g. `PlacesToSearch.traverse`
             */
            const buildUsageLocationsElement = function (getUsages)
            {
                const container = $("<div/>").addClass("usageLocationContainer");

                const usages = Array.isArray(getUsages) ? getUsages : getUsages(null);
                for (const node of usages)
                {
                    if (node.inUse())
                    {
                        const innerContainer = $("<div/>");
                        const title = $("<h2/>").text(node.text);
                        innerContainer.append(title);
                        container.append(innerContainer);
                        contentNode(innerContainer, node);
                    }
                }

                return container;
            };

            /**
             * @param {(model: Object) => UsageNode[] | Array} getUsages An array of UsageNodes or a function that is used to retrieve usage nodes, e.g. `PlacesToSearch.traverse`
             */
            const usageLocationOnlyIncludesSelf = function (getUsages, type, id)
            {
                const usages = Array.isArray(getUsages) ? getUsages : getUsages(null);
                for (const node of usages)
                {
                    if (node.inUseFromOtherObjects(type, id))
                    {
                        return false;
                    }
                }
                return true;
            };

            const contentNode = function (container, node)
            {
                const section = $("<ul/>");
                container.append(section);

                for (const childNode of node.children)
                {
                    const item = $("<li/>").addClass("fieldSearchFolder");
                    const svgName = childNode.used ? "ArrowRight" : "OpenFolderAlt";
                    const svg = skyward.svg.getSvg(svgName, "TreeBrowse");
                    item.append(svg);
                    item.append($("<span/>").text(childNode.text));
                    section.append(item);
                    if (childNode.children.length > 0)
                    {
                        contentNode(item, childNode);
                    }
                }
            };

            const SourceTypes =
            {
                Section: "section",
                Line: "line",
                Column: "column",
                CalculatedField: "calculatedField",
                Sort: "sort"
            };

            const SourceDetail = (function ()
            {
                /** @class */
                function SourceDetail(type, id, element)
                {
                    this.type = type;
                    this.id = id;
                    this.element = element;
                }

                SourceDetail.prototype.areEqual = function (type, id)
                {
                    return this.type === type && this.id === id;
                };

                return SourceDetail;
            })();

            const UsageNode = (function ()
            {
                /** @class */
                function UsageNode(text, sourceDetail = null, used = false, required = false)
                {
                    this.text = text;
                    this.sourceDetail = sourceDetail;
                    this.used = used;
                    this.required = required;
                    this.children = [];
                }

                UsageNode.prototype.addChild = function (child)
                {
                    this.children.push(child);
                };

                UsageNode.prototype.inUse = function ()
                {
                    return this.hasNode((node) => node.used);
                };

                UsageNode.prototype.inUseFromOtherObjects = function (type, id)
                {
                    return this.hasNode((node) =>
                    {
                        if (node.used && node.sourceDetail && node.sourceDetail.areEqual(type, id))
                        {
                            return false;
                        }

                        return node.used;
                    });
                };

                UsageNode.prototype.isRequired = function ()
                {
                    return this.hasNode((node) => node.required);
                };

                UsageNode.prototype.hasNode = function (predicate)
                {
                    for (let node of this.allNodes())
                    {
                        if (!predicate || predicate(node))
                        {
                            return true;
                        }
                    }
                    return false;
                };

                UsageNode.prototype.allNodes = function* ()
                {
                    yield this;
                    for (let node of this.children)
                    {
                        yield* node.allNodes();
                    }
                };

                UsageNode.defaultPredicate = function (node)
                {
                    return node.inUse();
                };

                return UsageNode;
            })();

            const HiddenConditionUsageNode = (function ()
            {
                /**
                 * @constructor
                 * @extends UsageNode
                 */
                function HiddenConditionUsageNode(sourceDetail = null, used = true, required = true)
                {
                    UsageNode.call(this, "Hidden condition", sourceDetail, used, required);
                }

                HiddenConditionUsageNode.prototype = Object.create(UsageNode.prototype);
                HiddenConditionUsageNode.prototype.constructor = HiddenConditionUsageNode;

                return HiddenConditionUsageNode;
            })();

            /**
             * Base class for searching a report for the specified identifier.
             */
            const PlacesToSearch = (function ()
            {
                /**
                 * @constructor
                 * @abstract
                 * @param {Object} model
                 * @param {string?} prefix - A prefix to use for the identifier field, e.g. "_prompt", "_column"
                 * @param {string?} filter - A jQuery selector to filter DOM elements. i.e. only show usages in the selected section
                 */
                function PlacesToSearch(model, prefix = null, filter = null)
                {
                    this.model = model;
                    this.prefix = prefix;
                    this.identifierField = null;
                    this.filter = filter;
                    this.usageRoots = {};
                    this.setIdentifierField();
                }

                /** @abstract */
                PlacesToSearch.prototype.setIdentifierField = function () { };

                PlacesToSearch.prototype.traverse = function (promptsModel, predicate = UsageNode.defaultPredicate)
                {
                    const usages = [];
                    for (const usageRoot of Object.values(this.usageRoots))
                    {
                        const usageNode = usageRoot(promptsModel, predicate);
                        if (usageNode instanceof UsageNode && predicate(usageNode))
                        {
                            usages.push(usageNode);
                        }
                    }
                    return usages;
                };

                /** @abstract */
                PlacesToSearch.prototype.traverseSectionAction = function (section, parentNode, predicate) { };

                PlacesToSearch.prototype.traverseSection = function (section, parentNode, predicate)
                {
                    const self = this;
                    const sectionData = getSectionData(section);
                    const sectionNode = new UsageNode(`'${section.data("displayName")}' Section`, new SourceDetail(SourceTypes.Section, sectionData.sectionId, section));

                    if (!this.filter || section.has(this.filter).length > 0)
                    {
                        this.traverseSectionAction(section, sectionNode, predicate);

                        if (this.usedInHiddenCondition(sectionData.isHiddenCondition))
                        {
                            sectionNode.addChild(new HiddenConditionUsageNode(new SourceDetail(SourceTypes.Section, sectionData.sectionId, section)));
                        }

                        this.traverseSorts(section, sectionNode);
                        this.traverseFilter(section, sectionNode, predicate);
                        this.traverseCalculatedFields(section, sectionNode, predicate);
                        this.traverseReportLines(section, sectionNode, predicate);
                    }

                    let detailSections = getDetailSections(section);
                    if (detailSections.length > 0 && this.filter)
                    {
                        detailSections = detailSections.filter((i, s) => $(s).has(this.filter).length > 0);
                    }

                    detailSections.each(function ()
                    {
                        self.traverseSection($(this), sectionNode, predicate);
                    });

                    if (predicate(sectionNode))
                    {
                        parentNode.addChild(sectionNode);
                    }
                };

                /** @abstract */
                PlacesToSearch.prototype.traverseReportLinesAction = function (section, line, parentNode, predicate) { };

                PlacesToSearch.prototype.traverseReportLines = function (section, parentNode, predicate)
                {
                    const self = this;
                    getSectionNonPreviewLines(section).each(function ()
                    {
                        const line = $(this);
                        const lineData = line.data();
                        const lineNode = new UsageNode(`'${lineData.reportLabelText}' Line`, new SourceDetail(SourceTypes.Line, lineData.lineId, line));

                        self.traverseReportLinesAction(section, line, lineNode, predicate);

                        if (self.usedInHiddenCondition(lineData.isHiddenCondition))
                        {
                            lineNode.addChild(new HiddenConditionUsageNode(new SourceDetail(SourceTypes.Line, lineData.lineId, line)));
                        }

                        self.traverseReportColumns(line, lineNode, predicate);
                        if (predicate(lineNode))
                        {
                            parentNode.addChild(lineNode);
                        }
                    });
                };

                /** @abstract */
                PlacesToSearch.prototype.traverseReportColumnsAction = function (line, column, parentNode, predicate) { };

                PlacesToSearch.prototype.traverseReportColumns = function (line, parentNode, predicate)
                {
                    const self = this;
                    line.find(".reportColumn").each(function ()
                    {
                        const column = $(this);
                        const columnData = column.data();
                        const columnNode = new UsageNode(`'${columnData.designerText}' Column`, new SourceDetail(SourceTypes.Column, columnData.columnId, column));
                        const calculatedField = columnData.operationId ? CalculatedFields_Model.Get(columnData.operationId) : null;

                        if (self.usedInHiddenCondition(columnData.isHiddenCondition))
                        {
                            columnNode.addChild(new HiddenConditionUsageNode(new SourceDetail(SourceTypes.Column, columnData.columnId, column)));
                        }

                        if (calculatedField && self.includesBindingString(calculatedField.expression))
                        {
                            columnNode.addChild(new UsageNode("Calculated Field", new SourceDetail(SourceTypes.CalculatedField, calculatedField.id, column), true));
                        }

                        if (columnData.columnFilterJson && self.includesBindingString(columnData.columnFilterJson))
                        {
                            columnNode.addChild(new UsageNode("Group Column Filter", new SourceDetail(SourceTypes.Column, columnData.columnId, column), true));
                        }

                        self.traverseReportColumnsAction(line, column, columnNode, predicate);

                        const fieldsToSearch = [columnData.absoluteFieldPath, columnData.fieldName, columnData.designerText];
                        const isFieldInUse = fieldsToSearch.some(field => self.includesBindingString(field));

                        if (isFieldInUse)
                        {
                            columnNode.used = true;
                        }

                        if (predicate(columnNode))
                        {
                            parentNode.addChild(columnNode);
                        }
                    });
                };

                /** @abstract */
                PlacesToSearch.prototype.traverseSorts = function (section, parentNode) { };

                PlacesToSearch.prototype.traverseFilter = function (section, parentNode, predicate)
                {
                    const filter = section.find(".filterViewValue:first-child");
                    const filterCondition = filter.data("filterCondition");
                    const filterNode = new UsageNode("Filter");

                    this.traverseSearchConditionGroup(filterCondition, filterNode, predicate);

                    if (predicate(filterNode))
                    {
                        parentNode.addChild(filterNode);
                    }
                };

                PlacesToSearch.prototype.traverseSearchConditionGroup = function (condition, parentNode, predicate, parentRelationshipNameDisplay = "")
                {
                    const filterJson = JSON.stringify(condition);
                    let newNode = null;

                    let identifier = this.identifierField.toString();
                    if (parentRelationshipNameDisplay && identifier?.indexOf(parentRelationshipNameDisplay) === 0)
                    {
                        identifier = identifier.substring(parentRelationshipNameDisplay.length + 1);
                    }

                    if (condition
                        && Object.prototype.hasOwnProperty.call(condition, "SearchCondition")
                        && Object.prototype.hasOwnProperty.call(condition, "@Type")
                        && condition["@Type"] === "SearchConditionGroup")
                    {
                        if (Array.isArray(condition.SearchCondition))
                        {
                            for (const innerCondition of condition.SearchCondition)
                            {
                                this.traverseSearchConditionGroup(innerCondition, parentNode, predicate);
                            }
                        }
                        else
                        {
                            this.traverseSearchConditionGroup(condition.SearchCondition, parentNode, predicate);
                        }
                    }
                    else if (condition
                        && Object.prototype.hasOwnProperty.call(condition, "SearchCondition")
                        && Object.prototype.hasOwnProperty.call(condition, "@Type")
                        && condition["@Type"] === "ExistsSearchCondition")
                    {
                        newNode = new UsageNode(`Exists (${condition["@RelationshipNameDisplay"]})`);
                        this.traverseSearchConditionGroup(condition.SearchCondition, newNode, predicate, condition["@RelationshipNameDisplay"]);
                    }
                    else if (this.includesBindingString(filterJson, identifier)
                        || ((!Object.prototype.hasOwnProperty.call(this.model, "InputType")
                            || this.model.InputType === "standardFilter") && this.includesReferenceString(filterJson, identifier)))
                    {
                        const searchCondition = skyward.searchCondition.getFromJson(condition);
                        newNode = new UsageNode(searchCondition.toFriendlyQuery(), undefined, true);
                    }

                    if (newNode && predicate(newNode))
                    {
                        parentNode.addChild(newNode);
                    }
                };

                PlacesToSearch.prototype.traverseCalculatedFields = function (section, parentNode, predicate)
                {
                    const self = this;
                    const calcFieldsNode = new UsageNode("Calculated Fields");

                    getSectionAllCalculatedFieldIDs(section).forEach(function (calculatedFieldID)
                    {
                        const calcField = CalculatedFields_Model.Get(calculatedFieldID);
                        if (calcField && self.includesBindingString(calcField.expression))
                        {
                            calcFieldsNode.addChild(new UsageNode(calcField.fieldName, new SourceDetail(SourceTypes.CalculatedField, calcField.id, section), true, true));
                        }
                    });

                    if (predicate(calcFieldsNode))
                    {
                        parentNode.addChild(calcFieldsNode);
                    }
                };

                /** @abstract */
                PlacesToSearch.prototype.usedInHiddenCondition = function (isHiddenCondition) { return false; };

                PlacesToSearch.prototype.stringMatchesIdentifier = function (stringToSearch, searchForOverride = null)
                {
                    const searchFor = searchForOverride ?? this.identifierField;
                    return !!searchFor && searchFor === stringToSearch;
                };

                /**
                 * Converts the objectToSearch to a string and checks if it includes the formatted string.
                 * @param {object} objectToSearch The object to be stringified and searched.
                 * @param {string?} searchForOverride An optional override for the identifier field to search for. If omitted, the identifierField will be used.
                 * @param {(text: string) => string} formatter A function that formats the identifier field for searching.
                 */
                PlacesToSearch.prototype.includesFormattedString = function (objectToSearch, searchForOverride, formatter)
                {
                    const searchFor = searchForOverride ?? this.identifierField;
                    const stringToSearch = typeof objectToSearch === "string" ? objectToSearch : JSON.stringify(objectToSearch)
                    const formattedSearchFor = formatter(searchFor);
                    return !!searchFor && !!stringToSearch && (stringToSearch.includes(formattedSearchFor) || this.stringMatchesIdentifier(stringToSearch, searchFor));
                };

                PlacesToSearch.prototype.includesBindingString = function (objectToSearch, searchForOverride = null)
                {
                    const prefix = this.prefix ? `${this.prefix}.` : "";
                    return this.includesFormattedString(objectToSearch, searchForOverride, t => `{${prefix}${t}}`);
                };

                PlacesToSearch.prototype.includesReferenceString = function (objectToSearch, searchForOverride = null)
                {
                    return this.includesFormattedString(objectToSearch, searchForOverride, t => `"${t}"`);
                };

                PlacesToSearch.prototype.setModel = function (model)
                {
                    this.model = model;
                    this.setIdentifierField();
                };

                PlacesToSearch.prototype.isInUse = function ()
                {
                    return this.traverse().some(node => node.inUse());
                };

                PlacesToSearch.prototype.getExposedMethods = function ()
                {
                    return {
                        setModel: this.setModel.bind(this),
                        traverse: this.traverse.bind(this),
                        isInUse: this.isInUse.bind(this),
                    };
                };

                return PlacesToSearch;
            })();

            /**
             * Search a report for the specified prompt using PromptUniqueId
             */
            const PromptPlacesToSearch = (function ()
            {
                /**
                 * @constructor
                 * @extends PlacesToSearch
                 */
                function PromptPlacesToSearch(promptModel)
                {
                    PlacesToSearch.call(this, promptModel, "_prompt");
                    this.usageRoots =
                    {
                        sections: this.usageRootSections.bind(this),
                        searchParameters: this.usageRootSearchParameters.bind(this),
                        prompts: this.usageRootPrompts.bind(this)
                    };
                }

                PromptPlacesToSearch.prototype = Object.create(PlacesToSearch.prototype);
                PromptPlacesToSearch.prototype.constructor = PromptPlacesToSearch;

                /** @override */
                PromptPlacesToSearch.prototype.setIdentifierField = function () { this.identifierField = this.model?.PromptUniqueId; };

                PromptPlacesToSearch.prototype.usageRootSections = function (model, predicate)
                {
                    const self = this;
                    const usageNode = new UsageNode("Sections");

                    designer.find(".jsHeaderSection").each(function ()
                    {
                        self.traverseHeaderFooter($(this), usageNode, predicate);
                    });

                    designer.find(".jsMainSection").each(function ()
                    {
                        self.traverseSection($(this), usageNode, predicate);
                    });

                    designer.find(".jsFooterSection").each(function ()
                    {
                        self.traverseHeaderFooter($(this), usageNode, predicate);
                    });

                    return usageNode;
                };

                PromptPlacesToSearch.prototype.usageRootSearchParameters = function (model, predicate)
                {
                    const self = this;
                    const usages = new UsageNode("Search Parameters");
                    skyward.reporting.searchParameters.model.forEach(function (searchParameter)
                    {
                        if (self.includesBindingString(searchParameter.value))
                        {
                            usages.addChild(new UsageNode(searchParameter.key, undefined, true, searchParameter.required));
                        }
                    });
                    return usages;
                };

                PromptPlacesToSearch.prototype.usageRootPrompts = function (model, predicate)
                {
                    const self = this;
                    const usages = new UsageNode("Prompts");
                    if (model)
                    {
                        model.forEach(function (otherPrompt)
                        {
                            if (otherPrompt !== self.model && otherPrompt.usesPrompt(self.model))
                            {
                                usages.addChild(new UsageNode(otherPrompt.Label, undefined, true));
                            }
                        });
                    }
                    return usages;
                };

                /** @override */
                PromptPlacesToSearch.prototype.usedInHiddenCondition = function (isHiddenCondition)
                {
                    return !!isHiddenCondition && (
                        this.stringMatchesIdentifier(isHiddenCondition.LeftOperand?.PromptUniqueID) ||
                        this.stringMatchesIdentifier(isHiddenCondition.RightOperand?.PromptUniqueID));
                };

                PromptPlacesToSearch.prototype.traverseHeaderFooter = function (section, parentNode, predicate)
                {
                    const sectionToolbar = section.children(".sectionToolbar");
                    const sectionName = sectionToolbar.data().reportLabelText;
                    const sectionData = getSectionData(section);
                    const newNode = new UsageNode(sectionName, new SourceDetail(SourceTypes.Section, sectionData.sectionId, section));

                    this.traverseReportLines(section, newNode, predicate);

                    if (predicate(newNode))
                    {
                        parentNode.addChild(newNode);
                    }
                };

                /** @override */
                PromptPlacesToSearch.prototype.traverseSectionAction = function (section, parentNode, predicate)
                {
                    const sectionData = getSectionData(section);

                    if (sectionData && sectionData.promptForSortOrder === true && sectionData.sortPromptGuid === this.model.PromptGuid)
                    {
                        parentNode.addChild(new UsageNode("Sort Order Prompt", new SourceDetail(SourceTypes.Section, sectionData.sectionId, section), true, true));
                    }
                };

                /** @override */
                PromptPlacesToSearch.prototype.traverseReportLinesAction = function (section, line, parentNode, predicate)
                {
                    const lineData = line.data();

                    if (lineData.relatedReportSortId)
                    {
                        const sortData = getSortDataByReportSortID(section, lineData.relatedReportSortId);
                        if (this.stringMatchesIdentifier(sortData.PageBreakPromptUniqueId))
                        {
                            const name = sortData.RelativeDataPath ?? sortData.FieldName;
                            parentNode.addChild(new UsageNode(`Related Sort '${name}' page break condition`, new SourceDetail(SourceTypes.Sort, sortData.ReportSortID, line), true, true));
                        }
                    }

                    if (this.stringMatchesIdentifier(lineData.printAtBottomOfPagePromptUniqueId))
                    {
                        parentNode.addChild(new UsageNode("Print at bottom of page condition", new SourceDetail(SourceTypes.Line, lineData.lineId, line), true, true));
                    }

                    if (this.stringMatchesIdentifier(lineData.printReportFooterOnNewPagePromptUniqueId))
                    {
                        parentNode.addChild(new UsageNode("Print on new page condition", new SourceDetail(SourceTypes.Line, lineData.lineId, line), true, true));
                    }

                    if (this.stringMatchesIdentifier(lineData.repeatLineAfterStartPromptUniqueId))
                    {
                        parentNode.addChild(new UsageNode("Repeat line at top condition", new SourceDetail(SourceTypes.Line, lineData.lineId, line), true, true));
                    }
                };

                /** @override */
                PromptPlacesToSearch.prototype.traverseSorts = function (section, parentNode)
                {
                    for (const sort of getSectionSorts(section))
                    {
                        if (this.stringMatchesIdentifier(sort.PageBreakPromptUniqueId))
                        {
                            const name = sort.DisplayName ?? sort.RelativeDataPath ?? sort.FieldName;
                            parentNode.addChild(new UsageNode(`Sort '${name}' page break condition`, new SourceDetail(SourceTypes.Sort, sort.ReportSortID, section), true));
                        }
                    }
                };

                return PromptPlacesToSearch;
            })();

            /**
             * Search a report for the specified field using AbsoluteFieldPath
             */
            const FieldPlacesToSearch = (function ()
            {
                /**
                 * @constructor
                 * @param {Object} fieldModel - The field model to search for
                 * @param {string} [selectedSectionId] - The ID of the selected section. Only fields in this section will be searched. If omitted, no section filtering is applied.
                 * @extends PlacesToSearch
                 */
                function FieldPlacesToSearch(fieldModel, selectedSectionId = null)
                {
                    PlacesToSearch.call(this, fieldModel, null, selectedSectionId ? `[data-section-id='${selectedSectionId}']` : null);
                    this.usageRoots =
                    {
                        sections: this.usageRootSections.bind(this)
                    };
                }

                FieldPlacesToSearch.prototype = Object.create(PlacesToSearch.prototype);
                FieldPlacesToSearch.prototype.constructor = FieldPlacesToSearch;

                /** @override */
                FieldPlacesToSearch.prototype.setIdentifierField = function () { this.identifierField = this.model?.absoluteFieldPath; };

                FieldPlacesToSearch.prototype.usageRootSections = function (objectModel, predicate)
                {
                    const self = this;
                    const usages = new UsageNode("Sections");

                    let sections = designer.find(".jsMainSection");

                    if (this.filter)
                    {
                        sections = sections.filter((i, s) => $(s).has(this.filter).length > 0);
                    }

                    sections.each(function ()
                    {
                        self.traverseSection($(this), usages, predicate);
                    });

                    return usages;
                };

                /** @override */
                FieldPlacesToSearch.prototype.traverseSorts = function (section, node)
                {
                    for (const sort of getSectionSorts(section))
                    {
                        if (this.stringMatchesIdentifier(sort.AbsoluteFieldPath))
                        {
                            const name = sort.DisplayName ?? sort.RelativeDataPath ?? sort.FieldName;
                            node.addChild(new UsageNode(`Sort '${name}'`, new SourceDetail(SourceTypes.Sort, sort.ReportSortID, section), true));
                        }
                    }
                };

                /** @override */
                FieldPlacesToSearch.prototype.usedInHiddenCondition = function (isHiddenCondition)
                {
                    return !!isHiddenCondition && (
                        this.stringMatchesIdentifier(isHiddenCondition.LeftOperand?.FieldPath) ||
                        this.stringMatchesIdentifier(isHiddenCondition.RightOperand?.FieldPath));
                };

                return FieldPlacesToSearch;
            })();

            /**
             * Search a report for the specified variable using UniqueId
             */
            const VariablePlacesToSearch = (function ()
            {
                /**
                 * @constructor
                 * @extends PlacesToSearch
                 */
                function VariablePlacesToSearch(variableModel)
                {
                    PlacesToSearch.call(this, variableModel, "_variable");
                    this.usageRoots = {
                        sections: this.usageRootSections.bind(this),
                    };
                }

                VariablePlacesToSearch.prototype = Object.create(PlacesToSearch.prototype);
                VariablePlacesToSearch.prototype.constructor = VariablePlacesToSearch;

                /** @override */
                VariablePlacesToSearch.prototype.setIdentifierField = function () { this.identifierField = this.model?.uniqueId; };

                VariablePlacesToSearch.prototype.usageRootSections = function (objectModel, predicate)
                {
                    const self = this;
                    const usages = new UsageNode("Sections");

                    designer.find(".jsMainSection").each(function ()
                    {
                        self.traverseSection($(this), usages, predicate);
                    });

                    return usages;
                };

                VariablePlacesToSearch.prototype.includesVariableString = function (objectToSearch)
                {
                    return this.includesFormattedString(objectToSearch, this.model.name, t => `Variable: ${t}`);
                };

                /** @override */
                VariablePlacesToSearch.prototype.traverseReportLinesAction = function (section, line, parentNode, predicate)
                {
                    const lineData = line.data();

                    if (this.includesReferenceString(lineData.variableIdsToReset))
                    {
                        parentNode.addChild(new UsageNode("Variable to reset", new SourceDetail(SourceTypes.Line, lineData.lineId, line), true));
                    }
                };

                /** @override */
                VariablePlacesToSearch.prototype.traverseReportColumnsAction = function (line, column, parentNode, predicate)
                {
                    const columnData = column.data();

                    if (this.stringMatchesIdentifier(columnData.variableIdToGet))
                    {
                        parentNode.used = true;
                    }

                    if (this.stringMatchesIdentifier(columnData.variableIdToSet))
                    {
                        parentNode.addChild(new UsageNode("Variable to set", new SourceDetail(SourceTypes.Column, columnData.columnId, column), true));
                    }

                    if (this.includesVariableString(columnData.displayCalculation))
                    {
                        parentNode.addChild(new UsageNode("Aggregate calculation", new SourceDetail(SourceTypes.Column, columnData.columnId, column), true));
                    }
                }

                return VariablePlacesToSearch;
            })();

            /**
             * Search a report for the specified column using ColumnId
             */
            const ColumnPlacesToSearch = (function ()
            {
                /**
                 * @constructor
                 * @extends PlacesToSearch
                 */
                function ColumnPlacesToSearch(columnModel)
                {
                    PlacesToSearch.call(this, columnModel, "_column");
                    this.usageRoots =
                    {
                        sections: this.usageRootSections.bind(this),
                    };
                }

                ColumnPlacesToSearch.prototype = Object.create(PlacesToSearch.prototype);
                ColumnPlacesToSearch.prototype.constructor = ColumnPlacesToSearch;

                /** @override */
                ColumnPlacesToSearch.prototype.setIdentifierField = function () { this.identifierField = this.model?.columnId; };

                ColumnPlacesToSearch.prototype.usageRootSections = function (objectModel, predicate)
                {
                    const self = this;
                    const usages = new UsageNode("Sections");

                    designer.find(".jsMainSection").each(function ()
                    {
                        self.traverseSection($(this), usages, predicate);
                    });

                    return usages;
                };

                /** @override */
                ColumnPlacesToSearch.prototype.usedInHiddenCondition = function (isHiddenCondition)
                {
                    return !!isHiddenCondition && (
                        this.stringMatchesIdentifier(isHiddenCondition.LeftOperand?.ColumnID) ||
                        this.stringMatchesIdentifier(isHiddenCondition.RightOperand?.ColumnID));
                };

                return ColumnPlacesToSearch;
            })();

            /**
             * Simple search for aggregate column usages on the same line
             */
            const SimpleAggregateColumnPlacesToSearch = (function ()
            {
                /**
                 * @constructor
                 * @extends PlacesToSearch
                 */
                function SimpleAggregateColumnPlacesToSearch(column)
                {
                    PlacesToSearch.call(this, column, "_column", column);
                    this.usageRoots =
                    {
                        sections: this.usageRootSections.bind(this),
                    };
                }

                SimpleAggregateColumnPlacesToSearch.prototype = Object.create(PlacesToSearch.prototype);
                SimpleAggregateColumnPlacesToSearch.prototype.constructor = SimpleAggregateColumnPlacesToSearch;

                /** @override */
                SimpleAggregateColumnPlacesToSearch.prototype.setIdentifierField = function () { this.identifierField = this.model?.data().columnId; };

                SimpleAggregateColumnPlacesToSearch.prototype.usageRootSections = function (objectModel, predicate)
                {
                    const self = this;
                    const usages = new UsageNode("Sections");

                    designer.find(".jsMainSection").each(function ()
                    {
                        self.traverseSection($(this), usages, predicate);
                    });
                    return usages;
                };

                /** @override */
                SimpleAggregateColumnPlacesToSearch.prototype.traverseFilter = function (section, parentNode, predicate) { };

                /** @override */
                SimpleAggregateColumnPlacesToSearch.prototype.traverseCalculatedFields = function (section, parentNode, predicate) { };

                /** @override */
                SimpleAggregateColumnPlacesToSearch.prototype.usedInHiddenCondition = function (isHiddenCondition)
                {
                    return !!isHiddenCondition && (
                        this.stringMatchesIdentifier(isHiddenCondition.LeftOperand?.ColumnID) ||
                        this.stringMatchesIdentifier(isHiddenCondition.RightOperand?.ColumnID));
                };

                /** @override */
                SimpleAggregateColumnPlacesToSearch.prototype.includesBindingString = function (objectToSearch, searchForOverride = null)
                {
                    return false;
                };

                return SimpleAggregateColumnPlacesToSearch;
            })();

            const promptPlacesToSearch = function (promptModel)
            {
                return new PromptPlacesToSearch(promptModel).getExposedMethods();
            };

            const fieldPlacesToSearch = function (fieldModel, selectedSectionId)
            {
                return new FieldPlacesToSearch(fieldModel, selectedSectionId).getExposedMethods();
            };

            const variablePlacesToSearch = function (variableModel)
            {
                return new VariablePlacesToSearch(variableModel).getExposedMethods();
            };

            const columnPlacesToSearch = function (columnModel)
            {
                return new ColumnPlacesToSearch(columnModel).getExposedMethods();
            };

            const simpleAggregateColumnPlacesToSearch = function (column)
            {
                return new SimpleAggregateColumnPlacesToSearch(column).getExposedMethods();
            };

            return { openModal, buildUsageLocationsElement, usageLocationOnlyIncludesSelf, promptPlacesToSearch, fieldPlacesToSearch, variablePlacesToSearch, columnPlacesToSearch, simpleAggregateColumnPlacesToSearch, SourceTypes, HiddenConditionUsageNode };
        })();

        var initializeVariables = function (variableData, viewTarget, canvas)
        {
            var variables = skyward.reporting.variables.init(variableData, viewTarget, canvas);
            var variablesToReset = {};

            var draggableOptions = {
                cursorAt: { top: 0, left: 0 },
                delay: 150,
                scope: 'designer',
                helper: function ()
                {
                    var item = $(this);
                    return $("<div/>")
                        .css({ "z-index": 999, "display": undefined, "background-color": "white", "border": "2px dotted", "width": "auto", "height": (DEFAULT_COLUMN_HEIGHT + "px") })
                        .data(item.data())
                        .append(item.find(".js-name").clone());
                },
                appendTo: $(document.body),
                grid: [1, 1],
                opacity: 0.8
            };
            var setupReportDesignerForVariables = function ()
            {
                variables.viewModel.view.find(".variable").draggable(draggableOptions);
                // TODO Would be nice to find a clean way to populate this in the viewEngine
                variables.collectionModel.forEach(function (v)
                {
                    var columns = v.gotBy();
                    var labelText = "Variable: " + v.name;
                    columns.data({
                        fieldType: v.dataTypeCategoryName,
                        designerText: labelText,
                    })
                        .attr({
                            "data-field-type": v.dataTypeCategoryName,
                            "data-designer-text": labelText,
                        })
                        .find(".variableLabel").text(labelText);
                });
            };
            var confirmVariableRemoved = function (model)
            {
                if (model.isInUse())
                {
                    var removeReferences = function ()
                    {
                        model.requiredBy().remove();
                        var setBy = model.setBy();
                        setBy.data("variableIdToSet", "");
                        if (setBy.is(".ui-selected"))
                        {
                            setColumnVariablePropertiesValues(); // passing no parameters clears the value
                        }
                        $.each(model.resetBy(), function (i, row)
                        {
                            var variableIds = $(row).data().variableIdsToReset;
                            variableIds.splice(variableIds.indexOf(model.uniqueId), 1);
                        });
                    };
                    var undoRemoval = function ()
                    {
                        variables.collectionModel.add(model);
                    };

                    skyward.modalLayer.confirmDelete("Variable", "This Variable is currently in use.")
                        .done(removeReferences)
                        .fail(undoRemoval)
                        .always(updateHiddenField);
                }
                else
                {
                    updateHiddenField();
                }

                skyward.reporting.updateVariableUsageLocations();
            };
            var variableNameChanged = function (model)
            {
                $.each(model.gotBy(), function ()
                {
                    var jqThis = $(this);
                    jqThis.find('.reportLineLabel').text(getColumnLabelWithPreText(jqThis));
                });
                // setting variables is not supported by multiple selected columns
                if (currentSelectedColumns.areColumnsSelected() && model.uniqueId && currentSelectedColumns.getFirstSelectedColumn().data("variableIdToSet") == model.uniqueId)
                {
                    setColumnVariablePropertiesValues(model);
                }
            };
            var variableViewModelAdded = function (viewModel)
            {
                viewModel.view.draggable(draggableOptions);
            };
            var insertVariableColumn = skyward.reporting.insertVariableColumn = function (model, view, position, insertedLine)
            {
                insertedLine = insertedLine ?? currentRow ?? currentSelectedColumns.getRowByFirstSelectedColumn();
                if (!insertedLine)
                {
                    return;
                }

                insertColumnHelperFunction(
                    {
                        newItem: createNewColumn(
                            {
                                type: COLUMN_TYPES.VARIABLE_COLUMN,
                                relatedItem: view,
                                dataTypeCategoryName: model.dataTypeCategoryName,
                                variableIdToGet: model.uniqueId,
                                variableBinding: model.getBindingString(),
                                text: "Variable: " + model.name
                            }),
                        reportLine: insertedLine,
                        section: (insertedLine).closest(".sectionSelected"),
                        position: position || { top: null, left: null },
                        autoCreatedColumn: true
                    });
            };
            skyward.reporting.selectVariableToSet = function (button)
            {
                // not supported when multiple columns are selected
                const currentSelectedColumn = currentSelectedColumns.getFirstSelectedColumn();
                var data = $(currentSelectedColumn).data();
                var fieldType = skyward.reporting.reportColumn.getFieldType(data)
                    || data.columnFieldType
                    || (variables.collectionModel.getById(data.variableIdToGet) || {}).dataTypeCategoryName
                    || "";

                if (data.fieldColumnType === COLUMN_TYPES.CALCULATED_COLUMN)
                {
                    fieldType = DATA_TYPES.NUMBER_DECIMAL;
                }
                else if (fieldType?.startsWith("Number") && fieldType !== DATA_TYPES.NUMBER_DECIMAL)
                {
                    fieldType = DATA_TYPES.NUMBER_LONG;
                }
                else if (!fieldType)
                {
                    fieldType = DATA_TYPES.STRING;
                }
                let allowAddNewVar = data.fieldColumnType !== COLUMN_TYPES.GROUP_COLUMN && !fieldType?.startsWith("String Enum");
                variables.viewModel.selectVariableToSet(fieldType, button, currentSelectedColumn.text(), allowAddNewVar)
                    .done(function (variableModel)
                    {
                        if (!variableModel)
                        {
                            return;
                        }
                        currentSelectedColumn.data("variableIdToSet", variableModel.uniqueId).attr("data-variable-id-to-set", variableModel.uniqueId);
                        setColumnVariablePropertiesValues(variableModel);
                        editorVariableToSet.trigger('change');
                    });
            };

            skyward.reporting.selectVariablesToReset = function (variableIDsToReset)
            {
                variablesToReset = variables.viewModel.selectVariablesToReset(editorVariablesToReset, variableIDsToReset, variablesToReset);
            };

            skyward.reporting.getVariableIDsToReset = function ()
            {
                var variableIDsToReset = [];
                var selectedVariablesToReset = variablesToReset.filter(function (value) { return value.checked === true; });
                selectedVariablesToReset.forEach(function (value)
                {
                    variableIDsToReset.push(value.model.uniqueId);
                });
                return variableIDsToReset;
            };

            const openVariableUsageLocationsModal = function (model)
            {
                const placesToSearch = usageLocations.variablePlacesToSearch(model);
                usageLocations.openModal("variable-usages-dialog", placesToSearch.traverse);
            };

            skyward.reporting.updateVariableUsageLocations = function ()
            {
                variables.viewModel.updateVariableUsageLocations();
            };

            _reportDesignerObserver.observe(variables.viewModel, "insertRequested", insertVariableColumn);
            _reportDesignerObserver.observe(variables.viewModel, "add", variableViewModelAdded);
            _reportDesignerObserver.observe(variables.collectionModel, "model:name", variableNameChanged);
            _reportDesignerObserver.observe(variables.collectionModel, "add", updateHiddenField);
            _reportDesignerObserver.observe(variables.collectionModel, "remove", confirmVariableRemoved);
            _reportDesignerObserver.observe(variables.collectionModel, "contentChanged", updateHiddenField);
            _reportDesignerObserver.observe(variables.viewModel, "openUsageLocations", openVariableUsageLocationsModal);
            setupReportDesignerForVariables();
        };

        var updateDesignerSidebarCalculatedFieldList = function (sectionData)
        {
            skyward.calculatedFieldList.updateCalculatedFieldList(
                skyward.treebrowse.getTreeBrowseDataByName("reportObjectBrowser"),
                Object.values(CalculatedFields_Model.AllFields()),
                "#" + $(".calculatedFieldListToolbar").next().addIds().attr("id"),
                sectionData.rootModule,
                sectionData.rootObject,
                sectionData.entireRelationshipPath,
                undefined
            );
        };

        function showLegend()
        {
            let info = $.ajax(skyward.global.virtualDir + "Reporting/Report/Legend",
                {
                    data:
                    {
                        renderType: "contentOnly"
                    }
                });
            skyward.modalLayer.showInformationBubble("Legend", info, [], { anchorTo: this });
        }

        let gatherDataForError = function ()
        {
            return {
                reportDefinition: JSON.stringify(skyward.reporting.getDesignerJson())
            };
        };

        // Event handlers
        let events =
        {
            showLegend,
            initCanvas
        };
        skyward.events.registerEvents("reporting", events);

    }(window.skyward = window.skyward || {}, window.jQuery)
);
